/* eslint-disable @typescript-eslint/no-unused-vars */
import {downloadRowsAsExcel, excelFileToNamedSheets} from 'common/HelperFunctions'
import CustomSkeleton, {CARD_PRESET} from 'common/components/CustomSkeleton'
import FilterSelect from 'common/components/FilterSelect'
import ColumnAssignmentModal, {
  type DataUploadSkipAnswers,
} from 'common/components/dataUpload/ColumnAssignmentModal'
import DataUploadReport from 'common/components/dataUpload/DataUploadReport'
import NewPeopleModal from 'common/components/dataUpload/NewPeopleModal'
import NewTeachersModal from 'common/components/dataUpload/NewTeachersModal'
import SheetAssignmentModal from 'common/components/dataUpload/SheetAssignmentModal'
import {Pagination} from 'common/components/grid/Pagination'
import {
  dispatchApi,
  isChunkedUploadAvailable,
  withUploadedFiles,
} from 'common/helpers/chunkedUpload'
import {
  buildCurriculumNameLabeler,
  buildCurriculumPrefixIndex,
  buildDisplayNamePairs,
  displayCurriculumName,
  stripCurriculumPrefix,
  stripCurriculumPrefixesInText,
} from 'common/helpers/curriculumDisplay'
import {
  DATA_UPLOAD_CARRY_TITLE,
  type DataUploadAllocationAssignment,
  type DataUploadAllocationQuestion,
  type DataUploadAssignmentQuestion,
  type DataUploadBranchAssignments,
  type DataUploadBranchQuestion,
  type DataUploadCarriedDrafts,
  type DataUploadClassOption,
  type DataUploadColumnAssignments,
  type DataUploadColumnQuestions,
  type DataUploadConversion,
  type DataUploadDraftAnswers,
  type DataUploadHeldCell,
  type DataUploadNewStudent,
  type DataUploadNewTeacher,
  type DataUploadSheetAssignments,
  type DataUploadSubjectMapping,
  type DataUploadSubjectMappings,
  MIXED_BRANCH,
  allocationAnswerKey,
  alreadyInListSplit,
  buildDataUploadConvertForm,
  dataUploadAcceptAttribute,
  dataUploadCountsSummary,
  dataUploadDraftList,
  dataUploadErrorIsInfo,
  dataUploadErrorMessage,
  dataUploadFileExtension,
  dataUploadHasAllocations,
  dataUploadTemplateToFile,
  firstDataUploadFileError,
  formatDataUploadBytes,
  isSpreadsheetUpload,
  normalizeDataUploadConversion,
  pendingQuestionsText,
  rejectedSubjectAliasCount,
  singleBranchAnswers,
  skippedAllocationAnswers,
  subjectAliasesBody,
  validateDataUploadFile,
  withConfirmedSubjectsApplied,
  withoutAnsweredAllocations,
  withoutAnsweredBranches,
  withoutAnsweredColumns,
  withoutConfirmedSubjects,
} from 'common/helpers/dataUpload'
import {FREEMIUM_TEACHER_LIMIT} from 'common/helpers/freemiumPlan'
import {
  OnboardingBatchEvent,
  trackOnboardingEvent,
  trackOnboardingEvents,
} from 'common/helpers/onboardingEventTracking'
import {
  isDataUploadConversionAvailable,
  useDataUploadConfig,
} from 'common/hooks/useDataUploadConfig'
import {useFreemiumTeacherLimit} from 'common/hooks/useFreemiumTeacherLimit'
import {useSupportUser} from 'common/hooks/useSupportUser'
import {
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  ChevronDown,
  ChevronUp,
  CircleX,
  Download,
  Info,
  Plus,
  Search,
  Trash2,
  Upload,
} from 'lucide-react'
import React, {FC, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState} from 'react'
import {Modal} from 'react-bootstrap'
import {createPortal} from 'react-dom'
import {useIntl} from 'react-intl'
import {useDispatch, useSelector} from 'react-redux'
import Select from 'react-select'
import {toast} from 'react-toastify'
import {RootState} from 'setup'
import {
  convertDataUpload,
  deleteDataUploadDraft,
  getDataUploadDraftTemplate,
  listDataUploadDrafts,
  putDataUploadSubjectAliases,
} from 'setup/redux/Actions/DataUpload/dataUpload'

import {toAbsoluteUrl} from '_metronic/helpers'

import {bulkCsvImport} from '../../../../../../setup/redux/Actions/CsvImport/csvImport'
import {downloadLessonPlanningTemplate} from '../../../../../../setup/redux/Actions/Onboarding/downloadLessonPlanningTemplate'
import {downloadStudentsTemplate} from '../../../../../../setup/redux/Actions/Onboarding/downloadStudentsTemplate'
import {getOnboardingData} from '../../../../../../setup/redux/Actions/Onboarding/getOnboardingData'
import {
  cachedPrefillFrequency,
  loadCurriculumDefaults,
  readStoredCurriculumPrefills,
} from '../curriculumPrefill'
import {
  discardOpenPrefillDrafts,
  saveDraftAnswers,
  useOnboardingPrefillDraft,
} from '../hooks/useOnboardingPrefillDraft'
import {useTeacherAllocationAi} from '../hooks/useTeacherAllocationAi'
import {useVersiAllocation} from '../hooks/useVersiAllocation'
import type {OnboardingPartialSubmit, UpdateDataOptions, WizardData} from '../index'
import {readStoredSchoolSystemDetails} from '../schoolSystemDetailsCache'
import type {LessonPlanningData} from '../types/teacherAllocationAi'
import {
  buildAllocationSlotKey,
  getTeacherFullName,
  parsePositiveNumber,
} from '../utils/allocationIds'
import {allocationServerApi} from '../utils/allocationServerApi'
import {currentClassQuestionOptions} from '../utils/classQuestionOptions'
import {lookupSubjectId} from '../utils/levelCurriculum'
import {queueRemovedSubjects} from '../utils/pendingDeletes'
import {STEP_TITLE_KEYS} from '../utils/stepTitles'
import {unassignSavedTeachers} from '../utils/teacherUnassign'
import {
  STUDENTS_TEMPLATE_LISTS_SHEET,
  buildStudentTemplateListsIndex,
  pickStudentClass,
  resolveCurriculumColumnClass,
} from '../utils/studentClassMatch'
import {
  type ParseStudentsOptions,
  STUDENTS_CURRICULUM_COLUMN_ENABLED,
  STUDENTS_TEMPLATE_SHEET,
  STUDENT_ROW_TRANSIENT_FIELDS,
  STUDENT_SUBJECT_DELIMITER,
  type StudentCsvRow,
  type StudentMissingField,
  type StudentReviewNote,
  parseStudentsRows,
  studentClassChoices,
  studentClassIdFor,
  studentClassIds,
  studentReviewNotes,
  studentRowKey,
} from '../utils/studentsFile'
import {
  ALLOCATION_UPLOADER_IN_CARD,
  TEACHER_ALLOCATION_V2,
  allocationServerEnabled,
  markAllocationServerUnavailable,
} from '../utils/teacherAllocationFlow'
import {
  ALL_FILTER_VALUE,
  type AllocationRow,
  type AllocationStatusFilter,
  allocationExampleFacts,
  branchValues,
  columnRemovalTargets,
  countAllocations,
  levelValues,
  prefillFromAllocationHints,
  resolveFilterValue,
  rowsInScope,
} from '../utils/teacherAllocationModel'
import {
  TeacherEntryRow,
  TeacherFileCounts,
  mergeTeacherEntryRows,
  namesToTeacherRows,
  parseCsvRows,
  withoutAssignments,
  withoutStudents,
} from '../utils/teacherEntry'
import {
  downloadTeacherSampleTemplate,
  downloadTeacherSampleTemplateCsv,
} from '../utils/teacherSampleTemplate'
import AllocationFileImport, {
  type AllocationImportDone,
  carriedAllocationDraftOf,
  openCarriedAllocation,
} from './AllocationFileImport'
import BufferedTextInput from './BufferedTextInput'
import GenericTable, {CheckboxOption, MultiValueContainer} from './GenericTable'
import HorizontalStepper from './HorizontalStepper'
import LessonPlanEditSheet from './LessonPlanEditSheet'
import Step4TeacherEntry from './Step4TeacherEntry'
import TemplateDownloadLink from './TemplateDownloadLink'
import {type UploaderRejectedFile} from './upload/OnboardingUploader'
import Step4TeacherReview from './Step4TeacherReview'
import StudentClassQuestionsModal from './StudentClassQuestionsModal'
import TableBusyOverlay from './TableBusyOverlay'
import TagRemoveIcon from './TagRemoveIcon'
import TeacherAllocationAiPanel from './TeacherAllocationAiPanel'
import UploadStatusPanel, {type UploadStatusItem} from './UploadStatusPanel'
import AllocationConfirmDialog from './teacherAllocation/AllocationConfirmDialog'
import AllocationGrid from './teacherAllocation/AllocationGrid'
import AllocationLanding from './teacherAllocation/AllocationLanding'
import {AllocationFooter} from './teacherAllocation/AllocationShared'
import type {AllocationTeacherOption} from './teacherAllocation/AllocationTeacherSelect'
import CellSettingsPanel from './teacherAllocation/CellSettingsPanel'
import HeldByOtherQuestions from './teacherAllocation/HeldByOtherQuestions'
import ServerTeacherAllocation from './teacherAllocation/ServerTeacherAllocation'
import VersiAllocationPage from './teacherAllocation/VersiAllocationPage'

interface Props {
  data: WizardData
  updateData: (data: Partial<WizardData>, options?: UpdateDataOptions) => void
  onComplete: () => void
  /** Called when the user presses Back on the first tab (exit to the previous wizard step). */
  onBack?: () => void
  onPartialSubmit?: OnboardingPartialSubmit
  isDark: boolean
  isGenerating?: boolean
  /** True while the wizard is still hydrating from the server on resume. Drives skeletons on the
   *  data-heavy classes/review screens so the user doesn't see a flash of empty state. */
  isHydrating?: boolean
  enabledTabs?: Array<'teachers' | 'classes' | 'subjects' | 'lessonPlanning' | 'students'>
  initialTab?: 'teachers' | 'classes' | 'subjects' | 'lessonPlanning' | 'students'
  /** Which screen of the Teachers tab to open: the Add Teachers entry screen or Review Teachers. */
  initialTeachersPhase?: 'entry' | 'review'
  stepperOverride?: {
    steps: Array<{
      id: string
      label: string
      topTabId?: 'teachers' | 'classes' | 'subjects' | 'lessonPlanning' | 'students'
      classesFlowStep?: number
    }>
    activeStepId: string
    onStepClick?: (stepId: string) => void
  }
  /** Reports the active sub-step id as the user navigates (for URL syncing). With a
   *  stepperOverride, reports the matched stepper id (classes/sections/review/subjects);
   *  otherwise the active tab id (teachers/students/lessonPlanning). */
  onActiveSubStepChange?: (subStepId: string) => void
  /** Called only when Add Teachers' Review button opens the Review Teachers screen. */
  onTeacherReview?: () => void
  /** Called when Review Teachers' Confirm and Continue is pressed. */
  onTeacherReviewConfirm?: () => void
  /** Called after the Lesson Planning Excel import lands server-side, so the wizard can
   *  re-fetch onboarding data and show the imported entities in the grids. */
  onExcelImported?: () => void
  /**
   * Set by this step to a function that writes its pending edits into the wizard data now and
   * returns the result — "Save and finish later" saves that, not the debounced copy.
   */
  saveLaterFlushRef?: React.MutableRefObject<(() => WizardData['manualData']) | null>
}

type Step4TabId = 'teachers' | 'classes' | 'subjects' | 'lessonPlanning' | 'students'
type Step4TabItem = {id: Step4TabId; label: string}

const subSteps = [
  {id: 'teachers', labelKey: 'ONBOARDING_WIZARD.STEP4_MANUAL.TABS.TEACHERS'},
  {id: 'classes', labelKey: 'ONBOARDING_WIZARD.STEP4_MANUAL.TABS.CLASSES'},
  {id: 'subjects', labelKey: 'ONBOARDING_WIZARD.STEP4_MANUAL.TABS.SUBJECTS'},
  {id: 'lessonPlanning', labelKey: 'ONBOARDING_WIZARD.STEP4_MANUAL.TABS.LESSON_PLANNING'},
]

const studentSubStep = [{id: 'students', labelKey: 'ONBOARDING_WIZARD.STEP4_MANUAL.TABS.STUDENTS'}]

interface PrefilledClassEntry {
  name: string
  subjects: string[]
}

interface ClassesReviewRow {
  id: string
  curriculum: string
  level: string
  gradeName: string
  sections: string[]
  subjects: string[]
}

interface SubjectLevelRow {
  curriculum: string
  level: string
  key: string
}

const uniqueStrings = (values: string[]) =>
  Array.from(new Set(values.map((v) => v.trim()).filter((v) => v.length > 0)))

/** Joins class name and curriculum in a student Class option shared by two curricula. */
const CLASS_CURRICULUM_SEP = '::curriculum::'

/** Rows named in the students "needs a look" panel before it falls back to "and N more". */
const STUDENT_ISSUE_LINES = 8

/**
 * The students grid's row checkboxes: the browser's own 16px box, as in Review Teachers. The
 * theme's `form-check-input` draws an oversized tick at this size.
 */
const STUDENT_SELECT_CHECKBOX_STYLE: React.CSSProperties = {
  width: 16,
  height: 16,
  margin: 0,
  verticalAlign: 'middle',
  accentColor: 'var(--bs-primary)',
  cursor: 'pointer',
}

/** The questions dialog when no branch is asked about: one list, not a new one per render. */
const NO_BRANCH_QUESTIONS: DataUploadBranchQuestion[] = []
/** The questions dialog when no class is asked about: one list, not a new one per render. */
const NO_ALLOCATION_QUESTIONS: DataUploadAllocationQuestion[] = []

/** A students class question's id: the class as written, and the rows that wrote it. */
const studentsClassQuestionId = (question: {written: string; recordIds: string[]}) =>
  `${question.written}|||${question.recordIds.join(',')}`

/**
 * The answers the Allocation prefill's template is fetched with. They are also kept on its draft
 * (`PUT data-upload/drafts/:id/answers`), so a resume after a logout does not ask them again.
 */
interface AllocationPrefillAnswers {
  branches: DataUploadBranchAssignments
  allocations: DataUploadAllocationAssignment[]
}

/** The answers as the draft keeps them; only the ones that hold something. */
const allocationDraftAnswers = (
  answers: AllocationPrefillAnswers | null | undefined
): DataUploadDraftAnswers | null => {
  if (!answers) return null
  const draftAnswers: DataUploadDraftAnswers = {
    ...(Object.keys(answers.branches).length > 0 ? {branchAssignments: answers.branches} : {}),
    ...(answers.allocations.length > 0 ? {allocationAssignments: answers.allocations} : {}),
  }
  return Object.keys(draftAnswers).length > 0 ? draftAnswers : null
}

/** One answer per teacher + subject: a later list's answer replaces an earlier one's. */
const mergeAllocationAnswers = (
  ...lists: ReadonlyArray<readonly DataUploadAllocationAssignment[]>
): DataUploadAllocationAssignment[] =>
  Array.from(
    new Map(lists.flat().map((answer) => [allocationAnswerKey(answer), answer])).values()
  )

/** The Allocation prefill's open questions, and the answers given so far. */
interface AllocationPrefillQuestions {
  draftId: string
  /** "Which branch?" */
  questions: DataUploadBranchQuestion[]
  /** "Which classes?" */
  allocations: DataUploadAllocationQuestion[]
  answers: AllocationPrefillAnswers
}

/** Teacher + subject pairs a list of allocation questions asks about. */
const allocationPairCount = (questions: readonly DataUploadAllocationQuestion[]) =>
  questions.reduce((sum, question) => sum + question.subjects.length, 0)
/** The new-teachers dialog when it has nobody to ask about: one list, not a new one per render. */
const NO_NEW_TEACHERS: DataUploadNewTeacher[] = []
/** The students dialog when it has nobody to show: one list, not a new one per render. */
const NO_NEW_STUDENTS: DataUploadNewStudent[] = []

/**
 * Tolerant class-name key so "Class 1A", "class 1-a", "Class 1 - A" and "1-A" all
 * refer to the same class: lowercase, alphanumerics only, and a leading "class"/"grade"
 * word is ignored (falling back to the unstripped key when nothing else remains).
 * Uploaded student files rarely spell the class exactly like the wizard does, and every
 * subjects-dropdown check matches on the class string — an unmatched spelling made each
 * subject click a silent no-op.
 */
/** Students grid rows per page: every row carries two dropdowns, so all at once lagged. */
const STUDENTS_PAGE_SIZE = 50

/**
 * Same content, field by field. The parent rebuilds `manualData` on every update but keeps each
 * unchanged field's value as it was, so identity settles almost every field; only a field whose
 * identity changed is compared by value. Serializing both objects whole (every student) ran on
 * each keystroke.
 */
/** A teacher row's identity on Review Teachers: its name as read, and its Employee ID. */
const teacherRowKey = (row: any): string =>
  [
    `${row?.first_name || ''} ${row?.last_name || ''}`.trim().toLowerCase(),
    String(row?.employeeId ?? '')
      .trim()
      .toLowerCase(),
  ].join('|')

const sameManualData = (a: Record<string, any>, b: Record<string, any>): boolean => {
  if (a === b) return true
  const keys = Object.keys(a)
  if (keys.length !== Object.keys(b).length) return false
  return keys.every(
    (key) => key in b && (a[key] === b[key] || JSON.stringify(a[key]) === JSON.stringify(b[key]))
  )
}

const normalizeClassMatchKey = (value: any): string => {
  const raw = String(value ?? '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '')
  const stripped = raw.replace(/^(class|grade)/, '')
  return stripped || raw
}

const splitSubjectsOutsideParens = (raw: string) => {
  const value = (raw || '').trim()
  if (!value) return []

  const output: string[] = []
  let token = ''
  let depth = 0

  for (const ch of value) {
    if (ch === '(') depth += 1
    if (ch === ')' && depth > 0) depth -= 1

    if (ch === ',' && depth === 0) {
      const clean = token.trim()
      if (clean) output.push(clean)
      token = ''
      continue
    }

    token += ch
  }

  const tail = token.trim()
  if (tail) output.push(tail)

  return output
}

interface TeacherOption {
  name: string
  employeeId?: string
}

interface TeacherSearchSelectProps {
  teachers: TeacherOption[]
  value: string
  selectedEmployeeId?: string
  onChange: (val: string) => void
  onSelectOption?: (opt: TeacherOption) => void
  hasError?: boolean
  isDark?: boolean
}

/**
 * "Watch a 2-minute guide" video on the Teacher Allocation landing page. Until a URL is
 * configured the action is shown without a link.
 */
const TEACHER_ALLOCATION_GUIDE_URL: string | undefined =
  String(import.meta.env.VITE_TEACHER_ALLOCATION_GUIDE_URL ?? '').trim() || undefined

/** A Teacher Allocation removal waiting for confirmation (handover, 3C). */
type AllocationRemoval =
  | {type: 'cell'; row: AllocationRow; subject: string}
  | {type: 'row'; row: AllocationRow}
  | {
      type: 'column'
      subject: string
      targets: AllocationRow[]
      filter: AllocationStatusFilter
      /** "Main Branch · Primary" — the Branch / Level scope the removal covers. */
      scope: string
    }

type PendingDeleteAction =
  | {type: 'category-row'; category: 'teachers' | 'students'; index: number}
  /** Students step: take back the rows the Teachers file prefilled. */
  | {type: 'students-prefill'}
  /** Review Teachers: one row or a bulk "Delete Selected" — original indices into manualData.teachers. */
  | {type: 'teacher-rows'; indices: number[]}
  /** Students grid: a bulk "Delete Selected" — original indices into manualData.students. */
  | {type: 'student-rows'; indices: number[]}
  | {type: 'classes-review-row'; branchName: string; rowId: string; rowIdx: number}

/** A class name the file wrote that matches several classes, asked once for every row with it. */
type StudentsClassQuestion = {
  id: string
  written: string
  labels: string[]
  rowKeys: string[]
  options: DataUploadClassOption[]
  picked?: number
}

/**
 * The last Students upload's report and class questions, kept outside the component so that
 * leaving the step and coming back does not lose them (the component unmounts in between). In
 * memory only: a page reload starts clean.
 */
const studentsUploadCache: {
  conversion: DataUploadConversion | null
  classQuestions: StudentsClassQuestion[]
  /** "Choose classes" was opened: it stays blue on return instead of pulsing red again. */
  classQuestionsSeen: boolean
} = {conversion: null, classQuestions: [], classQuestionsSeen: false}

/** A student that exists in the school: a Students save succeeded with it, or it has an id. */
const isSavedStudent = (row: any) => row?._saved === true || Boolean(parsePositiveNumber(row?.id))

/**
 * `data` with `removed` students queued in `deletedStudents`: the next Students save sends them
 * with `action:'delete'`. For rows taken out of the grid without a delete of their own (the grid
 * thinks they are new). One may still be saved — an upload template put a saved student back, or
 * its saved flag was lost — and without this it stayed in the school and came back with the next
 * template. A delete of a student the school doesn't have is harmless ("not found for deletion").
 * Rows with neither an id nor a registration number can't be found, so they are not queued.
 */
const withQueuedStudentDeletes = <T extends Record<string, any>>(data: T, removed: any[]): T => {
  const queued: any[] = (data as any).deletedStudents ?? []
  const known = new Set(queued.map(studentRowKey))
  const added = removed
    .filter(
      (row) =>
        (parsePositiveNumber(row?.id) || String(row?.registrationNumber ?? '').trim()) &&
        !known.has(studentRowKey(row))
    )
    .map((row) => ({
      ...(parsePositiveNumber(row?.id) ? {id: parsePositiveNumber(row.id)} : {}),
      registrationNumber: String(row?.registrationNumber ?? '').trim(),
      full_name: String(
        row?.full_name || `${row?.first_name || ''} ${row?.last_name || ''}`
      ).trim(),
      class: row?.class ?? '',
      ...(row?.branch ? {branch: row.branch} : {}),
      ...(row?.level ? {level: row.level} : {}),
      ...(row?.curriculum ? {curriculum: row.curriculum} : {}),
    }))
  return added.length > 0 ? {...data, deletedStudents: [...queued, ...added]} : data
}

const TeacherSearchSelect: FC<TeacherSearchSelectProps> = ({
  teachers,
  value,
  selectedEmployeeId,
  onChange,
  onSelectOption,
  hasError,
  isDark,
}) => {
  const intl = useIntl()
  const [inputValue, setInputValue] = useState(value)
  const [isOpen, setIsOpen] = useState(false)
  const [highlightedIdx, setHighlightedIdx] = useState(-1)
  const [dropdownPos, setDropdownPos] = useState({top: 0, left: 0, width: 180})
  const containerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const filledSlotRef = useRef<HTMLDivElement>(null)
  const dropdownRef = useRef<HTMLUListElement>(null)
  // Whether a teacher is committed to this slot — based on the persisted `value`,
  // NOT the in-progress `inputValue`, so typing a search term doesn't flip the UI
  // into the "filled slot" display and swap the input out from under the caret.
  const hasTeacherValue = (value || '').trim().length > 0
  // Show the editable input when there's no committed teacher, or while the
  // dropdown is open (i.e. the user is actively searching/editing).
  const showInput = !hasTeacherValue || isOpen
  const DROPDOWN_MAX_HEIGHT = 180
  const DROPDOWN_GAP = 4

  const filteredTeachers = useMemo(() => {
    if (!inputValue) return teachers
    return teachers.filter((t) => t.name.toLowerCase().includes(inputValue.toLowerCase()))
  }, [teachers, inputValue])

  const selectedTeacher = useMemo(() => {
    const v = (value || '').trim()
    if (!v) return null
    const eid = (selectedEmployeeId || '').toString().trim()
    if (eid) {
      return (
        teachers.find((t) => t.name === v && (t.employeeId || '').toString().trim() === eid) ?? null
      )
    }
    // Fallback: name-only match (may be ambiguous)
    return teachers.find((t) => t.name === v) ?? null
  }, [teachers, value, selectedEmployeeId])

  /** Show ID from matched teacher or persisted slot value so name + ID stay visible together. */
  const displayEmployeeId = (
    selectedTeacher?.employeeId ||
    (selectedEmployeeId || '').toString().trim() ||
    ''
  ).trim()

  useEffect(() => {
    setInputValue(value)
  }, [value])

  // When the dropdown opens on a previously-filled slot, the display div is
  // replaced by the input — move focus into it so the user can type immediately.
  useEffect(() => {
    if (isOpen && showInput) {
      inputRef.current?.focus()
    }
  }, [isOpen, showInput])

  const openDropdown = () => {
    if (!containerRef.current) return
    const rect = containerRef.current.getBoundingClientRect()
    // Only flip in the bottom 30% of the viewport
    const flipThresholdY = window.innerHeight * 0.7
    const estimatedHeight = Math.min(
      DROPDOWN_MAX_HEIGHT,
      Math.max(filteredTeachers.length * 34 + 8, 44)
    )
    const spaceAbove = rect.top - 8
    const spaceBelow = window.innerHeight - rect.bottom - 8
    const preferAbove = rect.top > flipThresholdY
    const openAbove =
      preferAbove &&
      (spaceBelow < estimatedHeight || (spaceAbove >= estimatedHeight && spaceAbove > spaceBelow))

    setDropdownPos({
      top: openAbove
        ? Math.max(8, rect.top - DROPDOWN_GAP - estimatedHeight)
        : Math.min(window.innerHeight - 8, rect.bottom + DROPDOWN_GAP),
      left: rect.left,
      width: Math.max(rect.width, 180),
    })
    // When editing an already-selected teacher, open the list unfiltered
    // (otherwise the inputValue equals the selected name and we only see that one option).
    // Only clear on the opening transition — not on every keystroke while already
    // open, which would wipe the search term the user is typing.
    if (!isOpen && hasTeacherValue) {
      setInputValue('')
      setHighlightedIdx(-1)
    }
    setIsOpen(true)
  }

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node
      if (containerRef.current?.contains(target) || dropdownRef.current?.contains(target)) return
      setIsOpen(false)
      setHighlightedIdx(-1)
      setInputValue(value)
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [value])

  const handleSelect = (opt: TeacherOption) => {
    onChange(opt.name)
    onSelectOption?.(opt)
    setInputValue(opt.name)
    setIsOpen(false)
    setHighlightedIdx(-1)
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement | HTMLDivElement>) => {
    if (!isOpen && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
      openDropdown()
      return
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setHighlightedIdx((prev) => Math.min(prev + 1, filteredTeachers.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setHighlightedIdx((prev) => Math.max(prev - 1, 0))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      if (highlightedIdx >= 0 && highlightedIdx < filteredTeachers.length) {
        handleSelect(filteredTeachers[highlightedIdx])
      }
    } else if (e.key === 'Escape') {
      setIsOpen(false)
      setHighlightedIdx(-1)
      setInputValue(value)
    }
  }

  return (
    <div ref={containerRef} style={{width: '100%', position: 'relative'}}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          background: hasError ? '#FFC6CC' : isDark ? '#5BA6FE' : '#EFF6FF',
          border: `1px solid ${
            hasError ? '#F64E60' : isDark ? 'rgba(255,255,255,0.4)' : 'rgba(5, 110, 233, 0.35)'
          }`,
          borderRadius: 6,
          padding: '3px 8px',
          gap: 4,
        }}
      >
        {hasError && (
          <AlertTriangle size={12} style={{color: '#F64E60', flexShrink: 0}} strokeWidth={2.2} />
        )}
        {!showInput ? (
          <div
            ref={filledSlotRef}
            role='button'
            tabIndex={0}
            onClick={openDropdown}
            onKeyDown={handleKeyDown}
            style={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 4,
              minWidth: 0,
              cursor: 'pointer',
              outline: 'none',
            }}
          >
            <span
              style={{
                fontSize: '11px',
                fontWeight: 700,
                color: hasError ? '#F64E60' : '#0263D4',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                minWidth: 0,
              }}
              title={value}
            >
              {value}
            </span>
            {displayEmployeeId ? (
              <span
                style={{
                  fontSize: '11px',
                  fontWeight: 700,
                  color: hasError ? '#F64E60' : '#0263D4',
                  whiteSpace: 'nowrap',
                  flexShrink: 0,
                }}
                title={displayEmployeeId}
              >
                ({displayEmployeeId})
              </span>
            ) : null}
          </div>
        ) : (
          <input
            ref={inputRef}
            type='text'
            className='teacher-search-input'
            data-has-error={hasError ? 'true' : undefined}
            value={inputValue}
            placeholder={intl.formatMessage({
              id: hasError
                ? 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.SELECT_ERROR'
                : 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.NAME_PLACEHOLDER',
            })}
            onChange={(e) => {
              setInputValue(e.target.value)
              openDropdown()
              setHighlightedIdx(-1)
            }}
            onFocus={openDropdown}
            onClick={openDropdown}
            onKeyDown={handleKeyDown}
            style={{
              flex: 1,
              background: 'transparent',
              border: 'none',
              outline: 'none',
              color: hasError ? '#F64E60' : isDark ? '#ffffff' : '#0263D4',
              fontSize: '11px',
              fontWeight: 400,
              minWidth: 0,
              cursor: 'pointer',
              textAlign: 'left',
            }}
          />
        )}
        {/* Always show the search icon — including when a teacher is assigned.
            The assigned name box is itself clickable to reopen the search, so a
            separate edit (pencil) icon is not shown. */}
        <Search
          size={12}
          style={{
            color: hasError
              ? '#F64E60'
              : isDark
                ? 'rgba(255,255,255,0.7)'
                : 'rgba(2, 99, 212, 0.75)',
            flexShrink: 0,
          }}
        />
      </div>
      {isOpen &&
        createPortal(
          <ul
            ref={dropdownRef}
            className='step4-teacher-search-dropdown'
            style={{
              position: 'fixed',
              top: dropdownPos.top,
              left: dropdownPos.left,
              width: dropdownPos.width,
              listStyle: 'none',
              margin: 0,
              padding: '4px',
              background: isDark ? '#80BBFF' : '#F0F7FF',
              border: isDark ? 'none' : '1px solid #BFDBFE',
              borderRadius: 8,
              boxShadow: isDark ? '0 8px 24px rgba(0,0,0,0.35)' : '0 8px 24px rgba(0,0,0,0.18)',
              maxHeight: 180,
              overflowY: 'auto',
              zIndex: 99999,
            }}
          >
            {filteredTeachers.length === 0 && (
              <li
                style={{
                  padding: '6px 10px',
                  fontSize: '12px',
                  fontWeight: 400,
                  color: '#0263D4',
                  textAlign: 'center',
                  cursor: 'default',
                }}
              >
                No teacher found
              </li>
            )}
            {filteredTeachers.map((t, idx) => (
              <li
                key={`${t.name}|||${t.employeeId || ''}|||${idx}`}
                onMouseDown={(e) => {
                  e.preventDefault()
                  handleSelect(t)
                }}
                onMouseEnter={() => setHighlightedIdx(idx)}
                style={{
                  padding: '6px 10px',
                  fontSize: '12px',
                  cursor: 'pointer',
                  fontWeight: highlightedIdx === idx ? 600 : 400,
                  background:
                    highlightedIdx === idx ? (isDark ? '#000066' : '#056EE9') : 'transparent',
                  color: highlightedIdx === idx ? '#ffffff' : '#0263D4',
                  borderRadius: 5,
                  transition: 'background 0.12s',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: 8,
                }}
              >
                <span>{t.name}</span>
                {t.employeeId && (
                  <span style={{fontSize: '11px', opacity: 0.9}}>{t.employeeId}</span>
                )}
              </li>
            ))}
          </ul>,
          document.body
        )}
    </div>
  )
}

/**
 * What a read found, held until the user adds it to the table (like the Add Teachers summary
 * before its review): the sheet's rows as read in the browser, or the server's conversion with
 * the questions to ask once its rows are in.
 */
type StagedStudents =
  | {kind: 'rows'; rows: string[][]; lists: string[][] | undefined}
  | {
      kind: 'conversion'
      conversion: DataUploadConversion
      columns: DataUploadColumnQuestions | null
      subjects: DataUploadSubjectMappings | null
    }

/**
 * The questions a Students read asks once its rows are in the grid, in one dialog: "which field
 * is this column?", "which branch are these students in?" and subjects the file writes under a
 * name the school does not use ("Maths" for "Mathematics"; proposed, never applied until
 * confirmed). Answering saves the ticked subjects for the school and reads the same file again
 * once.
 */
interface StudentsQuestions {
  file: File
  columns: DataUploadColumnQuestions | null
  branches: DataUploadBranchQuestion[]
  subjects: DataUploadSubjectMappings | null
  /** Reopened to change earlier branch answers: the cards start from them. */
  initialBranches?: DataUploadBranchAssignments
  /**
   * The read that asked a branch question, not yet in the grid: every row's class depends on
   * the answer, so only the re-read is applied. Skip applies this one as it is.
   */
  pendingConversion?: DataUploadConversion
  /** Offer "Save students only" / "Also choose classes and subjects" (not chosen yet). */
  askPeopleOnly?: boolean
  /** The read's class questions (`studentsClassQuestionId`), dropped on "Save students only". */
  classQuestionIds?: string[]
}

/**
 * A Students question closed with the X, not answered: kept as it was asked, so "Answer" opens
 * the same dialog again. Nothing is sent for it until then.
 */
type ParkedStudentsQuestion =
  | {kind: 'sheet'; question: DataUploadAssignmentQuestion}
  | {kind: 'questions'; questions: StudentsQuestions}

/** A file picked on the Students upload card, and what it gave. */
interface StudentsUploadLine {
  id: string
  file: File
  /**
   * waiting: queued behind another file; question: a dialog is asking about it; found: read,
   * its summary shown, waiting for "Add to table"; ready: its rows are in the table.
   */
  status: 'waiting' | 'reading' | 'question' | 'found' | 'ready' | 'error' | 'elsewhere'
  /** found: students the file holds. */
  found?: number
  added?: number
  /** Teachers the file holds that the school has not saved (a staff list read here). */
  teachersFound?: number
  /** Allocation lines the file also holds, kept for Teacher Allocation (`carried.allocation`). */
  allocationsFound?: number
  needsCheck?: number
  leftOut?: number
  /** Students the school already has (a re-upload): not added again, not "left out". */
  alreadyInList?: number
  /**
   * error: why nothing was added; elsewhere: which step the file belongs to, or information
   * that is not a failure (every line skipped on purpose).
   */
  message?: string
  staged?: StagedStudents
  /** question: its dialog was closed with the X; "Answer" opens it again. */
  parked?: ParkedStudentsQuestion
}

const Step4Manual: FC<Props> = ({
  data,
  updateData,
  onComplete,
  onBack,
  onPartialSubmit,
  isDark,
  isGenerating = false,
  isHydrating = false,
  enabledTabs,
  initialTab,
  initialTeachersPhase,
  stepperOverride,
  onActiveSubStepChange,
  onTeacherReview,
  onTeacherReviewConfirm,
  onExcelImported,
  saveLaterFlushRef,
}) => {
  const intl = useIntl()
  const dispatch = useDispatch()
  const {isSupportSession} = useSupportUser()

  const urddId = useSelector<RootState>((state) => state.userReducer.urddId) as string | null

  // --- Lesson Planning allocation-method analytics -----------------------------
  // How did this tenant allocate teachers on the Lesson Planning tab: by hand,
  // via the AI assistant, or via Excel import — plus "opened the AI assistant
  // but never applied anything". Standalone `teacher_allocation` event, one
  // substep per method (NOT metadata on onboarding_substep_final — that family
  // is deduped once per tenant/substep server-side, so repeats carrying the
  // method would never be stored). Each kind fires at most once per mount.
  const trackedAllocationEventsRef = useRef<Set<string>>(new Set())
  // Teacher Allocation held by the server (TEACHER_ALLOCATION_SERVER, AI servers): the grid,
  // prefill and Versi run on /onboarding/allocation/*. Falls back to the client flow when the
  // server answers those routes with a bare 404.
  const [allocationServerMode, setAllocationServerMode] = useState(allocationServerEnabled)
  /**
   * The landing's Upload card body (`ALLOCATION_UPLOADER_IN_CARD`): the shared uploader is shown
   * in it, as on Add Teachers. Null while the landing is not on the page.
   */
  const [allocationUploadSlot, setAllocationUploadSlot] = useState<HTMLDivElement | null>(null)
  /**
   * A re-uploaded allocation file's class subjects another teacher has now (`heldByOther`, AI
   * server): never imported over; asked here, "Replace?". Kept here, not in the import card,
   * which remounts after an import.
   */
  const [allocationHeldCells, setAllocationHeldCells] = useState<DataUploadHeldCell[]>([])
  /** Bumped after an allocation file was imported: the server flow reads its data again. */
  const [allocationServerRefreshKey, setAllocationServerRefreshKey] = useState(0)
  /** A server-side allocation edit happened since the wizard's own copy was last read. */
  const allocationServerDirtyRef = useRef(false)
  /**
   * The user changed a server-side allocation in this visit: the `assign_lesson_planning` edit
   * row rides on Continue. Its own flag because the dirty one above is cleared when the tab is
   * left (the wizard's copy is read again then), and that re-read is not counted as an edit, so
   * edit -> Back -> Continue would lose it. In memory only: an edit before a reload is not seen.
   */
  const allocationServerEditedRef = useRef(false)
  /** Server flow, Save and finish later: closes an open cell-settings panel first (§7). */
  const allocationServerFlushRef = useRef<(() => void) | null>(null)
  const registerAllocationServerFlush = useCallback((flush: (() => void) | null) => {
    allocationServerFlushRef.current = flush
  }, [])
  // Whether the AI panel's current open session applied at least one allocation.
  // Starts FALSE: the panel opens by itself on arrival at lesson planning (defaultOpen),
  // and that auto-open never fires onOpenStateChange(true) — the panel only reports
  // transitions — so closing that first session untouched must still count as
  // "opened the assistant but never used it". User-initiated opens also reset this
  // to false (see onOpenStateChange below); applying an allocation sets it true.
  const aiUsedSinceOpenRef = useRef(false)
  const trackAllocationEvent = useCallback(
    (
      allocationEvent:
        | 'manual_assignment'
        | 'ai_assignment'
        | 'excel_assignment'
        | 'ai_opened_not_used'
    ) => {
      if (trackedAllocationEventsRef.current.has(allocationEvent)) return
      // A tenant that applied an AI allocation this page load is an AI user: a later
      // open-and-leave must not count them as "opened, not used".
      if (
        allocationEvent === 'ai_opened_not_used' &&
        trackedAllocationEventsRef.current.has('ai_assignment')
      ) {
        return
      }
      trackedAllocationEventsRef.current.add(allocationEvent)
      trackOnboardingEvent(dispatch, urddId, 'teacher_allocation', allocationEvent)
    },
    [dispatch, urddId]
  )

  // Defer parent updates to avoid "setState during render" warnings.
  const deferUpdateData = useCallback(
    (next: Partial<WizardData>, options?: UpdateDataOptions) => {
      Promise.resolve().then(() => updateData(next, options))
    },
    [updateData]
  )

  const tabs = useMemo<Step4TabItem[]>(() => {
    const base: Step4TabItem[] = [
      ...subSteps,
      ...(data.mode === 'subject' ? studentSubStep : []),
    ].map((s) => ({
      id: s.id as Step4TabId,
      label: intl.formatMessage({id: s.labelKey}),
    }))
    if (!enabledTabs || enabledTabs.length === 0) return base
    const byId = new Map<Step4TabId, Step4TabItem>(base.map((t) => [t.id, t]))
    // Keep the exact order coming from `enabledTabs`.
    return enabledTabs.map((id) => byId.get(id)).filter((t): t is Step4TabItem => Boolean(t))
  }, [enabledTabs, intl, data.mode])

  const resolvedInitialTab = (() => {
    if (initialTab && tabs.some((t) => t.id === initialTab)) return initialTab
    const persisted = data.manualData.manualActiveTab
    if (persisted && tabs.some((t) => t.id === persisted)) return persisted
    return tabs[0]?.id || 'teachers'
  })()
  const [activeTab, setActiveTab] = useState<string>(resolvedInitialTab)
  const [localData, setLocalData] = useState(data.manualData)

  // The Teachers tab has two internal screens: "entry" (type/paste names or upload files)
  // and "review" (the editable table). Review is not a visible sub-step — it's where the
  // entry screen's output lands, and where duplicate names / low-confidence rows get fixed.
  // A tenant resuming with teachers already saved starts on review; an empty list starts
  // on entry.
  const [teachersPhase, setTeachersPhase] = useState<'entry' | 'review'>(() => {
    // /assign-subjects/review-teachers opens Review directly — even before the server's
    // teachers have hydrated (the review screen shows a skeleton meanwhile); starting on
    // "entry" instead would report the wrong sub-step, rewrite the URL to /teachers, and
    // strand a resumed user on the wrong screen. /assign-subjects/teachers opens the entry
    // screen even when teachers exist. No URL hint: review when there is something to review.
    if (initialTeachersPhase === 'review') return 'review'
    if (initialTeachersPhase === 'entry') return 'entry'
    return ((data.manualData.teachers as any[]) || []).length > 0 ? 'review' : 'entry'
  })
  // Set once the user moves between the two screens themselves; the hydration fallback
  // below must never override a deliberate navigation.
  const teachersPhaseNavigatedRef = useRef(false)
  const goToTeachersPhase = (phase: 'entry' | 'review') => {
    teachersPhaseNavigatedRef.current = true
    setTeachersPhase(phase)
  }
  // Resume fallback: a /review-teachers URL whose hydration brings back no teachers has
  // nothing to review, so fall back to the entry screen once hydration settles.
  useEffect(() => {
    if (isHydrating || teachersPhaseNavigatedRef.current) return
    if (teachersPhase === 'review' && ((data.manualData.teachers as any[]) || []).length === 0) {
      setTeachersPhase('entry')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isHydrating, data.manualData.teachers])
  // Coming back to the Teachers tab from a later one (Back from Students, a tab click) lands on
  // Review when there are teachers: the entry screen is for adding more. A resume that opened
  // on Students mounted with the teachers not hydrated yet, so the phase was still "entry".
  const previousTabRef = useRef<string | null>(null)
  useEffect(() => {
    const previous = previousTabRef.current
    previousTabRef.current = activeTab
    if (previous === null || previous === activeTab || activeTab !== 'teachers') return
    if (((localData.teachers as any[]) || []).length > 0) setTeachersPhase('review')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab])

  /**
   * Students and allocation lines found in the Teachers files, for Review's stat cards. The
   * entry screen's state goes when it unmounts, so the counts are kept here. A later visit to
   * Add Teachers with no checked file (typed names only) keeps the earlier counts.
   */
  const [teacherFileCounts, setTeacherFileCounts] = useState<TeacherFileCounts | null>(null)
  /**
   * A Teacher Allocation file's teachers (`carried.teachers`, a file with no assignments):
   * waiting for Add Teachers, where they show as that file's line, read from the draft.
   */
  const [queuedTeachersFile, setQueuedTeachersFile] = useState<{
    file: File
    draftId: string
  } | null>(null)
  /** Carried teachers drafts already taken onto Add Teachers in this visit. */
  const teachersCarriedDraftsTaken = useRef(new Set<string>())
  // Waiting teachers open Add Teachers (not Review), where their file's line is.
  useEffect(() => {
    if (activeTab === 'teachers' && queuedTeachersFile) setTeachersPhase('entry')
  }, [activeTab, queuedTeachersFile])
  /** What Add Teachers would hand to Review right now (see Step4TeacherEntry `pendingRowsRef`). */
  const teacherEntryPendingRef = useRef<
    (() => {rows: TeacherEntryRow[]; counts: TeacherFileCounts | null} | null) | null
  >(null)

  /** Append the entry screen's rows to the list and move to the review table. */
  const handleTeacherEntryReview = (rows: TeacherEntryRow[], counts: TeacherFileCounts | null) => {
    if (counts) setTeacherFileCounts(counts)
    let addedCount = 0
    setLocalData((prev) => {
      const existing = (prev.teachers as any[]) || []
      // A re-uploaded teacher the school already has (`_existingTeacherId`) is merged onto that
      // saved row — its allocation hints added, a blank Employee ID filled — never added twice.
      const {teachers: nextTeachers, added} = mergeTeacherEntryRows(existing, rows)
      addedCount = added
      const next = {...prev, teachers: nextTeachers, teachersCount: nextTeachers.length}
      // Rows added on Add Teachers: an `assign_teachers` edit, even though the wizard is
      // already switching to the Review screen by the time this deferred write lands.
      deferUpdateData({manualData: next}, {subStep: 'teachers'})
      return next
    })
    goToTeachersPhase('review')
    onTeacherReview?.()
    if (rows.length > 0) {
      // Defer so the toast reports the post-dedupe count computed inside the updater. Nothing new
      // (every teacher was already in the list): no "0 teachers added".
      Promise.resolve().then(() => {
        if (addedCount <= 0) return
        toast.success(
          intl.formatMessage(
            {id: 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.REVIEW.ADDED_TOAST'},
            {count: Math.max(0, addedCount)}
          )
        )
      })
    }
  }
  const onboardingTeacherCount = useMemo(
    () => ((localData.teachers as any[]) ?? []).length,
    [localData.teachers]
  )
  // report:false — the cap still blocks here, but reaching it during onboarding
  // must not raise the CreditsWall signal behind lifecycle email E14b. See the
  // hook's options docblock.
  const freemiumTeacherLimit = useFreemiumTeacherLimit(onboardingTeacherCount, {report: false})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [subjectInputByLevel, setSubjectInputByLevel] = useState<Record<string, string>>({})
  const [subjectErrorByLevel, setSubjectErrorByLevel] = useState<Record<string, string | null>>({})
  const [editingSubjectByLevel, setEditingSubjectByLevel] = useState<
    Record<string, {original: string; value: string} | null>
  >({})
  const skipSubjectInputBlurRef = useRef(false)
  const skipEditBlurCommitRef = useRef(false)
  const [lessonPlanAttempted, setLessonPlanAttempted] = useState(false)
  const [errorClearedSlots, setErrorClearedSlots] = useState<Set<string>>(new Set())
  const [showLessonNextHint, setShowLessonNextHint] = useState(false)

  /**
   * Teachers deleted on Review Teachers, until the parent's list no longer has them. A save that
   * started before the delete answers with the list it sent, and taking that in as it is put the
   * deleted teachers (and their assignments) back on Review and in Teacher Allocation.
   */
  const removedTeacherKeysRef = useRef<Set<string>>(new Set())

  useEffect(() => {
    let incoming = data.manualData ?? {}
    const removed = removedTeacherKeysRef.current
    if (removed.size > 0) {
      const teachers = ((incoming.teachers as any[]) ?? []) as any[]
      // Only the copies beyond what the list here still has: a second teacher with the same name
      // (no Employee ID yet) that was kept stays.
      const allowed = new Map<string, number>()
      ;(((localData?.teachers as any[]) ?? []) as any[]).forEach((row) => {
        const key = teacherRowKey(row)
        if (removed.has(key)) allowed.set(key, (allowed.get(key) ?? 0) + 1)
      })
      const kept = teachers.filter((row) => {
        const key = teacherRowKey(row)
        if (!removed.has(key)) return true
        const left = allowed.get(key) ?? 0
        if (left === 0) return false
        allowed.set(key, left - 1)
        return true
      })
      if (kept.length === teachers.length) {
        // The parent has caught up, and no save that predates the delete can still answer: a
        // teacher added again later is a new one.
        if (!isGenerating) removed.clear()
      } else {
        incoming = {...incoming, teachers: kept, teachersCount: kept.length}
        // The parent keeps what is saved next: it must not hold them either.
        deferUpdateData({manualData: incoming as WizardData['manualData']})
      }
    }
    setLocalData((prev) => (sameManualData(prev ?? {}, incoming) ? prev : incoming))
    // eslint-disable-next-line react-hooks/exhaustive-deps -- react to the parent's data only
  }, [data.manualData])
  const manualDataRef = useRef(data.manualData)

  const matchParentTeacher = useCallback((teacher: any, parentTeachers: any[]) => {
    const empId = String(teacher?.employeeId ?? '').trim()
    if (empId) {
      const byEmp = parentTeachers.find((p: any) => String(p?.employeeId ?? '').trim() === empId)
      if (byEmp) return byEmp
    }
    const name =
      teacher?._nameInput !== undefined
        ? String(teacher._nameInput || '').trim()
        : `${teacher?.first_name || ''} ${teacher?.last_name || ''}`.trim()
    if (!name) return undefined
    return parentTeachers.find((p: any) => {
      const parentName =
        p?._nameInput !== undefined
          ? String(p._nameInput || '').trim()
          : `${p?.first_name || ''} ${p?.last_name || ''}`.trim()
      return parentName === name
    })
  }, [])

  useEffect(() => {
    const parent = data.manualData
    const parentTeachers = parent.teachers ?? []
    const parentClasses = parent.classes ?? []
    manualDataRef.current = {
      ...localData,
      subjectIdByNameAndClass:
        (parent as any).subjectIdByNameAndClass ?? (localData as any).subjectIdByNameAndClass,
      teachers: (localData.teachers ?? []).map((t: any) => {
        const id =
          parsePositiveNumber(t?.id) ??
          parsePositiveNumber(matchParentTeacher(t, parentTeachers)?.id)
        return id ? {...t, id} : t
      }),
      classes: (localData.classes ?? []).map((c: any) => {
        const name = String(c?.name ?? '').trim()
        const id =
          parsePositiveNumber(c?.id) ??
          parsePositiveNumber(
            parentClasses.find((p: any) => String(p?.name ?? '').trim() === name)?.id
          )
        return id ? {...c, id} : c
      }),
    }
  }, [localData, data.manualData, matchParentTeacher])
  const persistTimerRef = useRef<number | null>(null)
  const didMountPersistRef = useRef(false)
  /**
   * One line per file picked on the Students card, like the Add Teachers files: what each one
   * added, or why it added nothing. Only files with a line report there; anything else read here
   * (the Teachers file's prefill) still says it by toast.
   */
  const studentsCsvInputRef = useRef<HTMLInputElement>(null)
  const [studentsUploads, setStudentsUploads] = useState<StudentsUploadLine[]>([])
  const studentsUploadFilesRef = useRef(new WeakSet<File>())
  const studentsUploadSeqRef = useRef(0)
  /** A Students upload line as the card shows it. */
  const studentsUploadItem = (line: StudentsUploadLine): UploadStatusItem => {
    const badges: Array<{text: string; tone: string}> = []
    if (line.status === 'found' && line.found) {
      badges.push({
        tone: 'primary',
        text: intl.formatMessage(
          {id: 'ONBOARDING_WIZARD.STEP4_MANUAL.UPLOAD.STUDENTS_FOUND'},
          {count: line.found}
        ),
      })
    }
    if (line.added) {
      badges.push({
        tone: 'success',
        text: intl.formatMessage(
          {id: 'ONBOARDING_WIZARD.STEP4_MANUAL.UPLOAD.STUDENTS_ADDED'},
          {count: line.added}
        ),
      })
    }
    if (line.alreadyInList) {
      badges.push({
        tone: 'primary',
        text: intl.formatMessage(
          {id: 'ONBOARDING_WIZARD.STEP4_MANUAL.UPLOAD.STUDENTS_ALREADY_IN_LIST'},
          {count: line.alreadyInList}
        ),
      })
    }
    // What else the file holds, beside its students: its teachers and its allocations.
    const hasStudents = Boolean(line.found || line.added)
    const othersFound = [
      line.teachersFound
        ? intl.formatMessage(
            {id: 'ONBOARDING_WIZARD.STEP4_MANUAL.UPLOAD.TEACHERS_FOUND'},
            {count: line.teachersFound}
          )
        : null,
      line.allocationsFound
        ? intl.formatMessage(
            {id: 'ONBOARDING_WIZARD.STEP4_MANUAL.UPLOAD.ALLOCATIONS_FOUND'},
            {count: line.allocationsFound}
          )
        : null,
    ].filter((text): text is string => Boolean(text))
    if (hasStudents) othersFound.forEach((text) => badges.push({tone: 'primary', text}))
    if (line.needsCheck) {
      badges.push({
        tone: 'warning',
        text: intl.formatMessage(
          {id: 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.FILE_STATUS_NEEDS_CHECK'},
          {count: line.needsCheck}
        ),
      })
    }
    if (line.leftOut) {
      badges.push({
        tone: 'secondary',
        text: intl.formatMessage(
          {id: 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.LEFT_OUT'},
          {count: line.leftOut}
        ),
      })
    }
    const reading = line.status === 'reading'
    return {
      id: line.id,
      file: line.file,
      busy: reading,
      busyText:
        reading && studentsUploadProgress !== null
          ? intl.formatMessage(
              {id: 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.FILE_STATUS_UPLOADING'},
              {percent: Math.round(studentsUploadProgress * 100)}
            )
          : intl.formatMessage({
              id: 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.FILE_STATUS_EXTRACTING',
            }),
      progress: reading ? studentsUploadProgress : null,
      error: line.status === 'error' ? (line.message ?? null) : null,
      hint:
        line.status === 'question'
          ? intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP4_MANUAL.UPLOAD.WAITING_ANSWER'})
          : line.status === 'elsewhere'
            ? (line.message ?? null)
            : // A file with no students (a staff list): what it did hold, as a line under its
              // size rather than a badge.
              !hasStudents && othersFound.length > 0
              ? othersFound.join(' · ')
              : null,
      hintTone:
        line.status !== 'question' &&
        line.status !== 'elsewhere' &&
        !hasStudents &&
        othersFound.length > 0
          ? 'success'
          : undefined,
      badges,
      settled: line.status === 'ready',
      action:
        line.status === 'found' && line.found ? (
          <button
            type='button'
            className='btn btn-sm btn-primary text-nowrap'
            disabled={studentsTableRefreshing}
            onClick={() => addStagedStudents(line)}
          >
            {intl.formatMessage(
              {id: 'ONBOARDING_WIZARD.STEP4_MANUAL.UPLOAD.ADD_STUDENTS'},
              {count: line.found}
            )}
          </button>
        ) : line.status === 'question' && line.parked ? (
          // Its dialog was closed with the X: the same questions, again.
          <button
            type='button'
            className='btn btn-sm btn-primary text-nowrap'
            disabled={
              studentsAnswering ||
              studentsReadingFile ||
              Boolean(studentsSheetQuestion) ||
              Boolean(studentsQuestions)
            }
            onClick={() => reopenStudentsQuestion(line)}
          >
            {intl.formatMessage({
              id:
                line.parked.kind === 'sheet'
                  ? 'DATA_UPLOAD.QUESTIONS.ANSWER_SHORT'
                  : 'DATA_UPLOAD.QUESTIONS.ANSWER',
            })}
          </button>
        ) : null,
    }
  }
  /**
   * A file's line leaves the panel, and the file stops reporting there: a later read of it (a
   * re-read after a question) says its errors by toast, like any other read.
   */
  const dropStudentsUpload = (file: File) => {
    // The files read with it go too: they were one upload.
    const gone = [file, ...(studentsBatchRef.current.get(file) ?? [])]
    gone.forEach((one) => studentsUploadFilesRef.current.delete(one))
    setStudentsUploads((prev) => prev.filter((line) => !gone.includes(line.file)))
  }
  /**
   * Files picked together (AI servers): read in ONE `data-upload/convert` request as one upload,
   * so a person in two files is one row. By the first file (whose line holds the summary and
   * "Add to table"): the others, whose lines follow its status.
   */
  const studentsBatchRef = useRef(new WeakMap<File, File[]>())
  /** The first file of the batch a file was read in. */
  const studentsBatchOfRef = useRef(new WeakMap<File, File>())

  /**
   * "Add to table" on a file's line: its read goes into the grid and the line goes, as the Add
   * Teachers summary does on Continue. A conversion then asks its column and subject questions,
   * as it did before. A read that adds nothing keeps its line, with the reason.
   */
  const addStagedStudents = (line: StudentsUploadLine) => {
    const read = line.staged
    if (!read) return
    if (read.kind === 'rows') {
      let added = 0
      applyStudentRows(read.rows, read.lists, {
        silent: true,
        onRows: (keys) => {
          // Two keys per row (see applyStudentRows).
          added = keys.length / 2
        },
      })
      if (added > 0) {
        dropStudentsUpload(line.file)
        return
      }
      markStudentsUpload(line.file, {
        status: 'error',
        staged: undefined,
        message: intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP4_MANUAL.STUDENTS.CSV_NO_ROWS'}),
      })
      return
    }
    applyStudentsConversion(line.file, read.conversion)
      .then((added) => {
        if (added > 0) dropStudentsUpload(line.file)
        else markStudentsUpload(line.file, {staged: undefined})
        askStudentsQuestions(line.file, read.conversion, read.columns, read.subjects)
      })
      .catch(() => {
        markStudentsUpload(line.file, {
          status: 'error',
          staged: undefined,
          message: dataUploadErrorMessage(intl.formatMessage, 'PARSE_FAILED', {
            config: studentsUploadConfig,
            useCaseKey: 'students',
            fileName: line.file.name,
          }),
        })
      })
  }

  /**
   * A conversion's rows left out, less the students the school already has (a re-upload): those
   * are said apart, as "already in your list", never as left out.
   */
  const studentsSkipCounts = (conversion: DataUploadConversion) => {
    const {alreadyInList, leftOut} = alreadyInListSplit(conversion)
    return {leftOut: leftOut.length, alreadyInList}
  }

  /** Update `file`'s line; false when it has none, so the caller says it another way. */
  const markStudentsUpload = (file: File, patch: Partial<StudentsUploadLine>): boolean => {
    if (!studentsUploadFilesRef.current.has(file)) return false
    // Read with it: the same status (and why it failed); the counts stay on its line.
    const together = studentsBatchRef.current.get(file) ?? []
    setStudentsUploads((prev) =>
      prev.map((line) =>
        line.file === file
          ? {...line, ...patch}
          : together.includes(line.file) && patch.status
            ? {
                ...line,
                status: patch.status,
                ...(patch.status === 'error' ? {message: patch.message} : {}),
              }
            : line
      )
    )
    return true
  }
  /** "Answer" on a line whose question was closed with the X: the same dialog opens again. */
  const reopenStudentsQuestion = (line: StudentsUploadLine) => {
    const parked = line.parked
    if (!parked) return
    markStudentsUpload(line.file, {parked: undefined})
    if (parked.kind === 'sheet') {
      // The queue went on when it was closed: nothing waits on this answer.
      setStudentsSheetQuestion({file: line.file, question: parked.question, done: () => {}})
      return
    }
    setStudentsQuestions(parked.questions)
  }
  /**
   * The X on the Students questions: closed, not answered, and nothing is read again. A line
   * waiting on them (a branch question, before its rows go in) keeps them ("Answer questions");
   * questions asked once the rows are in wait above the grid, the file's line being done.
   */
  const parkStudentsQuestions = (questions: StudentsQuestions) => {
    setStudentsQuestions(null)
    // Opened to change answers already given ("Change"): nothing waits on it.
    if (questions.initialBranches) return
    const waiting = studentsUploads.some(
      (line) => line.file === questions.file && line.status === 'question'
    )
    const parked: ParkedStudentsQuestion = {kind: 'questions', questions}
    if (waiting && markStudentsUpload(questions.file, {parked})) return
    setStudentsParkedQuestions(questions)
  }
  const [isImportingStudentsFile, setIsImportingStudentsFile] = useState(false)
  // "Any file -> our template" (data-upload/convert): AI servers only. Elsewhere the upload
  // stays CSV/Excel, exactly as before.
  const studentsConversionAvailable = isDataUploadConversionAvailable()
  const studentsUploadConfig = useDataUploadConfig('students', studentsConversionAvailable)
  /** True while a PDF / Word / photo is being read server-side (can take a minute a page). */
  const [studentsReadingFile, setStudentsReadingFile] = useState(false)
  /**
   * A re-read whose rows go straight into the table (after a column or subject answer): the
   * table shows a skeleton until they land. A fresh upload never touches the table before "Add
   * to table", so its progress shows on the file's line only.
   */
  const [studentsTableRefreshing, setStudentsTableRefreshing] = useState(false)
  /** Report of the last conversion, shown above the grid until dismissed or replaced. */
  // Started from the cache: the report outlives leaving the step and coming back (see below).
  const [studentsConversion, setStudentsConversion] = useState<DataUploadConversion | null>(
    () => studentsUploadCache.conversion
  )
  /**
   * Row keys the report's file put in the grid (both keys of each row). The report goes once
   * none of them is left — even when the server placed none of its rows in a class, so its
   * `references` alone could not tell. Null when unknown (a prefill draft): references decide.
   */
  const [studentsConversionKeys, setStudentsConversionKeys] = useState<ReadonlySet<string> | null>(
    null
  )
  /** 0..1 while the file's pieces are being sent; null otherwise. */
  const [studentsUploadProgress, setStudentsUploadProgress] = useState<number | null>(null)
  /**
   * A workbook that holds several lists of people and says of none of them whose it is: the
   * file is held here with the question until the user answers, then sent again with the
   * answers. Nothing was read, so there is no report and no template yet.
   */
  const [studentsSheetQuestion, setStudentsSheetQuestion] = useState<{
    file: File
    question: DataUploadAssignmentQuestion
    /** Resumes the file queue: called once this file has been answered (and read) or skipped. */
    done: () => void
  } | null>(null)
  /**
   * A class name the file wrote that matches several classes (two boards both have "Grade 6-B"),
   * asked once for every row that wrote it (`report.classQuestions`). `rowKeys` are those rows'
   * `studentRowKey`s; `picked` the chosen `classId`, if any.
   */
  const [studentsClassQuestions, setStudentsClassQuestions] = useState<StudentsClassQuestion[]>(
    () => studentsUploadCache.classQuestions
  )
  /**
   * The Students upload report and its class questions live in component state, which is lost
   * when the user goes to another step and comes back. They are mirrored into
   * `studentsUploadCache` so they are still there on return, until the file's students are
   * deleted (the report hides itself then) or the report is dismissed.
   */
  useEffect(() => {
    studentsUploadCache.conversion = studentsConversion
    studentsUploadCache.classQuestions = studentsClassQuestions
  }, [studentsConversion, studentsClassQuestions])
  /** Busy while an answered question or a confirmed mapping is being sent. */
  const [studentsAnswering, setStudentsAnswering] = useState(false)
  /**
   * Sheet answers already given for a file, so EVERY later post of it carries them.
   *
   * The server does not remember them: a post without `sheetAssignments` has it ask the question
   * again, whatever was answered a moment ago. A conversion is posted more than once for one file
   * — the answer itself, and again after subject mappings are confirmed, which only take effect on
   * a fresh read — and the second of those used to go bare, so the dialog reappeared on a file the
   * user had just answered for. Keyed by `File`, which is the identity the user picked; a WeakMap
   * so nothing is held once the file is gone.
   */
  const answeredSheetsRef = useRef(new WeakMap<File, DataUploadSheetAssignments>())
  /** Column answers given for a file, sent with every later read of it, like the sheet answers. */
  const answeredColumnsRef = useRef(new WeakMap<File, DataUploadColumnAssignments>())
  /** Another step's rows a Students read kept (`carried`), by file: its allocations. */
  const studentsCarriedRef = useRef(new WeakMap<File, DataUploadCarriedDrafts>())
  /**
   * A file whose students Teacher Allocation already read (`carried.students`): it is loaded
   * from that draft (`GET drafts/:id/template`), not read again. Closed once added to the table.
   */
  const studentsCarriedDraftRef = useRef(new WeakMap<File, string>())
  /** The file the Students summary is about (for its "Assign teachers from this file"). */
  const [studentsConversionFile, setStudentsConversionFile] = useState<File | null>(null)
  /**
   * "Save students only" (true) or "Also choose classes and subjects" (false), per file, once
   * chosen. Only-students: its unclear classes are not asked ("Choose classes") and its subject
   * proposals are not confirmed; those rows keep a blank class to fill in the grid.
   */
  const studentsPeopleOnlyRef = useRef(new WeakMap<File, boolean>())
  /**
   * Branch answers given for a file ("these students are in Modal Town", or "mixed"). Not saved
   * per school — the branch is a fact about this upload — so, like the sheet answers, they go
   * with every later read of the file, and a sheet answered once is never asked about again.
   */
  const answeredBranchesRef = useRef(new WeakMap<File, DataUploadBranchAssignments>())
  /**
   * Subject mappings confirmed for a file, by lower-cased written name. Saved as aliases, so the
   * next read normally reports them as `applied`; a rejected or slow alias can come back as a
   * suggestion, and this keeps it from being asked again.
   */
  const answeredSubjectsRef = useRef(new WeakMap<File, Map<string, DataUploadSubjectMapping>>())
  /**
   * Teachers a Students upload names that the school has not saved (the file carries a teacher
   * list too): "save them as new teachers?". Asked once per file; shown after the other dialogs.
   */
  const [studentsNewTeachers, setStudentsNewTeachers] = useState<{
    file: File
    teachers: DataUploadNewTeacher[]
  } | null>(null)
  const [savingStudentsNewTeachers, setSavingStudentsNewTeachers] = useState(false)
  const studentsAskedTeachersRef = useRef(new WeakMap<File, Set<string>>())
  /** A screen (`assignedTo`) by its step title, for "this is the X list". */
  const stepTitle = (assignedTo: string | undefined) =>
    STEP_TITLE_KEYS[assignedTo ?? '']
      ? intl.formatMessage({id: STEP_TITLE_KEYS[assignedTo!]})
      : (assignedTo ?? '')
  /** Row keys each file's last read put in the grid, so a re-read of it replaces them. */
  const studentsFileRowsRef = useRef(new WeakMap<File, Set<string>>())
  /**
   * The branch questions answered for the last file read, with their answers, so the user can
   * change one later ("Both" instead of a branch): the server never asks an answered
   * sheet again, and a changed answer replaces the branch the old one set.
   */
  const [studentsBranchAnswered, setStudentsBranchAnswered] = useState<{
    file: File
    questions: DataUploadBranchQuestion[]
    answers: DataUploadBranchAssignments
    /** Row keys the file's last read put in the grid: its "Which class?" questions. */
    rowKeys?: ReadonlySet<string>
  } | null>(null)
  /** The questions a read asks once its rows are in the grid (see `StudentsQuestions`). */
  const [studentsQuestions, setStudentsQuestions] = useState<StudentsQuestions | null>(null)
  /**
   * Questions closed with the X whose file has no line on the card any more (its rows were
   * added to the table): "Answer questions" above the grid opens them again. A file with a line
   * keeps them on it (`parked`).
   */
  const [studentsParkedQuestions, setStudentsParkedQuestions] =
    useState<StudentsQuestions | null>(null)
  /**
   * Questions skipped for a file that the server keeps no skip value for: a branch question (by
   * key) and a subject proposal (by lower-cased written name). A later read of the file does not
   * ask them again.
   */
  const studentsSkippedRef = useRef(
    new WeakMap<File, {branches: Set<string>; subjects: Set<string>}>()
  )
  /**
   * One line per imported row the wizard could not finish on its own (a class this school
   * doesn't have, a subject the class doesn't teach, no subjects at all). The rows are in the
   * grid with those cells flagged; this panel says why, and stays until dismissed or replaced.
   */
  const [studentsIssues, setStudentsIssues] = useState<string[] | null>(null)
  /**
   * Keys of the Students uploads' new teachers saved since (the new-teachers popup): the report
   * then says "N teachers saved to the Teachers step" instead of the server's "not in your
   * school yet", which would make the save look like it did nothing.
   */
  const [studentsSavedTeacherKeys, setStudentsSavedTeacherKeys] = useState<ReadonlySet<string>>(
    () => new Set()
  )
  /** Students grid rows ticked for "Delete Selected", by index into manualData.students. */
  const [selectedStudentRows, setSelectedStudentRows] = useState<Set<number>>(() => new Set())
  /** Students grid page (1-based) and rows per page. */
  const [studentsPage, setStudentsPage] = useState(1)
  const [studentsPageSize, setStudentsPageSize] = useState(STUDENTS_PAGE_SIZE)
  // Row indices shift on add/delete, so any selection is stale the moment the list length changes.
  const studentsLength = ((localData.students as any[]) || []).length
  useEffect(() => {
    setSelectedStudentRows(new Set())
  }, [studentsLength])

  // Lesson planning grid state: {className: {subjectName: {teacher, frequency, duration}}}
  const [lessonPlanningData, setLessonPlanningData] = useState<LessonPlanningData>(
    (data.manualData.lessonPlanningData as any) || {}
  )
  const lessonPlanningDataRef = useRef(lessonPlanningData)
  lessonPlanningDataRef.current = lessonPlanningData
  /** Teacher Allocation V2: slots whose saved teacher came from an uploaded file (file icon). */
  const [importedSlots, setImportedSlots] = useState<Set<string>>(
    () => new Set(data.manualData?.importedLessonSlots ?? [])
  )
  /** Teacher Allocation V2: sections removed from this timetable setup (row removal). */
  const [removedLessonClasses, setRemovedLessonClasses] = useState<Set<string>>(
    () => new Set(data.manualData?.removedLessonClasses ?? [])
  )
  const [allocationHintsSignature, setAllocationHintsSignature] = useState(
    () => data.manualData?.allocationHintsSignature ?? ''
  )

  // Modal state for editing a cell (frequency/duration only - teacher is selected on slot)
  const [lessonEditModal, setLessonEditModal] = useState<{
    show: boolean
    className: string
    subjectName: string
    frequency: string
    duration: string
    hasExistingData: boolean
  }>({
    show: false,
    className: '',
    subjectName: '',
    frequency: '',
    duration: '',
    hasExistingData: false,
  })

  // Selection state for rows/columns
  const [selectedRows, setSelectedRows] = useState<string[]>([])
  const [selectedCols, setSelectedCols] = useState<string[]>([])
  const [activeLessonBranch, setActiveLessonBranch] = useState<string>('')
  const [isLessonPlanningLoading, setIsLessonPlanningLoading] = useState(false)
  const [activeLessonLevel, setActiveLessonLevel] = useState<string>('')
  const [activeLessonCurriculum, setActiveLessonCurriculum] = useState<string>('')
  // Names only: state persisted by an older build may still hold `{name, id}` entries.
  const branchOptions = Array.from(
    new Set(
      ((localData.branches || data.manualData?.branches || []) as unknown[])
        .map((entry: any) => String(typeof entry === 'string' ? entry : (entry?.name ?? '')).trim())
        .filter(Boolean)
    )
  )

  const normalizeBranchName = useCallback(
    (branch?: string) => (branch || '').toString().trim() || 'Main',
    []
  )

  const getLessonClassKey = useCallback(
    (className: string, branch?: string, curriculum?: string) => {
      const cleanCurriculum = (curriculum || '').toString().trim()
      return `${normalizeBranchName(branch)}|||${cleanCurriculum}|||${className}`
    },
    [normalizeBranchName]
  )

  const [classesFlowStep, setClassesFlowStep] = useState<number>(
    () => data.manualData?.classesFlowStep ?? 2
  )
  // Flips true the moment the user navigates the internal classes flow. The flow-step seeding
  // effect below only runs while this is false, so it seeds from async hydration on resume but
  // never fights in-session navigation (saveProgress can persist a momentarily-stale flow step).
  const classesFlowNavigatedRef = useRef(false)

  // Report the active sub-step upward so the parent can keep the URL in sync. With a
  // stepperOverride (Curriculum step), the active tab + classesFlowStep map to a stepper
  // id (classes/sections/review/subjects); otherwise the active tab id is reported.
  //
  // The callback is read through a ref and is NOT an effect dependency: the parent passes
  // an inline arrow that also posts the tab's `onboarding_substep_final` analytics row, and
  // its identity changes on every parent render — including the one the callback itself
  // triggers (it sets the parent's sub-step slug). With it in the deps the effect re-ran
  // on that render and the row was posted twice ~1 ms apart, racing past server dedupe.
  const onActiveSubStepChangeRef = useRef(onActiveSubStepChange)
  onActiveSubStepChangeRef.current = onActiveSubStepChange
  useEffect(() => {
    const report = onActiveSubStepChangeRef.current
    if (!report) return
    let subStepId: string = activeTab
    if (activeTab === 'teachers' && teachersPhase === 'review') subStepId = 'review-teachers'
    if (stepperOverride) {
      const match = stepperOverride.steps.find(
        (s) =>
          (s.topTabId ?? s.id) === activeTab &&
          (s.classesFlowStep === undefined || s.classesFlowStep === classesFlowStep)
      )
      if (match) subStepId = match.id
    }
    report(subStepId)
  }, [activeTab, classesFlowStep, stepperOverride, teachersPhase])
  const initialReviewRowsByBranch =
    ((data.manualData as any)?.classesReviewRowsByBranch as Record<string, ClassesReviewRow[]>) ??
    {}
  const initialReviewRowIdCounter = Object.values(initialReviewRowsByBranch)
    .flat()
    .reduce((max, r) => {
      const match = /review-row-(\d+)/.exec(r.id || '')
      const n = match ? Number(match[1]) : 0
      return Number.isFinite(n) ? Math.max(max, n) : max
    }, 0)
  const [classesExpandedCurriculum, setClassesExpandedCurriculum] = useState<Set<string>>(new Set())
  const [sectionCountInput, setSectionCountInput] = useState(
    () => data.manualData?.classesSectionCount ?? '5'
  )
  const [sectionNomenclatureInput, setSectionNomenclatureInput] = useState(
    () => data.manualData?.classesSectionNomenclature ?? 'A,B,C,D,E'
  )
  const MAX_SECTIONS = 26
  const [sectionCountError, setSectionCountError] = useState<string | null>(null)
  const [classInputByLevel, setClassInputByLevel] = useState<Record<string, string>>({})
  const [prefilledClassesByCurriculumLevel, setPrefilledClassesByCurriculumLevel] = useState<
    Record<string, Record<string, PrefilledClassEntry[]>>
  >({})
  const [reviewRowsByBranch, setReviewRowsByBranch] =
    useState<Record<string, ClassesReviewRow[]>>(initialReviewRowsByBranch)
  const [expandedBranch, setExpandedBranch] = useState<Set<string>>(
    () => new Set(Object.keys(initialReviewRowsByBranch))
  )
  // Inline editing enhancement: Review table is always editable in-place.
  const classesPrefillInitializedRef = useRef(false)
  const classesDetailsFetchInProgressRef = useRef(false)
  const reviewRowIdCounterRef = useRef(initialReviewRowIdCounter)
  const didHydratePersistedReviewRef = useRef(false)
  // True once the user edits the Review table this session (section edits / row deletes). Gates the
  // hydration seeding effect below so it can never clobber in-session work.
  const reviewRowsEditedRef = useRef(false)

  const createReviewRow = useCallback((base: Omit<ClassesReviewRow, 'id'>): ClassesReviewRow => {
    reviewRowIdCounterRef.current += 1
    return {
      id: `review-row-${reviewRowIdCounterRef.current}`,
      ...base,
    }
  }, [])

  const selectedLevelsByCurriculum = useMemo(() => {
    const levelsByCurriculum = data.manualData?.levelsByCurriculum ?? {}
    const selectedCurriculums = data.manualData?.curriculums ?? Object.keys(levelsByCurriculum)
    const out: Record<string, string[]> = {}

    selectedCurriculums.forEach((curriculumName) => {
      const curriculum = levelsByCurriculum[curriculumName]
      if (!curriculum) return

      if (curriculum.levelsSameAcrossBranches === false) {
        const merged = uniqueStrings(Object.values(curriculum.levelsPerBranch ?? {}).flat())
        out[curriculumName] = merged
        return
      }

      out[curriculumName] = uniqueStrings((curriculum.levels ?? []).map((l: any) => l?.name || ''))
    })

    return out
  }, [data.manualData?.curriculums, data.manualData?.levelsByCurriculum])

  const getSubjectLevelKey = useCallback(
    (curriculumName: string, levelName: string) => `${curriculumName}|||${levelName}`,
    []
  )

  const subjectLevelRows = useMemo<SubjectLevelRow[]>(() => {
    return Object.entries(selectedLevelsByCurriculum).flatMap(([curriculumName, levels]) =>
      levels.map((levelName) => ({
        curriculum: curriculumName,
        level: levelName,
        key: getSubjectLevelKey(curriculumName, levelName),
      }))
    )
  }, [getSubjectLevelKey, selectedLevelsByCurriculum])

  const defaultSubjectSelectionsByLevel = useMemo(() => {
    const selections: Record<string, string[]> = {}

    subjectLevelRows.forEach(({curriculum, level, key}) => {
      const entries = prefilledClassesByCurriculumLevel[curriculum]?.[level] ?? []
      selections[key] = uniqueStrings(entries.flatMap((entry) => entry.subjects || []))
    })

    return selections
  }, [prefilledClassesByCurriculumLevel, subjectLevelRows])

  const getEffectiveSubjectSelectionsByLevel = useCallback(
    (subjectSelectionsByLevel?: Record<string, string[]>) => {
      const nextSelections: Record<string, string[]> = {}
      const providedSelections =
        subjectSelectionsByLevel ?? localData.subjectSelectionsByLevel ?? {}

      subjectLevelRows.forEach(({key}) => {
        if (Object.prototype.hasOwnProperty.call(providedSelections, key)) {
          nextSelections[key] = uniqueStrings(providedSelections[key] ?? [])
          return
        }

        nextSelections[key] = uniqueStrings(defaultSubjectSelectionsByLevel[key] ?? [])
      })

      return nextSelections
    },
    [defaultSubjectSelectionsByLevel, localData.subjectSelectionsByLevel, subjectLevelRows]
  )

  // Subjects are chosen per curriculum/level, but the bulk-import creates a `subject` row per
  // CLASS, and a student can only be linked to a subject that exists in their own class. So this
  // emits one row per (subject, class) for every class of that level — the same shape Step 3's
  // applyAcademicStructure writes. It used to emit one row per (subject, level) carrying the
  // FIRST class of that level, so the students sync (which sends subjects along, before Lesson
  // Planning has run) created e.g. "English" in "Playgroup-A" only, and every student in the
  // level's other classes failed with `subject "English" does not exist in class "…"`.
  const flattenSubjectsFromSelections = useCallback(
    (subjectSelectionsByLevel: Record<string, string[]>) => {
      const existingSubjects = [
        ...((data.manualData?.subjects as any[]) ?? []),
        ...((localData.subjects as any[]) ?? []),
      ]
      const existingByClass = new Map<string, any>()
      const existingByKey = new Map<string, any>()
      const existingByName = new Map<string, any>()
      existingSubjects.forEach((subject) => {
        const name = String(subject?.name ?? '').trim()
        const curriculum = String(subject?.curriculum ?? '').trim()
        const level = String(subject?.level ?? '').trim()
        const className = String(subject?.class ?? '').trim()
        const branch = String(subject?.branch ?? '').trim()
        if (!name) return
        if (className) {
          // Keyed by curriculum when the row has one: two curricula can share a class name,
          // and each class's subject row (and id) is its own.
          const classKeys = curriculum
            ? [`${name}|||${className}|||${branch}|||${curriculum}`]
            : [`${name}|||${className}|||${branch}`, `${name}|||${className}`]
          classKeys.forEach((classKey) => {
            if (!existingByClass.has(classKey)) existingByClass.set(classKey, subject)
          })
        }
        const key = `${name}|||${curriculum}|||${level}`
        if (!existingByKey.has(key)) existingByKey.set(key, subject)
        if (!existingByName.has(name)) existingByName.set(name, subject)
      })

      const subjectIdByNameAndClass =
        ((localData as any)?.subjectIdByNameAndClass as Record<string, number> | undefined) ??
        ((data.manualData as any)?.subjectIdByNameAndClass as Record<string, number> | undefined) ??
        {}
      const classRows = ((localData.classes as any[]) ?? (data.manualData?.classes as any[]) ?? [])
        .map((cls: any) => ({
          name: String(cls?.name ?? '').trim(),
          branch: String(cls?.branch ?? '').trim(),
          level: String(cls?.level ?? '').trim(),
          curriculum: String(cls?.curriculum ?? '').trim(),
        }))
        .filter((cls) => cls.name && cls.level)

      return subjectLevelRows.flatMap(({curriculum, level, key}) => {
        const names = uniqueStrings(subjectSelectionsByLevel[key] ?? [])
        if (names.length === 0) return []
        // A class row written before the curriculum was tracked carries none — match on level.
        const levelClasses = classRows.filter(
          (cls) => cls.level === level && (!cls.curriculum || cls.curriculum === curriculum)
        )
        const templateFor = (name: string) =>
          existingByKey.get(`${name}|||${curriculum}|||${level}`) ?? existingByName.get(name)

        if (levelClasses.length === 0) {
          // No classes for this level yet: keep a class-less row so the payload builder expands
          // it over the level's classes once they exist, instead of pinning it to one class.
          return names.map((name) => {
            const existing = templateFor(name)
            return {
              name,
              curriculum,
              level,
              teacher: '',
              class: '',
              frequency: String(existing?.frequency ?? '').trim(),
              duration: String(existing?.duration ?? '').trim(),
            }
          })
        }

        const seen = new Set<string>()
        return names.flatMap((name) =>
          levelClasses.flatMap((cls) => {
            const dedupeKey = `${name}|||${cls.name}|||${cls.branch}`
            if (seen.has(dedupeKey)) return []
            seen.add(dedupeKey)
            const perClass =
              existingByClass.get(
                `${name}|||${cls.name}|||${cls.branch}|||${cls.curriculum || curriculum}`
              ) ??
              existingByClass.get(`${name}|||${cls.name}|||${cls.branch}`) ??
              existingByClass.get(`${name}|||${cls.name}`)
            const template = perClass ?? templateFor(name)
            const id =
              parsePositiveNumber(perClass?.id) ??
              lookupSubjectId(
                subjectIdByNameAndClass,
                name,
                cls.name,
                cls.curriculum || curriculum,
                cls.branch
              )
            return [
              {
                name,
                curriculum,
                level,
                ...(cls.branch ? {branch: cls.branch} : {}),
                // A teacher belongs to one class-subject; never copy it across the level.
                teacher: String(perClass?.teacher ?? '').trim(),
                class: cls.name,
                frequency: String(template?.frequency ?? '').trim(),
                duration: String(template?.duration ?? '').trim(),
                ...(id ? {id} : {}),
                ...(perClass?.action ? {action: perClass.action} : {}),
              },
            ]
          })
        )
      })
    },
    [data.manualData, localData, subjectLevelRows]
  )

  const getSubjectsForCurriculumLevel = useCallback(
    (curriculumName: string, levelName: string) => {
      const selections = getEffectiveSubjectSelectionsByLevel()
      return uniqueStrings(selections[getSubjectLevelKey(curriculumName, levelName)] ?? [])
    },
    [getEffectiveSubjectSelectionsByLevel, getSubjectLevelKey]
  )

  const parseSections = useCallback(() => {
    const requestedCount = Number.parseInt(sectionCountInput, 10)
    const count = Number.isFinite(requestedCount) && requestedCount > 0 ? requestedCount : 0
    const fromNomenclature = uniqueStrings(
      sectionNomenclatureInput
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean)
    )

    if (count > 0 && fromNomenclature.length >= count) {
      return fromNomenclature.slice(0, count)
    }

    if (count > 0 && fromNomenclature.length > 0) {
      const padded = [...fromNomenclature]
      let idx = 1
      while (padded.length < count) {
        padded.push(String(idx))
        idx += 1
      }
      return padded
    }

    if (fromNomenclature.length > 0) return fromNomenclature
    if (count <= 0) return []
    return Array.from({length: count}, (_, i) => String(i + 1))
  }, [sectionCountInput, sectionNomenclatureInput])

  const deriveManualDataFromReview = useCallback(
    (review: Record<string, ClassesReviewRow[]>, prevSubjects: any[] = []) => {
      const classes: any[] = []
      const subjectsSet = new Set<string>()

      Object.entries(review).forEach(([branchName, rows]) => {
        rows.forEach((row) => {
          const sections = row.sections.length > 0 ? row.sections : ['A']
          sections.forEach((sectionName) => {
            const section = sectionName.trim()
            if (!row.gradeName.trim() || !section) return
            classes.push({
              branch: branchName,
              level: row.level,
              curriculum: row.curriculum,
              grade: row.gradeName,
              section,
              teacher: '',
              name: `${row.gradeName}-${section}`,
            })
          })

          row.subjects.forEach((subjectName) => {
            const clean = subjectName.trim()
            if (clean) subjectsSet.add(clean)
          })
        })
      })

      const nextSubjects =
        subjectsSet.size > 0 ? Array.from(subjectsSet).map((name) => ({name})) : prevSubjects

      return {
        classes,
        classesCount: classes.length,
        subjects: nextSubjects,
        subjectsCount: nextSubjects.length,
      }
    },
    []
  )

  const syncManualDataFromReview = useCallback(
    (review: Record<string, ClassesReviewRow[]>) => {
      setLocalData((prev) => {
        const derived = deriveManualDataFromReview(review, (prev.subjects as any[]) || [])

        // Prune the pending delete list of any class that is back in the derived set (re-added),
        // so a re-added class is sent as insert — not both insert and delete.
        const key = (c: any) =>
          [
            String(c?.name ?? '')
              .trim()
              .toLowerCase(),
            String(c?.branch ?? '')
              .trim()
              .toLowerCase(),
            String(c?.level ?? '')
              .trim()
              .toLowerCase(),
          ].join('|||')
        const presentKeys = new Set(((derived.classes as any[]) ?? []).map(key))
        const prevDeleted = ((prev as any).deletedClasses as any[]) ?? []
        const nextDeleted = prevDeleted.filter((c) => !presentKeys.has(key(c)))

        const next = {
          ...prev,
          ...derived,
          deletedClasses: nextDeleted,
        }
        deferUpdateData({manualData: next})
        return next
      })
    },
    [deriveManualDataFromReview, deferUpdateData]
  )

  const buildReviewRowsFromPrefill = useCallback(() => {
    const sections = parseSections()
    const branches = branchOptions.length > 0 ? branchOptions : ['Main Branch']
    const levelsByCurriculum = data.manualData?.levelsByCurriculum ?? {}

    // Per-branch authority from the current table (seeded from server data on resume). The prefill
    // source is curriculum/level-scoped (branch-agnostic), so blindly replicating it to every
    // branch resurrects grades that were deleted from only some branches. Rule: a grade present in
    // SOME branch's current rows but missing from this branch's was deleted here — skip it. A grade
    // in no branch at all is newly added on the Classes screen — replicate it everywhere as before.
    // Existing rows are reused so per-row section edits survive the rebuild.
    const gradeKey = (curriculum: string, level: string, grade: string) =>
      `${curriculum}|||${level}|||${grade}`
    const knownGrades = new Set<string>()
    const rowByBranchGrade = new Map<string, ClassesReviewRow>()
    Object.entries(reviewRowsByBranch).forEach(([branchName, rows]) => {
      rows.forEach((row) => {
        const key = gradeKey(row.curriculum, row.level, row.gradeName)
        knownGrades.add(key)
        rowByBranchGrade.set(`${branchName}|||${key}`, row)
      })
    })

    const out: Record<string, ClassesReviewRow[]> = {}
    branches.forEach((branchName) => {
      const rows: ClassesReviewRow[] = []
      const branchHasRows = (reviewRowsByBranch[branchName] ?? []).length > 0

      Object.entries(prefilledClassesByCurriculumLevel).forEach(([curriculumName, levels]) => {
        const curriculumData = levelsByCurriculum[curriculumName]
        const allowedLevels = (() => {
          if (!curriculumData) {
            return Object.keys(levels)
          }
          if (curriculumData.levelsSameAcrossBranches === false) {
            return uniqueStrings(curriculumData.levelsPerBranch?.[branchName] ?? [])
          }
          return uniqueStrings((curriculumData.levels ?? []).map((level: any) => level?.name || ''))
        })()

        allowedLevels.forEach((levelName) => {
          const entries = levels[levelName] ?? []
          entries.forEach((entry) => {
            if (!entry.name.trim()) return
            const key = gradeKey(curriculumName, levelName, entry.name)
            const existingRow = rowByBranchGrade.get(`${branchName}|||${key}`)
            if (existingRow) {
              // Keep the branch's own row (so per-branch deletions stay deleted), but refresh
              // both subjects and sections — a later change on the Sections screen must reach
              // grades that already have a row, not only newly-added ones.
              rows.push({
                ...existingRow,
                sections: [...sections],
                subjects: uniqueStrings(entry.subjects || []),
              })
              return
            }
            // Known in another branch but absent here → deleted from this branch; don't resurrect.
            if (branchHasRows && knownGrades.has(key)) return
            rows.push(
              createReviewRow({
                curriculum: curriculumName,
                level: levelName,
                gradeName: entry.name,
                sections: [...sections],
                subjects: uniqueStrings(entry.subjects || []),
              })
            )
          })
        })
      })

      out[branchName] = rows
    })

    return out
  }, [
    branchOptions,
    createReviewRow,
    data.manualData?.levelsByCurriculum,
    parseSections,
    prefilledClassesByCurriculumLevel,
    reviewRowsByBranch,
  ])

  useEffect(() => {
    if (classesPrefillInitializedRef.current) return
    // Wait until the curriculum/level config has loaded (async on resume) before prefilling,
    // otherwise we'd mark prefill "done" against empty data and never re-run.
    if (Object.keys(selectedLevelsByCurriculum).length === 0) return

    const levelsByCurriculum = data.manualData?.levelsByCurriculum ?? {}
    const selectedCurriculums = data.manualData?.curriculums ?? Object.keys(levelsByCurriculum)

    // Build the per-curriculum/level prefilled-classes map from a given school-system-details set,
    // preferring any per-curriculum levelDetails the user/cache already carries.
    const buildPrefillMap = (systemDetails: ReturnType<typeof readStoredSchoolSystemDetails>) => {
      const nextMap: Record<string, Record<string, PrefilledClassEntry[]>> = {}
      Object.entries(selectedLevelsByCurriculum).forEach(([curriculumName, selectedLevels]) => {
        const userLevelDetails = levelsByCurriculum[curriculumName]?.levelDetails ?? {}
        const systemLevelDetails = systemDetails[curriculumName] ?? {}
        const levelMap: Record<string, PrefilledClassEntry[]> = {}
        selectedLevels.forEach((levelName) => {
          const userClasses = userLevelDetails[levelName]?.classes
          const systemClasses = systemLevelDetails[levelName]?.classes
          // Prefer user/server-edited classes when they actually exist; otherwise fall back to the
          // fetched school-system-details defaults. An EMPTY levelDetails entry (e.g. a resume
          // where the server returned the level but no classes yet) must not mask the defaults —
          // so we resolve per level instead of spread-merging, which would let {} clobber them.
          const rawClasses =
            Array.isArray(userClasses) && userClasses.length > 0
              ? userClasses
              : Array.isArray(systemClasses)
                ? systemClasses
                : []
          // The chosen class list may carry NAMES without subjects — e.g. on resume the server
          // returns reviewed classes but no subjects until they're saved. Index the fetched
          // school-system-details classes by name so we can backfill each class's default subjects.
          const systemSubjectsByClassName = new Map<string, string[]>()
          ;(Array.isArray(systemClasses) ? systemClasses : []).forEach((cls: any) => {
            const nm = (cls?.name || cls?.class || '').toString().trim().toLowerCase()
            if (!nm) return
            const subs = Array.isArray(cls?.subjects)
              ? cls.subjects
              : splitSubjectsOutsideParens((cls?.subjects || '').toString())
            systemSubjectsByClassName.set(nm, uniqueStrings(subs))
          })
          const classesForLevel = rawClasses
            .map((cls: any) => {
              const className = (cls?.name || cls?.class || '').toString().trim()
              const subjectList = Array.isArray(cls?.subjects)
                ? cls.subjects
                : splitSubjectsOutsideParens((cls?.subjects || '').toString())
              let subjects = uniqueStrings(subjectList)
              // Backfill default subjects from the fetched details when the class has none.
              if (subjects.length === 0) {
                const fromSystem = systemSubjectsByClassName.get(className.toLowerCase())
                if (fromSystem && fromSystem.length > 0) subjects = fromSystem
              }
              return {
                name: className,
                subjects,
              }
            })
            .filter((cls: PrefilledClassEntry) => cls.name.length > 0)
          levelMap[levelName] = classesForLevel
        })
        if (Object.keys(levelMap).length > 0) {
          nextMap[curriculumName] = levelMap
        }
      })
      return nextMap
    }

    const applyPrefill = (systemDetails: ReturnType<typeof readStoredSchoolSystemDetails>) => {
      const nextMap = buildPrefillMap(systemDetails)
      setPrefilledClassesByCurriculumLevel(nextMap)
      setClassesExpandedCurriculum(new Set(Object.keys(nextMap)))
      classesPrefillInitializedRef.current = true
    }

    const storedSystemDetails = readStoredSchoolSystemDetails()
    // "Past review": the bulk-import runs at/after the curriculum review, so once the server
    // returns reviewed classes they arrive in resume-data (classesReviewRowsByBranch). When that
    // exists the Classes step honours resume-data (the review table is never overwritten below).
    const hasResumeClasses = Object.keys(initialReviewRowsByBranch).length > 0
    // Have subjects already been saved? They come back in resume-data (subjects array) or are
    // held in-session (subjectSelectionsByLevel). If so the Subjects step needs no defaults.
    const savedSubjectSelections = (data.manualData?.subjectSelectionsByLevel ?? {}) as Record<
      string,
      string[]
    >
    const hasSavedSubjects =
      Object.values(savedSubjectSelections).some((arr) => Array.isArray(arr) && arr.length > 0) ||
      ((data.manualData?.subjects as any[]) ?? []).length > 0
    // Do the selected curriculums already have cached class details (e.g. fetched at the levels
    // step)? If so we can prefill straight from cache without another API call.
    const hasCachedClasses = selectedCurriculums.some(
      (c) => Object.keys(storedSystemDetails[c] ?? {}).length > 0
    )
    // Fetch defaults when the cache is empty and at least one step still needs them: classes not
    // yet reviewed (Classes step) OR subjects not yet saved (Subjects step). The response's
    // classes carry their subjects, so one fetch prefills both. It is non-destructive — reviewed
    // classes and saved subject selections always take precedence over these defaults.
    const needsDefaults = !hasResumeClasses || !hasSavedSubjects

    if (needsDefaults && !hasCachedClasses && selectedCurriculums.length > 0) {
      // Landed on Classes/Subjects without cached details (levels skipped, a fresh resume, or the
      // user left the subjects step without saving): fetch school-system-details for the selected
      // curriculum and prefill classes + subjects from the response. Guard against a duplicate
      // fetch if the effect re-runs before the async response marks prefill done.
      if (classesDetailsFetchInProgressRef.current) return
      classesDetailsFetchInProgressRef.current = true
      // timetable/prefill for catalog picks, the legacy details endpoint for older ones; the
      // loader merges whatever resolved into the cache and always completes.
      loadCurriculumDefaults(
        dispatch,
        {
          country: data.country,
          curriculums: selectedCurriculums,
          codes: data.manualData?.curriculumCodes,
        },
        () => {
          classesDetailsFetchInProgressRef.current = false
          applyPrefill(readStoredSchoolSystemDetails())
        }
      )
      return
    }

    applyPrefill(storedSystemDetails)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    data.manualData?.levelsByCurriculum,
    data.manualData?.curriculums,
    selectedLevelsByCurriculum,
  ])

  // When returning to Classes tab on the review step (3), rebuild review table from persisted class names and section config
  useEffect(() => {
    // Guard on actual ROWS, not branch keys: a resume/back-navigation can leave
    // reviewRowsByBranch holding branch keys with empty arrays (e.g. {"Main Branch": []}) —
    // a state that would otherwise pass a keys-length check and permanently block the rebuild,
    // leaving the Review grid empty. Only skip when some branch genuinely has rows, or the user
    // has edited the table this session (so we never resurrect intentional deletions).
    const hasAnyReviewRows = Object.values(reviewRowsByBranch).some(
      (rows) => (rows?.length ?? 0) > 0
    )
    if (
      !classesPrefillInitializedRef.current ||
      classesFlowStep !== 3 ||
      reviewRowsEditedRef.current ||
      hasAnyReviewRows ||
      Object.keys(prefilledClassesByCurriculumLevel).length === 0
    ) {
      return
    }
    const review = buildReviewRowsFromPrefill()
    setExpandedBranch(new Set(Object.keys(review)))
    setReviewRowsByBranch(review)
    syncManualDataFromReview(review)
  }, [
    classesFlowStep,
    reviewRowsByBranch,
    prefilledClassesByCurriculumLevel,
    buildReviewRowsFromPrefill,
    syncManualDataFromReview,
  ])

  // Hydrate localData from persisted Review table (so refresh keeps everything consistent).
  useEffect(() => {
    if (didHydratePersistedReviewRef.current) return
    if (classesFlowStep !== 3) return
    if (Object.keys(reviewRowsByBranch).length === 0) return

    didHydratePersistedReviewRef.current = true
    setExpandedBranch(new Set(Object.keys(reviewRowsByBranch)))
    syncManualDataFromReview(reviewRowsByBranch)
  }, [classesFlowStep, reviewRowsByBranch, syncManualDataFromReview])

  const didRepairClassesFromReviewRef = useRef(false)
  useEffect(() => {
    if (didRepairClassesFromReviewRef.current) return
    if (classesFlowStep !== 3) return

    const persistedReview =
      ((data.manualData as any)?.classesReviewRowsByBranch as Record<string, ClassesReviewRow[]>) ??
      {}
    if (Object.keys(persistedReview).length === 0) return

    const derived = deriveManualDataFromReview(
      persistedReview,
      (data.manualData?.subjects as any[]) || []
    )
    const currentClasses = ((data.manualData?.classes as any[]) || []) as any[]

    const currentByBranch = new Map<string, number>()
    currentClasses.forEach((c: any) => {
      const b = (c?.branch || '').toString()
      currentByBranch.set(b, (currentByBranch.get(b) || 0) + 1)
    })
    const derivedByBranch = new Map<string, number>()
    ;(derived.classes || []).forEach((c: any) => {
      const b = (c?.branch || '').toString()
      derivedByBranch.set(b, (derivedByBranch.get(b) || 0) + 1)
    })

    const needsRepair = Array.from(derivedByBranch.entries()).some(
      ([branch, count]) => count > 0 && (currentByBranch.get(branch) || 0) < count
    )
    if (!needsRepair) return

    didRepairClassesFromReviewRef.current = true
    updateData({
      manualData: {
        ...data.manualData,
        ...derived,
      },
    })
    // localData will mirror from wizard state; no need to setLocalData here.
  }, [classesFlowStep, data.manualData, deriveManualDataFromReview, updateData])

  // Persist Classes tab data to wizard state so it survives navigation (forward/back) and refresh
  useEffect(() => {
    if (!classesPrefillInitializedRef.current) return

    const levelsByCurriculum = data.manualData?.levelsByCurriculum ?? {}
    const nextLevelsByCurriculum = {...levelsByCurriculum}

    Object.entries(prefilledClassesByCurriculumLevel).forEach(([curriculumName, levelMap]) => {
      const existing = nextLevelsByCurriculum[curriculumName] ?? {}
      const nextLevelDetails: Record<string, {classes: PrefilledClassEntry[]}> = {
        ...(existing.levelDetails ?? {}),
      }
      Object.entries(levelMap).forEach(([levelName, entries]) => {
        nextLevelDetails[levelName] = {classes: entries}
      })
      nextLevelsByCurriculum[curriculumName] = {
        ...existing,
        levelDetails: nextLevelDetails,
      }
    })

    updateData({
      manualData: {
        ...data.manualData,
        levelsByCurriculum: nextLevelsByCurriculum,
      },
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefilledClassesByCurriculumLevel, updateData])

  // Persist Review table edits so refresh keeps grade/sections/subjects changes.
  useEffect(() => {
    if (classesFlowStep !== 3) return
    if (!classesPrefillInitializedRef.current) return

    const t = window.setTimeout(() => {
      const nextManualData = saveProgress('classes', 3)
      const derived = deriveManualDataFromReview(
        reviewRowsByBranch,
        ((nextManualData as any)?.subjects as any[]) || (data.manualData?.subjects as any[]) || []
      )
      updateData({
        manualData: {
          ...(nextManualData as any),
          ...derived,
          classesReviewRowsByBranch: reviewRowsByBranch,
          classesFlowStep: 3,
        } as any,
      })
    }, 300)

    return () => window.clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [classesFlowStep, reviewRowsByBranch])

  // Persist section count and nomenclature so they survive navigation
  useEffect(() => {
    updateData({
      manualData: {
        ...data.manualData,
        classesSectionCount: sectionCountInput,
        classesSectionNomenclature: sectionNomenclatureInput,
      },
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally only persist when section inputs change
  }, [sectionCountInput, sectionNomenclatureInput, updateData])

  // Seed the section inputs from server-hydrated data. The inputs are `useState`-initialized at
  // mount, so a resume that populates classesSectionCount/Nomenclature asynchronously (after this
  // component has mounted) would otherwise be missed. Compare-based (not a one-time guard): the
  // persist effect above keeps manualData mirrored to the inputs, so this only fires when the value
  // changes externally (hydration) and never clobbers a user edit (values match after an edit).
  useEffect(() => {
    const count = data.manualData?.classesSectionCount
    const nomenclature = data.manualData?.classesSectionNomenclature
    if (count && String(count) !== sectionCountInput) setSectionCountInput(String(count))
    if (nomenclature && String(nomenclature) !== sectionNomenclatureInput) {
      setSectionNomenclatureInput(String(nomenclature))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.manualData?.classesSectionCount, data.manualData?.classesSectionNomenclature])

  // Seed the internal classes-flow position (1=Sections, 2=Classes, 3=Review) from server-hydrated
  // data. Like the section inputs above, `classesFlowStep` is `useState`-initialized at mount, so a
  // resume that restores it asynchronously — e.g. a user who reached Review and left without saving —
  // would otherwise land back on the default Classes screen instead of where they left off. Guarded
  // by classesFlowNavigatedRef so it ONLY applies before the user starts navigating: once they move
  // within the flow, saveProgress may persist a momentarily-stale flow step, and re-seeding from that
  // would revert the screen (making "Next" appear to do nothing).
  useEffect(() => {
    if (classesFlowNavigatedRef.current) return
    const persisted = data.manualData?.classesFlowStep
    if (typeof persisted === 'number' && persisted !== classesFlowStep) {
      setClassesFlowStep(persisted)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.manualData?.classesFlowStep])

  // Seed the Review table from server-hydrated data. reviewRowsByBranch is useState-initialized at
  // mount from the (possibly stale) localStorage copy, so a resume whose hydration replaces
  // classesReviewRowsByBranch — e.g. a grade deleted last session that the backend no longer
  // returns — would otherwise keep showing the stale rows. Only applies while the user hasn't
  // navigated the classes flow or edited the review table this session.
  useEffect(() => {
    if (classesFlowNavigatedRef.current || reviewRowsEditedRef.current) return
    const persisted = (data.manualData as any)?.classesReviewRowsByBranch as
      | Record<string, ClassesReviewRow[]>
      | undefined
    if (!persisted || Object.keys(persisted).length === 0) return
    if (JSON.stringify(reviewRowsByBranch ?? {}) === JSON.stringify(persisted)) return
    setReviewRowsByBranch(persisted)
    setExpandedBranch(new Set(Object.keys(persisted)))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [(data.manualData as any)?.classesReviewRowsByBranch])

  useEffect(() => {
    const tabExists = tabs.some((tab) => tab.id === activeTab)
    if (!tabExists) {
      setActiveTab(tabs[0]?.id || 'teachers')
    }
  }, [tabs, activeTab])

  // Frequency/Duration info tooltip visibility (lesson planning tab)
  const [infoTooltipOpen, setInfoTooltipOpen] = useState(false)

  // Delete confirmation for lesson planning slot
  const [deleteSlotConfirm, setDeleteSlotConfirm] = useState<{
    className: string
    subjectName: string
  } | null>(null)
  // Delete confirmation for whole row/column
  const [deleteRowColConfirm, setDeleteRowColConfirm] = useState<{
    type: 'row' | 'col'
    name: string
    rowKey?: string
    rowSubjects?: string[]
  } | null>(null)
  const [pendingDeleteAction, setPendingDeleteAction] = useState<PendingDeleteAction | null>(null)
  const [donAskAgainDelete, setDonAskAgainDelete] = useState(false)
  const [donAskAgainDeleteRow, setDonAskAgainDeleteRow] = useState(false)
  const deleteSlotRef = useRef<{className: string; subjectName: string} | null>(null)

  const LESSON_DELETE_SKIP_CONFIRM_KEY = 'onboarding_lesson_delete_skip_confirm'
  const ROW_DELETE_SKIP_CONFIRM_KEY = 'onboarding_row_delete_skip_confirm'

  const [deletedSlots, setDeletedSlots] = useState<Set<string>>(
    () => new Set((data.manualData?.deletedLessonSlots as string[]) || [])
  )
  /** What each cell had when it was marked not taught on this visit, for Restore subject. */
  const notTaughtCellsRef = useRef(new Map<string, LessonPlanningData[string][string]>())
  const slotKey = useCallback((cn: string, sn: string) => `${cn}|||${sn}`, [])
  const isSlotDeleted = useCallback(
    (cn: string, sn: string) => {
      return deletedSlots.has(slotKey(cn, sn))
    },
    [deletedSlots, slotKey]
  )

  const performDeleteSlot = useCallback(
    (slot: {className: string; subjectName: string}) => {
      const {className: cn, subjectName: sn} = slot
      setDeletedSlots((prev) => {
        const next = new Set(prev)
        next.add(slotKey(cn, sn))
        return next
      })
      setLessonPlanningData((prev) => {
        const next = {...prev}
        const currentClassData = next[cn]
        if (!currentClassData) return prev
        const rest = Object.fromEntries(Object.entries(currentClassData).filter(([k]) => k !== sn))
        next[cn] = rest
        return next
      })
      deleteSlotRef.current = null
      setDeleteSlotConfirm(null)
    },
    [slotKey]
  )

  const toggleRow = (className: string) => {
    setSelectedRows((prev) =>
      prev.includes(className) ? prev.filter((c) => c !== className) : [...prev, className]
    )
  }

  const toggleCol = (subjectName: string) => {
    setSelectedCols((prev) =>
      prev.includes(subjectName) ? prev.filter((s) => s !== subjectName) : [...prev, subjectName]
    )
  }

  const toggleAllRows = (classes: any[]) => {
    const classKeys = classes
      .map((c: any) => getLessonClassKey(c.name || '', c.branch, c.curriculum))
      .filter(Boolean)
    if (selectedRows.length === classes.length) {
      setSelectedRows([])
    } else {
      setSelectedRows(classKeys)
    }
  }

  useEffect(() => {
    const classes = (localData.classes as any[]) || []
    const branchesFromClasses = Array.from(
      new Set(classes.map((c: any) => normalizeBranchName(c?.branch)).filter(Boolean))
    )
    // Always include explicit branch options so newly-added branches (with no classes yet)
    // remain selectable and don't get auto-reset back to "Main".
    const availableBranches = Array.from(
      new Set([...branchOptions.map(normalizeBranchName), ...branchesFromClasses].filter(Boolean))
    )

    if (!activeLessonBranch && availableBranches.length > 0) {
      setActiveLessonBranch(availableBranches[0])
      return
    }

    if (activeLessonBranch && !availableBranches.includes(activeLessonBranch)) {
      setActiveLessonBranch(availableBranches[0] || '')
    }
  }, [activeLessonBranch, branchOptions, localData.classes, normalizeBranchName])

  // Clear loading state after branch change
  useEffect(() => {
    if (isLessonPlanningLoading) {
      const timer = setTimeout(() => {
        setIsLessonPlanningLoading(false)
      }, 400)
      return () => clearTimeout(timer)
    }
  }, [isLessonPlanningLoading])
  // Delete whole row: mark all slots in that row as deleted
  const deleteRow = useCallback(
    (classKey: string, subjectNames: string[]) => {
      const keys = subjectNames.map((sn) => slotKey(classKey, sn))
      setDeletedSlots((prev) => {
        const next = new Set(prev)
        keys.forEach((k) => next.add(k))
        return next
      })
    },
    [slotKey]
  )
  // Delete whole column
  const deleteColumn = useCallback(
    (subjectName: string, classNames: string[]) => {
      const keys = classNames.map((cn) => slotKey(cn, subjectName))
      setDeletedSlots((prev) => {
        const next = new Set(prev)
        keys.forEach((k) => next.add(k))
        return next
      })
    },
    [slotKey]
  )

  const handleAddSubject = (curriculumName: string, levelName: string) => {
    const key = getSubjectLevelKey(curriculumName, levelName)
    const name = (subjectInputByLevel[key] || '').trim()
    if (!name) return

    const effectiveSelections = getEffectiveSubjectSelectionsByLevel()
    const rowSubjects = effectiveSelections[key] ?? []
    if (rowSubjects.some((subjectName) => subjectName.toLowerCase() === name.toLowerCase())) {
      setSubjectErrorByLevel((prev) => ({
        ...prev,
        [key]: intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP4_MANUAL.SUBJECTS.DUPLICATE_ERROR'}),
      }))
      return
    }

    const nextSelections = {
      ...effectiveSelections,
      [key]: [...rowSubjects, name],
    }
    const nextSubjects = flattenSubjectsFromSelections(nextSelections)

    setSubjectErrorByLevel((prev) => ({...prev, [key]: null}))
    setLocalData((prev) => {
      const nextLocal = {
        ...prev,
        subjectSelectionsByLevel: nextSelections,
        subjects: nextSubjects,
        subjectsCount: nextSubjects.length,
      }
      // Persist immediately so navigation/back never reverts to defaults.
      deferUpdateData({
        manualData: {
          ...nextLocal,
          manualActiveTab: 'subjects',
        },
      })
      return nextLocal
    })
    setSubjectInputByLevel((prev) => ({...prev, [key]: ''}))
  }

  const handleAddSubjectWithValue = (
    curriculumName: string,
    levelName: string,
    rawValue: string
  ) => {
    const key = getSubjectLevelKey(curriculumName, levelName)
    const trimmed = (rawValue || '').trim()
    if (!trimmed) return

    if ((subjectInputByLevel[key] || '') !== rawValue) {
      setSubjectInputByLevel((prev) => ({...prev, [key]: rawValue}))
      setTimeout(() => handleAddSubject(curriculumName, levelName), 0)
      return
    }

    handleAddSubject(curriculumName, levelName)
  }

  const handleRemoveSubject = (curriculumName: string, levelName: string, subjectName: string) => {
    const key = getSubjectLevelKey(curriculumName, levelName)
    const effectiveSelections = getEffectiveSubjectSelectionsByLevel()
    const nextSelections = {
      ...effectiveSelections,
      [key]: (effectiveSelections[key] ?? []).filter((name) => name !== subjectName),
    }
    const nextSubjects = flattenSubjectsFromSelections(nextSelections)

    setLocalData((prev) => {
      // The removed subject's saved rows are queued for delete: the rebuilt rows only leave it
      // out, which the bulk-import never reads as a removal.
      const nextLocal = queueRemovedSubjects(
        {...data.manualData, ...prev, subjectSelectionsByLevel: effectiveSelections},
        {
          ...prev,
          subjectSelectionsByLevel: nextSelections,
          subjects: nextSubjects,
          subjectsCount: nextSubjects.length,
        }
      ) as typeof prev
      // Persist immediately so deletes are retained (even if the row becomes empty).
      deferUpdateData({
        manualData: {
          ...nextLocal,
          manualActiveTab: 'subjects',
        },
      })
      return nextLocal
    })
    setSubjectErrorByLevel((prev) => ({...prev, [key]: null}))
  }

  const startEditSubject = (curriculumName: string, levelName: string, subjectName: string) => {
    const key = getSubjectLevelKey(curriculumName, levelName)
    setEditingSubjectByLevel((prev) => ({
      ...prev,
      [key]: {
        original: subjectName,
        value: subjectName,
      },
    }))
    setSubjectErrorByLevel((prev) => ({...prev, [key]: null}))
  }

  const cancelEditSubject = (curriculumName: string, levelName: string) => {
    const key = getSubjectLevelKey(curriculumName, levelName)
    setEditingSubjectByLevel((prev) => ({...prev, [key]: null}))
  }

  const commitEditSubject = (curriculumName: string, levelName: string) => {
    const key = getSubjectLevelKey(curriculumName, levelName)
    const editing = editingSubjectByLevel[key]
    if (!editing) return

    const originalName = (editing.original || '').trim()
    const nextName = (editing.value || '').trim()
    if (!originalName) {
      setEditingSubjectByLevel((prev) => ({...prev, [key]: null}))
      return
    }

    if (!nextName) {
      handleRemoveSubject(curriculumName, levelName, originalName)
      setEditingSubjectByLevel((prev) => ({...prev, [key]: null}))
      return
    }

    const effectiveSelections = getEffectiveSubjectSelectionsByLevel()
    const rowSubjects = effectiveSelections[key] ?? []
    const duplicateExists = rowSubjects.some(
      (subjectName) =>
        subjectName.toLowerCase() === nextName.toLowerCase() &&
        subjectName.toLowerCase() !== originalName.toLowerCase()
    )
    if (duplicateExists) {
      setSubjectErrorByLevel((prev) => ({
        ...prev,
        [key]: intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP4_MANUAL.SUBJECTS.DUPLICATE_ERROR'}),
      }))
      return
    }

    const nextSelections = {
      ...effectiveSelections,
      [key]: rowSubjects.map((subjectName) =>
        subjectName === originalName ? nextName : subjectName
      ),
    }
    const nextSubjects = flattenSubjectsFromSelections(nextSelections)

    setSubjectErrorByLevel((prev) => ({...prev, [key]: null}))
    setLocalData((prev) => {
      // A renamed subject is a new subject: the old name's saved rows are queued for delete.
      const nextLocal = queueRemovedSubjects(
        {...data.manualData, ...prev, subjectSelectionsByLevel: effectiveSelections},
        {
          ...prev,
          subjectSelectionsByLevel: nextSelections,
          subjects: nextSubjects,
          subjectsCount: nextSubjects.length,
        }
      ) as typeof prev
      deferUpdateData({
        manualData: {
          ...nextLocal,
          manualActiveTab: 'subjects',
        },
      })
      return nextLocal
    })
    setEditingSubjectByLevel((prev) => ({...prev, [key]: null}))
  }

  const handleSubjectKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    curriculumName: string,
    levelName: string
  ) => {
    const key = getSubjectLevelKey(curriculumName, levelName)
    if (e.key === 'Enter') {
      e.preventDefault()
      // Avoid double-add: Enter adds in onKeyDown, but the input blur handler
      // can also run and re-add / cause flicker.
      skipSubjectInputBlurRef.current = true
      handleAddSubject(curriculumName, levelName)
    }
    if (e.key === 'Backspace' && !(subjectInputByLevel[key] || '').trim()) {
      const rowSubjects = getSubjectsForCurriculumLevel(curriculumName, levelName)
      if (rowSubjects.length > 0) {
        e.preventDefault()
        handleRemoveSubject(curriculumName, levelName, rowSubjects[rowSubjects.length - 1])
      }
    }
  }

  const handleDataChange = (category: string, index: number, field: string, value: any) => {
    const itemsKey = `${category}` as keyof typeof localData
    const newItems = [...(localData[itemsKey] as any[])]

    const normalizeStudentSubject = (raw: any, fallbackClass = '') => {
      if (raw && typeof raw === 'object') {
        const encoded = String(raw.value ?? '').trim()
        if (encoded.includes(STUDENT_SUBJECT_DELIMITER)) {
          const [namePart, classPart] = encoded.split(STUDENT_SUBJECT_DELIMITER)
          const name = String(namePart ?? '').trim()
          const cls = String(classPart ?? fallbackClass).trim()
          const definedSubject = (localData.subjects as any[]).find((s) => s.name === name)
          return {
            name,
            class: cls,
            teacher: raw.teacher ?? definedSubject?.teacher,
          }
        }

        const name = String(raw.name ?? raw.subjectName ?? raw.label ?? '').trim()
        const cls = String(raw.class ?? raw.className ?? fallbackClass).trim()
        const definedSubject = (localData.subjects as any[]).find((s) => s.name === name)
        return {
          name,
          class: cls,
          teacher: raw.teacher ?? definedSubject?.teacher,
        }
      }

      const rawStr = String(raw ?? '').trim()
      if (!rawStr) return {name: '', class: fallbackClass, teacher: undefined}

      if (rawStr.includes(STUDENT_SUBJECT_DELIMITER)) {
        const [namePart, classPart] = rawStr.split(STUDENT_SUBJECT_DELIMITER)
        const name = String(namePart ?? '').trim()
        const cls = String(classPart ?? fallbackClass).trim()
        const definedSubject = (localData.subjects as any[]).find((s) => s.name === name)
        return {name, class: cls, teacher: definedSubject?.teacher}
      }

      if (data.mode === 'subject' && rawStr.includes('-')) {
        const lastDash = rawStr.lastIndexOf('-')
        const name = rawStr.slice(0, lastDash).trim()
        const cls = rawStr.slice(lastDash + 1).trim() || fallbackClass
        const definedSubject = (localData.subjects as any[]).find((s) => s.name === name)
        return {name, class: cls, teacher: definedSubject?.teacher}
      }

      const definedSubject = (localData.subjects as any[]).find((s) => s.name === rawStr)
      return {
        name: rawStr,
        class: String(definedSubject?.class ?? fallbackClass).trim(),
        teacher: definedSubject?.teacher,
      }
    }

    if (category === 'students' && field === 'subjects') {
      const subjectStrings = Array.isArray(value)
        ? value
        : value.split(',').map((s: string) => s.trim())
      const currentClass = String(newItems[index]?.class ?? '').trim()
      let normalizedSubjects = subjectStrings
        .map((raw: any) => normalizeStudentSubject(raw, currentClass))
        .filter((entry: any) => entry.name)

      // A row class that doesn't resolve to any known class (e.g. an uploaded file
      // whose class names don't exist in this school) must NOT veto the selection —
      // treat it like the no-class case below and adopt the picked subject's class.
      const knownClassKeys = new Set(
        ((localData.classes as any[]) || [])
          .map((c: any) => normalizeClassMatchKey(c?.name))
          .filter(Boolean)
      )
      const currentClassKey = normalizeClassMatchKey(currentClass)
      const classIsKnown = Boolean(currentClassKey) && knownClassKeys.has(currentClassKey)

      if (currentClass && classIsKnown) {
        // Tolerant compare: a file-imported row's class spelling may differ from the
        // wizard's ("Class 1A" vs "Class 1-A") while still being the same class.
        normalizedSubjects = normalizedSubjects
          .filter(
            (entry: any) => !entry.class || normalizeClassMatchKey(entry.class) === currentClassKey
          )
          .map((entry: any) => ({...entry, class: currentClass}))
      } else if (normalizedSubjects.length > 0) {
        const firstClass = String(normalizedSubjects[0]?.class ?? '').trim()
        if (firstClass) {
          normalizedSubjects = normalizedSubjects
            .filter((entry: any) => String(entry.class ?? '').trim() === firstClass)
            .map((entry: any) => ({...entry, class: firstClass}))
          newItems[index] = {
            ...newItems[index],
            class: firstClass,
          }
        }
      }

      newItems[index] = {
        ...newItems[index],
        [field]: normalizedSubjects,
      }
    } else if (category === 'students' && field === 'class') {
      // A tree option comes as its `class_id` (CH-1): the row stores that id, plus the class's
      // stored name and curriculum for the payload. Otherwise a class name two curricula share
      // comes as "name + curriculum" (studentClassOptions).
      const raw = String(value ?? '')
      const pickedNode = treeClasses.find((cls) => String(cls.classId) === raw)
      const [pickedClass, pickedCurriculum] = pickedNode
        ? [pickedNode.name, pickedNode.curriculum]
        : raw.split(CLASS_CURRICULUM_SEP)
      const selectedClass = String(pickedClass ?? '').trim()
      const existingSubjects = Array.isArray(newItems[index]?.subjects)
        ? newItems[index].subjects
        : []
      const normalizedSubjects = existingSubjects
        .map((raw: any) => normalizeStudentSubject(raw, selectedClass))
        .filter((entry: any) => entry.name)

      const nextSubjects = selectedClass
        ? normalizedSubjects
            .filter((entry: any) => !entry.class || entry.class === selectedClass)
            .map((entry: any) => ({...entry, class: selectedClass}))
        : normalizedSubjects

      // The curriculum, branch and level belong to the picked class: a tree option pins all
      // three (the save places a student by class name + branch + level, and a name two
      // branches share is refused without them), a name shared by two curricula pins the
      // curriculum, anything else none (the payload resolves them from the class). Stale ones
      // from the previous class would misplace the student.
      const {
        curriculum: _previousCurriculum,
        classId: _previousClassId,
        branch: _previousBranch,
        level: _previousLevel,
        _missing: previousMissing,
        classMissing,
        previousClass,
        ...rest
      } = newItems[index] ?? {}
      // A row whose class went with a removed curriculum is settled once a class is picked; it
      // then goes out as any other row (the server creates the student again if it removed it).
      const stillLost = classMissing === true && !selectedClass
      // No subject left for the new class: the file's own (kept when its class could not be
      // placed), else — subject-wise school — every subject the class teaches.
      const filledSubjects =
        selectedClass && nextSubjects.length === 0
          ? subjectsForPickedClass(
              newItems[index],
              pickedNode ?? {name: selectedClass, curriculum: pickedCurriculum},
              selectedClass
            )
          : nextSubjects
      const missing = withoutFilledMissing(
        previousMissing,
        Boolean(selectedClass),
        filledSubjects.length > 0
      )
      newItems[index] = {
        ...rest,
        ...(pickedCurriculum?.trim() ? {curriculum: pickedCurriculum.trim()} : {}),
        ...(pickedNode
          ? {
              classId: pickedNode.classId,
              ...(pickedNode.branch ? {branch: pickedNode.branch} : {}),
              ...(pickedNode.level ? {level: pickedNode.level} : {}),
            }
          : {}),
        ...(missing.length > 0 ? {_missing: missing} : {}),
        ...(stillLost ? {classMissing, previousClass} : {}),
        class: selectedClass,
        subjects: filledSubjects,
      }
    } else if (category === 'classes' && (field === 'grade' || field === 'section')) {
      const updated = {...newItems[index], [field]: value}
      const g = (updated.grade || '').toString().trim()
      const s = (updated.section || '').toString().trim()
      updated.name = g && s ? `${g}-${s}` : `${g}${s}`
      newItems[index] = updated
    } else {
      newItems[index] = {...newItems[index], [field]: value}
    }
    setLocalData((prev) => {
      const next = {...prev, [itemsKey]: newItems}
      deferUpdateData({manualData: next})
      return next
    })
  }

  const [focusNewRowAt, setFocusNewRowAt] = useState<{category: string; index: number} | null>(null)
  useEffect(() => {
    if (!focusNewRowAt) return
    const {category, index} = focusNewRowAt
    const id = `step4-row-first-input-${category}-${index}`
    const timer = setTimeout(() => {
      document.getElementById(id)?.focus()
      setFocusNewRowAt(null)
    }, 0)
    return () => clearTimeout(timer)
  }, [focusNewRowAt])

  const handleAddRow = (category: string, requestFocusOnNew = false) => {
    // if (category === 'teachers' && !freemiumTeacherLimit.canAddTeacher) {
    //   toast.info(freemiumTeacherLimit.message)
    //   return
    // }

    const itemsKey = `${category}` as keyof typeof localData
    const countKey = `${category}Count` as keyof typeof localData
    const existingItems = (localData[itemsKey] as any[]) || []
    const count = (localData[countKey] as number) || 0
    const char = String.fromCharCode(65 + (count % 26))
    const num = count + 1

    let newItem: any = {}
    if (category === 'teachers') {
      newItem = {
        first_name: '',
        last_name: '',
        employeeId: '',
        level: '',
        email: '',
        cnic: '',
      }
    } else if (category === 'classes') {
      newItem = {
        branch: '',
        grade: '',
        section: '',
        teacher: '',
        name: '',
        level: '',
      }
    } else if (category === 'subjects') {
      newItem = {
        name: `Subject ${char}`,
        teacher: `Teacher ${char}`,
        class: 'Class 1A',
        frequency: '1/day',
        duration: '1',
      }
    } else if (category === 'students') {
      newItem = {
        full_name: '',
        class: '',
        registrationNumber: `REG${num}`,
        subjects: [],
      }
    } else if (category === 'lessonPlanning') {
      newItem = {}
    }

    const newItems = [...existingItems, newItem]
    const newIndex = existingItems.length
    setLocalData((prev) => {
      const next = {
        ...prev,
        [countKey]: newItems.length,
        [itemsKey]: newItems,
      }
      deferUpdateData({manualData: next})
      return next
    })
    // A new student is the last row: show its page, or the focus below finds no input.
    if (category === 'students') setStudentsPage(Math.floor(newIndex / studentsPageSize) + 1)
    if (requestFocusOnNew && (category === 'teachers' || category === 'students')) {
      setFocusNewRowAt({category, index: newIndex})
    }
  }
  // `handleAddRow` reads `localData` from its render. After a buffered cell commits on Enter, the
  // row is added from the next render's copy, or it would overwrite the value just committed.
  const latestHandleAddRowRef = useRef(handleAddRow)
  useLayoutEffect(() => {
    latestHandleAddRowRef.current = handleAddRow
  })
  const addStudentRowAfterCommit = () =>
    window.setTimeout(() => latestHandleAddRowRef.current('students', true), 0)

  // --- Lesson Planning Excel import (same APIs as the Excel Import module) -----------
  const excelImportInputRef = useRef<HTMLInputElement | null>(null)
  const [excelImportFile, setExcelImportFile] = useState<File | null>(null)
  /**
   * The last import, when some of its rows were refused: shown under the upload buttons with
   * "View details". Kept here, not in the panel: the import's refresh re-hydrates the wizard and
   * remounts the tab.
   */
  const [allocationLastImport, setAllocationLastImport] = useState<AllocationImportDone | null>(
    null
  )
  const [downloadingExcelTemplate, setDownloadingExcelTemplate] = useState(false)

  /**
   * "Download template" on Lesson Planning: `GET onboarding/lesson-planning-template`. The
   * backend builds the workbook (teacher / class / subject dropdowns) from what is SAVED, so
   * unsynced class/subject edits are saved first, the same sync Next runs (unchanged sections
   * are skipped) — otherwise the file came back with teachers but no subjects. Plain function,
   * like the students template handler: saveProgress is declared further down.
   */
  const handleDownloadExcelTemplate = () => {
    if (downloadingExcelTemplate) return
    setDownloadingExcelTemplate(true)
    const release = () => setDownloadingExcelTemplate(false)

    const requestTemplate = () => {
      dispatch(
        downloadLessonPlanningTemplate(
          (res: any) => {
            release()
            // The object's Content-Disposition header makes the browser save it under the
            // server's file name.
            const url = res?.result?.url
            if (url) {
              const a = document.createElement('a')
              a.href = url
              a.download = ''
              a.rel = 'noopener noreferrer'
              document.body.appendChild(a)
              a.click()
              document.body.removeChild(a)
            }
          },
          () => {
            release()
            toast.error(intl.formatMessage({id: 'GENERAL.SOMETHING_WENT_WRONG'}))
          }
        ) as never
      )
    }

    if (onPartialSubmit) {
      // Only classes and subjects can be missing server-side here; the checkpoint's other
      // sections (duration, timings, teachers, ...) are not compared, so their snapshots
      // drifting cannot turn a download into a bulk-import.
      onPartialSubmit(
        ['classes', 'subjects'],
        saveProgress(activeTab),
        requestTemplate,
        {sectionsOnly: ['classes', 'subjects']},
        release
      )
    } else {
      requestTemplate()
    }
  }

  // On the AI servers `csv-import/upload` also takes a PDF, Word file, photo or scan and
  // converts it into the Teacher Allocation template first. Elsewhere: spreadsheets only.
  const allocationConversionAvailable = isDataUploadConversionAvailable()
  const allocationUploadConfig = useDataUploadConfig('allocation', allocationConversionAvailable)

  /**
   * Files picked together on Teacher Allocation: sent in ONE request and read as one upload, so
   * a teacher listed in one file and placed in another is not asked about. The first is the
   * card's file; the rest go with it (a line each). Tied to that first file: anything else put
   * on the card (the Teachers file's allocations) goes alone.
   */
  const [allocationBatch, setAllocationBatch] = useState<{
    primary: File
    companions: File[]
  } | null>(null)
  const allocationCompanionFiles =
    allocationBatch && allocationBatch.primary === excelImportFile ? allocationBatch.companions : []

  /** `file` (and the files picked with it) onto the upload card, read as allocations. */
  const startAllocationFile = (file: File, companions: File[] = []) => {
    setAllocationLastImport(null)
    handedAllocationFileRef.current = null
    setAllocationBatch(companions.length > 0 ? {primary: file, companions} : null)
    setExcelImportFile(file)
    setExcelImportFromPrefill(false)
    setAllocationPrefillReport(null)
  }

  /**
   * One file's X on the Teacher Allocation uploader, when several are read together: the others
   * are read again without it (the first left leads). The last one goes as the card's own X.
   */
  const removeAllocationFile = (removed: File) => {
    const left = [excelImportFile, ...allocationCompanionFiles].filter(
      (one): one is File => Boolean(one) && one !== removed
    )
    if (left.length === 0) return
    const [first, ...rest] = left
    if (first === excelImportFile) {
      setAllocationBatch(rest.length > 0 ? {primary: first, companions: rest} : null)
      return
    }
    startAllocationFile(first, rest)
  }

  /** Files refused on the last add to the Teacher Allocation uploader, with the reason each. */
  const [allocationRejected, setAllocationRejected] = useState<UploaderRejectedFile[]>([])

  /**
   * Files dropped or picked for Teacher Allocation (the shared uploader, or the landing card's
   * "Upload files"). Checked like Add Teachers: each file against the data-upload limits, then the
   * file count and total size across the selection; refused files are listed with the reason and
   * never discard the accepted ones. With picked files on the card already, the new ones join
   * them and all are read again together (a teacher listed in one and placed in another is
   * matched); the Teachers file's allocations on the card are replaced instead.
   */
  const handleAllocationFiles = (picked: File[]) => {
    const t = (id: string, values?: Record<string, string | number>) =>
      intl.formatMessage({id: `ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.${id}`}, values)
    const current =
      excelImportFile && !excelImportFromPrefill ? [excelImportFile, ...allocationCompanionFiles] : []
    const rejected: UploaderRejectedFile[] = []
    const seen = new Set(current.map((file) => `${file.name}::${file.size}`))
    const maxFiles = allocationConversionAvailable
      ? Math.max(1, allocationUploadConfig.maxFiles)
      : Number.POSITIVE_INFINITY
    const maxTotalBytes = allocationConversionAvailable
      ? (allocationUploadConfig.maxTotalBytes ??
        allocationUploadConfig.maxFileBytes * allocationUploadConfig.maxFiles)
      : Number.POSITIVE_INFINITY
    let count = current.length
    let total = current.reduce((sum, file) => sum + file.size, 0)
    const accepted: File[] = []
    picked.forEach((file) => {
      const legacyXls = dataUploadFileExtension(file.name) === 'xls'
      // Legacy .xls keeps working as a plain spreadsheet upload; everything else is checked
      // against the data-upload limits before it is sent (an oversized file fails with an HTML
      // 413 that cannot be read).
      const refusal =
        allocationConversionAvailable && !legacyXls
          ? validateDataUploadFile(file, allocationUploadConfig)
          : null
      const reject = (text: string) => rejected.push({name: file.name, text})
      if (refusal) {
        reject(
          dataUploadErrorMessage(intl.formatMessage, refusal, {
            config: allocationUploadConfig,
            useCaseKey: 'allocation',
            fileName: file.name,
          })
        )
      } else if (seen.has(`${file.name}::${file.size}`)) {
        reject(t('FILE_ERROR_DUPLICATE'))
      } else if (count >= maxFiles) {
        reject(t('FILE_REJECT_TOO_MANY', {limit: maxFiles}))
      } else if (total + file.size > maxTotalBytes) {
        reject(t('FILE_REJECT_TOTAL', {limit: formatDataUploadBytes(maxTotalBytes)}))
      } else {
        seen.add(`${file.name}::${file.size}`)
        count += 1
        total += file.size
        accepted.push(file)
      }
    })
    // Read together, only files the server can convert: a legacy .xls goes on its own.
    const all = [...current, ...accepted]
    const together =
      all.length > 1
        ? accepted.filter((file) => {
            if (dataUploadFileExtension(file.name) !== 'xls') return true
            rejected.push({
              name: file.name,
              text: intl.formatMessage(
                {id: 'ONBOARDING_WIZARD.STEP4_MANUAL.LESSON_PLANNING.XLS_ON_ITS_OWN'},
                {file: file.name}
              ),
            })
            return false
          })
        : accepted
    setAllocationRejected(rejected)
    if (together.length === 0) return
    // A new file leads, so the card reads again; the ones already on it go with it.
    const [first, ...rest] = together
    startAllocationFile(first, [...current, ...rest])
  }

  /** The upload card holds the Teachers file's allocations, not a picked file. */
  const [excelImportFromPrefill, setExcelImportFromPrefill] = useState(false)
  /** A prefill whose rows all waited on something (teachers not saved yet): says why. */
  const [allocationPrefillReport, setAllocationPrefillReport] =
    useState<DataUploadConversion | null>(null)

  /**
   * "Don't offer the Teachers file's allocations again" for the draft on the card (or asked
   * about). Called once the user is done with it: imported, removed, or its report dismissed.
   */
  const markAllocationPrefillHandledRef = useRef<(() => void) | null>(null)
  const markAllocationPrefillHandled = () => {
    markAllocationPrefillHandledRef.current?.()
    markAllocationPrefillHandledRef.current = null
  }

  /** The allocation prefill's template, onto the upload card (or its report, when empty). */
  const openAllocationPrefill = (conversion: DataUploadConversion) => {
    if (!conversion.template || conversion.report.written === 0) {
      setAllocationPrefillReport(conversion)
      return
    }
    setAllocationPrefillReport(null)
    setAllocationLastImport(null)
    setExcelImportFile(dataUploadTemplateToFile(conversion.template))
    setExcelImportFromPrefill(true)
  }
  /**
   * The allocation prefill asks which branch its lines are in (its classes exist in several and
   * the Teachers file did not say), and which classes lines with no class, or a class no single
   * class takes, are in. Asked first; the template is then fetched again with every answer so
   * far (`?branchAssignments=`, `?allocationAssignments=`), which may ask again (a class that
   * still has several sections once its branch is known), until nothing is open.
   */
  const [allocationPrefillQuestion, setAllocationPrefillQuestion] =
    useState<AllocationPrefillQuestions | null>(null)
  const [allocationPrefillAnswering, setAllocationPrefillAnswering] = useState(false)
  /**
   * The questions above, skipped or closed with the X: the allocations stay offered ("Answer
   * questions" asks them again), and a later visit shows that button, not the questions.
   */
  const [allocationPrefillSkipped, setAllocationPrefillSkipped] =
    useState<AllocationPrefillQuestions | null>(null)
  const skippedAllocationDraftsRef = useRef(new Set<string>())
  /**
   * "Which classes?" answers given on the Teachers step (Step4TeacherEntry reports them). The
   * prefill template is fetched with them, so those pairs are not asked again here.
   */
  const teachersAllocationAnswersRef = useRef<DataUploadAllocationAssignment[]>([])
  /** The prefill template is being fetched again with the Teachers step's answers. */
  const [allocationPrefillFetching, setAllocationPrefillFetching] = useState(false)
  /** The allocation prefill's answers so far: sent with "applied" when it is imported. */
  const allocationPrefillAnswersRef = useRef<AllocationPrefillAnswers | null>(null)
  /** Every answer so far, kept here and on the draft (fire and forget). */
  const rememberAllocationPrefillAnswers = (draftId: string, answers: AllocationPrefillAnswers) => {
    allocationPrefillAnswersRef.current = answers
    saveDraftAnswers(dispatch, draftId, allocationDraftAnswers(answers))
  }
  /**
   * A prefill fetch that failed. Every line skipped (`ALL_LINES_SKIPPED`: "Which classes?"
   * answered with none) is what the user chose, not a failure: said as information, and the
   * Teachers file's allocations are done with, nothing being left to import.
   */
  const allocationPrefillFailed = (error: unknown) => {
    const message = dataUploadErrorMessage(intl.formatMessage, error, {useCaseKey: 'allocation'})
    if (!dataUploadErrorIsInfo(error)) {
      toast.error(message)
      return
    }
    toast.info(message)
    setAllocationPrefillQuestion(null)
    setAllocationPrefillSkipped(null)
    markAllocationPrefillHandled()
  }

  /** The prefill draft as our template, fetched with `answers`. */
  const fetchAllocationPrefill = (draftId: string, answers: AllocationPrefillAnswers) =>
    dispatchApi(dispatch, (ok, fail) =>
      getDataUploadDraftTemplate(
        draftId,
        {
          flavour: 'onboarding',
          branchAssignments: answers.branches,
          allocationAssignments: answers.allocations,
        },
        ok,
        fail
      )
    ).then((res: any) => normalizeDataUploadConversion(res?.result ?? res))

  /** Ask what the template still leaves open, or put it on the upload card. */
  const continueAllocationPrefill = (
    draftId: string,
    conversion: DataUploadConversion,
    answers: AllocationPrefillAnswers
  ) => {
    allocationPrefillAnswersRef.current = answers
    const open = withoutAnsweredBranches(conversion.branchQuestions, answers.branches)
    const allocations = withoutAnsweredAllocations(
      conversion.allocationQuestions,
      answers.allocations
    )
    // One branch to choose from: that one, without asking.
    const single = singleBranchAnswers(open)
    const questions = open.filter((question) => !(question.key in single))
    const next = {...answers, branches: {...answers.branches, ...single}}
    if (Object.keys(single).length > 0) {
      if (questions.length === 0 && allocations.length === 0) {
        // Nothing left to ask: fetched again with the branch, which may still ask for classes.
        setAllocationPrefillFetching(true)
        fetchAllocationPrefill(draftId, next)
          .then((fetched) => {
            if (fetched) continueAllocationPrefill(draftId, fetched, next)
          })
          .catch(allocationPrefillFailed)
          .finally(() => setAllocationPrefillFetching(false))
        return
      }
    }
    if (questions.length > 0 || allocations.length > 0) {
      // The single-branch answers go with the Confirm re-fetch too.
      const pending = {draftId, questions, allocations, answers: next}
      if (skippedAllocationDraftsRef.current.has(draftId)) setAllocationPrefillSkipped(pending)
      else setAllocationPrefillQuestion(pending)
      return
    }
    openAllocationPrefill(conversion)
  }

  /**
   * A file handed over from the Students step ("Save and assign" on its new teachers): it is
   * read here as allocations, so the Teachers file's allocations wait until the next visit
   * instead of replacing it on the card.
   */
  const handedAllocationFileRef = useRef<File | null>(null)
  /**
   * A Students-step file whose new teachers were saved to be assigned: its allocations wait
   * for this step, and open on its upload card (with their class questions) when it is opened.
   * The Students step is not left mid-flow for them.
   */
  const [queuedAllocationFile, setQueuedAllocationFile] = useState<{
    file: File
    /** Its allocations as the Students step read them (`carried.allocation`): not read again. */
    draftId?: string | null
  } | null>(null)
  useEffect(() => {
    if (activeTab !== 'lessonPlanning' || !queuedAllocationFile) return
    const {file, draftId} = queuedAllocationFile
    if (draftId) {
      openCarriedAllocation(file, draftId)
      openedAllocationDraftsRef.current.add(draftId)
    }
    handedAllocationFileRef.current = file
    setAllocationLastImport(null)
    setAllocationPrefillReport(null)
    setExcelImportFromPrefill(false)
    setExcelImportFile(file)
    setQueuedAllocationFile(null)
    setWaitingAllocationDraft(null)
  }, [activeTab, queuedAllocationFile])

  /** A carried draft is done with (imported, saved, or not wanted): closed on the server. */
  const closeCarriedDraft = (draftId: string | null | undefined) => {
    if (!draftId) return
    dispatchApi(dispatch, (ok, fail) => deleteDataUploadDraft(draftId, ok, fail)).catch(
      () => undefined
    )
  }
  /**
   * A carried draft found after a reload (`GET data-upload/drafts?title=onboarding-carry`), with
   * no file to show for it: the card names it by the step it came from.
   */
  const carriedFileNamed = (id: string) =>
    new File([], intl.formatMessage({id}), {type: 'application/octet-stream'})
  /** Carried allocation drafts opened on the card in this visit: never offered again. */
  const openedAllocationDraftsRef = useRef(new Set<string>())
  /** Teacher Allocation: allocations a Students upload read, waiting to be opened here. */
  const [waitingAllocationDraft, setWaitingAllocationDraft] = useState<{
    draftId: string
    records: number
  } | null>(null)
  // The waiting carried drafts, looked up when a step opens (they outlive a reload).
  useEffect(() => {
    if (!isDataUploadConversionAvailable()) return
    const wanted =
      activeTab === 'lessonPlanning'
        ? (['allocation', 'students'] as const)
        : activeTab === 'students'
          ? (['students'] as const)
          : activeTab === 'teachers'
            ? (['teachers'] as const)
            : []
    wanted.forEach((useCaseKey) => {
      if (useCaseKey === 'students' && data.mode !== 'subject') return
      dispatchApi(dispatch, (ok, fail) =>
        listDataUploadDrafts({useCaseKey, title: DATA_UPLOAD_CARRY_TITLE}, ok, fail)
      )
        .then((res) => {
          const [draft] = dataUploadDraftList(res)
          if (!draft) return
          // A Teacher Allocation file's teachers, found after a reload: named by where it came from.
          if (useCaseKey === 'teachers') {
            if (teachersCarriedDraftsTaken.current.has(draft.id)) return
            setQueuedTeachersFile(
              (prev) =>
                prev ?? {
                  file: carriedFileNamed('ONBOARDING_WIZARD.STEP4_MANUAL.STUDENTS.CARRIED_FILE'),
                  draftId: draft.id,
                }
            )
            return
          }
          if (useCaseKey === 'allocation') {
            if (openedAllocationDraftsRef.current.has(draft.id)) return
            setWaitingAllocationDraft((prev) =>
              prev?.draftId === draft.id ? prev : {draftId: draft.id, records: draft.records}
            )
            return
          }
          // Students waiting: "Go to Students" here, or read at once on the Students step.
          setQueuedStudentsFile(
            (prev) =>
              prev ??
              (studentsCarriedDraftsTaken.current.has(draft.id)
                ? null
                : {
                    file: carriedFileNamed('ONBOARDING_WIZARD.STEP4_MANUAL.STUDENTS.CARRIED_FILE'),
                    count: draft.records,
                    draftId: draft.id,
                  })
          )
        })
        .catch(() => undefined)
    })
  }, [activeTab])

  // Leaving the tab drops the file on its card: it is not posted again when the tab reopens (a
  // students image read again, its error shown over the Teachers file's allocations). The
  // Teachers file's allocations come back on their own: that draft is not marked handled yet.
  useEffect(() => {
    if (activeTab === 'lessonPlanning') return
    handedAllocationFileRef.current = null
    setAllocationBatch(null)
    setAllocationPrefillHeld(null)
    setExcelImportFile(null)
    setExcelImportFromPrefill(false)
    setAllocationPrefillReport(null)
  }, [activeTab])

  /**
   * The Teachers file's allocations, held while a file the user picked is on the card: they
   * never replace it (or hide it mid-upload). "Use the allocations from the Teachers file"
   * opens them when the user wants them.
   */
  const [allocationPrefillHeld, setAllocationPrefillHeld] = useState<{
    draftId: string
    conversion: DataUploadConversion
  } | null>(null)

  /** The Teachers file's allocations onto the card (after their questions, if any). */
  const applyAllocationPrefill = (draftId: string, conversion: DataUploadConversion) => {
    setExcelImportFile(null)
    setExcelImportFromPrefill(false)
    setAllocationPrefillReport(null)
    setAllocationPrefillSkipped(null)
    setAllocationPrefillHeld(null)
    // Answers kept on the draft (given before a logout) come back with its template; the
    // Teachers step's answers from this visit replace theirs for the same teacher + subject.
    const stored = conversion.answers
    const answers: AllocationPrefillAnswers = {
      branches: {...(stored?.branchAssignments ?? {})},
      allocations: mergeAllocationAnswers(
        stored?.allocationAssignments ?? [],
        teachersAllocationAnswersRef.current
      ),
    }
    if (teachersAllocationAnswersRef.current.length > 0) {
      rememberAllocationPrefillAnswers(draftId, answers)
    }
    // Some classes were already picked on the Teachers step: fetched again with those
    // answers first, so their lines are in the template and only the rest is asked.
    const answeredOnTeachers =
      answers.allocations.length > 0 &&
      allocationPairCount(
        withoutAnsweredAllocations(conversion.allocationQuestions, answers.allocations)
      ) < allocationPairCount(withoutAnsweredAllocations(conversion.allocationQuestions, []))
    if (!answeredOnTeachers) {
      continueAllocationPrefill(draftId, conversion, answers)
      return
    }
    setAllocationPrefillFetching(true)
    fetchAllocationPrefill(draftId, answers)
      .then((next) => {
        if (next) continueAllocationPrefill(draftId, next, answers)
      })
      .catch(allocationPrefillFailed)
      .finally(() => setAllocationPrefillFetching(false))
  }

  /** "Use the allocations from the Teachers file": named by the step the file was uploaded on. */
  const useAllocationsFrom = (step: 'teachers' | 'students') =>
    intl.formatMessage(
      {id: 'ONBOARDING_WIZARD.STEP4_MANUAL.LESSON_PLANNING.USE_ALLOCATIONS_FROM'},
      {step: intl.formatMessage({id: STEP_TITLE_KEYS[step]})}
    )

  /**
   * "Answer questions (N)": the Teachers file's allocation questions left open (closed with the
   * X, or a branch question skipped), opened again in the same dialog.
   */
  const reopenAllocationPrefillQuestions = () => {
    const skipped = allocationPrefillSkipped
    if (!skipped) return
    // Answering again: what this answer leaves open is asked, not put back behind the button.
    skippedAllocationDraftsRef.current.delete(skipped.draftId)
    setAllocationPrefillQuestion(skipped)
    setAllocationPrefillSkipped(null)
  }
  const allocationPrefillAnswerLabel = (skipped: AllocationPrefillQuestions) =>
    intl.formatMessage(
      {id: 'DATA_UPLOAD.QUESTIONS.ANSWER_COUNT'},
      {count: skipped.questions.length + allocationPairCount(skipped.allocations)}
    )

  /** A file the user picked (not the Teachers file's allocations) is on the upload card. */
  const ownAllocationFileOnCard = Boolean(excelImportFile) && !excelImportFromPrefill

  // The Teachers file may hold the allocations too (`onboarding/prefill`). Fetched when this
  // step opens: the template only fills rows whose teacher, class and subject the school has
  // by then. It goes through the import modal exactly as if the user had picked it.
  const {
    reading: allocationPrefillReading,
    percent: allocationPrefillPercent,
    rowsFound: allocationPrefillRowsFound,
  } = useOnboardingPrefillDraft({
    useCaseKey: 'allocation',
    // The server flow places the Teachers file's lines itself (POST allocation/prefill).
    active: activeTab === 'lessonPlanning' && !allocationServerMode,
    templateParams: {flavour: 'onboarding'},
    deferHandled: true,
    answers: () => allocationDraftAnswers(allocationPrefillAnswersRef.current),
    onDraft: ({draftId, conversion, markHandled}) => {
      // A file handed over from the Students step is on the card: the draft waits (it is not
      // marked handled, so the next visit offers it).
      if (handedAllocationFileRef.current) return
      markAllocationPrefillHandledRef.current = markHandled
      // A file the user picked is on the card (picked before the draft was found): it stays;
      // the Teachers file's allocations wait behind "Use the allocations from the Teachers file".
      if (ownAllocationFileOnCard) {
        setAllocationPrefillHeld({draftId, conversion})
        return
      }
      applyAllocationPrefill(draftId, conversion)
    },
  })

  // After the Excel-import refresh lands (isHydrating true -> false), reseed the
  // lesson-planning grid from the re-hydrated wizard data. The grid keeps its own state
  // and never re-reads the parent after mount, so the imported allocations would
  // otherwise stay invisible until a full reload.
  const pendingExcelReseedRef = useRef(false)
  const prevHydratingRef = useRef(isHydrating)

  // Server-side Teacher Allocation: once edits were saved there, the wizard's own copy of the
  // grid is read again from the server (the same re-read as after an allocation file import),
  // so a later save (Academic Structure, Students) never sends stale teachers back over them.
  // On leaving the tab the grid here is reseeded too; on leaving the step only the wizard's.
  const onExcelImportedRef = useRef(onExcelImported)
  onExcelImportedRef.current = onExcelImported
  useEffect(() => {
    if (activeTab === 'lessonPlanning' || !allocationServerDirtyRef.current) return
    allocationServerDirtyRef.current = false
    pendingExcelReseedRef.current = true
    onExcelImportedRef.current?.()
  }, [activeTab])
  useEffect(
    () => () => {
      if (!allocationServerDirtyRef.current) return
      allocationServerDirtyRef.current = false
      onExcelImportedRef.current?.()
    },
    []
  )
  useEffect(() => {
    const wasHydrating = prevHydratingRef.current
    prevHydratingRef.current = isHydrating
    if (!pendingExcelReseedRef.current) return
    if (wasHydrating && !isHydrating) {
      pendingExcelReseedRef.current = false
      const reseeded = ((data.manualData.lessonPlanningData as any) || {}) as LessonPlanningData
      const imported: string[] = []
      Object.entries(reseeded).forEach(([classKey, subjects]) =>
        Object.entries(subjects || {}).forEach(([subject, cell]) => {
          const teacher = String(cell?.teacher ?? '').trim()
          const before = String(
            lessonPlanningDataRef.current[classKey]?.[subject]?.teacher ?? ''
          ).trim()
          if (teacher && teacher !== before) imported.push(`${classKey}|||${subject}`)
        })
      )
      if (imported.length > 0) setImportedSlots((prev) => new Set([...prev, ...imported]))
      setLessonPlanningData(reseeded)
    }
  }, [isHydrating, data.manualData])

  /**
   * New teachers an upload names (Teacher Allocation lines, or a Students file's teacher list),
   * saved the way the Add Teachers step saves teachers: added to the list, then the wizard's
   * `teachers` sync (bulk-import).
   * One already in the list (same name and Employee ID) is not added twice. Rejects when the
   * sync fails (it has already said why).
   */
  const saveNewTeachersToWizard = (teachers: DataUploadNewTeacher[]) =>
    new Promise<void>((resolve, reject) => {
      if (!onPartialSubmit || teachers.length === 0) {
        reject(new Error('Teachers cannot be saved here'))
        return
      }
      const existing = (localData.teachers as any[]) || []
      const known = new Set(
        existing.map((teacher: any) =>
          [
            `${teacher?.first_name ?? ''} ${teacher?.last_name ?? ''}`.trim().toLowerCase(),
            String(teacher?.employeeId ?? '')
              .trim()
              .toLowerCase(),
          ].join('|')
        )
      )
      const fresh = teachers.filter(
        (teacher) =>
          !known.has(
            [teacher.name.trim().toLowerCase(), (teacher.employeeId ?? '').toLowerCase()].join('|')
          )
      )
      if (fresh.length === 0) {
        resolve()
        return
      }
      const rows = namesToTeacherRows(fresh.map((teacher) => teacher.name)).map((row, index) => ({
        ...row,
        employeeId: fresh[index].employeeId ?? row.employeeId,
        email: fresh[index].email ?? row.email,
      }))
      const next = {
        ...localData,
        teachers: [...existing, ...rows],
        teachersCount: existing.length + rows.length,
      }
      setLocalData(next)
      onPartialSubmit(
        ['teachers'],
        next as any,
        () => resolve(),
        undefined,
        (error) => reject(error)
      )
    })

  /** `localData` as of the last render: read by saves that run after another save resolved. */
  const latestLocalDataRef = useRef(localData)
  useLayoutEffect(() => {
    latestLocalDataRef.current = localData
  })

  /**
   * "Save students only" on Teacher Allocation: a workbook's new students into the Students list
   * — name, registration number and, when the server placed it (`classId`), that class with its
   * branch, level and the file's subjects it teaches — then the wizard's `students` sync. One
   * already in the list (same registration number) is not added twice; a row without a class
   * waits in the grid for one. Rejects when the sync fails (it has already said why).
   */
  const saveNewStudentsToWizard = (newStudents: DataUploadNewStudent[]) =>
    new Promise<void>((resolve, reject) => {
      if (!onPartialSubmit) {
        reject(new Error('Students cannot be saved here'))
        return
      }
      // Read now, not from this render: a teacher save just before it changed the list.
      const current = latestLocalDataRef.current
      const existing = (current.students as any[]) || []
      const known = new Set(
        existing
          .map((student: any) =>
            String(student?.registrationNumber ?? '')
              .trim()
              .toLowerCase()
          )
          .filter(Boolean)
      )
      const rows = newStudents
        .filter((student) => {
          const number = (student.registrationNumber ?? '').trim().toLowerCase()
          if (!number) return true
          if (known.has(number)) return false
          known.add(number)
          return true
        })
        .map((student) => {
          const base = {
            full_name: student.name,
            registrationNumber: student.registrationNumber ?? '',
          }
          const record = student.classId ? classById(student.classId) : undefined
          if (!record) return {...base, class: '', subjects: []}
          const className = String(record.name ?? '').trim()
          const taught = new Map(
            uniqueStrings(getSubjectsForClass(record)).map((name) => [name.toLowerCase(), name])
          )
          return {
            ...base,
            classId: record.id,
            class: className,
            branch: String(record.branch ?? '').trim() || undefined,
            level: String(record.level ?? '').trim() || undefined,
            curriculum: String(record.curriculum ?? '').trim() || undefined,
            // Only what that class teaches; the rest is picked in the grid.
            subjects: student.subjects
              .filter((name) => taught.has(name.toLowerCase()))
              .map(
                (name) =>
                  `${taught.get(name.toLowerCase())}${STUDENT_SUBJECT_DELIMITER}${className}`
              ),
          }
        })
      if (rows.length === 0) {
        resolve()
        return
      }
      const students = [...existing, ...rows]
      const next = {...current, students, studentsCount: students.length}
      setLocalData(next)
      onPartialSubmit(
        ['students'],
        next as any,
        () => {
          toast.success(
            intl.formatMessage(
              {id: 'ONBOARDING_WIZARD.STEP4_MANUAL.STUDENTS.ADDED_FROM_FILE'},
              {count: rows.length}
            )
          )
          resolve()
        },
        undefined,
        (error) => reject(error)
      )
    })

  /**
   * A Teacher Allocation file's new students, asked about once its allocations are done there
   * (imported, or nothing left to import): the step's own data first, the other steps' after.
   */
  const [allocationNewStudents, setAllocationNewStudents] = useState<{
    file: File
    students: DataUploadNewStudent[]
    /** Their read, kept for the Students step (`carried.students`). */
    draftId?: string | null
  } | null>(null)
  const [savingAllocationStudents, setSavingAllocationStudents] = useState(false)
  /**
   * "Save and choose classes & subjects": the file waits for the Students step, where it is
   * read (as one card) the next time that step opens, or from "Go to Students". Nothing jumps
   * there on its own.
   */
  const [queuedStudentsFile, setQueuedStudentsFile] = useState<{
    file: File
    count: number
    /** Its students as Teacher Allocation read them (`carried.students`): not read again. */
    draftId?: string | null
  } | null>(null)
  /** Carried students drafts already taken onto the Students card in this visit. */
  const studentsCarriedDraftsTaken = useRef(new Set<string>())
  useEffect(() => {
    if (activeTab !== 'students' || !queuedStudentsFile) return
    const {file, draftId} = queuedStudentsFile
    setQueuedStudentsFile(null)
    if (draftId) {
      studentsCarriedDraftsTaken.current.add(draftId)
      studentsCarriedDraftRef.current.set(file, draftId)
    }
    // A file already on the Students card (read there before) keeps that card and its read.
    if (!studentsUploadFilesRef.current.has(file)) handleStudentsFiles([file])
  }, [activeTab, queuedStudentsFile])

  const handleExcelImported = useCallback(
    (done: AllocationImportDone) => {
      // Like "Add to table" on Students, a clean import leaves the panel; one with refused rows
      // stays, so what failed can be looked at.
      setAllocationLastImport(done.failed > 0 ? done : null)
      // What actually went in, from the payload sent: "30 teachers, 78 assignments applied."
      if ((done.assignments ?? 0) > 0) {
        toast.success(
          intl.formatMessage(
            {
              id: done.teachers
                ? 'ONBOARDING_WIZARD.STEP4_MANUAL.UPLOAD.APPLIED_SUMMARY'
                : 'ONBOARDING_WIZARD.STEP4_MANUAL.UPLOAD.APPLIED_SUMMARY_NO_TEACHERS',
            },
            {teachers: done.teachers ?? 0, assignments: done.assignments ?? 0}
          ),
          {autoClose: 8000}
        )
      }
      handedAllocationFileRef.current = null
      // Read from the Students step's carried draft: imported, so it is closed.
      closeCarriedDraft(carriedAllocationDraftOf(done.file))
      if (excelImportFromPrefill) markAllocationPrefillHandled()
      setExcelImportFromPrefill(false)
      trackAllocationEvent('excel_assignment')
      pendingExcelReseedRef.current = true
      onExcelImported?.()
      // The import wrote to the server: the server-side allocation reads it again.
      if (allocationServerMode) setAllocationServerRefreshKey((key) => key + 1)
    },
    [allocationServerMode, excelImportFromPrefill, intl, onExcelImported, trackAllocationEvent]
  )

  // Styled two-column template (Teacher Name, Employee ID (Optional)) with an instruction
  // block; see utils/teacherSampleTemplate.ts. Plain CSV of the same layout if Excel fails.
  const handleDownloadTeachersSampleCsv = useCallback(async () => {
    try {
      await downloadTeacherSampleTemplate()
    } catch {
      downloadTeacherSampleTemplateCsv()
    }
  }, [])

  const [downloadingStudentsTemplate, setDownloadingStudentsTemplate] = useState(false)

  // Hard-coded two-row sample: the fallback where the server has no students template
  // (non-AI servers answer 404).
  const handleDownloadStudentsSampleCsv = useCallback(async () => {
    const rows = [
      ['Registration Number', 'Full Name', 'Class', 'Subjects'],
      ['REG001', 'Example Student', 'Class 1A', 'English;Mathematics'],
      ['REG002', 'Another Student', 'Class 1A', 'English;Science'],
    ]
    try {
      await downloadRowsAsExcel(rows, 'onboarding-students-sample.xlsx')
    } catch {
      // Fall back to a plain CSV download if the Excel build fails
      const csv = `\uFEFF${rows
        .map((r) => r.map((c) => (c.includes(';') ? `"${c}"` : c)).join(','))
        .join('\n')}\n`
      const blob = new Blob([csv], {type: 'text/csv;charset=utf-8'})
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = 'onboarding-students-sample.csv'
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(url)
    }
  }, [])

  /**
   * "Download template" on Add Student: `GET onboarding/students-template`, built from what is
   * SAVED (classes, their subjects, existing students). Unsynced class/subject edits are saved
   * first, the same sync Next runs (unchanged sections are skipped), so the file never shows
   * the previous classes. Plain function: saveProgress is declared further down.
   */
  const handleDownloadStudentsTemplate = () => {
    if (downloadingStudentsTemplate) return
    setDownloadingStudentsTemplate(true)
    const release = () => setDownloadingStudentsTemplate(false)

    const requestTemplate = () => {
      dispatch(
        downloadStudentsTemplate(STUDENTS_CURRICULUM_COLUMN_ENABLED, release, (err: any) => {
          release()
          const status = Number(err?.statusCode ?? err?.status)
          const message = typeof err?.message === 'string' ? err.message.trim() : ''
          if (status === 409 || /^add classes\b/i.test(message)) {
            // No classes/subjects saved yet; the message says what to add.
            toast.error(message || intl.formatMessage({id: 'GENERAL.SOMETHING_WENT_WRONG'}))
          } else if (status === 404 || !status) {
            // Non-AI server: no template endpoint, keep the local sample.
            void handleDownloadStudentsSampleCsv()
          } else {
            toast.error(intl.formatMessage({id: 'GENERAL.SOMETHING_WENT_WRONG'}))
          }
        }) as never
      )
    }

    if (onPartialSubmit) {
      onPartialSubmit(
        ['classes', 'subjects'],
        saveProgress(activeTab),
        requestTemplate,
        undefined,
        release
      )
    } else {
      requestTemplate()
    }
  }

  /**
   * Checks uploaded rows against the wizard's own data before they reach the grid (a paste
   * into Excel skips the template's dropdown check): the class must be a wizard class (exact
   * or tolerant match, scoped to the row's branch/level when the label carried them) and every
   * subject one of that class's subjects. Valid rows come back resolved to the wizard's class
   * name and subject spelling.
   *
   * Nothing is dropped and nothing blocks the file. A value the wizard cannot resolve is
   * cleared and its cell flagged in `_missing`, so the row lands in the grid with that cell
   * highlighted and Next waits until the user picks one — the same treatment a cell the file
   * left blank already gets. `issues` explains each one, for the panel above the table.
   * Plain function: getSubjectsForClass is declared further down.
   */
  /**
   * The subject names a file row listed: from its encoded subjects, else from `_fileSubjects`
   * (a row with no class yet keeps them there, since a subject hangs off a class).
   */
  const fileSubjectNames = (row: StudentCsvRow): string[] => {
    const encoded = row.subjects
      .map((value) => String(value.split(STUDENT_SUBJECT_DELIMITER)[0] ?? '').trim())
      .filter(Boolean)
    return encoded.length > 0 ? encoded : (row._fileSubjects ?? [])
  }

  const resolveImportedStudents = (
    rows: StudentCsvRow[],
    classIds?: ReadonlyMap<string, number>
  ): {rows: StudentCsvRow[]; issues: string[]} => {
    const classes = (localData.classes as any[]) || []
    const lower = (value: any) =>
      String(value ?? '')
        .trim()
        .toLowerCase()
    const resolved: StudentCsvRow[] = []
    const issues: string[] = []
    const flag = (row: StudentCsvRow, ...fields: StudentMissingField[]): StudentMissingField[] => [
      ...new Set([...(row._missing ?? []), ...fields]),
    ]

    rows.forEach((row) => {
      // The server already placed the row, by id (`report.references`): that class, whatever
      // the name cell says. Name matching below is only the fallback for a row it did not place.
      const referencedId = studentClassIdFor(classIds, row)
      const referencedClass = referencedId ? classById(referencedId) : undefined
      if (!row.class.trim() && !referencedClass) {
        // No class in the file (or the server couldn't pick one): kept, and the grid asks for
        // it. Its subjects hang off that class, so they are flagged too and picked after it.
        resolved.push({...row, class: '', subjects: [], _missing: flag(row, 'class', 'subjects')})
        return
      }
      const values = {row: row.rowNumber, registrationNumber: row.registrationNumber}
      const exactMatches = referencedClass
        ? [referencedClass]
        : classes.filter((c: any) => lower(c?.name) === lower(row.class))
      const matchKey = normalizeClassMatchKey(row.class)
      const byName =
        exactMatches.length > 0
          ? exactMatches
          : classes.filter((c: any) => normalizeClassMatchKey(c?.name) === matchKey)
      // A named level must match exactly; a plain name that exists under more than one level
      // (or branch) is refused here rather than checked against whichever class came first —
      // its subjects differ per level, so "first" rejected valid rows and passed invalid ones.
      const match: ReturnType<typeof pickStudentClass> = referencedClass
        ? {kind: 'match', classRecord: referencedClass}
        : pickStudentClass(
            byName,
            {branch: row.branch, level: row.level, curriculum: row.curriculum},
            data.manualData?.curriculumShortCodes
          )
      const labelQualifier = [[row.level, row.curriculum].filter(Boolean).join(', '), row.branch]
        .filter(Boolean)
        .join(' - ')
      // Curriculum-column layout: show the Curriculum + Class cells as typed, never the
      // stored (prefixed) name the pair was resolved to.
      const label = row.typedClass
        ? row.typedCurriculum
          ? `${row.typedClass} (${row.typedCurriculum})`
          : row.typedClass
        : labelQualifier
          ? `${row.class} (${labelQualifier})`
          : row.class

      // The class cannot be pinned down: hand the row to the grid with the cell blank and
      // flagged, listing the candidates found under that name first in its dropdown.
      const askForClass = (
        messageId:
          | 'ONBOARDING_WIZARD.STEP4_MANUAL.STUDENTS.AMBIGUOUS_CLASS'
          | 'ONBOARDING_WIZARD.STEP4_MANUAL.STUDENTS.ONBOARDING_ONLY_CLASS'
      ) => {
        issues.push(intl.formatMessage({id: messageId}, {...values, className: label}))
        const choices = uniqueStrings(
          byName.map((c: any) => String(c?.name ?? '').trim()).filter(Boolean)
        )
        resolved.push({
          ...row,
          class: '',
          subjects: [],
          // The file's subjects, by name: put back once the user picks the class (they hang off
          // a class, and which one is not known yet).
          _fileSubjects: fileSubjectNames(row),
          _missing: flag(row, 'class', 'subjects'),
          ...(choices.length > 0 ? {_classChoices: choices} : {}),
        })
      }

      if ((!referencedClass && row.unresolved === 'ambiguous') || match.kind === 'ambiguous') {
        askForClass('ONBOARDING_WIZARD.STEP4_MANUAL.STUDENTS.AMBIGUOUS_CLASS')
        return
      }
      if (match.kind === 'none') {
        askForClass('ONBOARDING_WIZARD.STEP4_MANUAL.STUDENTS.ONBOARDING_ONLY_CLASS')
        return
      }
      const classRecord = match.classRecord

      const className = String(classRecord.name ?? '').trim()
      const allowedSubjects = new Map(
        uniqueStrings(getSubjectsForClass(classRecord)).map((name) => [name.toLowerCase(), name])
      )
      // A row the file gave no class (the server placed it by id) carries its subjects as names.
      const subjectNames = fileSubjectNames(row)
      const unknownSubjects = subjectNames.filter(
        (name) => !allowedSubjects.has(name.toLowerCase())
      )
      // Only what this class actually teaches is kept; a subject it does not teach is left for
      // the user to correct in the grid rather than refusing the row — and with it the file.
      const subjects = subjectNames
        .filter((name) => allowedSubjects.has(name.toLowerCase()))
        .map(
          (name) =>
            `${allowedSubjects.get(name.toLowerCase())}${STUDENT_SUBJECT_DELIMITER}${className}`
        )
      if (unknownSubjects.length > 0) {
        issues.push(
          intl.formatMessage(
            {id: 'ONBOARDING_WIZARD.STEP4_MANUAL.STUDENTS.ONBOARDING_ONLY_SUBJECT'},
            {
              ...values,
              className: row.typedClass ? label : className,
              count: unknownSubjects.length,
              subjects: unknownSubjects.map((name) => `"${name}"`).join(', '),
            }
          )
        )
      } else if (subjectNames.length === 0 && data.mode === 'subject') {
        // The school's own sheet often has no Subject columns at all: say so once per row and
        // let the grid's subject picker fill them, now that the class is known.
        issues.push(
          intl.formatMessage(
            {id: 'ONBOARDING_WIZARD.STEP4_MANUAL.STUDENTS.MISSING_SUBJECTS'},
            values
          )
        )
      }

      // The match is unique, so pin its branch, level and curriculum on the row: the payload
      // builder sends them, and the backend needs them when the class name exists more than once.
      resolved.push({
        ...row,
        ...(referencedClass ? {classId: referencedClass.id} : {}),
        class: className,
        branch: String(classRecord.branch ?? '').trim() || undefined,
        level: String(classRecord.level ?? '').trim() || undefined,
        curriculum: String(classRecord.curriculum ?? '').trim() || undefined,
        subjects,
        // Placed: the file's names became `subjects`, so they need not ride along any more.
        _fileSubjects: undefined,
        ...(subjects.length === 0 ? {_missing: flag(row, 'subjects')} : {}),
      })
    })

    return {rows: resolved, issues}
  }

  /**
   * Rows of an uploaded students file -> wizard grid. `lists` is the template's very hidden
   * `Lists` sheet (absent for a CSV or a hand-made workbook). Returns false when nothing was
   * imported (the reason has been toasted). Plain function: curriculumPrefixIndex and
   * resolveImportedStudents' dependencies are declared further down.
   *
   * Rows with a blank class or registration number are kept: the grid highlights the blanks
   * and Next waits for them. `classChoices` (lower-cased student name -> class names) are the
   * server's candidates for a class it would not guess; the class dropdown lists them first.
   * Returns the registration numbers / row keys added, so a prefill can be removed again.
   *
   * `classIds` (the server's `report.references`) place a row in its class by id, with no name
   * matching; `reviewNotes` flag the values the server filled in but wants checked.
   */
  /** How a students sheet is parsed: for adding its rows, and for its summary before that. */
  const studentParseOptions = (lists: string[][] | undefined): ParseStudentsOptions => {
    const classes = (localData.classes as any[]) || []
    const wizardClassNames = new Set(
      classes
        .map((c: any) =>
          String(c?.name ?? '')
            .trim()
            .toLowerCase()
        )
        .filter(Boolean)
    )
    const listsIndex = buildStudentTemplateListsIndex(lists)
    return {
      wizardClassNames,
      allowMissingColumns: true,
      resolveCurriculumClass: (cells) =>
        resolveCurriculumColumnClass(
          cells,
          listsIndex,
          classes,
          (name, curriculum) => displayCurriculumName(curriculumPrefixIndex, name, curriculum),
          data.manualData?.curriculumShortCodes
        ),
    }
  }

  const applyStudentRows = (
    rows: string[][],
    lists: string[][] | undefined,
    options: {
      silent?: boolean
      classChoices?: ReadonlyMap<string, string[]>
      classIds?: ReadonlyMap<string, number>
      reviewNotes?: ReadonlyMap<string, StudentReviewNote>
      /**
       * Row keys an earlier read of the SAME file added. A re-read replaces them: its rows
       * without a registration number would otherwise be appended beside the old ones.
       */
      replaceKeys?: ReadonlySet<string>
      /** Every row key this read brought in (new or merged), for the next re-read of the file. */
      onRows?: (keys: string[]) => void
      /**
       * The keys of the rows the file itself holds (not the saved roster the template repeats):
       * kept even when the user removed them earlier — uploading them again brings them back.
       */
      fileRowKeys?: ReadonlySet<string>
    } = {}
  ): string[] | false => {
    const parsed = parseStudentsRows(rows, studentParseOptions(lists))
    if (parsed.error) {
      toast.error(
        intl.formatMessage({
          id: 'ONBOARDING_WIZARD.STEP4_MANUAL.STUDENTS.CSV_INVALID',
        })
      )
      return false
    }
    // Rows the save would refuse (a name in Urdu, a total row, a heading) are left out; say so.
    const leftOut = parsed.skipped.length
      ? dataUploadCountsSummary(
          intl.formatMessage,
          {
            read: parsed.rows.length + parsed.skipped.length,
            kept: parsed.rows.length,
            // A row the browser parsed is complete, so every kept row is ready to save.
            ready: parsed.rows.length,
            leftOut: parsed.skipped.reduce<Record<string, number>>((acc, row) => {
              const code = row.code ?? ''
              acc[code] = (acc[code] ?? 0) + 1
              return acc
            }, {}),
            needValues: {},
          },
          'students'
        )
      : null
    if (leftOut) toast.warning(leftOut, {autoClose: 10000})
    if (parsed.rows.length === 0) {
      if (!leftOut) {
        toast.error(
          intl.formatMessage({
            id: 'ONBOARDING_WIZARD.STEP4_MANUAL.STUDENTS.CSV_NO_ROWS',
          })
        )
      }
      return false
    }
    // A class or subject not added in onboarding no longer refuses the file: the row reaches
    // the grid with that cell blank and highlighted, and every reason is listed in the panel
    // above the table (a toast disappeared before the user could act on it).
    const resolvedImport = resolveImportedStudents(parsed.rows, options.classIds)
    let importedRows = resolvedImport.rows
    setStudentsIssues(resolvedImport.issues.length > 0 ? resolvedImport.issues : null)
    if (importedRows.length === 0) {
      toast.error(
        intl.formatMessage({
          id: 'ONBOARDING_WIZARD.STEP4_MANUAL.STUDENTS.CSV_NO_ROWS',
        })
      )
      return false
    }
    // A converted or prefilled template lists every student saved in the school, including
    // the ones the user removed but whose delete hasn't been saved yet: don't add them back.
    // The file's own students do come back (uploaded again on purpose); back in the grid, their
    // queued delete is not sent (the save skips a removed student who is present again).
    const pendingDeletes = new Set(
      (((localData as any).deletedStudents as any[]) ?? []).map(studentRowKey)
    )
    if (pendingDeletes.size > 0) {
      importedRows = importedRows.filter(
        (row) =>
          !pendingDeletes.has(studentRowKey(row)) ||
          Boolean(options.fileRowKeys?.has(studentRowKey(row))) ||
          Boolean(options.fileRowKeys?.has(studentRowKey({full_name: row.full_name})))
      )
      if (importedRows.length === 0) return []
    }

    setLocalData((prev) => {
      // Merge by registration number: the template pre-fills saved students, so appending
      // would duplicate them on a re-upload. A listed student's class, subjects, branch and
      // level become what the row says (as on the backend); id and other fields are kept.
      // Students left out of the file are not removed.
      // A re-read of the same file: its earlier rows with no registration number go (unsaved
      // ones only), so the new read takes their place instead of sitting beside them.
      const nextStudents = ((prev.students as any[]) || []).filter(
        (student: any) =>
          !(
            options.replaceKeys?.has(studentRowKey(student)) &&
            !isSavedStudent(student) &&
            !String(student?.registrationNumber ?? '').trim()
          )
      )
      const indexByReg = new Map<string, number>()
      nextStudents.forEach((student: any, index) => {
        const key = String(student?.registrationNumber ?? '')
          .trim()
          .toLowerCase()
        if (key && !indexByReg.has(key)) indexByReg.set(key, index)
      })
      importedRows.forEach((imported) => {
        // Upload-only fields (row number, the cells as typed) never reach wizard state.
        const row: Record<string, any> = {...imported}
        STUDENT_ROW_TRANSIENT_FIELDS.forEach((field) => delete row[field])
        const nameKey = imported.full_name.trim().toLowerCase()
        const choices = options.classChoices?.get(nameKey)
        if (choices && choices.length > 0) row._classChoices = choices
        const review = options.reviewNotes?.get(nameKey)
        if (review) row._review = review
        const key = imported.registrationNumber.trim().toLowerCase()
        // No registration number yet: always a new row (the grid asks for it).
        if (!key) {
          nextStudents.push(row)
          return
        }
        const index = indexByReg.get(key)
        if (index === undefined) {
          indexByReg.set(key, nextStudents.length)
          nextStudents.push(row)
          return
        }
        // first/last name would win over full_name in the payload; the file's name wins. The
        // upload-only flags of an earlier read (a class it could not place, its candidates,
        // review notes, the class id it picked) go: this read decides them afresh.
        const {
          _missing: _staleMissing,
          _classChoices: _staleChoices,
          _review: _staleReview,
          _fileSubjects: _staleFileSubjects,
          classId: _staleClassId,
          ...kept
        } = nextStudents[index]
        nextStudents[index] = {
          ...kept,
          ...row,
          first_name: undefined,
          last_name: undefined,
        }
      })
      const next = {
        ...prev,
        students: nextStudents,
        studentsCount: nextStudents.length,
      }
      updateData({manualData: next})
      return next
    })
    if (!options.silent) {
      toast.success(
        intl.formatMessage(
          {id: 'ONBOARDING_WIZARD.STEP4_MANUAL.STUDENTS.CSV_IMPORTED'},
          {count: importedRows.length}
        )
      )
    }
    // Both keys of each row: a class question names its students by registration number when
    // the server placed them, else by name only (a row it could not place has no reference).
    options.onRows?.(
      importedRows.flatMap((row) => [studentRowKey(row), studentRowKey({full_name: row.full_name})])
    )
    // Only rows that were not already in the grid: removing a prefill must never take a
    // student the school had before.
    const existingKeys = new Set(((localData.students as any[]) || []).map(studentRowKey))
    return importedRows.map(studentRowKey).filter((key) => !existingKeys.has(key))
  }

  /** First sheet and `Lists` sheet of a students workbook. */
  const readStudentsWorkbook = (file: File) =>
    excelFileToNamedSheets(file, [STUDENTS_TEMPLATE_LISTS_SHEET]).then((workbook) => ({
      rows: workbook.rows,
      lists: workbook.sheets[STUDENTS_TEMPLATE_LISTS_SHEET] as string[][] | undefined,
      isOurTemplate:
        workbook.firstSheetName.trim().toLowerCase() === STUDENTS_TEMPLATE_SHEET.toLowerCase(),
    }))

  /**
   * A file that is not our template (a PDF, Word file, photo or scan, or a spreadsheet in the
   * school's own layout): `POST data-upload/convert` reads it and returns OUR Students
   * template, filled in, which then goes through the same parser as a hand-filled one. The
   * workbook holds the saved students plus the new ones, so the all-or-nothing student sync
   * never reads "everyone else has left". Nothing is saved here: the user sees the report
   * first and the existing Next does the save.
   *
   * The template only offers classes and subjects that are SAVED, so unsynced class/subject
   * edits are saved first, like the template download. Plain function: saveProgress is
   * declared further down.
   */
  /**
   * A conversion's rows into the grid, with its class questions and report. A re-read of the
   * same file (after a branch or column answer) replaces what the earlier read put there: its
   * rows without a registration number, their upload flags and their "Choose classes"
   * questions. Plain function: its helpers are declared further down.
   */
  const applyStudentsConversion = (file: File, conversion: DataUploadConversion) => {
    // The file added no students (a staff list, say): its template is only the saved roster
    // (`report.kept`), which must not be put back over the grid — it would bring back students
    // removed here. Only its summary shows (and, before this, its new-teachers question).
    setStudentsConversionFile(file)
    if (conversion.report.written === 0) {
      setStudentsConversionKeys(null)
      setStudentsConversion(conversion)
      markStudentsUpload(file, {
        status: 'ready',
        added: 0,
        teachersFound: conversion.counts?.newPeople?.teachers ?? conversion.newTeachers.length,
        allocationsFound: conversion.carried?.allocation?.records,
        needsCheck: conversion.report.needsReview.length,
        ...studentsSkipCounts(conversion),
      })
      return Promise.resolve(0)
    }
    return readStudentsWorkbook(dataUploadTemplateToFile(conversion.template!)).then(
      ({rows, lists}) => {
        const earlier = studentsFileRowsRef.current.get(file)
        let added = 0
        applyStudentRows(rows, lists, {
          silent: true,
          ...conversionRowOptions(conversion),
          replaceKeys: earlier,
          onRows: (keys) => {
            // Two keys per row (see applyStudentRows).
            added = keys.length / 2
            studentsFileRowsRef.current.set(file, new Set(keys))
            setStudentsConversionKeys(new Set(keys))
            setStudentsBranchAnswered((prev) =>
              prev && prev.file === file ? {...prev, rowKeys: new Set(keys)} : prev
            )
          },
        })
        if (earlier) {
          setStudentsClassQuestions((prev) =>
            prev.filter((question) => !question.rowKeys.some((key) => earlier.has(key)))
          )
        }
        // "Save students only": the unclear classes stay blank in the grid, not asked.
        addStudentsClassQuestions(conversion, studentsPeopleOnlyRef.current.get(file) !== true)
        // Shown even when the rows were refused: it lists what was left out and carries the
        // filled template, which the user can fix and upload again.
        setStudentsConversion(conversion)
        // Its students are in the table now: the draft they were carried in is done with.
        if (added > 0) {
          closeCarriedDraft(studentsCarriedDraftRef.current.get(file))
          studentsCarriedDraftRef.current.delete(file)
        }
        markStudentsUpload(
          file,
          added > 0
            ? {
                status: 'ready',
                // The file's own students: the template's rows also hold the saved roster.
                added: conversion.report.written,
                needsCheck: conversion.report.needsReview.length,
                ...studentsSkipCounts(conversion),
              }
            : {
                status: 'error',
                message: dataUploadErrorMessage(intl.formatMessage, 'NO_RELEVANT_DATA', {
                  useCaseKey: 'students',
                }),
              }
        )
        return added
      }
    )
  }

  const convertStudentsFile = (
    file: File,
    done: () => void,
    options: {
      sheetAssignments?: DataUploadSheetAssignments
      columnAssignments?: DataUploadColumnAssignments
      branchAssignments?: DataUploadBranchAssignments
      /** The file's rows are already in the table: put the re-read in, no summary step. */
      applyNow?: boolean
    } = {}
  ) => {
    // Answers given for this file earlier in the flow ride along, since the server keeps none.
    const sheetAssignments = options.sheetAssignments ?? answeredSheetsRef.current.get(file)
    if (sheetAssignments) answeredSheetsRef.current.set(file, sheetAssignments)
    const columnAssignments = options.columnAssignments ?? answeredColumnsRef.current.get(file)
    if (columnAssignments) answeredColumnsRef.current.set(file, columnAssignments)
    const branchAssignments = options.branchAssignments ?? answeredBranchesRef.current.get(file)
    if (branchAssignments) answeredBranchesRef.current.set(file, branchAssignments)
    // Questions kept from its last read are asked again by this one, if still open.
    setStudentsParkedQuestions((prev) => (prev?.file === file ? null : prev))
    setStudentsReadingFile(true)
    if (options.applyNow) setStudentsTableRefreshing(true)
    const finish = () => {
      setStudentsTableRefreshing(false)
      setStudentsReadingFile(false)
      setStudentsAnswering(false)
      done()
    }
    const fail = (error: unknown) => {
      const message = dataUploadErrorMessage(intl.formatMessage, error, {
        config: studentsUploadConfig,
        useCaseKey: 'students',
        fileName: file.name,
      })
      // Every line skipped on purpose (`ALL_LINES_SKIPPED`): what the user chose, said as
      // information on the line, not as a failure.
      if (dataUploadErrorIsInfo(error)) {
        if (!markStudentsUpload(file, {status: 'elsewhere', message})) {
          toast.info(message, {autoClose: 10000})
        }
        finish()
        return
      }
      if (!markStudentsUpload(file, {status: 'error', message})) {
        toast.error(message, {autoClose: 10000})
      }
      finish()
    }

    // The files picked with it: one request, read as one upload (answers kept by this file).
    const together = [file, ...(studentsBatchRef.current.get(file) ?? [])]
    const convert = (uploadIds: string[] | null) =>
      dispatchApi(dispatch, (ok, onFailure) =>
        convertDataUpload(
          buildDataUploadConvertForm(together, {
            useCaseKey: 'students',
            flavour: 'onboarding',
            curriculumColumn: STUDENTS_CURRICULUM_COLUMN_ENABLED,
            uploadIds,
            // Not remembered between uploads: they go with every re-post of the same file.
            sheetAssignments,
            columnAssignments,
            branchAssignments,
          }),
          ok,
          onFailure
        )
      )

    const requestConversion = () => {
      // Read on Teacher Allocation already: its carried draft, as our template, no file sent.
      const carriedDraft = studentsCarriedDraftRef.current.get(file)
      // Its answers are kept on the draft too: offered again after a logout, it is not asked
      // again (the template uses the stored branch answers when it is sent none).
      if (carriedDraft) {
        saveDraftAnswers(dispatch, carriedDraft, {
          ...(sheetAssignments && Object.keys(sheetAssignments).length > 0
            ? {sheetAssignments}
            : {}),
          ...(columnAssignments && Object.keys(columnAssignments).length > 0
            ? {columnAssignments}
            : {}),
          ...(branchAssignments && Object.keys(branchAssignments).length > 0
            ? {branchAssignments}
            : {}),
        })
      }
      // On the AI servers the file goes in 512 KB pieces first (nginx refuses bodies > 1 MB).
      const converted = carriedDraft
        ? dispatchApi(dispatch, (ok, onFailure) =>
            getDataUploadDraftTemplate(
              carriedDraft,
              {
                flavour: 'onboarding',
                curriculumColumn: STUDENTS_CURRICULUM_COLUMN_ENABLED,
                branchAssignments,
              },
              ok,
              onFailure
            )
          )
        : isChunkedUploadAvailable()
          ? withUploadedFiles(
              dispatch,
              together,
              {useCaseKey: 'students', onProgress: setStudentsUploadProgress},
              (uploadIds) => {
                setStudentsUploadProgress(null)
                return convert(uploadIds)
              }
            )
          : convert(null)
      converted
        .then((res: any) => {
          setStudentsUploadProgress(null)
          const conversion = normalizeDataUploadConversion(res?.result ?? res)
          // A carried draft's answers given before a logout: they stay answered here.
          const stored = conversion?.answers
          if (stored?.branchAssignments && !answeredBranchesRef.current.has(file)) {
            answeredBranchesRef.current.set(file, stored.branchAssignments)
          }
          if (stored?.sheetAssignments && !answeredSheetsRef.current.has(file)) {
            answeredSheetsRef.current.set(file, stored.sheetAssignments)
          }
          if (stored?.columnAssignments && !answeredColumnsRef.current.has(file)) {
            answeredColumnsRef.current.set(file, stored.columnAssignments)
          }
          // The file's allocations, read with it and kept for Teacher Allocation.
          if (conversion?.carried) studentsCarriedRef.current.set(file, conversion.carried)
          // Nothing was read: the workbook holds several unlabelled lists of people. Ask whose
          // each sheet is and send the same file again with the answers.
          if (conversion?.assignmentQuestion) {
            // The queue waits here: the next file starts only after this one is answered or
            // skipped, or its question would replace this one in the open dialog.
            setStudentsReadingFile(false)
            setStudentsAnswering(false)
            setStudentsSheetQuestion({file, question: conversion.assignmentQuestion, done})
            markStudentsUpload(file, {status: 'question'})
            return
          }
          // Teachers the file also lists that the school lacks: offered once per file.
          const asked = studentsAskedTeachersRef.current.get(file) ?? new Set<string>()
          const newTeachers = (conversion?.newTeachers ?? []).filter(
            (teacher) => !asked.has(teacher.key)
          )
          if (newTeachers.length > 0) {
            newTeachers.forEach((teacher) => asked.add(teacher.key))
            studentsAskedTeachersRef.current.set(file, asked)
            setStudentsNewTeachers({file, teachers: newTeachers})
          }
          // Another screen's list (the file said so): tell the user where it goes, not "no
          // students found".
          const owner = conversion?.files.find((f) => f.assignedTo && f.assignedTo !== 'students')
          if (!conversion?.template && owner) {
            const message = intl.formatMessage(
              {id: 'DATA_UPLOAD.CONVERT.BELONGS_TO_STEP'},
              {step: stepTitle(owner.assignedTo)}
            )
            if (!markStudentsUpload(file, {status: 'elsewhere', message})) {
              toast.info(message, {autoClose: 10000})
            }
            finish()
            return
          }
          if (!conversion?.template) {
            fail((conversion && firstDataUploadFileError(conversion)?.code) || 'NO_RELEVANT_DATA')
            return
          }
          // Columns it could not place for sure, sheets whose branch it could not tell, and
          // subjects written under a name the school does not use (nothing applied until
          // confirmed — a mapping only reaches rows on a fresh read).
          const openColumns = withoutAnsweredColumns(
            conversion.columnQuestions,
            answeredColumnsRef.current.get(file)
          )
          // A branch or subject skipped for this file is not asked again either.
          const skipped = studentsSkippedRef.current.get(file)
          const openBranches = withoutAnsweredBranches(
            conversion.branchQuestions,
            answeredBranchesRef.current.get(file)
          ).filter((question) => !skipped?.branches.has(question.key))
          // Mappings confirmed earlier for this file are not asked again; when the dialog opens
          // for something else they show as done.
          const confirmedSubjects = answeredSubjectsRef.current.get(file)
          const openSubjects = withoutConfirmedSubjects(
            conversion.subjectMappings ?? null,
            new Set([...(confirmedSubjects?.keys() ?? []), ...(skipped?.subjects ?? [])])
          )
          const subjects =
            (openSubjects?.suggestions.length ?? 0) > 0
              ? withConfirmedSubjectsApplied(openSubjects, confirmedSubjects)
              : null
          if (openBranches.length > 0) {
            // Ask first: the answer changes every row's class, so the grid only gets the
            // re-read. Skip applies this read as it is (rows with "Choose classes").
            setStudentsQuestions({
              file,
              columns: openColumns,
              branches: openBranches,
              subjects,
              pendingConversion: conversion,
            })
            markStudentsUpload(file, {status: 'question'})
            finish()
            return
          }
          // A fresh read shows its summary and waits for "Add to table"; a re-read after a
          // question on rows already in the table goes straight in.
          if (
            !options.applyNow &&
            conversion.report.written > 0 &&
            markStudentsUpload(file, {
              status: 'found',
              found: conversion.report.written,
              teachersFound:
                conversion.counts?.newPeople?.teachers ?? conversion.newTeachers.length,
              allocationsFound: conversion.carried?.allocation?.records,
              needsCheck: conversion.report.needsReview.length,
              ...studentsSkipCounts(conversion),
              staged: {kind: 'conversion', conversion, columns: openColumns, subjects},
            })
          ) {
            finish()
            return
          }
          return applyStudentsConversion(file, conversion)
            .then(() => {
              // Asked now that the rows are in the grid.
              askStudentsQuestions(file, conversion, openColumns, subjects)
              finish()
            })
            .catch(() => fail('PARSE_FAILED'))
        })
        .catch((error) => {
          setStudentsUploadProgress(null)
          fail(error)
        })
    }

    if (onPartialSubmit) {
      onPartialSubmit(
        ['classes', 'subjects'],
        saveProgress(activeTab),
        requestConversion,
        undefined,
        finish
      )
    } else {
      requestConversion()
    }
  }

  /**
   * The sheet question answered (or skipped: every sheet `IGNORE_SHEET`): the same file is sent
   * again with the answers, once; they ride along with every later read of it.
   */
  const answerStudentsSheet = (assignments: DataUploadSheetAssignments) => {
    const pending = studentsSheetQuestion
    if (!pending) return
    setStudentsAnswering(true)
    setStudentsSheetQuestion(null)
    // Kept against the file too: the post after a confirmed subject mapping has to
    // carry them as well, or the server asks this same question again.
    answeredSheetsRef.current.set(pending.file, assignments)
    // The re-read finishes this file; its `done` then starts the next one.
    convertStudentsFile(pending.file, pending.done, {sheetAssignments: assignments})
  }

  /**
   * Read one selected file into the grid. Rows are appended, so several files add up: a school
   * with a sheet per campus uploads them together and gets one list.
   *
   * Each file is converted on its own, because a file sent in pieces is named by a single
   * `uploadId` and the endpoint resolves one of those per call; merging the rows server-side
   * (so one student in two files is one row) needs `uploadIds` — see
   * docs/data-upload-backend-requests.md.
   */
  const importStudentsFile = (file: File, done: () => void) => {
    // Read on Teacher Allocation already (its carried draft), or read with other files: to the
    // server as is, not opened here.
    if (studentsCarriedDraftRef.current.has(file) || studentsBatchRef.current.has(file)) {
      markStudentsUpload(file, {status: 'reading'})
      convertStudentsFile(file, done)
      return
    }
    const extension = dataUploadFileExtension(file.name)
    const isSpreadsheet = extension === 'xlsx' || extension === 'csv'
    /** Why nothing was read, on the file's line (or as a toast when it has none). */
    const refuse = (message: string) => {
      if (!markStudentsUpload(file, {status: 'error', message})) toast.error(message)
    }
    markStudentsUpload(file, {status: 'reading'})
    if (studentsConversionAvailable) {
      // Enforced before sending: an oversized file fails with an HTML 413 that can't be read.
      const refusal = validateDataUploadFile(file, studentsUploadConfig)
      if (refusal) {
        refuse(
          dataUploadErrorMessage(intl.formatMessage, refusal, {
            config: studentsUploadConfig,
            useCaseKey: 'students',
            fileName: file.name,
          })
        )
        done()
        return
      }
    } else if (!isSpreadsheet) {
      refuse(intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP4_MANUAL.STUDENTS.CSV_INVALID'}))
      done()
      return
    }

    const onReadError = () => {
      if (studentsConversionAvailable) {
        // The browser could not open it; the server reads every format it accepts.
        convertStudentsFile(file, () => finishImport())
        return
      }
      finishImport(() =>
        refuse(intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP4_MANUAL.STUDENTS.CSV_READ_ERROR'}))
      )
    }

    const startedAt = Date.now()
    // Keep the table skeleton visible briefly so the import feedback is noticeable
    const finishImport = (apply?: () => void) => {
      const remaining = Math.max(0, 600 - (Date.now() - startedAt))
      window.setTimeout(() => {
        apply?.()
        done()
      }, remaining)
    }

    /**
     * Our own template, or any sheet the parser recognises, keeps today's in-browser flow (no
     * AI). Only a sheet it cannot read is handed to the conversion, where that exists.
     */
    const importSheet = (rows: string[][], lists: string[][] | undefined, ours: boolean) => {
      const readable = !parseStudentsRows(rows).error
      if (ours || readable || !studentsConversionAvailable) {
        finishImport(() => {
          // A file with a line shows its summary and waits for "Add to table"; any other read
          // goes straight in, as before.
          if (!studentsUploadFilesRef.current.has(file)) {
            applyStudentRows(rows, lists)
            return
          }
          const preview = parseStudentsRows(rows, studentParseOptions(lists))
          if (preview.error || preview.rows.length === 0) {
            markStudentsUpload(file, {
              status: 'error',
              message: intl.formatMessage({
                id: preview.error
                  ? 'ONBOARDING_WIZARD.STEP4_MANUAL.STUDENTS.CSV_INVALID'
                  : 'ONBOARDING_WIZARD.STEP4_MANUAL.STUDENTS.CSV_NO_ROWS',
              }),
            })
            return
          }
          markStudentsUpload(file, {
            status: 'found',
            found: preview.rows.length,
            leftOut: preview.skipped.length,
            staged: {kind: 'rows', rows, lists},
          })
        })
        return
      }
      convertStudentsFile(file, () => finishImport())
    }

    if (extension === 'xlsx') {
      readStudentsWorkbook(file)
        .then(({rows, lists, isOurTemplate}) => importSheet(rows, lists, isOurTemplate))
        .catch(onReadError)
      return
    }

    if (extension === 'csv') {
      const reader = new FileReader()
      reader.onload = () => importSheet(parseCsvRows(String(reader.result ?? '')), undefined, false)
      reader.onerror = onReadError
      reader.readAsText(file, 'UTF-8')
      return
    }

    convertStudentsFile(file, () => finishImport())
  }

  const handleStudentsFiles = (picked: File[]) => {
    // One read and one card per file: a file already on the card (being read, asked about, or
    // waiting for "Add to table") keeps that card and its read.
    const selected = picked.filter((file) => !studentsUploadFilesRef.current.has(file))
    if (selected.length === 0) return

    // Every file picked: each is read on its own, one after another (below), so the server's
    // per-request file limit does not cap how many can be picked at once.
    const files = selected

    // A line per file on the card, in the order they will be read.
    const lines = files.map((file): StudentsUploadLine => {
      studentsUploadFilesRef.current.add(file)
      studentsUploadSeqRef.current += 1
      return {id: `students-upload-${studentsUploadSeqRef.current}`, file, status: 'waiting'}
    })
    setStudentsUploads((prev) => [...prev, ...lines])

    setStudentsConversion(null)
    setStudentsConversionKeys(null)
    setStudentsIssues(null)
    setStudentsPrefill(null)
    setStudentsBranchAnswered(null)
    setIsImportingStudentsFile(true)

    // Several files on the AI servers: ONE request, read as one upload (a person in two files is
    // one row). Each is checked against the limits first; one refused is left out, with why.
    if (studentsConversionAvailable && files.length > 1) {
      const readable = files.filter((file) => {
        const refusal = validateDataUploadFile(file, studentsUploadConfig)
        if (!refusal) return true
        markStudentsUpload(file, {
          status: 'error',
          message: dataUploadErrorMessage(intl.formatMessage, refusal, {
            config: studentsUploadConfig,
            useCaseKey: 'students',
            fileName: file.name,
          }),
        })
        return false
      })
      if (readable.length > 1) {
        const [first, ...rest] = readable
        studentsBatchRef.current.set(first, rest)
        rest.forEach((file) => studentsBatchOfRef.current.set(file, first))
        importStudentsFile(first, () => setIsImportingStudentsFile(false))
        return
      }
      if (readable.length === 0) {
        setIsImportingStudentsFile(false)
        return
      }
      importStudentsFile(readable[0], () => setIsImportingStudentsFile(false))
      return
    }

    // One at a time, in the order they were picked: each appends its rows, and a file that
    // asks a question (whose list is this sheet?) must be answered before the next one starts.
    const next = (index: number) => {
      if (index >= files.length) {
        setIsImportingStudentsFile(false)
        return
      }
      importStudentsFile(files[index], () => next(index + 1))
    }
    next(0)
  }

  /** Students the Teachers file prefilled, so Remove takes back exactly those rows. */
  const [studentsPrefill, setStudentsPrefill] = useState<{
    draftId: string
    keys: string[]
    count: number
  } | null>(null)
  /**
   * Every student row the Teachers file put in the grid (kept after Remove clears
   * `studentsPrefill`), so Review's "Student records found" follows the deletes.
   */
  const fileStudentKeysRef = useRef(new Set<string>())
  /** Those students taken out of the grid: Review's count loses them (0 once none is left). */
  const forgetFileStudents = (keys: Iterable<string>) => {
    const left = fileStudentKeysRef.current
    let removed = 0
    for (const key of keys) if (left.delete(key)) removed += 1
    if (removed > 0) setTeacherFileCounts((prev) => withoutStudents(prev, removed, left.size))
  }

  /**
   * `resume-data.curriculums`: the curriculum-rooted tree the class pickers are built from
   * (CH-1). Fetched again whenever this step opens, so classes saved earlier in this visit are
   * in it too.
   */
  const [classTree, setClassTree] = useState<any[]>([])
  useEffect(() => {
    if (activeTab !== 'students') return
    dispatch(
      getOnboardingData(
        (res: any) => {
          const result = res?.result ?? res?.data ?? res
          setClassTree(Array.isArray(result?.curriculums) ? result.curriculums : [])
        },
        () => undefined
      ) as any
    )
  }, [activeTab, dispatch])
  /**
   * Deletes saved teachers or students on the server NOW (bulk-import, `action:'delete'`), and
   * resolves with the keys of the rows that are gone: a teacher's id, a student's
   * `studentRowKey`. The caller removes only those from the grid, so a row the server refused
   * stays where the user can see it, and the reason is shown with its name. "Not found for
   * deletion" counts as gone: it already was.
   */
  const deleteSavedRows = (kind: 'teachers' | 'students', rows: any[]): Promise<Set<string>> => {
    const keyOf = (row: any) =>
      kind === 'teachers' ? String(parsePositiveNumber(row?.id)) : studentRowKey(row)
    // The rows' delete icons spin until the server has answered.
    const busyKeys = rows.map((row) => `${kind === 'teachers' ? 't' : 's'}:${keyOf(row)}`)
    setDeletingRows((prev) => new Set([...prev, ...busyKeys]))
    const done = () =>
      setDeletingRows((prev) => {
        const next = new Set(prev)
        busyKeys.forEach((key) => next.delete(key))
        return next
      })
    const nameOf = (row: any) =>
      kind === 'teachers'
        ? [row?.first_name, row?.last_name]
            .map((part: any) => String(part ?? '').trim())
            .filter(Boolean)
            .join(' ')
        : String(row?.full_name || `${row?.first_name || ''} ${row?.last_name || ''}`).trim()
    const payload =
      kind === 'teachers'
        ? {
            teachers: rows.map((row) => ({
              id: parsePositiveNumber(row.id),
              first_name: nameOf(row),
              last_name: String(row?.employeeId ?? ''),
              // node-api also finds the teacher by Employee ID when the id does not match.
              ...(String(row?.employeeId ?? '').trim()
                ? {employee_id: String(row.employeeId).trim()}
                : {}),
              action: 'delete',
            })),
            source: 'onboarding',
          }
        : {
            students: rows.map((row) => ({
              ...(parsePositiveNumber(row?.id) ? {id: parsePositiveNumber(row.id)} : {}),
              first_name: nameOf(row),
              last_name: '',
              email: '',
              cnic: '',
              class: row?.class ?? '',
              ...(row?.branch ? {branch: row.branch} : {}),
              ...(row?.level ? {level: row.level} : {}),
              registrationNumber: String(row?.registrationNumber ?? '').trim(),
              subjects: [],
              action: 'delete',
            })),
            source: 'onboarding',
          }
    /** "Could not delete Sobia Khalid. <why>" — the why in words a school can act on. */
    const refuse = (names: string[], reason: unknown) => {
      const text = String(reason ?? '')
      // A foreign-key refusal from the database: the person is still used somewhere.
      const inUse = /foreign key|constraint|parent row|in use|assigned/i.test(text)
      toast.error(
        intl.formatMessage(
          {
            id: inUse
              ? 'ONBOARDING_WIZARD.STEP4_MANUAL.DELETE_IN_USE'
              : 'ONBOARDING_WIZARD.STEP4_MANUAL.DELETE_FAILED',
          },
          {names: names.filter(Boolean).join(', '), reason: inUse ? '' : text}
        ),
        {autoClose: 10000}
      )
    }
    return dispatchApi(dispatch, (ok, fail) => bulkCsvImport(payload, ok, fail))
      .then((res: any) => {
        const errors =
          res?.errors ??
          res?.importReport?.errors ??
          res?.result?.errors ??
          res?.result?.importReport?.errors ??
          res?.data?.errors ??
          res?.result?.data?.errors
        // "Not found for deletion" means a student was already gone. A teacher is now found by id
        // or Employee ID, so "Teacher not found for deletion" is a real failure: their saved
        // assignments may still be on the server, and the save must not read as done.
        const real = (Array.isArray(errors) ? errors : []).filter(
          (entry: any) =>
            kind === 'teachers' ||
            !/not found/i.test(String(entry?.error || entry?.message || ''))
        )
        const failed = new Set<string>()
        real.forEach((entry: any) => {
          const data = entry?.data ?? {}
          const row = rows.find((candidate) =>
            kind === 'teachers'
              ? parsePositiveNumber(candidate?.id) === parsePositiveNumber(data.id)
              : String(candidate?.registrationNumber ?? '').trim() ===
                String(data.registrationNumber ?? '').trim()
          )
          if (row) failed.add(keyOf(row))
        })
        // An error that names no row: none of them can be trusted as deleted.
        if (real.length > 0 && failed.size === 0) rows.forEach((row) => failed.add(keyOf(row)))
        if (real.length > 0) {
          refuse(
            rows.filter((row) => failed.has(keyOf(row))).map(nameOf),
            real[0]?.error || real[0]?.message
          )
        }
        return new Set(rows.map(keyOf).filter((key) => !failed.has(key)))
      })
      .catch((error: any) => {
        refuse(rows.map(nameOf), error?.message ?? error?.result?.message)
        return new Set<string>()
      })
      .finally(done)
  }
  /** Rows whose delete is on its way to the server: `t:<teacher id>`, `s:<studentRowKey>`. */
  const [deletingRows, setDeletingRows] = useState<Set<string>>(new Set())
  /** Students whose delete is on its way: more than one shows a single spinner over the grid. */
  const studentsBulkDeleting = Array.from(deletingRows).filter((key) => key.startsWith('s:')).length

  /**
   * Whether a level is one of the wizard's levels right now: in Academic Structure's list for its
   * curriculum (by name, or by code — the tree's `curriculum_key`), and not queued for deletion.
   * The server tree, and a class saved before, can still name a level whose delete is pending or
   * was refused; its classes must not be offered on the Students step. With no level list yet,
   * nothing is held back.
   */
  const isCurrentLevel = useMemo(() => {
    const lower = (value: unknown) =>
      String(value ?? '')
        .trim()
        .toLowerCase()
    const levelsByCurriculum = (localData.levelsByCurriculum ?? {}) as NonNullable<
      WizardData['manualData']['levelsByCurriculum']
    >
    const byCurriculum = new Map<string, Set<string>>()
    const every = new Set<string>()
    Object.entries(levelsByCurriculum).forEach(([curriculum, config]) => {
      const names = new Set<string>()
      if (config?.levelsSameAcrossBranches === false) {
        Object.values(config.levelsPerBranch ?? {}).forEach((levels) =>
          (levels ?? []).forEach((name) => names.add(lower(name)))
        )
      } else {
        ;(config?.levels ?? []).forEach((level) => names.add(lower(level?.name)))
      }
      names.forEach((name) => every.add(name))
      byCurriculum.set(lower(curriculum), names)
      const code = (localData.curriculumCodes as Record<string, string> | undefined)?.[curriculum]
      if (code) byCurriculum.set(lower(code), names)
    })
    const deleted = ((localData.deletedLevels ?? []) as NonNullable<
      WizardData['manualData']['deletedLevels']
    >).map((entry) => ({
      name: lower(entry.name),
      curriculum: lower(entry.curriculum),
      branch: lower(entry.branch),
    }))
    return (place: {curriculum?: string; curriculumKey?: string | null; level?: string; branch?: string}) => {
      if (every.size === 0) return true
      const level = lower(place.level)
      if (!level) return true
      const curriculum = lower(place.curriculum)
      const names = curriculum
        ? (byCurriculum.get(curriculum) ??
          (place.curriculumKey ? byCurriculum.get(lower(place.curriculumKey)) : undefined))
        : every
      if (!names?.has(level)) return false
      const branch = lower(place.branch)
      return !deleted.some(
        (entry) =>
          entry.name === level &&
          (!entry.curriculum || !curriculum || entry.curriculum === curriculum) &&
          (!entry.branch || !branch || entry.branch === branch)
      )
    }
  }, [localData.curriculumCodes, localData.deletedLevels, localData.levelsByCurriculum])

  /** Every class in the tree, flattened: its id, stored and display names, and where it sits. */
  const treeClasses = useMemo(
    () =>
      classTree.flatMap((curriculum: any) =>
        (curriculum?.levels ?? [])
          .filter((level: any) =>
            isCurrentLevel({
              curriculum: curriculum?.curriculum,
              curriculumKey: curriculum?.curriculum_key ?? null,
              level: level?.level,
              branch: level?.branch,
            })
          )
          .flatMap((level: any) =>
          (level?.grades ?? []).flatMap((grade: any) =>
            (grade?.classes ?? [])
              .filter((cls: any) => parsePositiveNumber(cls?.class_id))
              .map((cls: any) => ({
                classId: parsePositiveNumber(cls.class_id) as number,
                name: String(cls?.class_name ?? '').trim(),
                displayName: String(cls?.display_name ?? cls?.class_name ?? '').trim(),
                schoolDisplayName: String(cls?.school_display_name ?? '').trim(),
                curriculum: String(curriculum?.curriculum ?? '').trim(),
                curriculumShortCode: String(curriculum?.curriculum_short_code ?? '').trim(),
                curriculumKey: (curriculum?.curriculum_key as string | null) ?? null,
                level: String(level?.level ?? '').trim(),
                levelId: parsePositiveNumber(level?.level_id) ?? null,
                branch: String(level?.branch ?? '').trim(),
                branchId: parsePositiveNumber(level?.branch_id) ?? null,
              }))
          )
        )
      ),
    [classTree, isCurrentLevel]
  )

  /**
   * Rows whose class was picked by id before the pick also stored its branch and level: fill
   * them in from the tree once it loads. The save places a student by class name + branch +
   * level, so without them a class name two branches share is refused ("exists in more than
   * one branch or curriculum") although the row shows the right class.
   */
  useEffect(() => {
    if (treeClasses.length === 0) return
    const byId = new Map(treeClasses.map((cls) => [cls.classId, cls]))
    const needsPlacement = (student: any) => {
      const node = byId.get(parsePositiveNumber(student?.classId) as number)
      return Boolean(
        node &&
        ((node.branch && !String(student?.branch ?? '').trim()) ||
          (node.level && !String(student?.level ?? '').trim()))
      )
    }
    if (!((localData.students as any[]) || []).some(needsPlacement)) return
    setLocalData((prev) => {
      const students = ((prev.students as any[]) || []).map((student: any) => {
        if (!needsPlacement(student)) return student
        const node = byId.get(parsePositiveNumber(student.classId) as number)!
        return {
          ...student,
          ...(node.branch && !String(student?.branch ?? '').trim() ? {branch: node.branch} : {}),
          ...(node.level && !String(student?.level ?? '').trim() ? {level: node.level} : {}),
        }
      })
      const next = {...prev, students}
      deferUpdateData({manualData: next})
      return next
    })
  }, [treeClasses, localData.students])

  /**
   * The reverse: rows placed by class NAME (an upload the server did not place by id) carry
   * the class, branch and level but no `classId`, and the tree-built picker is keyed by id, so
   * the cell shows "Select..." although the class is known. Pin the id when exactly one tree
   * class has that name in that branch and level; the curriculum only narrows when it can.
   */
  useEffect(() => {
    if (treeClasses.length === 0) return
    const lower = (value: unknown) =>
      String(value ?? '')
        .trim()
        .toLowerCase()
    const nodeFor = (student: any) => {
      if (parsePositiveNumber(student?.classId) || !lower(student?.class)) return undefined
      let nodes = treeClasses.filter((cls) => lower(cls.name) === lower(student.class))
      if (lower(student?.branch))
        nodes = nodes.filter((cls) => lower(cls.branch) === lower(student.branch))
      if (lower(student?.level))
        nodes = nodes.filter((cls) => lower(cls.level) === lower(student.level))
      const byCurriculum = nodes.filter(
        (cls) => lower(cls.curriculum) === lower(student?.curriculum)
      )
      if (byCurriculum.length > 0) nodes = byCurriculum
      return nodes.length === 1 ? nodes[0] : undefined
    }
    if (!((localData.students as any[]) || []).some(nodeFor)) return
    setLocalData((prev) => {
      const students = ((prev.students as any[]) || []).map((student: any) => {
        const node = nodeFor(student)
        if (!node) return student
        return {
          ...student,
          classId: node.classId,
          ...(node.branch && !lower(student?.branch) ? {branch: node.branch} : {}),
          ...(node.level && !lower(student?.level) ? {level: node.level} : {}),
        }
      })
      const next = {...prev, students}
      deferUpdateData({manualData: next})
      return next
    })
  }, [treeClasses, localData.students])

  // The Teachers file may hold the students too (`onboarding/prefill`): read its draft when this
  // step opens and apply it like a converted upload. Nothing is saved until Next.
  const {reading: studentsPrefillReading, percent: studentsPrefillPercent} =
    useOnboardingPrefillDraft({
      useCaseKey: 'students',
      active: activeTab === 'students',
      templateParams: {flavour: 'onboarding', curriculumColumn: STUDENTS_CURRICULUM_COLUMN_ENABLED},
      onDraft: ({draftId, conversion}) => {
        if (!conversion.template) {
          setStudentsConversionKeys(null)
          setStudentsConversion(conversion)
          return
        }
        readStudentsWorkbook(dataUploadTemplateToFile(conversion.template))
          .then(({rows, lists}) => {
            const added = applyStudentRows(rows, lists, {
              silent: true,
              ...conversionRowOptions(conversion),
              onRows: (keys) => setStudentsConversionKeys(new Set(keys)),
            })
            addStudentsClassQuestions(conversion)
            setStudentsConversion(conversion)
            if (added && added.length > 0) {
              setStudentsPrefill({draftId, keys: added, count: conversion.report.written})
              added.forEach((key) => fileStudentKeysRef.current.add(key))
            }
          })
          .catch(() => undefined)
      },
    })

  /** "Reading… 40%" bar for a prefill job; nothing until the job reports a percent. */
  const renderPrefillProgress = (percent: number | null) =>
    percent === null ? null : (
      <div className='mt-2 mx-auto' style={{maxWidth: 320, width: '100%'}}>
        <div
          className='progress'
          style={{height: 6}}
          role='progressbar'
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={percent}
        >
          <div className='progress-bar' style={{width: `${percent}%`}} />
        </div>
        <div className='mt-1' style={{fontSize: '0.82rem'}}>
          {intl.formatMessage({id: 'DATA_UPLOAD.CONVERT.READING_PERCENT'}, {percent})}
        </div>
      </div>
    )

  /** The grid rows the Teachers file prefilled (what Remove takes back). */
  const studentsPrefillRows = () => {
    const keys = new Set(studentsPrefill?.keys ?? [])
    return ((localData.students as any[]) || []).filter((student: any) =>
      keys.has(studentRowKey(student))
    )
  }

  /**
   * Takes back the prefilled rows. Rows already saved (Next was pressed after the prefill) are
   * deleted on the server right away with `action:'delete'`; unsaved rows just go.
   */
  const removeStudentsPrefill = () => {
    if (!studentsPrefill) return
    const keys = new Set(studentsPrefill.keys)
    dispatch(
      deleteDataUploadDraft(
        studentsPrefill.draftId,
        () => {},
        () => {}
      ) as never
    )
    /** Takes rows out of the grid by key. */
    const dropStudents = (gone: Set<string>) => {
      if (gone.size === 0) return
      forgetFileStudents(gone)
      setLocalData((prev) => {
        const all = (prev.students as any[]) || []
        const students = all.filter((student: any) => !gone.has(studentRowKey(student)))
        // Rows the grid calls new go with no delete of their own: queued for the save.
        const next = withQueuedStudentDeletes(
          {...prev, students, studentsCount: students.length},
          all.filter((student: any) => gone.has(studentRowKey(student)) && !isSavedStudent(student))
        )
        updateData({manualData: next})
        return next
      })
    }
    const prefilled = ((localData.students as any[]) || []).filter((student: any) =>
      keys.has(studentRowKey(student))
    )
    const saved = prefilled.filter(isSavedStudent)
    // Unsaved rows exist only here: they go now.
    dropStudents(new Set(prefilled.filter((row) => !isSavedStudent(row)).map(studentRowKey)))
    setStudentsPrefill(null)
    setStudentsConversion(null)
    if (saved.length === 0) {
      toast.success(
        intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP4_MANUAL.STUDENTS.PREFILL_REMOVED'})
      )
      return
    }
    // Saved ones are deleted on the server first, and leave the grid only once they are gone.
    void deleteSavedRows('students', saved).then((gone) => {
      dropStudents(gone)
      if (gone.size === saved.length) {
        toast.success(
          intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP4_MANUAL.STUDENTS.PREFILL_REMOVED'})
        )
      }
    })
  }

  const isTeacherRowValid = (
    item: any,
    index: number,
    items: any[],
    nameToIndices: Record<string, number[]>
  ) => {
    const getFullName = (t: any) =>
      (t._nameInput !== undefined
        ? t._nameInput
        : `${t.first_name || ''} ${t.last_name || ''}`.trim()
      ).trim()
    const name = getFullName(item).toLowerCase()
    if (!name) return false
    const dupIndices = nameToIndices[name]
    if (dupIndices && dupIndices.length > 1) {
      const ids = dupIndices.map((i) => (items[i].employeeId || '').toString().trim())
      if (ids.some((id) => !id)) return false
      if (new Set(ids).size !== ids.length) return false
    }
    return true
  }

  /**
   * Whether a student row's class resolves to one of this school's classes (tolerant match,
   * same rule the class cell's "Class not found" highlight and the subjects dropdown use). A
   * file-imported class that matches nothing keeps its row from validating — its subjects
   * could never be shown or synced — so the Students Next stays disabled until it is fixed.
   */
  /** The school's class names, normalized once: every student row checks its class against them. */
  const classNameKeys = useMemo(
    () =>
      new Set(
        ((localData.classes as any[]) || [])
          .map((c: any) => normalizeClassMatchKey(c?.name))
          .filter(Boolean)
      ),
    [localData.classes]
  )
  const isKnownStudentClass = (className: any): boolean => {
    const key = normalizeClassMatchKey(String(className ?? '').trim())
    return Boolean(key) && classNameKeys.has(key)
  }

  /** Removing a saved student deletes it from the school: always confirmed, never skipped. */
  const alwaysConfirmDelete = (action: PendingDeleteAction | null) =>
    action?.type === 'students-prefill' ||
    (action?.type === 'student-rows' &&
      action.indices.some((i) => isSavedStudent(((localData.students as any[]) || [])[i]))) ||
    (action?.type === 'category-row' &&
      action.category === 'students' &&
      isSavedStudent(((localData.students as any[]) || [])[action.index]))

  /** A required cell the uploaded file left blank (`_missing`, set by parseStudentsRows). */
  const isMissingFromFile = (row: any, field: StudentMissingField) =>
    Array.isArray(row?._missing) && row._missing.includes(field)

  const isStudentRowValid = (item: any) => {
    const fullName = (
      item.full_name || `${item.first_name || ''} ${item.last_name || ''}`.trim()
    ).trim()
    return (
      fullName.length > 0 &&
      item.classMissing !== true &&
      (item.class || '').toString().trim() !== '' &&
      isKnownStudentClass(item.class) &&
      (item.registrationNumber || '').toString().trim() !== '' &&
      Array.isArray(item.subjects) &&
      item.subjects.length > 0
    )
  }

  const handleRowEnterKey = (
    category: string,
    index: number,
    e: React.KeyboardEvent,
    isValid: boolean
  ) => {
    if (e.key !== 'Enter') return
    if (!isValid) return
    e.preventDefault()
    handleAddRow(category, true)
  }

  const saveProgress = useCallback(
    (newTab?: string, classesFlowStepOverride?: number) => {
      const nextSubjectSelectionsByLevel = getEffectiveSubjectSelectionsByLevel(
        localData.subjectSelectionsByLevel
      )
      const nextSubjects = flattenSubjectsFromSelections(nextSubjectSelectionsByLevel)
      const teachers = (localData.teachers as any[]) || []
      const classes = (localData.classes as any[]) || []
      const subjects = nextSubjects
      const students = (localData.students as any[]) || []
      const flowStepToPersist =
        typeof classesFlowStepOverride === 'number' ? classesFlowStepOverride : classesFlowStep

      // Merge the latest "Classes step" edits (prefill table) into manual state.
      // Otherwise navigation/refresh can overwrite changes because `localData` may lag behind.
      const levelsByCurriculumBase =
        (localData as any)?.levelsByCurriculum ?? (data.manualData as any)?.levelsByCurriculum ?? {}
      const nextLevelsByCurriculum = {...levelsByCurriculumBase}
      Object.entries(prefilledClassesByCurriculumLevel).forEach(([curriculumName, levelMap]) => {
        const existing = nextLevelsByCurriculum[curriculumName] ?? {}
        const nextLevelDetails: Record<string, {classes: PrefilledClassEntry[]}> = {
          ...(existing.levelDetails ?? {}),
        }
        Object.entries(levelMap).forEach(([levelName, entries]) => {
          nextLevelDetails[levelName] = {classes: entries}
        })
        nextLevelsByCurriculum[curriculumName] = {
          ...existing,
          levelDetails: nextLevelDetails,
        }
      })

      const nextManualData = {
        ...localData,
        teachersCount: teachers.length,
        classesCount: classes.length,
        subjectsCount: subjects.length,
        studentsCount: students.length,
        manualActiveTab: newTab || activeTab,
        subjectSelectionsByLevel: nextSubjectSelectionsByLevel,
        subjects,
        lessonPlanningData: lessonPlanningData,
        deletedLessonSlots: Array.from(deletedSlots),
        importedLessonSlots: Array.from(importedSlots),
        removedLessonClasses: Array.from(removedLessonClasses),
        allocationHintsSignature,
        classesFlowStep: flowStepToPersist,
        levelsByCurriculum: nextLevelsByCurriculum,
      }
      deferUpdateData({
        manualData: {
          ...nextManualData,
        },
      })
      return nextManualData
    },
    [
      activeTab,
      classesFlowStep,
      data.manualData,
      deletedSlots,
      flattenSubjectsFromSelections,
      getEffectiveSubjectSelectionsByLevel,
      lessonPlanningData,
      importedSlots,
      removedLessonClasses,
      allocationHintsSignature,
      localData,
      prefilledClassesByCurriculumLevel,
      deferUpdateData,
    ]
  )

  useEffect(() => {
    if (!saveLaterFlushRef) return
    saveLaterFlushRef.current = () => {
      allocationServerFlushRef.current?.()
      const saved = saveProgress(activeTab) as WizardData['manualData']
      // Add Teachers: names typed and files read there are not in the list until Review
      // Teachers is pressed. Add them as it would (and open Review, so the screen's own copy
      // cannot be added a second time), so the save keeps them. Not reported as a Review
      // visit (onTeacherReview): the user did not press it.
      const pending =
        activeTab === 'teachers' && teachersPhase === 'entry'
          ? teacherEntryPendingRef.current?.()
          : null
      if (!pending) return saved
      // Teachers the school already has merge onto their saved rows, as on Review.
      const {teachers} = mergeTeacherEntryRows((saved.teachers as any[]) || [], pending.rows)
      const next = {...saved, teachers, teachersCount: teachers.length}
      if (pending.counts) setTeacherFileCounts(pending.counts)
      setLocalData((prev) => ({...prev, teachers, teachersCount: teachers.length}))
      deferUpdateData({manualData: next}, {subStep: 'teachers'})
      goToTeachersPhase('review')
      return next
    }
    return () => {
      saveLaterFlushRef.current = null
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- goToTeachersPhase only sets state
  }, [activeTab, deferUpdateData, saveLaterFlushRef, saveProgress, teachersPhase])

  // Auto-persist edits so forward/back always shows latest data.
  // Debounced to avoid excessive re-renders / API sync triggers.
  const schedulePersist = useCallback(() => {
    if (persistTimerRef.current) {
      window.clearTimeout(persistTimerRef.current)
    }
    persistTimerRef.current = window.setTimeout(() => {
      // Persist based on current active tab (keeps manualActiveTab consistent too)
      saveProgress(activeTab)
    }, 350)
  }, [activeTab, saveProgress])

  useEffect(() => {
    if (!didMountPersistRef.current) {
      didMountPersistRef.current = true
      return
    }
    // Skip debounced auto-persist in Subjects (it persists immediately).
    if (activeTab === 'subjects') return
    // Skip debounced auto-persist in Step3 Curriculum Classes to avoid update loops; we persist on navigation instead.
    if (stepperOverride && activeTab === 'classes') return
    schedulePersist()
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: schedule persist on relevant edits
  }, [
    activeTab,
    classesFlowStep,
    localData.teachers,
    localData.students,
    localData.subjectSelectionsByLevel,
    localData.subjects,
    localData.classes,
    localData.manualActiveTab,
    reviewRowsByBranch,
    prefilledClassesByCurriculumLevel,
    lessonPlanningData,
    deletedSlots,
    importedSlots,
    removedLessonClasses,
  ])

  useEffect(() => {
    return () => {
      if (persistTimerRef.current) {
        window.clearTimeout(persistTimerRef.current)
        persistTimerRef.current = null
      }
    }
  }, [])

  // Display-only: level and class names carry a curriculum prefix ("FBISE Class 1-A") so the
  // same name under two boards never collides on the server. The UI shows them without it;
  // state, lesson-planning keys and every payload keep the full name. Never write a
  // `displayName` result back into data.
  const curriculumPrefixIndex = useMemo(
    () =>
      buildCurriculumPrefixIndex(
        (localData.classes as any[]) || [],
        (localData.curriculums as string[] | undefined) ?? []
      ),
    [localData.classes, localData.curriculums]
  )
  /**
   * Prefixes a class name in a server report may carry, longest first. `curriculumPrefixIndex`
   * derives them from the curricula's own names; `curriculumShortCodes` adds the codes that a
   * name does not spell out ("BISE KP" for a board written in full). Used to read the server's
   * class candidates, never to write a name back into state.
   */
  const classChoicePrefixes = useMemo(
    () =>
      Array.from(
        new Set([
          ...curriculumPrefixIndex.all,
          ...Object.values(
            localData.curriculumShortCodes ?? data.manualData?.curriculumShortCodes ?? {}
          ).map((code) => String(code ?? '').trim()),
        ])
      )
        .filter(Boolean)
        .sort((a, b) => b.length - a.length),
    [curriculumPrefixIndex, localData.curriculumShortCodes, data.manualData?.curriculumShortCodes]
  )

  /**
   * A class candidate as `report.needsReview[].choices.class` writes it, in this wizard's own
   * spelling. The server qualifies a name two boards share with the curriculum's short code
   * ("FBISE Pre-primary 1-A") whether or not the school stores it that way, and an unqualified
   * school's class then matched neither the option's value nor its label, so the candidates
   * never reached the top of the row's dropdown. Dropping a prefix that names a curriculum this
   * wizard knows matches either spelling: the stored name when the school keeps the prefix, the
   * display label when it does not. Plain function: classChoicePrefixes is declared above, and
   * this only ever runs from an upload callback.
   */
  const wizardClassChoiceName = (choice: string) =>
    stripCurriculumPrefix(choice, classChoicePrefixes)

  /**
   * A class of the `resume-data.curriculums` tree by its `class_id`, shaped like a wizard class
   * row (stored name, branch, level, curriculum). Plain function: upload callbacks only.
   */
  /**
   * The subjects a student gets when a class is picked and it has none for that class: the
   * file's own (`_fileSubjects`, kept when the upload could not place the class) that the class
   * teaches; with none in the file, a subject-wise school gets every subject the class teaches,
   * so the user does not add them one by one. Encoded "name|||class", like an upload's.
   */
  const subjectsForPickedClass = (
    row: any,
    picked: {name?: string; branch?: string; level?: string; curriculum?: string},
    className: string
  ): string[] => {
    const same = (a: unknown, b: unknown) =>
      !String(b ?? '').trim() ||
      String(a ?? '')
        .trim()
        .toLowerCase() ===
        String(b ?? '')
          .trim()
          .toLowerCase()
    const record =
      ((localData.classes as any[]) || []).find(
        (c: any) =>
          same(c?.name, className) &&
          same(c?.branch, picked.branch) &&
          same(c?.level, picked.level) &&
          // Branch + level pin a class; the curriculum only helps without a level (its stored
          // name and the tree's display name may be spelled differently).
          (Boolean(String(picked.level ?? '').trim()) || same(c?.curriculum, picked.curriculum))
      ) ?? picked
    const allowed = new Map(
      uniqueStrings(getSubjectsForClass(record)).map((name) => [name.toLowerCase(), name])
    )
    const fromFile = ((row?._fileSubjects as unknown[]) ?? [])
      .map((name) => allowed.get(String(name ?? '').toLowerCase()))
      .filter((name): name is string => Boolean(name))
    const names =
      fromFile.length > 0 ? fromFile : data.mode === 'subject' ? [...allowed.values()] : []
    return names.map((name) => `${name}${STUDENT_SUBJECT_DELIMITER}${className}`)
  }

  /** `_missing` once a class is picked: `class` goes, and `subjects` too when some were filled. */
  const withoutFilledMissing = (
    missing: unknown,
    classPicked: boolean,
    subjectsFilled: boolean
  ): string[] =>
    (Array.isArray(missing) ? missing : []).filter(
      (field) => !(classPicked && field === 'class') && !(subjectsFilled && field === 'subjects')
    )

  const classById = (classId: number): any => {
    const node = treeClasses.find((cls) => cls.classId === classId)
    return node
      ? {
          id: node.classId,
          name: node.name,
          branch: node.branch,
          level: node.level,
          curriculum: node.curriculum,
        }
      : undefined
  }

  /**
   * The questions a read asks once its rows are in the grid: columns, subjects and, the first
   * time a file has unclear classes or subject proposals, "Save students only" (the default) or
   * "Also choose classes and subjects". Once chosen, "only" asks neither again for that file.
   */
  const askStudentsQuestions = (
    file: File,
    conversion: DataUploadConversion,
    columns: DataUploadColumnQuestions | null,
    proposedSubjects: DataUploadSubjectMappings | null
  ) => {
    const choice = studentsPeopleOnlyRef.current.get(file)
    const subjects = choice === true ? null : proposedSubjects
    const classQuestionIds =
      choice === undefined ? conversion.report.classQuestions.map(studentsClassQuestionId) : []
    const askPeopleOnly = choice === undefined && (Boolean(subjects) || classQuestionIds.length > 0)
    if (!columns && !subjects && !askPeopleOnly) return
    setStudentsQuestions({file, columns, branches: [], subjects, askPeopleOnly, classQuestionIds})
  }

  /**
   * A conversion's class questions (CH-3). Each row is found in the grid by its registration
   * number (from `references`), else its name. Plain function: upload callbacks only.
   */
  const addStudentsClassQuestions = (conversion: DataUploadConversion, ask = true) => {
    const regByRecord = new Map(
      conversion.report.references.map((ref) => [ref.recordId, ref.registrationNumber])
    )
    // Not asked ("Save students only"): nothing added, but the read's rows still drop the
    // questions an earlier read asked about them.
    const added = (ask ? conversion.report.classQuestions : []).map((question) => ({
      id: studentsClassQuestionId(question),
      written: question.written,
      labels: question.labels.filter(Boolean),
      rowKeys: question.recordIds.flatMap((recordId, index) => {
        const reg = regByRecord.get(recordId)
        const label = question.labels[index] ?? ''
        return [
          ...(reg ? [studentRowKey({registrationNumber: reg})] : []),
          ...(label ? [studentRowKey({full_name: label})] : []),
        ]
      }),
      options: question.options,
    }))
    /**
     * Every row this read covers. A re-read of the same file (after a branch or column answer)
     * decides those rows afresh: a question an earlier read asked about one of them is dropped,
     * or "Which class?" would still ask for rows the branch answer has already placed.
     */
    const readKeys = new Set([
      ...conversion.report.references.flatMap((ref) => [
        ...(ref.registrationNumber
          ? [studentRowKey({registrationNumber: ref.registrationNumber})]
          : []),
        ...(ref.label ? [studentRowKey({full_name: ref.label})] : []),
      ]),
      ...conversion.report.needsReview
        .map((row) => row.label)
        .filter(Boolean)
        .map((label) => studentRowKey({full_name: label})),
      ...added.flatMap((question) => question.rowKeys),
    ])
    setStudentsClassQuestions((prev) => [
      ...prev.filter(
        (question) =>
          !added.some((next) => next.id === question.id) &&
          !question.rowKeys.some((key) => readKeys.has(key))
      ),
      ...added,
    ])
    // New questions: the button asks for attention again until it is opened.
    if (added.length > 0) setStudentsClassQuestionsSeen(false)
  }

  /** The class questions are asked in a dialog, opened from the report's "Check" chip. */
  const [studentsClassQuestionsOpen, setStudentsClassQuestionsOpen] = useState(false)
  /**
   * The "Choose classes" button was opened since its questions arrived. Until then it is red and
   * pulsing (like "Request Timetable Generation"): the rows can't be saved without a class.
   */
  const [studentsClassQuestionsSeen, setStudentsClassQuestionsSeen] = useState(
    () => studentsUploadCache.classQuestionsSeen
  )
  useEffect(() => {
    studentsUploadCache.classQuestionsSeen = studentsClassQuestionsSeen
  }, [studentsClassQuestionsSeen])
  /**
   * The upload report and class questions as they stand for the students still in the grid: a
   * student the user removed leaves them too, and once none of the file's students is left the
   * report goes. Matched by registration number (through `references`), else by name, so
   * editing a name does not hide its line.
   */
  const presentStudentKeys = new Set(
    ((localData.students as any[]) || []).flatMap((student: any) => [
      studentRowKey(student),
      studentRowKey({full_name: student?.full_name}),
    ])
  )
  /** The last upload's rows are all deleted: its report and panels go with them. */
  const studentsFileRowsGone =
    studentsConversionKeys !== null &&
    !Array.from(studentsConversionKeys).some((key) => presentStudentKeys.has(key))
  const visibleStudentsConversion = (() => {
    if (!studentsConversion) return null
    // A file that added no students still shows its summary; otherwise an empty grid has none.
    if (
      studentsConversion.report.written > 0 &&
      ((localData.students as any[]) || []).length === 0
    ) {
      return null
    }
    // Every row the file put in the grid is gone (rows added by hand don't keep it).
    if (studentsFileRowsGone) return null
    const {report} = studentsConversion
    const regByRecord = new Map(
      report.references.map((ref) => [ref.recordId, ref.registrationNumber])
    )
    const isPresent = (recordId?: string, label?: string) => {
      const reg = recordId ? regByRecord.get(recordId) : undefined
      return reg
        ? presentStudentKeys.has(studentRowKey({registrationNumber: reg}))
        : presentStudentKeys.has(studentRowKey({full_name: label}))
    }
    const references = report.references.filter((ref) => isPresent(ref.recordId, ref.label))
    // Every student the file added is gone: nothing left to report.
    if (report.references.length > 0 && references.length === 0) return null
    const written = report.references.length > 0 ? references.length : report.written
    return {
      ...studentsConversion,
      counts: studentsConversion.counts
        ? {
            ...studentsConversion.counts,
            ready:
              studentsConversion.counts.ready == null
                ? null
                : Math.min(studentsConversion.counts.ready, written),
          }
        : studentsConversion.counts,
      report: {
        ...report,
        written,
        references,
        needsReview: report.needsReview.filter((row) => isPresent(row.recordId, row.label)),
      },
    }
  })()
  /**
   * The one branch the last file was answered with, when every sheet of it got the same one
   * (not "Both"): its "Which class?" questions then only offer that branch's classes.
   */
  const answeredStudentsBranch = (() => {
    if (!studentsBranchAnswered?.rowKeys) return null
    const values = Object.values(studentsBranchAnswered.answers)
    const [first] = values
    if (typeof first !== 'number' || values.some((value) => value !== first)) return null
    const name =
      studentsBranchAnswered.questions
        .flatMap((question) => question.branches)
        .find((entry) => entry.branchId === first)?.branch ?? ''
    return {
      branchId: first,
      name: name.trim().toLowerCase(),
      rowKeys: studentsBranchAnswered.rowKeys,
    }
  })()
  const visibleClassQuestions = studentsClassQuestions
    .filter((question) => question.rowKeys.some((key) => presentStudentKeys.has(key)))
    .map((question) => {
      // Names of removed students leave the "2 students (…)" line too.
      const labels = question.labels.filter((label) =>
        presentStudentKeys.has(studentRowKey({full_name: label}))
      )
      // The school's classes as they are now: the question was asked with the classes the file
      // was read against, and Academic Structure may have deleted, re-curriculumed or added some
      // since. A pick whose class is gone is no longer an answer.
      const writtenKey = normalizeClassMatchKey(question.written)
      const options = currentClassQuestionOptions(
        question.options,
        treeClasses,
        (cls) =>
          Boolean(writtenKey) &&
          [cls.name, wizardClassChoiceName(cls.name), cls.displayName].some(
            (name) => normalizeClassMatchKey(name) === writtenKey
          )
      )
      const picked = options.some((option) => option.classId === question.picked)
        ? question.picked
        : undefined
      const withLabels = {
        ...question,
        ...(labels.length > 0 ? {labels} : {}),
        options,
        picked,
      }
      // A file answered with one branch: that branch's classes only (by id, else by name).
      // Nothing is hidden when none of them is in it.
      const branch = answeredStudentsBranch
      if (!branch || !question.rowKeys.some((key) => branch.rowKeys.has(key))) return withLabels
      const inBranch = question.options.filter((option) =>
        option.branchId != null
          ? option.branchId === branch.branchId
          : option.branch.trim().toLowerCase() === branch.name
      )
      return inBranch.length > 0 ? {...withLabels, options: inBranch} : withLabels
    })

  const studentsClassQuestionsButton = (
    <span
      className={`d-inline-block${studentsClassQuestionsSeen ? '' : ' class-questions-cta-wrap'}`}
    >
      <button
        type='button'
        className={`btn btn-sm py-1 px-3 ${
          studentsClassQuestionsSeen ? 'btn-light-primary' : 'class-questions-cta'
        }`}
        onClick={() => {
          setStudentsClassQuestionsSeen(true)
          setStudentsClassQuestionsOpen(true)
        }}
      >
        {intl.formatMessage(
          {id: 'ONBOARDING_WIZARD.STEP4_MANUAL.STUDENTS.CLASS_QUESTIONS_OPEN'},
          {count: visibleClassQuestions.length}
        )}
      </button>
    </span>
  )

  /** The chosen `classId` fills every row in the question's `recordIds`. */
  const answerStudentsClassQuestion = (questionId: string, option: DataUploadClassOption) => {
    const question = studentsClassQuestions.find((entry) => entry.id === questionId)
    if (!question) return
    setStudentsClassQuestions((prev) =>
      prev.map((entry) => (entry.id === questionId ? {...entry, picked: option.classId} : entry))
    )
    const keys = new Set(question.rowKeys)
    setLocalData((prev) => {
      const students = ((prev.students as any[]) || []).map((student: any) => {
        const hit =
          keys.has(studentRowKey(student)) ||
          keys.has(studentRowKey({full_name: student?.full_name}))
        if (!hit) return student
        // Branch and level too: the save places a student by class name + branch + level, and
        // the name asked about exists more than once by definition.
        // Its subjects: the ones it has for this class, else the file's, else (subject-wise
        // school) the class's own — the same rule as a pick in the grid.
        const kept = ((student?.subjects as any[]) ?? []).filter((subject: any) => {
          const cls =
            typeof subject === 'string'
              ? String(subject.split(STUDENT_SUBJECT_DELIMITER)[1] ?? '').trim()
              : String(subject?.class ?? '').trim()
          return !cls || cls === option.name
        })
        const subjects =
          kept.length > 0 ? kept : subjectsForPickedClass(student, option, option.name)
        const missing = withoutFilledMissing(student?._missing, true, subjects.length > 0)
        const {_missing: _previousMissing, ...rest} = student
        return {
          ...rest,
          ...(missing.length > 0 ? {_missing: missing} : {}),
          classId: option.classId,
          class: option.name,
          curriculum: option.curriculum || undefined,
          branch: option.branch || undefined,
          level: option.level || undefined,
          subjects,
        }
      })
      const next = {...prev, students}
      updateData({manualData: next})
      return next
    })
  }

  /**
   * Everything a conversion report says about its rows, for `applyStudentRows`: the class each
   * row was placed in by id, the candidates for a class it would not guess (by id when the
   * server sent `options`, else by name), and the values it filled in but wants checked.
   */
  const conversionRowOptions = (conversion: DataUploadConversion) => ({
    classChoices: studentClassChoices(conversion, wizardClassChoiceName, (classId) => {
      const name = String(classById(classId)?.name ?? '').trim()
      return name || undefined
    }),
    classIds: studentClassIds(conversion),
    reviewNotes: studentReviewNotes(conversion),
    // The file's own students (placed, to check, or asked about), not the roster it repeats.
    fileRowKeys: new Set([
      ...conversion.report.references.flatMap((ref) => [
        ...(ref.registrationNumber
          ? [studentRowKey({registrationNumber: ref.registrationNumber})]
          : []),
        ...(ref.label ? [studentRowKey({full_name: ref.label})] : []),
      ]),
      ...conversion.report.needsReview
        .map((row) => row.label)
        .filter(Boolean)
        .map((label) => studentRowKey({full_name: label})),
      ...conversion.report.classQuestions.flatMap((question) =>
        question.labels.filter(Boolean).map((label) => studentRowKey({full_name: label}))
      ),
    ]),
  })

  // One naming rule (bugs 375/377): the plain level/class name ("Class 1"), with the
  // curriculum's short code in front only when another curriculum in the school has a level or
  // class reading the same ("FBISE Class 1" / "BISE KP Class 1"). Display only, like the prefix
  // strip.
  const curriculumNameLabel = useMemo(
    () =>
      buildCurriculumNameLabeler(
        [
          ...((localData.classes as any[]) || []),
          ...Object.entries(localData.levelsByCurriculum ?? {}).flatMap(([curriculum, cfg]) =>
            (cfg?.levels ?? []).map((level) => ({name: level?.name, curriculum}))
          ),
        ],
        localData.curriculumShortCodes ?? data.manualData?.curriculumShortCodes
      ),
    [
      localData.classes,
      localData.curriculumShortCodes,
      localData.levelsByCurriculum,
      data.manualData?.curriculumShortCodes,
    ]
  )
  const displayName = (name: unknown, curriculum?: unknown) => curriculumNameLabel(name, curriculum)
  /** Every stored class/level name whose display form differs → that display form. */
  const displayNamePairs = useMemo(
    () =>
      buildDisplayNamePairs(
        curriculumPrefixIndex,
        (localData.classes as any[]) || [],
        curriculumNameLabel
      ),
    [curriculumPrefixIndex, localData.classes, curriculumNameLabel]
  )
  /** Free text (AI chat, warnings) with prefixed class/level names shown without the prefix. */
  const displayFreeText = useCallback(
    (text: string) => stripCurriculumPrefixesInText(text, displayNamePairs),
    [displayNamePairs]
  )
  /** Curriculum of a class (by its stored name), for option labels that only have the name. */
  const curriculumOfClass = (name: unknown) =>
    ((localData.classes as any[]) || []).find(
      (c: any) => String(c?.name ?? '').trim() === String(name ?? '').trim()
    )?.curriculum
  /** Curriculum of a level (by its stored name), for level tabs and messages. */
  const curriculumOfLevel = (level: unknown) =>
    ((localData.classes as any[]) || []).find(
      (c: any) => String(c?.level ?? '').trim() === String(level ?? '').trim()
    )?.curriculum
  /**
   * The school has more than one branch. Branches set up from the same class list have the same
   * class names, so every class label then names its branch too.
   */
  const severalClassBranches =
    new Set(treeClasses.map((cls) => cls.branch).filter(Boolean)).size > 1 ||
    branchOptions.length > 1
  /**
   * A class label with its branch after it ("Grade 1-A · BISE Punjab · Modal Town") when the
   * school has more than one branch; today's label otherwise.
   */
  const withClassBranch = (label: string, branch: unknown) => {
    const name = String(branch ?? '').trim()
    return severalClassBranches && name ? `${label} · ${name}` : label
  }
  /**
   * Class options for a student row, each carrying the stored `name` it stands for.
   *
   * Built from the `resume-data.curriculums` tree (CH-1): value = `class_id`, label =
   * `display_name`, with the curriculum beside it when the picker lists more than one board
   * ("Grade 6-B · FBISE"). Without a tree (a server that does not send it) the options are the
   * wizard's own classes, as before: one per name, or per curriculum when two curricula share
   * the name (value "name + curriculum", split again in handleDataChange).
   */
  const studentClassOptions = (() => {
    if (treeClasses.length > 0) {
      // The school's label (`school_display_name`): the board prefix only where two boards have
      // a class of that name — "Cambridge Grade 7-A" / "BISE KP Grade 7-A", plain "Grade 8-A".
      // No " · board" suffix. `displayName` still matches the names uploads write.
      return treeClasses.map((cls) => ({
        value: String(cls.classId),
        label: withClassBranch(
          cls.schoolDisplayName || curriculumNameLabel(cls.name, cls.curriculum),
          cls.branch
        ),
        name: cls.name,
      }))
    }
    const curriculaByName = new Map<string, Set<string>>()
    ;((localData.classes as any[]) || []).forEach((c: any) => {
      const name = String(c?.name ?? '').trim()
      if (!name) return
      // A class of a level that is gone (or going) is not offered, as in the tree above.
      if (!isCurrentLevel({curriculum: c?.curriculum, level: c?.level, branch: c?.branch})) return
      const set = curriculaByName.get(name) ?? new Set<string>()
      const curriculum = String(c?.curriculum ?? '').trim()
      if (curriculum) set.add(curriculum)
      curriculaByName.set(name, set)
    })
    return Array.from(curriculaByName.entries()).flatMap(([name, curricula]) =>
      curricula.size > 1
        ? Array.from(curricula).map((curriculum) => ({
            value: `${name}${CLASS_CURRICULUM_SEP}${curriculum}`,
            label: displayName(name, curriculum),
            name,
          }))
        : [{value: name, label: displayName(name, Array.from(curricula)[0]), name}]
    )
  })()
  const studentClassPlaceholderOption = {
    value: '',
    label: intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP4_MANUAL.SELECT_PLACEHOLDER'}),
    name: '',
  }
  /** The class picker's options for a row with no candidates of its own (almost every row). */
  const studentClassSelectOptions = [studentClassPlaceholderOption, ...studentClassOptions]
  /**
   * The option a student row shows: its `classId` when the tree builds the picker; otherwise its
   * class, with its curriculum when the name is shared.
   *
   * A `classId` the tree no longer has (its class was deleted, or the curriculum changed) shows
   * the placeholder: the picker prints an unmatched value as-is, so the stale id ("29") showed
   * in the cell. The cell is still highlighted as "Class not found".
   */
  const studentClassValue = (row: any) => {
    if (treeClasses.length > 0) {
      const classId = row?.classId ? String(row.classId) : ''
      return classId && studentClassOptions.some((opt) => opt.value === classId) ? classId : ''
    }
    const name = String(row?.class ?? '').trim()
    const curriculum = String(row?.curriculum ?? '').trim()
    const encoded = `${name}${CLASS_CURRICULUM_SEP}${curriculum}`
    return curriculum && studentClassOptions.some((opt) => opt.value === encoded) ? encoded : name
  }
  const subjectOptions = Array.from(
    new Set(((localData.subjects as any[]) || []).map((s: any) => s.name).filter(Boolean))
  )

  const classSubjectsByLevelAndName = useMemo(() => {
    const map: Record<string, string[]> = {}

    Object.values(prefilledClassesByCurriculumLevel).forEach((levels) => {
      Object.entries(levels).forEach(([levelName, classes]) => {
        classes.forEach((entry) => {
          const className = (entry.name || '').toString().trim().toLowerCase()
          if (!className) return

          const subjects = uniqueStrings(entry.subjects || [])
          const levelKey = `${(levelName || '').toString().trim().toLowerCase()}|||${className}`

          map[levelKey] = uniqueStrings([...(map[levelKey] || []), ...subjects])
          map[className] = uniqueStrings([...(map[className] || []), ...subjects])
        })
      })
    })

    return map
  }, [prefilledClassesByCurriculumLevel])

  const getClassBaseName = useCallback((classItem: any) => {
    const gradeName = (classItem?.grade || '').toString().trim()
    if (gradeName) return gradeName

    const className = (classItem?.name || '').toString().trim()
    const sectionName = (classItem?.section || '').toString().trim()
    if (className && sectionName) {
      const suffix = `-${sectionName}`
      if (className.endsWith(suffix)) {
        return className.slice(0, -suffix.length).trim()
      }
    }

    return className
  }, [])

  const getSubjectsForClass = useCallback(
    (classItem: any): string[] => {
      const classKey = getLessonClassKey(
        classItem?.name || '',
        classItem?.branch,
        classItem?.curriculum
      )
      const dynamicSubjects = uniqueStrings(Object.keys(lessonPlanningData[classKey] || {})).filter(
        (sn) => !isSlotDeleted(classKey, sn)
      )

      if (Array.isArray(classItem?.subjects) && classItem.subjects.length > 0) {
        return uniqueStrings([
          ...classItem.subjects.map((s: any) => (typeof s === 'string' ? s : s?.name || '')),
          ...dynamicSubjects,
        ])
      }

      const curriculumName = (classItem?.curriculum || '').toString().trim()
      const levelName = (classItem?.level || '').toString().trim()
      const levelSubjects = curriculumName
        ? getSubjectsForCurriculumLevel(curriculumName, levelName)
        : []
      if (levelSubjects.length > 0) return uniqueStrings([...levelSubjects, ...dynamicSubjects])

      const baseName = getClassBaseName(classItem).toLowerCase()
      const normalizedLevelName = levelName.toLowerCase()

      if (!baseName) return uniqueStrings([...subjectOptions, ...dynamicSubjects])

      const scopedKey = `${normalizedLevelName}|||${baseName}`
      const scopedSubjects = classSubjectsByLevelAndName[scopedKey] || []
      if (scopedSubjects.length > 0) return uniqueStrings([...scopedSubjects, ...dynamicSubjects])

      const baseSubjects = classSubjectsByLevelAndName[baseName] || []
      if (baseSubjects.length > 0) return uniqueStrings([...baseSubjects, ...dynamicSubjects])

      return uniqueStrings([...subjectOptions, ...dynamicSubjects])
    },
    [
      classSubjectsByLevelAndName,
      getClassBaseName,
      getLessonClassKey,
      getSubjectsForCurriculumLevel,
      isSlotDeleted,
      lessonPlanningData,
      subjectOptions,
    ]
  )

  // Keep downstream Lesson Planning data consistent with Subjects selections.
  // When subjects for a curriculum/level are changed, remove any lesson-planning cells
  // (and deleted-slot markers) that refer to subjects that are no longer allowed.
  useEffect(() => {
    if (activeTab !== 'lessonPlanning') {
      // Still keep state consistent even if user isn't on lessonPlanning,
      // so coming back later shows correct data.
    }

    const classRecords = (localData.classes as any[]) || []
    if (classRecords.length === 0) return

    const allowedSubjectsByClassKey = new Map<string, Set<string>>()
    classRecords.forEach((classItem: any) => {
      const classKey = getLessonClassKey(
        classItem?.name || '',
        classItem?.branch,
        classItem?.curriculum
      )
      const curriculumName = (classItem?.curriculum || '').toString().trim()
      const levelName = (classItem?.level || '').toString().trim()

      // Preferred: subjects defined for this class row, else subjects defined for curriculum/level,
      // else fall back to current global subject options.
      const allowed = (() => {
        if (Array.isArray(classItem?.subjects) && classItem.subjects.length > 0) {
          return uniqueStrings(
            classItem.subjects.map((s: any) => (typeof s === 'string' ? s : s?.name || ''))
          )
        }
        const levelSubjects = curriculumName
          ? getSubjectsForCurriculumLevel(curriculumName, levelName)
          : []
        if (levelSubjects.length > 0) return uniqueStrings(levelSubjects)
        return uniqueStrings(subjectOptions)
      })()

      // Important: allow "user-added" subjects created from the Lesson Planning grid (+ cells).
      // These are stored in `lessonPlanningData[classKey]` and should not be wiped by the
      // consistency cleanup that reacts to Subjects tab changes.
      const dynamicSubjects = uniqueStrings(Object.keys(lessonPlanningData[classKey] || {}))

      allowedSubjectsByClassKey.set(
        classKey,
        new Set(uniqueStrings([...allowed, ...dynamicSubjects]))
      )
    })

    let didChangeLP = false
    const nextLessonPlanningData: typeof lessonPlanningData = {...lessonPlanningData}
    Object.entries(lessonPlanningData || {}).forEach(([classKey, subjectMap]) => {
      const allowed = allowedSubjectsByClassKey.get(classKey)
      if (!allowed) return
      const nextSubjectMap: any = {...(subjectMap || {})}
      Object.keys(nextSubjectMap).forEach((sn) => {
        if (!allowed.has(sn)) {
          delete nextSubjectMap[sn]
          didChangeLP = true
        }
      })
      nextLessonPlanningData[classKey] = nextSubjectMap
    })

    let didChangeDeleted = false
    const nextDeleted = new Set(deletedSlots)
    Array.from(nextDeleted).forEach((encoded) => {
      const parts = encoded.split('|||')
      if (parts.length < 2) return
      const classKey = parts[0]
      const subjectName = parts.slice(1).join('|||')
      const allowed = allowedSubjectsByClassKey.get(classKey)
      if (allowed && !allowed.has(subjectName)) {
        nextDeleted.delete(encoded)
        didChangeDeleted = true
      }
    })

    if (didChangeLP) {
      setLessonPlanningData(nextLessonPlanningData)
    }
    if (didChangeDeleted) {
      setDeletedSlots(nextDeleted)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: sync based on subject selections/classes
  }, [
    localData.subjectSelectionsByLevel,
    localData.subjects,
    localData.classes,
    prefilledClassesByCurriculumLevel,
  ])

  // Keep Lesson Planning consistent with Teachers edits:
  // if a teacher is removed/renamed, clear assignments that no longer match.
  useEffect(() => {
    const teachers = (localData.teachers as any[]) || []
    const getFullName = (t: any) =>
      (t?._nameInput !== undefined
        ? t._nameInput
        : `${t?.first_name || ''} ${t?.last_name || ''}`.trim()
      ).trim()
    const validNames = new Set(teachers.map((t: any) => getFullName(t)).filter(Boolean))
    const validIds = new Set(
      teachers.map((t: any) => (t.employeeId || '').toString().trim()).filter(Boolean)
    )

    let changed = false
    const next: typeof lessonPlanningData = {...lessonPlanningData}
    Object.entries(lessonPlanningData || {}).forEach(([classKey, subjectMap]) => {
      const nextSubjectMap: any = {...(subjectMap || {})}
      Object.entries(nextSubjectMap).forEach(([sn, cell]: any) => {
        const name = (cell?.teacher || '').toString().trim()
        const eid = (cell?.teacherEmployeeId || '').toString().trim()
        if (!name) return
        const okName = validNames.has(name)
        const okId = eid ? validIds.has(eid) : true
        if (!okName || !okId) {
          nextSubjectMap[sn] = {...cell, teacher: '', teacherEmployeeId: ''}
          changed = true
        }
      })
      next[classKey] = nextSubjectMap
    })

    if (changed) setLessonPlanningData(next)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- sync teacher edits to lesson planning
  }, [localData.teachers])

  const studentsSubjectOptions = useMemo(() => {
    const optionsByClass: Record<string, string[]> = {}
    const classRecords = (localData.classes as any[]) || []

    classRecords.forEach((classItem: any) => {
      const className = String(classItem?.name ?? '').trim()
      if (!className) return
      const subjectsForClass = uniqueStrings(getSubjectsForClass(classItem))
      optionsByClass[className] = subjectsForClass
    })

    return optionsByClass
  }, [getSubjectsForClass, localData.classes])

  const getStudentSubjectSelectOptions = useCallback(
    (selectedClassName: string) => {
      const normalizedClass = String(selectedClassName ?? '').trim()

      if (normalizedClass) {
        // Exact match first, then a tolerant one (case/spacing/dash differences) so a
        // file-imported class spelling still resolves to its own class's subjects.
        const matchKey = normalizeClassMatchKey(normalizedClass)
        const matched = studentsSubjectOptions[normalizedClass]
          ? ([normalizedClass, studentsSubjectOptions[normalizedClass]] as const)
          : Object.entries(studentsSubjectOptions).find(
              ([className]) => normalizeClassMatchKey(className) === matchKey
            )
        if (matched) {
          const [className, subjects] = matched
          return subjects.map((subjectName) => ({
            value: `${subjectName}${STUDENT_SUBJECT_DELIMITER}${className}`,
            label: subjectName,
            subjectName,
            className,
          }))
        }
      }

      return Object.entries(studentsSubjectOptions).flatMap(([className, subjects]) =>
        subjects.map((subjectName) => ({
          value: `${subjectName}${STUDENT_SUBJECT_DELIMITER}${className}`,
          label: `${subjectName}-${displayName(className, curriculumOfClass(className))}`,
          subjectName,
          className,
        }))
      )
    },
    [studentsSubjectOptions]
  )

  const getValidatedSubjectsForClass = useCallback(
    (classItem: any): string[] => {
      const applicableFromCurriculum = getSubjectsForClass(classItem)
      const classKey = getLessonClassKey(
        classItem?.name || '',
        classItem?.branch,
        classItem?.curriculum
      )
      const dynamicActive = uniqueStrings(Object.keys(lessonPlanningData[classKey] || {})).filter(
        (sn) => !isSlotDeleted(classKey, sn)
      )

      const userSubjectsInClass = subjectOptions.filter((sn) =>
        applicableFromCurriculum.includes(sn)
      )
      return uniqueStrings([...userSubjectsInClass, ...dynamicActive])
    },
    [getSubjectsForClass, getLessonClassKey, isSlotDeleted, lessonPlanningData, subjectOptions]
  )

  const aiDefaultFrequencyStr = useMemo(() => {
    const defaultFreq = data.manualData?.defaultSubjectFrequency
    const defaultPer = data.manualData?.defaultFrequencyPer || 'week'
    return defaultFreq && defaultFreq >= 1 ? `${defaultFreq}/${defaultPer}` : '1/week'
  }, [data.manualData?.defaultSubjectFrequency, data.manualData?.defaultFrequencyPer])

  const aiActiveFilters = useMemo(
    () => ({
      branch: activeLessonBranch || branchOptions[0] || 'Main',
      level: activeLessonLevel,
      curriculum: activeLessonCurriculum,
    }),
    [activeLessonBranch, activeLessonLevel, activeLessonCurriculum, branchOptions]
  )

  // The "all branches" scope toggle only makes sense when the school actually
  // has more than one branch (from explicit options or from classes).
  const hasMultipleBranches = useMemo(() => {
    const classes = (localData.classes as any[]) || []
    const names = new Set(
      [
        ...branchOptions.map(normalizeBranchName),
        ...classes.map((c: any) => normalizeBranchName(c?.branch)),
      ].filter(Boolean)
    )
    return names.size > 1
  }, [branchOptions, localData.classes, normalizeBranchName])

  // Bumped to force the AI allocation chatbot window closed when leaving the
  // lesson-planning step via Next.
  const [aiWidgetCloseSignal, setAiWidgetCloseSignal] = useState(0)
  const allocationAi = useTeacherAllocationAi({
    manualDataRef,
    lessonPlanningData,
    setLessonPlanningData,
    normalizeBranchName,
    getSubjectsForClass,
    getLessonClassKey,
    isSlotDeleted,
    activeFilters: aiActiveFilters,
    defaultFrequency: aiDefaultFrequencyStr,
    defaultDuration: '1',
    onApplied: () => {
      aiUsedSinceOpenRef.current = true
      trackAllocationEvent('ai_assignment')
    },
  })

  // With a single branch the persisted session may still carry an
  // "all_branches" scope — snap it back, the toggle is disabled in that case.
  useEffect(() => {
    if (!hasMultipleBranches && allocationAi.scope === 'all_branches') {
      allocationAi.setScope('active_filters')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- setScope identity is stable
  }, [hasMultipleBranches, allocationAi.scope])

  // When a new AI proposal lands, bring its first highlighted (yellow) cell
  // into view — the targeted slots can be far down or off to the side of the
  // grid, and the user would otherwise not see what is being proposed.
  useEffect(() => {
    if (allocationAi.previewSlotKeys.size === 0) return
    const raf = requestAnimationFrame(() => {
      document
        .querySelector('.step4-ai-preview-cell')
        ?.scrollIntoView({behavior: 'smooth', block: 'center', inline: 'center'})
    })
    return () => cancelAnimationFrame(raf)
  }, [allocationAi.previewSlotKeys])

  const prevActiveTabRef = useRef(activeTab)
  useEffect(() => {
    const prevTab = prevActiveTabRef.current
    prevActiveTabRef.current = activeTab

    // Only act on a genuine move *away* from lesson planning. This effect also runs on
    // mount, where activeTab is 'teachers' by default - resetting there would wipe the
    // session we just restored from sessionStorage.
    if (prevTab !== 'lessonPlanning' || activeTab === 'lessonPlanning') return

    // Drop the un-applied preview (its context goes stale once you leave), but keep the
    // conversation, baseline and undo stack - those are meant to survive coming back.
    allocationAi.cancelPreview()
    // eslint-disable-next-line react-hooks/exhaustive-deps -- clear pending AI preview when leaving tab
  }, [activeTab])

  // --- Teacher Allocation (Da1TimeTable handover): landing, grid, cell settings, Versi -------
  // Which screen of the Teacher Allocation page is open. Always the landing on arrival: neither
  // the grid nor the assistant opens by itself.
  const [allocationView, setAllocationView] = useState<'landing' | 'grid' | 'ai'>('landing')
  /** The grid shows Versi's pending proposal read-only ("Preview in full grid"). */
  const [gridPreviewMode, setGridPreviewMode] = useState(false)
  const [gridBranch, setGridBranch] = useState(ALL_FILTER_VALUE)
  const [gridLevel, setGridLevel] = useState(ALL_FILTER_VALUE)
  const [aiBranch, setAiBranch] = useState(ALL_FILTER_VALUE)
  const [aiLevel, setAiLevel] = useState(ALL_FILTER_VALUE)
  const [cellSettings, setCellSettings] = useState<{classKey: string; subject: string} | null>(null)
  const [allocationRemoval, setAllocationRemoval] = useState<AllocationRemoval | null>(null)

  const allocationRows = useMemo<AllocationRow[]>(
    () =>
      ((localData.classes as any[]) || []).flatMap((c: any) => {
        const className = String(c?.name ?? '').trim()
        if (!className) return []
        const classKey = getLessonClassKey(className, c?.branch, c?.curriculum)
        if (removedLessonClasses.has(classKey)) return []
        return [
          {
            classKey,
            className,
            label: displayName(className, c?.curriculum),
            branch: normalizeBranchName(c?.branch),
            level: String(c?.level ?? '').trim(),
            curriculum: String(c?.curriculum ?? '').trim(),
            grade: String(c?.grade ?? '').trim(),
            section: String(c?.section ?? '').trim(),
            subjects: getValidatedSubjectsForClass(c).filter(Boolean),
          },
        ]
      }),
    // displayName is a plain function over curriculumPrefixIndex, which is listed instead.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      localData.classes,
      removedLessonClasses,
      getLessonClassKey,
      getValidatedSubjectsForClass,
      normalizeBranchName,
      curriculumPrefixIndex,
      curriculumNameLabel,
    ]
  )
  const allocationRowByKey = useMemo(
    () => new Map(allocationRows.map((row) => [row.classKey, row])),
    [allocationRows]
  )
  const allocationCounts = useMemo(
    () => countAllocations(allocationRows, lessonPlanningData, deletedSlots),
    [allocationRows, lessonPlanningData, deletedSlots]
  )
  // Stable while the teacher list is: the grid's rows are memoized on it.
  const allocationTeacherOptions = useMemo<AllocationTeacherOption[]>(
    () =>
      ((localData.teachers as any[]) || [])
        .map((t: any) => ({
          name: getTeacherFullName(t) || String(t?.employeeId ?? '').trim(),
          employeeId: String(t?.employeeId ?? '').trim() || undefined,
        }))
        .filter((t) => t.name),
    [localData.teachers]
  )
  const allocationBranchValues = branchValues(allocationRows)
  const resolvedGridBranch = resolveFilterValue(gridBranch, allocationBranchValues)
  const resolvedGridLevel = resolveFilterValue(
    gridLevel,
    levelValues(allocationRows, resolvedGridBranch)
  )
  const resolvedAiBranch = resolveFilterValue(aiBranch, allocationBranchValues)
  const resolvedAiLevel = resolveFilterValue(aiLevel, levelValues(allocationRows, resolvedAiBranch))
  const allocationLevelLabel = (level: string) => displayName(level, curriculumOfLevel(level))

  /** A new cell's weekly classes and consecutive periods: the saved institution settings. */
  const allocationDefaultCell = (row: {curriculum: string; level: string}, subject: string) => ({
    frequency:
      cachedPrefillFrequency(readStoredCurriculumPrefills(), row.curriculum, row.level, subject) ??
      aiDefaultFrequencyStr,
    duration: '1',
  })

  const dropImportedSlots = (keys: string[]) => {
    if (keys.length === 0) return
    setImportedSlots((prev) => {
      if (!keys.some((key) => prev.has(key))) return prev
      const next = new Set(prev)
      keys.forEach((key) => next.delete(key))
      return next
    })
  }

  const versi = useVersiAllocation({
    manualDataRef,
    lessonPlanningData,
    setLessonPlanningData,
    deletedSlots,
    setDeletedSlots,
    counts: allocationCounts,
    workingScope: {branch: resolvedAiBranch, level: resolvedAiLevel},
    normalizeBranchName,
    getSubjectsForClass,
    getLessonClassKey,
    defaultFrequency: aiDefaultFrequencyStr,
    defaultDuration: '1',
    onApplied: (slotKeys) => {
      dropImportedSlots(slotKeys)
      trackAllocationEvent('ai_assignment')
    },
  })

  // Leaving preview once the proposal is gone (applied, discarded or refreshed).
  useEffect(() => {
    if (gridPreviewMode && !versi.pendingItem && !versi.isApplying) setGridPreviewMode(false)
  }, [gridPreviewMode, versi.isApplying, versi.pendingItem])

  // Pre-fill from the Add Teachers files (handover, Point 2): the teacher-subject-class/section
  // relationships found there fill matching empty slots, once per set of hints - no separate
  // confirmation; the user reviews them in the grid. Ambiguous matches are left unresolved.
  useEffect(() => {
    if (!TEACHER_ALLOCATION_V2 || allocationServerMode) return
    if (activeTab !== 'lessonPlanning' || isHydrating || allocationRows.length === 0) return
    const teachers = (localData.teachers as any[]) || []
    const hinted = teachers.filter(
      (t: any) => Array.isArray(t?.allocationHints) && t.allocationHints.length > 0
    )
    if (hinted.length === 0) return
    const signature = JSON.stringify(
      hinted.map((t: any) => [getTeacherFullName(t), t.employeeId ?? '', t.allocationHints])
    )
    if (signature === allocationHintsSignature) return
    const {grid, filledKeys} = prefillFromAllocationHints({
      rows: allocationRows,
      teachers: hinted,
      grid: lessonPlanningData,
      deleted: deletedSlots,
      defaultCell: allocationDefaultCell,
    })
    setAllocationHintsSignature(signature)
    if (filledKeys.length === 0) return
    setLessonPlanningData(grid)
    setImportedSlots((prev) => new Set([...prev, ...filledKeys]))
    // eslint-disable-next-line react-hooks/exhaustive-deps -- runs when the tab opens or hints change
  }, [
    activeTab,
    isHydrating,
    localData.teachers,
    allocationRows.length,
    allocationHintsSignature,
    allocationServerMode,
  ])

  const allocationUndoToast = (message: string, undo: () => void) =>
    toast.info(
      ({closeToast}: {closeToast?: () => void}) => (
        <span className='ta-toast'>
          <span>{message}</span>
          <button
            type='button'
            className='ta-toast__undo'
            onClick={() => {
              undo()
              closeToast?.()
            }}
          >
            {intl.formatMessage({id: 'ONBOARDING_WIZARD.TEACHER_ALLOCATION.UNDO'})}
          </button>
        </span>
      ),
      {autoClose: 8000, className: 'ta-toast-wide'}
    )

  const selectAllocationTeacher = (
    row: AllocationRow,
    subject: string,
    teacher: AllocationTeacherOption | null
  ) => {
    if (teacher) trackAllocationEvent('manual_assignment')
    const defaults = allocationDefaultCell(row, subject)
    setLessonPlanningData((prev) => {
      const existing = prev[row.classKey]?.[subject]
      return {
        ...prev,
        [row.classKey]: {
          ...prev[row.classKey],
          [subject]: {
            teacher: teacher?.name ?? '',
            teacherEmployeeId: teacher?.employeeId ?? '',
            frequency: existing?.frequency || defaults.frequency,
            duration: existing?.duration || defaults.duration,
          },
        },
      }
    })
    dropImportedSlots([buildAllocationSlotKey(row.classKey, subject)])
  }

  /** Marks slots as not taught (requirement and teacher removed); returns an undo. */
  const removeAllocationSlots = (slots: Array<{classKey: string; subject: string}>) => {
    const before = slots.map((s) => {
      const key = buildAllocationSlotKey(s.classKey, s.subject)
      return {
        ...s,
        key,
        cell: lessonPlanningData[s.classKey]?.[s.subject],
        deleted: deletedSlots.has(key),
        imported: importedSlots.has(key),
      }
    })
    const keys = before.map((b) => b.key)
    // Kept so Restore brings back what the cell had, teacher included (the server does the same).
    before.forEach((b) => {
      if (b.cell && !b.deleted) notTaughtCellsRef.current.set(b.key, {...b.cell})
    })
    setDeletedSlots((prev) => new Set([...prev, ...keys]))
    setLessonPlanningData((prev) => {
      const next = {...prev}
      slots.forEach(({classKey, subject}) => {
        if (!next[classKey]?.[subject]) return
        const map = {...next[classKey]}
        delete map[subject]
        next[classKey] = map
      })
      return next
    })
    dropImportedSlots(keys)
    return () => {
      before.forEach((b) => {
        if (!b.deleted) notTaughtCellsRef.current.delete(b.key)
      })
      setDeletedSlots((prev) => {
        const next = new Set(prev)
        before.forEach((b) => (b.deleted ? next.add(b.key) : next.delete(b.key)))
        return next
      })
      setLessonPlanningData((prev) => {
        const next = {...prev}
        before.forEach((b) => {
          if (!b.cell) return
          next[b.classKey] = {...(next[b.classKey] ?? {}), [b.subject]: {...b.cell}}
        })
        return next
      })
      setImportedSlots((prev) => {
        const next = new Set(prev)
        before.forEach((b) => {
          if (b.imported) next.add(b.key)
        })
        return next
      })
    }
  }

  const confirmAllocationRemoval = () => {
    const removal = allocationRemoval
    if (!removal) return
    setAllocationRemoval(null)
    const doneMessage = (id: string, values: Record<string, string | number>) =>
      intl.formatMessage({id: `ONBOARDING_WIZARD.TEACHER_ALLOCATION.REMOVE.${id}`}, values)
    if (removal.type === 'cell') {
      setCellSettings(null)
      const undo = removeAllocationSlots([
        {classKey: removal.row.classKey, subject: removal.subject},
      ])
      allocationUndoToast(
        doneMessage('DONE_CELL', {subject: removal.subject, section: removal.row.label}),
        undo
      )
    } else if (removal.type === 'row') {
      const {row} = removal
      const undoSlots = removeAllocationSlots(
        row.subjects.map((subject) => ({classKey: row.classKey, subject}))
      )
      setRemovedLessonClasses((prev) => new Set([...prev, row.classKey]))
      allocationUndoToast(doneMessage('DONE_ROW', {section: row.label}), () => {
        undoSlots()
        setRemovedLessonClasses((prev) => {
          const next = new Set(prev)
          next.delete(row.classKey)
          return next
        })
      })
    } else {
      const undo = removeAllocationSlots(
        removal.targets.map((row) => ({classKey: row.classKey, subject: removal.subject}))
      )
      allocationUndoToast(
        doneMessage('DONE_COLUMN', {subject: removal.subject, count: removal.targets.length}),
        undo
      )
    }
  }

  const restoreAllocationSlot = (classKey: string, subject: string) => {
    // Reinstates the requirement with what the cell had when it was marked not taught — its
    // earlier teacher included. Nothing is made up (handover, 3B): a cell that had no teacher, or
    // was not taught when the page loaded, comes back without one.
    const key = buildAllocationSlotKey(classKey, subject)
    const earlier = notTaughtCellsRef.current.get(key)
    notTaughtCellsRef.current.delete(key)
    setDeletedSlots((prev) => {
      const next = new Set(prev)
      next.delete(key)
      return next
    })
    if (earlier) {
      setLessonPlanningData((prev) => ({
        ...prev,
        [classKey]: {...(prev[classKey] ?? {}), [subject]: {...earlier}},
      }))
    }
    setCellSettings(null)
  }

  const requestColumnRemoval = (subject: string, gridStatus: AllocationStatusFilter) => {
    const scoped = rowsInScope(allocationRows, resolvedGridBranch, resolvedGridLevel)
    const targets = columnRemovalTargets(
      scoped,
      subject,
      gridStatus,
      lessonPlanningData,
      deletedSlots
    )
    if (targets.length === 0) {
      toast.info(
        intl.formatMessage(
          {id: 'ONBOARDING_WIZARD.TEACHER_ALLOCATION.REMOVE.NOTHING_TO_REMOVE'},
          {subject}
        )
      )
      return
    }
    const branchPart =
      resolvedGridBranch ||
      intl.formatMessage({id: 'ONBOARDING_WIZARD.TEACHER_ALLOCATION.REMOVE.ALL_BRANCHES'})
    const levelPart = resolvedGridLevel
      ? allocationLevelLabel(resolvedGridLevel)
      : intl.formatMessage({id: 'ONBOARDING_WIZARD.TEACHER_ALLOCATION.REMOVE.ALL_LEVELS'})
    setAllocationRemoval({
      type: 'column',
      subject,
      targets,
      filter: gridStatus,
      scope: `${branchPart} · ${levelPart}`,
    })
  }

  const openVersi = (fromGrid: boolean) => {
    // From the grid the working scope carries over; from the landing page it starts at All.
    setAiBranch(fromGrid ? resolvedGridBranch : ALL_FILTER_VALUE)
    setAiLevel(fromGrid ? resolvedGridLevel : ALL_FILTER_VALUE)
    setGridPreviewMode(false)
    setAllocationView('ai')
  }

  const openAllocationGrid = (options?: {preview?: boolean}) => {
    if (options?.preview) {
      // The proposal's scope; the grid shows every status while previewing.
      setGridBranch(resolvedAiBranch)
      setGridLevel(resolvedAiLevel)
    }
    setGridPreviewMode(Boolean(options?.preview))
    setAllocationView('grid')
  }

  const hasSubjectsForLevelInBranch = useCallback(
    (branch: string, levelName: string): boolean => {
      const classes = (localData.classes as any[]) || []
      const classesInLevel = classes.filter(
        (c: any) =>
          normalizeBranchName(c?.branch) === branch &&
          (c.level || '').toString().trim() === levelName
      )

      if (classesInLevel.length === 0) return true

      for (const c of classesInLevel) {
        const applicableSubjects = getSubjectsForClass(c)
        const selectedSubjectsForClass = subjectOptions.filter((sn) =>
          applicableSubjects.includes(sn)
        )
        if (selectedSubjectsForClass.length > 0) {
          return true
        }
      }

      return false
    },
    [localData.classes, normalizeBranchName, getSubjectsForClass, subjectOptions]
  )

  const getFirstLevelWithoutSubjects = useCallback((): string | null => {
    const classes = (localData.classes as any[]) || []
    const branches = Array.from(
      new Set(classes.map((c: any) => normalizeBranchName(c?.branch)).filter(Boolean))
    )

    for (const branch of branches) {
      const classesInBranch = classes.filter((c: any) => normalizeBranchName(c?.branch) === branch)
      const levelsInBranch = Array.from(
        new Set(classesInBranch.map((c: any) => (c.level || '').toString().trim()).filter(Boolean))
      )
      for (const level of levelsInBranch) {
        if (!hasSubjectsForLevelInBranch(branch, level)) {
          return level
        }
      }
    }
    return null
  }, [localData.classes, normalizeBranchName, hasSubjectsForLevelInBranch])

  const isBranchLessonPlanningComplete = useCallback(
    (branch: string) => {
      const classes = (localData.classes as any[]) || []
      const classesInBranch = classes.filter((c: any) => normalizeBranchName(c?.branch) === branch)

      if (classesInBranch.length === 0) return true
      for (const c of classesInBranch) {
        const cn = getLessonClassKey(c.name, c.branch, c.curriculum)
        if (!cn) continue
        const classSubjects = getValidatedSubjectsForClass(c)
        for (const sn of classSubjects) {
          if (!sn) continue
          if (isSlotDeleted(cn, sn)) continue
          const teacher = lessonPlanningData[cn]?.[sn]?.teacher || ''
          if (!teacher.trim()) return false
        }
      }
      return true
    },
    [
      lessonPlanningData,
      localData.classes,
      normalizeBranchName,
      getLessonClassKey,
      isSlotDeleted,
      getValidatedSubjectsForClass,
    ]
  )

  const isLevelLessonPlanningComplete = useCallback(
    (branch: string, levelName: string) => {
      const classes = (localData.classes as any[]) || []
      const classesInLevel = classes.filter(
        (c: any) =>
          normalizeBranchName(c?.branch) === branch &&
          (c.level || '').toString().trim() === levelName
      )

      if (classesInLevel.length === 0) return true

      for (const c of classesInLevel) {
        const cn = getLessonClassKey(c.name, c.branch, c.curriculum)
        if (!cn) continue
        // For level badge status, validate every subject visible for this class in LP grid.
        const classSubjects = getSubjectsForClass(c)
        for (const sn of classSubjects) {
          if (!sn) continue
          if (isSlotDeleted(cn, sn)) continue
          const teacher = lessonPlanningData[cn]?.[sn]?.teacher || ''
          if (!teacher.trim()) return false
        }
      }

      return true
    },
    [
      getLessonClassKey,
      getSubjectsForClass,
      isSlotDeleted,
      lessonPlanningData,
      localData.classes,
      normalizeBranchName,
    ]
  )

  /**
   * How much of the Teacher Allocation grid is filled: class-subject slots (deleted ones left
   * out, as in validation) and how many of them have a teacher.
   */
  const getLessonPlanningFill = () => {
    let total = 0
    let filled = 0
    for (const c of (localData.classes as any[]) || []) {
      const cn = getLessonClassKey(c.name, c.branch, c.curriculum)
      if (!cn) continue
      for (const sn of getValidatedSubjectsForClass(c)) {
        if (!sn || isSlotDeleted(cn, sn)) continue
        total += 1
        if ((lessonPlanningData[cn]?.[sn]?.teacher || '').trim()) filled += 1
      }
    }
    return {total, filled}
  }

  const isTabValid = (tabId: string) => {
    if (tabId === 'lessonPlanning') {
      const classes = (localData.classes as any[]) || []

      // First check: ensure subjects are selected for each level in each branch
      const branches = Array.from(
        new Set(classes.map((c: any) => normalizeBranchName(c?.branch)).filter(Boolean))
      )
      for (const branch of branches) {
        const classesInBranch = classes.filter(
          (c: any) => normalizeBranchName(c?.branch) === branch
        )
        const levelsInBranch = Array.from(
          new Set(
            classesInBranch.map((c: any) => (c.level || '').toString().trim()).filter(Boolean)
          )
        )
        for (const level of levelsInBranch) {
          if (!hasSubjectsForLevelInBranch(branch, level)) {
            return false // No subjects selected for this level
          }
        }
      }

      // Second check: ensure teachers are assigned to all subjects
      for (const c of classes) {
        const cn = getLessonClassKey(c.name, c.branch, c.curriculum)
        if (!cn) continue
        const classSubjects = getValidatedSubjectsForClass(c)
        for (const sn of classSubjects) {
          if (!sn) continue
          if (isSlotDeleted(cn, sn)) continue
          const teacher = lessonPlanningData[cn]?.[sn]?.teacher || ''
          if (!teacher.trim()) return false
        }
      }
      return true
    }
    if (tabId === 'subjects') {
      const selections = getEffectiveSubjectSelectionsByLevel(localData.subjectSelectionsByLevel)
      return (
        subjectLevelRows.length > 0 &&
        subjectLevelRows.every(({key}) => (selections[key] ?? []).length > 0)
      )
    }
    const items = (localData[tabId as keyof typeof localData] as any[]) || []
    if (items.length === 0) return false
    if (tabId === 'teachers') {
      const getFullName = (t: any) =>
        (t._nameInput !== undefined
          ? t._nameInput
          : `${t.first_name || ''} ${t.last_name || ''}`.trim()
        ).trim()
      const hasName = (t: any) => getFullName(t).length > 0
      if (!items.every(hasName)) return false
      const nameToIndices: Record<string, number[]> = {}
      items.forEach((t: any, i: number) => {
        const name = getFullName(t).toLowerCase()
        if (name) {
          if (!nameToIndices[name]) nameToIndices[name] = []
          nameToIndices[name].push(i)
        }
      })
      const duplicateNames = Object.values(nameToIndices).filter((indices) => indices.length > 1)
      for (const indices of duplicateNames) {
        const ids = indices.map((i) => (items[i].employeeId || '').toString().trim())
        const missingId = ids.some((id) => !id)
        if (missingId) return false
        const uniqueIds = new Set(ids)
        if (uniqueIds.size !== ids.length) return false
      }
      return true
    }
    if (tabId === 'students') {
      // One rule for the row highlight, the Enter-key handler and the Next button: a row
      // with a class this school doesn't have (file import) blocks Next until it's fixed.
      return items.every(isStudentRowValid)
    }
    const excludeKeys: Record<string, string[]> = {
      teachers: ['employeeId'],
      classes: ['teacher', 'name', 'employeeId'],
      subjects: ['teacher', 'class', 'frequency', 'duration', 'employeeId'],
    }
    const keysToExclude = excludeKeys[tabId] || ['employeeId']
    return items.every((item: any) =>
      Object.entries(item)
        .filter(([k]) => !keysToExclude.includes(k))
        .every(
          ([, v]) =>
            v !== undefined &&
            v !== null &&
            (Array.isArray(v) ? v.length > 0 : v.toString().trim() !== '')
        )
    )
  }

  /**
   * Leave the current tab for the next one. `afterSave` runs once the tab's data is
   * confirmed saved (or at once when the tab has nothing to sync), right before the
   * switch — completion tracking hangs off it so a failed save, which keeps the user
   * on the tab, never reports the tab as done.
   */
  const handleNextTab = (afterSave?: () => void) => {
    const currentIdx = tabs.findIndex((t) => t.id === activeTab)
    if (currentIdx < tabs.length - 1) {
      const fieldsToSync: string[] = []
      if (activeTab === 'teachers') fieldsToSync.push('teachers')
      else if (activeTab === 'classes') fieldsToSync.push('classes')
      else if (activeTab === 'students') fieldsToSync.push('students')
      // Don't hit API when leaving subjects tab – only when lesson planning is complete
      else if (activeTab === 'lessonPlanning') fieldsToSync.push('lessonPlanning')

      const nextTabId = tabs[currentIdx + 1].id
      const nextClassesFlowStep = nextTabId === 'classes' ? 2 : classesFlowStep
      const nextManualData = saveProgress(nextTabId, nextClassesFlowStep)
      if (nextTabId === 'classes') setClassesFlowStep(2)
      if (fieldsToSync.length > 0 && onPartialSubmit) {
        setIsSubmitting(true)
        onPartialSubmit(
          fieldsToSync,
          nextManualData,
          () => {
            setIsSubmitting(false)
            afterSave?.()
            setActiveTab(nextTabId)
          },
          undefined,
          // Failed save: the sync already toasted; release the button so the user can retry.
          () => setIsSubmitting(false)
        )
      } else {
        afterSave?.()
        setActiveTab(nextTabId)
      }
    } else {
      // Important: persist current tab data (e.g. Subjects selections)
      // before leaving this step.
      saveProgress(activeTab)
      afterSave?.()
      onComplete()
    }
  }

  /** Same `afterSave` contract as handleNextTab, for the step's last tab. */
  const handleFinishStep = (afterSave?: () => void) => {
    if (activeTab !== 'lessonPlanning' && !isTabValid(activeTab)) return

    const nextManualData = saveProgress(activeTab)
    const releaseOnError = () => setIsSubmitting(false)
    if (activeTab === 'lessonPlanning' && onPartialSubmit) {
      setIsSubmitting(true)
      onPartialSubmit(
        ['subjects', 'lessonPlanning'],
        nextManualData,
        () => {
          setIsSubmitting(false)
          afterSave?.()
          onComplete()
        },
        undefined,
        releaseOnError
      )
      return
    }

    if (activeTab === 'students' && onPartialSubmit) {
      setIsSubmitting(true)
      onPartialSubmit(
        ['students'],
        nextManualData,
        () => {
          setIsSubmitting(false)
          afterSave?.()
          onComplete()
        },
        undefined,
        releaseOnError
      )
      return
    }

    if (activeTab === 'subjects' && onPartialSubmit) {
      setIsSubmitting(true)
      onPartialSubmit(
        ['subjects'],
        nextManualData,
        () => {
          setIsSubmitting(false)
          afterSave?.()
          onComplete()
        },
        undefined,
        releaseOnError
      )
      return
    }

    afterSave?.()
    onComplete()
  }

  const handleBackTab = () => {
    const currentIdx = tabs.findIndex((t) => t.id === activeTab)
    if (currentIdx > 0) {
      const prevTabId = tabs[currentIdx - 1].id
      // Re-entering Classes from a later tab lands on its last sub-step (Review).
      const prevClassesFlowStep = prevTabId === 'classes' ? 3 : classesFlowStep
      saveProgress(prevTabId, prevClassesFlowStep)
      if (prevTabId === 'classes') {
        setActiveTab('classes')
        goToClassesFlowStep(3)
        return
      }
      setActiveTab(prevTabId)
    } else {
      // Persist current tab data before leaving this step.
      saveProgress(activeTab)
      onBack?.()
    }
  }

  // Classes flow runs 2 (Classes) -> 1 (Sections) -> 3 (Review); back walks it in reverse.
  const handleClassesBack = () => {
    if (classesFlowStep === 3) {
      goToClassesFlowStep(1)
      return
    }
    if (classesFlowStep === 1) {
      goToClassesFlowStep(2)
      return
    }
    handleBackTab()
  }

  const showBackButton = tabs.findIndex((t) => t.id === activeTab) > 0 || !!onBack

  const isLastTab = activeTab === tabs[tabs.length - 1]?.id

  // Teachers sub-step: "entry" (type/paste names or upload an Excel file) then "review" — a
  // full-page state inside the same sub-step, never a third top sub-step. Both screens are
  // their own components; this only wires them to manualData and the wizard navigation.
  const renderTeachersUI = () => {
    const items = (localData.teachers as any[]) || []

    if (teachersPhase === 'entry') {
      return (
        <div className='step4-manual-content'>
          <Step4TeacherEntry
            isDark={isDark}
            existingCount={items.length}
            showBackButton={showBackButton}
            onBack={handleBackTab}
            onReview={handleTeacherEntryReview}
            onDownloadTemplate={handleDownloadTeachersSampleCsv}
            onAllocationAnswers={(answers) => {
              teachersAllocationAnswersRef.current = answers
            }}
            pendingRowsRef={teacherEntryPendingRef}
            // The files' students / allocation counts land after Review may already be open
            // (Review no longer waits for them): its stat cards fill in when they do.
            onFileCounts={(counts) => {
              if (counts) setTeacherFileCounts(counts)
            }}
            carriedTeachers={queuedTeachersFile}
            onCarriedTeachersTaken={(draftId) => {
              teachersCarriedDraftsTaken.current.add(draftId)
              setQueuedTeachersFile(null)
            }}
            onCarriedDraftDone={closeCarriedDraft}
          />
        </div>
      )
    }

    const requestDeleteTeacherRows = (indices: number[]) => {
      if (indices.length === 0) return
      const action: PendingDeleteAction = {type: 'teacher-rows', indices}
      const allEmpty = indices.every((i) =>
        ['first_name', 'last_name', 'employeeId', 'level', 'email', 'cnic', '_nameInput'].every(
          (field) => !String(items[i]?.[field] ?? '').trim()
        )
      )
      if (allEmpty || localStorage.getItem(ROW_DELETE_SKIP_CONFIRM_KEY) === 'true') {
        performDeleteAction(action)
      } else {
        setPendingDeleteAction(action)
      }
    }

    return (
      <div className='step4-manual-content'>
        <Step4TeacherReview
          isDark={isDark}
          isHydrating={isHydrating}
          isSubmitting={isSubmitting}
          teachers={items}
          // No teacher left (all deleted): no assignment either.
          fileCounts={withoutAssignments(teacherFileCounts, 0, items.length)}
          onTeachersChange={(nextTeachers) => {
            setLocalData((prev) => {
              const next = {...prev, teachers: nextTeachers, teachersCount: nextTeachers.length}
              deferUpdateData({manualData: next}, {subStep: 'review-teachers'})
              return next
            })
          }}
          onDeleteRows={requestDeleteTeacherRows}
          deletingIds={
            new Set(
              Array.from(deletingRows)
                .filter((key) => key.startsWith('t:'))
                .map((key) => Number(key.slice(2)))
            )
          }
          onBack={() => goToTeachersPhase('entry')}
          onContinue={() => {
            // The review's completion rows ride on the teachers bulk import's success,
            // so a failed save (user stays here, toast) reports nothing.
            if (activeTab === tabs[tabs.length - 1].id) handleFinishStep(onTeacherReviewConfirm)
            else handleNextTab(onTeacherReviewConfirm)
          }}
        />
      </div>
    )
  }

  const missingClassLevelsOnEntryScreen = useMemo(() => {
    const missing: Array<{curriculum: string; level: string}> = []

    Object.entries(selectedLevelsByCurriculum).forEach(([curriculumName, levels]) => {
      levels.forEach((levelName) => {
        const entries = prefilledClassesByCurriculumLevel[curriculumName]?.[levelName] ?? []
        const hasClass = entries.some((entry) => (entry.name || '').toString().trim().length > 0)
        if (!hasClass) {
          missing.push({curriculum: curriculumName, level: levelName})
        }
      })
    })

    return missing
  }, [prefilledClassesByCurriculumLevel, selectedLevelsByCurriculum])

  const setPrefilledEntries = (
    curriculumName: string,
    levelName: string,
    updater: (entries: PrefilledClassEntry[]) => PrefilledClassEntry[]
  ) => {
    setPrefilledClassesByCurriculumLevel((prev) => {
      const curr = prev[curriculumName] ?? {}
      const entries = curr[levelName] ?? []
      return {
        ...prev,
        [curriculumName]: {
          ...curr,
          [levelName]: updater(entries),
        },
      }
    })
  }

  const handleAddPrefilledClass = (curriculumName: string, levelName: string) => {
    const key = `${curriculumName}|||${levelName}`
    const inputValue = (classInputByLevel[key] || '').trim()
    if (!inputValue) return

    setPrefilledEntries(curriculumName, levelName, (entries) => {
      if (entries.some((entry) => entry.name.toLowerCase() === inputValue.toLowerCase())) {
        return entries
      }
      return [...entries, {name: inputValue, subjects: []}]
    })

    setClassInputByLevel((prev) => ({...prev, [key]: ''}))
  }

  const handleAddPrefilledClassWithValue = (
    curriculumName: string,
    levelName: string,
    rawValue: string
  ) => {
    const key = `${curriculumName}|||${levelName}`
    const trimmed = (rawValue || '').trim()
    if (!trimmed) return

    if ((classInputByLevel[key] || '') !== rawValue) {
      setClassInputByLevel((prev) => ({...prev, [key]: rawValue}))
      setTimeout(() => handleAddPrefilledClass(curriculumName, levelName), 0)
      return
    }

    handleAddPrefilledClass(curriculumName, levelName)
  }

  const goToClassesFlowStep = (targetFlowStep: number) => {
    if (targetFlowStep === classesFlowStep) return
    // User is now driving the flow; stop the hydration seeding effect from re-seeding a stale value.
    classesFlowNavigatedRef.current = true

    // Persist any typed class names even if user did not press Enter.
    // This keeps the "Classes" sub-step consistent when navigating away.
    if (classesFlowStep === 2 && targetFlowStep !== 2) {
      Object.entries(classInputByLevel).forEach(([key, value]) => {
        const [curriculumName, levelName] = key.split('|||')
        if (curriculumName && levelName && (value || '').trim()) {
          handleAddPrefilledClassWithValue(curriculumName, levelName, value)
        }
      })
    }

    if (targetFlowStep === 1) {
      setClassesFlowStep(1)
      updateData({
        manualData: {...data.manualData, classesFlowStep: 1},
      })
      saveProgress('classes')
      return
    }

    if (targetFlowStep === 3) {
      // Only reconcile the Review table from the prefill source once it has actually loaded.
      // On a fresh remount (e.g. pressing Back into this step from a later tab) prefill hasn't
      // initialized yet, so rebuilding here yields empty per-branch rows that clobber the
      // hydrated review table — the grid disappears. Leave the existing rows in place; the
      // recovery effect rebuilds once prefill finishes loading.
      if (classesPrefillInitializedRef.current) {
        const review = buildReviewRowsFromPrefill()
        setExpandedBranch(new Set(Object.keys(review)))
        setReviewRowsByBranch(review)
        syncManualDataFromReview(review)
      }
      setClassesFlowStep(3)
      updateData({
        manualData: {...data.manualData, classesFlowStep: 3},
      })
      saveProgress('classes')
      return
    }

    if (targetFlowStep === 2) {
      setClassesFlowStep(2)
      updateData({
        manualData: {...data.manualData, classesFlowStep: 2},
      })
      saveProgress('classes')
    }
  }

  const handleClassesNext = () => {
    if (classesFlowStep === 2) {
      goToClassesFlowStep(1)
      return
    }

    if (classesFlowStep === 1) {
      goToClassesFlowStep(3)
      return
    }

    if (!isTabValid('classes')) return
    // Ensure Review edits are persisted before leaving Classes tab so the
    // subsequent flow (Subjects / later Lesson Planning) sees the latest rows/sections.
    if (classesFlowStep === 3) {
      saveProgress('classes', 3)
      deferUpdateData({
        manualData: {
          ...data.manualData,
          classesReviewRowsByBranch: reviewRowsByBranch,
          classesFlowStep: 3,
        } as any,
      })
    }
    handleNextTab()
  }

  const updateReviewRows = useCallback(
    (updater: (rows: Record<string, ClassesReviewRow[]>) => Record<string, ClassesReviewRow[]>) => {
      // User is editing the review table; stop the hydration seeding effect from re-seeding.
      reviewRowsEditedRef.current = true
      setReviewRowsByBranch((prev) => {
        const next = updater(prev)
        syncManualDataFromReview(next)
        return next
      })
    },
    [syncManualDataFromReview]
  )

  const isClassesStepOneValid = (() => {
    const n = Number.parseInt(sectionCountInput, 10)
    return Number.isFinite(n) && n > 0 && n <= MAX_SECTIONS && !sectionCountError
  })()

  const isClassesReviewComplete = useMemo(() => {
    if (classesFlowStep !== 3) return true
    const branchRows = Object.values(reviewRowsByBranch)
    if (branchRows.length === 0) return false

    return branchRows.every((rows) => {
      if (!rows || rows.length === 0) return false
      return rows.every((row) => {
        return (
          row.curriculum.trim().length > 0 &&
          row.level.trim().length > 0 &&
          row.gradeName.trim().length > 0 &&
          Array.isArray(row.sections) &&
          row.sections.length > 0 &&
          row.sections.every((section) => section.trim().length > 0)
        )
      })
    })
  }, [classesFlowStep, reviewRowsByBranch])

  const isClassesCurrentStepValid =
    classesFlowStep === 2
      ? missingClassLevelsOnEntryScreen.length === 0
      : classesFlowStep === 1
        ? isClassesStepOneValid
        : isTabValid('classes') && isClassesReviewComplete

  const performDeleteAction = useCallback(
    (action: PendingDeleteAction) => {
      const blurActiveElement = () => {
        if (typeof document !== 'undefined' && document.activeElement instanceof HTMLElement) {
          document.activeElement.blur()
        }
      }

      if (action.type === 'category-row') {
        const itemsKey = `${action.category}` as keyof typeof localData
        const countKey = `${action.category}Count` as keyof typeof localData
        // Blur BEFORE splicing. A row input is usually still focused when the
        // trash icon is clicked (its onMouseDown preventDefault keeps focus),
        // and blurring fires that input's onBlur commit, which writes back to
        // its captured rowIndex. Blurring after the splice made that commit
        // land on the post-splice array — resurrecting the deleted row (or
        // overwriting the next one), so the row only really went away on the
        // second delete. Blur first, and splice via the functional updater so
        // it runs on top of the blur-committed state.
        blurActiveElement()
        // A saved student is deleted on the server first, and leaves the grid only once it is
        // gone there; refused, it stays and the reason is shown.
        const target =
          action.category === 'students'
            ? ((localData.students as any[]) || [])[action.index]
            : null
        if (target && isSavedStudent(target)) {
          const key = studentRowKey(target)
          void deleteSavedRows('students', [target]).then((gone) => {
            if (!gone.has(key)) return
            setLocalData((prev) => {
              const students = ((prev.students as any[]) || []).filter(
                (student: any) => studentRowKey(student) !== key
              )
              const next = {...prev, students, studentsCount: students.length}
              deferUpdateData({manualData: next})
              return next
            })
          })
          return
        }
        setLocalData((prev) => {
          const existingItems = [...(prev[itemsKey] as any[])]
          const [removed] = existingItems.splice(action.index, 1)
          const updated = {
            ...prev,
            [countKey]: existingItems.length,
            [itemsKey]: existingItems,
          }
          // A student the grid calls new: queued for the save's delete (see the helper).
          const next =
            action.category === 'students' && removed
              ? withQueuedStudentDeletes(updated, [removed])
              : updated
          deferUpdateData({manualData: next})
          return next
        })
        return
      }
      if (action.type === 'teacher-rows') {
        // Same blur-before-splice rule as category-row (see above).
        blurActiveElement()
        const drop = new Set(action.indices)
        const allTeachers = (localData.teachers as any[]) || []
        const picked = allTeachers.filter((_, i) => drop.has(i))
        const saved = picked.filter((row) => parsePositiveNumber(row?.id))
        if (allTeachers.length > 0 && picked.length === allTeachers.length) {
          // The whole list is cleared: what the earlier files said about allocations goes too
          // (the Teachers step's "which classes?" answers and any open allocation prefill draft),
          // so adding names again — even the same ones — starts with nothing assigned.
          teachersAllocationAnswersRef.current = []
          void discardOpenPrefillDrafts(dispatch, ['allocation'])
        } else if (picked.length > 0) {
          // Some teachers removed: their own "which classes?" answers go (12 Oct review, point 5),
          // so a later prefill fetch cannot place lines on them again; the others' answers stay.
          // Their allocation cells are cleared by the teacher-sync effect once they leave the list.
          const gone = new Set(
            picked.flatMap((row) => {
              const name = `${row?.first_name || ''} ${row?.last_name || ''}`.trim().toLowerCase()
              const employeeId = String(row?.employeeId ?? '').trim().toLowerCase()
              return [name && `n:${name}`, employeeId && `e:${employeeId}`].filter(Boolean)
            })
          )
          teachersAllocationAnswersRef.current = teachersAllocationAnswersRef.current.filter(
            (answer) =>
              !gone.has(`n:${answer.teacher.trim().toLowerCase()}`) &&
              !(answer.employeeId && gone.has(`e:${answer.employeeId.trim().toLowerCase()}`))
          )
        }
        // Kept out of whatever the parent sends back meanwhile (see `removedTeacherKeysRef`).
        picked
          .filter((row) => !parsePositiveNumber(row?.id))
          .forEach((row) => removedTeacherKeysRef.current.add(teacherRowKey(row)))
        // Unsaved rows exist only here: they go now (by index, on top of the blur commit).
        setLocalData((prev) => {
          const teachers = ((prev.teachers as any[]) || []).filter(
            (row, i) => !drop.has(i) || parsePositiveNumber(row?.id)
          )
          const next = {...prev, teachers, teachersCount: teachers.length}
          // Rows are only deleted from the Review Teachers table.
          deferUpdateData({manualData: next}, {subStep: 'review-teachers'})
          return next
        })
        // Server allocation: a row with no id can still hold cells there (the file's assignments
        // were applied to it by name), which would come back on the grid. Its delete goes by the
        // name as read and the Employee ID (`unsaved`); the grid is re-read once it has gone.
        const unsaved = picked.filter((row) => !parsePositiveNumber(row?.id))
        if (allocationServerMode && unsaved.length > 0) {
          void dispatchApi(dispatch, (ok, fail) =>
            bulkCsvImport(
              {
                teachers: unsaved.map((row) => {
                  const employeeId = String(row?.employeeId ?? '').trim()
                  return {
                    first_name: `${row?.first_name || ''} ${row?.last_name || ''}`.trim(),
                    last_name: employeeId,
                    ...(employeeId ? {employee_id: employeeId} : {}),
                    action: 'delete',
                    unsaved: true,
                  }
                }),
                source: 'onboarding',
              },
              ok,
              fail
            )
          )
            .then(() => {
              allocationServerDirtyRef.current = true
              setAllocationServerRefreshKey((key) => key + 1)
            })
            // Never saved as teachers: a failed clean-up has nothing to restore on the list.
            .catch(() => undefined)
        }
        if (saved.length === 0) return
        // Saved teachers are deleted on the server first and leave the table only once they are
        // gone there; one the server refuses (still used in Teacher Allocation) stays.
        const deleteSaved = () =>
          deleteSavedRows('teachers', saved).then((gone) => {
            if (gone.size === 0) return
            saved
              .filter((row) => gone.has(String(parsePositiveNumber(row?.id))))
              .forEach((row) => removedTeacherKeysRef.current.add(teacherRowKey(row)))
            setLocalData((prev) => {
              const teachers = ((prev.teachers as any[]) || []).filter(
                (row) => !gone.has(String(parsePositiveNumber(row?.id)))
              )
              const next = {...prev, teachers, teachersCount: teachers.length}
              deferUpdateData({manualData: next}, {subStep: 'review-teachers'})
              return next
            })
          })
        if (!allocationServerMode) {
          void deleteSaved()
          return
        }
        // Server allocation: their cells are cleared first (12 Oct review, point 5c), since the
        // import deletes teachers before it reads subject rows and refuses an allocated one.
        // The rows spin from here; `deleteSavedRows` stops them once the delete has answered.
        setDeletingRows(
          (prev) =>
            new Set([...prev, ...saved.map((row) => `t:${String(parsePositiveNumber(row?.id))}`)])
        )
        void unassignSavedTeachers(
          dispatch,
          saved.map((row) => parsePositiveNumber(row?.id) as number)
        ).then(({cleared}) => {
          if (cleared.length > 0) {
            allocationServerDirtyRef.current = true
            setAllocationServerRefreshKey((key) => key + 1)
            // Review's "Assignments found" loses the cells that went with them.
            setTeacherFileCounts((prev) => withoutAssignments(prev, cleared.length))
          }
          return deleteSaved()
        })
        return
      }
      if (action.type === 'student-rows') {
        // Same blur-before-splice rule as category-row (see above).
        blurActiveElement()
        const drop = new Set(action.indices)
        const picked = ((localData.students as any[]) || []).filter((_, i) => drop.has(i))
        const saved = picked.filter((row) => isSavedStudent(row))
        // Unsaved rows exist only here: they go now (by index, on top of the blur commit).
        setLocalData((prev) => {
          const all = (prev.students as any[]) || []
          const students = all.filter((row, i) => !drop.has(i) || isSavedStudent(row))
          // Queued for the save's delete: the grid calls them new, they may be saved still.
          const next = withQueuedStudentDeletes(
            {...prev, students, studentsCount: students.length},
            all.filter((row, i) => drop.has(i) && !isSavedStudent(row))
          )
          deferUpdateData({manualData: next})
          return next
        })
        forgetFileStudents(picked.filter((row) => !isSavedStudent(row)).map(studentRowKey))
        if (saved.length === 0) return
        // Saved students are deleted on the server first and leave the grid only once they are
        // gone there; one the server refuses stays, with the reason shown.
        void deleteSavedRows('students', saved).then((gone) => {
          if (gone.size === 0) return
          forgetFileStudents(gone)
          setLocalData((prev) => {
            const students = ((prev.students as any[]) || []).filter(
              (row) => !(isSavedStudent(row) && gone.has(studentRowKey(row)))
            )
            const next = {...prev, students, studentsCount: students.length}
            deferUpdateData({manualData: next})
            return next
          })
        })
        return
      }
      // Handled by handleConfirmDeleteAction (it needs the live prefill state).
      if (action.type === 'students-prefill') return
      // Record the removed classes (one per section) so the next bulk-import can send them with
      // action:'delete'. Without this the row just disappears from the payload and the backend keeps
      // it, so it reappears on resume. Written onto localData first so the review-sync below (whose
      // deferUpdateData spreads localData) carries deletedClasses to the parent in the same pass.
      const target = (reviewRowsByBranch[action.branchName] ?? [])[action.rowIdx]
      if (target) {
        const sectionNames = (target.sections ?? []).map((s) => (s || '').trim()).filter(Boolean)
        const sections = sectionNames.length > 0 ? sectionNames : ['A']
        const newlyDeleted = sections.map((section) => ({
          grade: target.gradeName,
          section,
          name: `${target.gradeName}-${section}`,
          branch: action.branchName,
          level: target.level,
          curriculum: target.curriculum,
        }))
        setLocalData((prev) => {
          const existing = ((prev as any).deletedClasses as any[]) ?? []
          const seen = new Set(existing.map((c: any) => `${c?.name}|||${c?.branch}|||${c?.level}`))
          const merged = [...existing]
          newlyDeleted.forEach((c) => {
            const key = `${c.name}|||${c.branch}|||${c.level}`
            if (!seen.has(key)) {
              seen.add(key)
              merged.push(c)
            }
          })
          return {...prev, deletedClasses: merged} as any
        })
      }
      updateReviewRows((prev) => {
        const next = {...prev}
        const list = [...(next[action.branchName] ?? [])]
        list.splice(action.rowIdx, 1)
        next[action.branchName] = list
        return next
      })
      blurActiveElement()
    },
    [
      reviewRowsByBranch,
      updateReviewRows,
      deferUpdateData,
      localData.students,
      localData.teachers,
      deleteSavedRows,
      allocationServerMode,
      dispatch,
    ]
  )

  const handleConfirmDeleteAction = () => {
    if (!pendingDeleteAction) return
    if (pendingDeleteAction.type === 'students-prefill') {
      removeStudentsPrefill()
      setPendingDeleteAction(null)
      return
    }
    if (donAskAgainDeleteRow && !alwaysConfirmDelete(pendingDeleteAction)) {
      localStorage.setItem(ROW_DELETE_SKIP_CONFIRM_KEY, 'true')
    }
    performDeleteAction(pendingDeleteAction)
    setPendingDeleteAction(null)
    setDonAskAgainDeleteRow(false)
  }

  const renderClassesUI = () => {
    const textColor = isDark ? '#ffffff' : '#181C32'
    const mutedColor = isDark ? '#A7A8AA' : '#7E8299'
    const bodyBg = isDark ? '#11141C' : '#ffffff'
    const classLevelGroups = Object.entries(prefilledClassesByCurriculumLevel)

    const showCurriculumColumn = (() => {
      const gradeNameBranches: Record<string, Set<string>> = {}
      Object.entries(reviewRowsByBranch).forEach(([branchName, branchRows]) => {
        branchRows.forEach((r) => {
          const key = r.gradeName.trim().toLowerCase()
          if (!key) return
          if (!gradeNameBranches[key]) gradeNameBranches[key] = new Set()
          gradeNameBranches[key].add(branchName)
        })
      })
      return Object.values(gradeNameBranches).some((branches) => branches.size > 1)
    })()

    return (
      <div className='step4-manual-content'>
        {classesFlowStep === 1 && (
          <div className='step4-classes-screen step4-classes-screen-1'>
            <div className='text-center px-4'>
              <h5 className='mb-2' style={{color: textColor, fontWeight: 500, fontSize: '2rem'}}>
                How many sections does each class have?
              </h5>
              <p className='mb-0' style={{color: mutedColor, fontSize: '1rem'}}>
                Enter the average number. You can review, add, or remove sections for each class on
                the next screen.
              </p>
            </div>

            <div
              className='step4-classes-fields'
              style={{display: 'flex', gap: 16, alignItems: 'flex-start', flexWrap: 'nowrap'}}
            >
              <div className='step4-classes-field' style={{minWidth: 220, flex: '0 0 220px'}}>
                <label>Section Count</label>
                <input
                  type='text'
                  inputMode='numeric'
                  value={sectionCountInput}
                  onChange={(e) => {
                    const raw = e.target.value.replace(/[^0-9]/g, '')
                    setSectionCountInput(raw)
                    if (!raw) return
                    const newCount = Number.parseInt(raw, 10)
                    if (!Number.isFinite(newCount) || newCount < 1) return
                    if (newCount > MAX_SECTIONS) {
                      setSectionCountError(
                        `Maximum ${MAX_SECTIONS} sections are allowed. Please reduce the count.`
                      )
                      return
                    } else {
                      setSectionCountError(null)
                    }
                    const current = uniqueStrings(sectionNomenclatureInput.split(','))
                    if (newCount > current.length) {
                      const next = [...current]
                      for (let i = current.length; i < newCount; i++) {
                        next.push(String.fromCharCode(65 + i))
                      }
                      setSectionNomenclatureInput(next.join(','))
                    } else if (newCount < current.length) {
                      setSectionNomenclatureInput(current.slice(0, newCount).join(','))
                    }
                  }}
                  className='form-control form-control-solid'
                />
                {sectionCountError && (
                  <div
                    style={{
                      color: '#dc3545',
                      fontSize: '0.85rem',
                      marginTop: 6,
                      display: 'block',
                    }}
                  >
                    {sectionCountError}
                  </div>
                )}
              </div>
              <div className='step4-classes-field step4-classes-field-wide' style={{flex: 1}}>
                <label>Section Nomenclature</label>
                <input
                  type='text'
                  value={sectionNomenclatureInput}
                  onChange={(e) => {
                    const val = e.target.value.replace(/[^a-zA-Z0-9,]/g, '')
                    setSectionNomenclatureInput(val)
                    const parsed = uniqueStrings(val.split(','))
                    if (parsed.length === 0) return

                    if (parsed.length > MAX_SECTIONS) {
                      setSectionCountError(
                        `Maximum ${MAX_SECTIONS} sections are allowed. Please reduce the count.`
                      )
                      setSectionCountInput(String(parsed.length))
                      return
                    }

                    setSectionCountError(null)
                    setSectionCountInput(String(parsed.length))
                  }}
                  className='form-control form-control-solid'
                />
              </div>
            </div>
          </div>
        )}

        {classesFlowStep === 2 && (
          <div className='step4-classes-screen step4-classes-screen-2'>
            <div className='text-center px-4 mb-4'>
              <h5 className='mb-1' style={{color: textColor, fontWeight: 500, fontSize: '2rem'}}>
                Add each class name in the rows below.
              </h5>
              <p className='mb-0' style={{color: mutedColor, fontSize: '1rem'}}>
                Data is pre-filled. You can change it if needed.
              </p>
            </div>

            <div className='step4-classes-level-groups'>
              {isHydrating && classLevelGroups.length === 0 ? (
                <CustomSkeleton variant={CARD_PRESET.LIST_ITEM} count={4} />
              ) : classLevelGroups.length === 0 ? (
                <div className='text-center px-4 py-8'>
                  <p className='mb-0' style={{color: mutedColor, fontSize: '1rem'}}>
                    Select at least one curriculum and level in Basic Setup to add classes here.
                  </p>
                </div>
              ) : (
                classLevelGroups.map(([curriculumName, levels]) => {
                  const expanded =
                    classesExpandedCurriculum.size === 0 ||
                    classesExpandedCurriculum.has(curriculumName)

                  return (
                    <div key={curriculumName} className='step4-classes-level-group'>
                      <button
                        type='button'
                        className={`step4-classes-level-group-header${expanded ? ' is-expanded' : ''}`}
                        onClick={() => {
                          setClassesExpandedCurriculum((prev) => {
                            const base =
                              prev.size === 0
                                ? new Set(classLevelGroups.map(([k]) => k))
                                : new Set(prev)
                            if (expanded) base.delete(curriculumName)
                            else base.add(curriculumName)
                            return base
                          })
                        }}
                      >
                        {expanded ? (
                          <ChevronUp size={18} color='#056EE9' />
                        ) : (
                          <ChevronDown size={18} />
                        )}
                        <span>{curriculumName}</span>
                      </button>

                      {expanded && (
                        <div className='step4-classes-level-group-body'>
                          {Object.entries(levels).map(([levelName, entries]) => {
                            const inputKey = `${curriculumName}|||${levelName}`

                            return (
                              <div key={levelName} className='step4-classes-level-row'>
                                <div className='step4-classes-level-input'>
                                  {entries.map((entry, idx) => (
                                    <span key={`${entry.name}-${idx}`} className='step2-branch-tag'>
                                      {entry.name}
                                      <button
                                        type='button'
                                        className='step2-branch-tag-remove'
                                        onClick={(e) => {
                                          e.stopPropagation()
                                          setPrefilledEntries(curriculumName, levelName, (items) =>
                                            items.filter((_, itemIdx) => itemIdx !== idx)
                                          )
                                        }}
                                      >
                                        <TagRemoveIcon />
                                      </button>
                                    </span>
                                  ))}
                                  <input
                                    type='text'
                                    className='step2-branch-input-field step4-classes-chip-input'
                                    value={classInputByLevel[inputKey] || ''}
                                    placeholder='Enter class name and press Enter'
                                    onChange={(e) =>
                                      setClassInputByLevel((prev) => ({
                                        ...prev,
                                        [inputKey]: e.target.value,
                                      }))
                                    }
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') {
                                        e.preventDefault()
                                        handleAddPrefilledClass(curriculumName, levelName)
                                      } else if (
                                        e.key === 'Backspace' &&
                                        !(classInputByLevel[inputKey] || '') &&
                                        entries.length > 0
                                      ) {
                                        // Mirror the levels behavior: backspace on an empty input
                                        // removes the last class chip.
                                        e.preventDefault()
                                        setPrefilledEntries(curriculumName, levelName, (items) =>
                                          items.slice(0, -1)
                                        )
                                      }
                                    }}
                                    onBlur={(e) =>
                                      handleAddPrefilledClassWithValue(
                                        curriculumName,
                                        levelName,
                                        e.currentTarget.value
                                      )
                                    }
                                  />
                                </div>
                                <div className='step4-classes-level-pill'>{levelName}</div>
                              </div>
                            )
                          })}
                        </div>
                      )}
                    </div>
                  )
                })
              )}
            </div>

            {missingClassLevelsOnEntryScreen.length > 0 && (
              <p className='step4-subjects-validation-note mt-3'>
                {intl.formatMessage({
                  id: 'ONBOARDING_WIZARD.STEP4_MANUAL.CLASSES.LEVEL_VALIDATION',
                })}
              </p>
            )}
          </div>
        )}

        {classesFlowStep === 3 && (
          <div className='step4-classes-screen step4-classes-screen-3'>
            <div className='text-center px-4 mb-4'>
              <h5 className='mb-1' style={{color: textColor, fontWeight: 500, fontSize: '2rem'}}>
                Review
              </h5>
              <p className='mb-0' style={{color: mutedColor, fontSize: '1rem'}}>
                Review the details below.
              </p>
            </div>

            <div className='step4-classes-branch-groups step4-classes-branch-groups-scroll'>
              {isHydrating && Object.keys(reviewRowsByBranch).length === 0 && (
                <CustomSkeleton variant={CARD_PRESET.TABLE} />
              )}
              {Object.entries(reviewRowsByBranch).map(([branchName, rows]) => {
                const isOpen = expandedBranch.size === 0 || expandedBranch.has(branchName)
                return (
                  <div key={branchName} className='step4-classes-branch-group'>
                    <button
                      type='button'
                      className={`step4-classes-level-group-header${isOpen ? ' is-expanded' : ''}`}
                      onClick={() => {
                        setExpandedBranch((prev) => {
                          const base =
                            prev.size === 0
                              ? new Set(Object.keys(reviewRowsByBranch))
                              : new Set(prev)
                          if (isOpen) base.delete(branchName)
                          else base.add(branchName)
                          return base
                        })
                      }}
                    >
                      {isOpen ? <ChevronUp size={18} color='#056EE9' /> : <ChevronDown size={18} />}
                      <span>{branchName}</span>
                    </button>

                    {isOpen && (
                      <div className='step4-classes-review-table-wrap'>
                        <GenericTable
                          isDark={isDark}
                          data={rows as any[]}
                          onChange={() => {}}
                          hashLabel='#'
                          selectPlaceholder=''
                          containerClassName=''
                          tableClassName='table table-borderless align-middle mb-0 step4-teachers-table'
                          tbodyStyle={{backgroundColor: bodyBg}}
                          rowKey={(r: any) => r.id}
                          rowClassName={() => `step4-classes-review-row is-editing`}
                          columns={[
                            ...(showCurriculumColumn
                              ? [
                                  {
                                    key: 'curriculum',
                                    label: 'Curriculum Options',
                                    type: 'text' as const,
                                    render: ({row}: any) => (
                                      <input
                                        type='text'
                                        className='form-control form-control-solid'
                                        style={{border: 'none', boxShadow: 'none'}}
                                        value={row.curriculum}
                                        disabled
                                        readOnly
                                        tabIndex={-1}
                                      />
                                    ),
                                  },
                                ]
                              : []),
                            {
                              key: 'level',
                              label: 'Level',
                              type: 'text' as const,
                              render: ({row}: any) => (
                                <input
                                  type='text'
                                  className='form-control form-control-solid'
                                  style={{border: 'none', boxShadow: 'none'}}
                                  value={row.level}
                                  disabled
                                  readOnly
                                  tabIndex={-1}
                                />
                              ),
                            },
                            {
                              key: 'gradeName',
                              label: 'Class Name',
                              type: 'text' as const,
                              render: ({row, rowIndex}) => (
                                <input
                                  type='text'
                                  className='form-control form-control-solid'
                                  style={{border: 'none', boxShadow: 'none'}}
                                  value={row.gradeName}
                                  disabled
                                  readOnly
                                  tabIndex={-1}
                                />
                              ),
                            },
                            {
                              key: 'sections',
                              label: 'Sections',
                              type: 'text' as const,
                              render: ({row}: any) => (
                                <input
                                  type='text'
                                  className='form-control form-control-solid'
                                  style={{border: 'none', boxShadow: 'none'}}
                                  value={(row.sections ?? []).join(',')}
                                  disabled
                                  readOnly
                                  tabIndex={-1}
                                />
                              ),
                            },
                            {
                              key: 'actions',
                              label: 'Actions',
                              type: 'text' as const,
                              render: ({row, rowIndex}) => (
                                <div className='d-flex justify-content-center gap-2'>
                                  <button
                                    type='button'
                                    className='btn btn-icon btn-sm btn-light-danger'
                                    onClick={() => {
                                      const action = {
                                        type: 'classes-review-row' as const,
                                        branchName,
                                        rowId: row.id,
                                        rowIdx: rowIndex,
                                      }
                                      if (
                                        localStorage.getItem(ROW_DELETE_SKIP_CONFIRM_KEY) === 'true'
                                      ) {
                                        performDeleteAction(action)
                                      } else {
                                        setPendingDeleteAction(action)
                                      }
                                    }}
                                  >
                                    <Trash2 size={14} />
                                  </button>
                                </div>
                              ),
                            },
                          ]}
                        />
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          </div>
        )}

        <div className='d-flex justify-content-end gap-3 w-100 mt-4 step4-classes-next-footer'>
          {(classesFlowStep !== 2 || showBackButton) && (
            <button
              type='button'
              className='onboarding-btn-back me-auto'
              disabled={isSubmitting}
              onClick={handleClassesBack}
            >
              <ArrowLeft size={16} className='me-2' />
              {intl.formatMessage({id: 'ONBOARDING_WIZARD.FOOTER.BACK'})}
            </button>
          )}
          <button
            type='button'
            className='onboarding-btn-next'
            disabled={!isClassesCurrentStepValid || isSubmitting}
            onClick={handleClassesNext}
          >
            {isSubmitting ? (
              <>
                <span className='spinner-border spinner-border-sm align-middle me-2' />
                {intl.formatMessage({id: 'COMMON.LOADING'})}
              </>
            ) : (
              <>
                {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP4_MANUAL.BTN_NEXT'})}
                <ArrowRight size={16} className='ms-2' />
              </>
            )}
          </button>
        </div>
      </div>
    )
  }

  const renderSubjectsUI = () => {
    const subjectSelections = getEffectiveSubjectSelectionsByLevel(
      localData.subjectSelectionsByLevel
    )
    const missingLevels = subjectLevelRows.filter(
      ({key}) => (subjectSelections[key] ?? []).length === 0
    )
    const textColor = isDark ? '#ffffff' : '#181C32'
    const mutedColor = isDark ? '#A7A8AA' : '#7E8299'
    const tableBorderColor = isDark ? 'rgba(255,255,255,0.14)' : 'rgba(24, 28, 50, 0.12)'

    return (
      <div className='step4-manual-content'>
        <div className='step4-subjects-layout'>
          <div className='text-center px-4 mb-4'>
            <h5 className='mb-2' style={{color: textColor, fontWeight: 600, fontSize: '1.65rem'}}>
              {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP4_MANUAL.SUBJECTS.HEADING'})}
            </h5>
            <p className='mb-1' style={{color: mutedColor, fontSize: '0.96rem', lineHeight: 1.45}}>
              {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP4_MANUAL.SUBJECTS.TABLE_SUBTITLE'})}
            </p>
            <p className='mb-0' style={{color: mutedColor, fontSize: '0.92rem', lineHeight: 1.45}}>
              {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP4_MANUAL.SUBJECTS.TABLE_HELPER'})}
            </p>
          </div>

          <div className='step4-subjects-table-card' style={{borderColor: tableBorderColor}}>
            <GenericTable
              isDark={isDark}
              data={Object.entries(selectedLevelsByCurriculum).flatMap(([curriculumName, levels]) =>
                levels.map((levelName, index) => ({
                  curriculumName,
                  levels,
                  levelName,
                  index,
                  key: getSubjectLevelKey(curriculumName, levelName),
                }))
              )}
              onChange={() => {}}
              hashLabel=''
              selectPlaceholder=''
              containerClassName=''
              tableClassName='step4-subjects-table'
              theadClassName={undefined}
              headerRowClassName={undefined}
              hashThClassName=''
              hashTdClassName=''
              hashThStyle={{display: 'none'}}
              hashTdStyle={{display: 'none'}}
              columns={[
                {
                  key: 'curriculumName',
                  label: intl.formatMessage({
                    id: 'ONBOARDING_WIZARD.STEP4_MANUAL.SUBJECTS.COL_CURRICULUM',
                  }),
                  type: 'text',
                  render: ({row}) => {
                    if (row.index !== 0) {
                      return {node: null, hidden: true}
                    }
                    return {
                      node: <>{row.curriculumName}</>,
                      tdProps: {
                        rowSpan: row.levels.length,
                        className: 'step4-subjects-curriculum-cell',
                      },
                    }
                  },
                },
                {
                  key: 'levelName',
                  label: intl.formatMessage({
                    id: 'ONBOARDING_WIZARD.STEP4_MANUAL.SUBJECTS.COL_LEVEL',
                  }),
                  type: 'text',
                  tdClassName: 'step4-subjects-level-cell',
                  render: ({row}) => <>{row.levelName}</>,
                },
                {
                  key: 'subjects',
                  label: intl.formatMessage({
                    id: 'ONBOARDING_WIZARD.STEP4_MANUAL.SUBJECTS.COL_SUBJECTS',
                  }),
                  type: 'text',
                  render: ({row}) => {
                    const key = row.key
                    const subjects = subjectSelections[key] ?? []
                    const rowError = subjectErrorByLevel[key]
                    const editing = editingSubjectByLevel[key]
                    return (
                      <>
                        <div className='step4-subjects-chip-box'>
                          {subjects.map((subjectName: string) => (
                            <span key={`${key}-${subjectName}`} className='step4-subject-chip'>
                              {editing?.original === subjectName ? (
                                <>
                                  <input
                                    type='text'
                                    value={editing.value}
                                    onChange={(e) => {
                                      const value = e.target.value
                                      setEditingSubjectByLevel((prev) => ({
                                        ...prev,
                                        [key]: {
                                          ...(prev[key] || {original: subjectName, value: ''}),
                                          value,
                                        },
                                      }))
                                      if (subjectErrorByLevel[key]) {
                                        setSubjectErrorByLevel((prev) => ({...prev, [key]: null}))
                                      }
                                    }}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') {
                                        e.preventDefault()
                                        commitEditSubject(row.curriculumName, row.levelName)
                                      }
                                      if (e.key === 'Escape') {
                                        e.preventDefault()
                                        cancelEditSubject(row.curriculumName, row.levelName)
                                      }
                                    }}
                                    onBlur={() => {
                                      if (skipEditBlurCommitRef.current) {
                                        skipEditBlurCommitRef.current = false
                                        return
                                      }
                                      commitEditSubject(row.curriculumName, row.levelName)
                                    }}
                                    style={{
                                      border: 'none',
                                      background: 'transparent',
                                      color: 'inherit',
                                      minWidth: 70,
                                      outline: 'none',
                                      fontSize: 'inherit',
                                    }}
                                    autoFocus
                                  />
                                  <button
                                    type='button'
                                    className='step4-subject-chip-remove'
                                    onMouseDown={(e) => {
                                      e.preventDefault()
                                      e.stopPropagation()
                                    }}
                                    onClick={() => {
                                      commitEditSubject(row.curriculumName, row.levelName)
                                    }}
                                    aria-label='Save'
                                  >
                                    ✓
                                  </button>
                                  <button
                                    type='button'
                                    className='step4-subject-chip-remove'
                                    onMouseDown={(e) => {
                                      e.preventDefault()
                                      e.stopPropagation()
                                      skipEditBlurCommitRef.current = true
                                    }}
                                    onClick={() => {
                                      cancelEditSubject(row.curriculumName, row.levelName)
                                    }}
                                    aria-label='Cancel'
                                  >
                                    <TagRemoveIcon />
                                  </button>
                                </>
                              ) : (
                                <>
                                  <button
                                    type='button'
                                    style={{
                                      border: 'none',
                                      background: 'transparent',
                                      color: 'inherit',
                                      padding: 0,
                                      cursor: 'pointer',
                                      fontSize: 'inherit',
                                    }}
                                    onMouseDown={(e) => {
                                      skipSubjectInputBlurRef.current = true
                                      e.preventDefault()
                                    }}
                                    onClick={() => {
                                      startEditSubject(
                                        row.curriculumName,
                                        row.levelName,
                                        subjectName
                                      )
                                    }}
                                    aria-label='Edit'
                                    title='Edit subject'
                                  >
                                    {subjectName}
                                  </button>
                                  <button
                                    type='button'
                                    className='step4-subject-chip-remove'
                                    onMouseDown={(e) => {
                                      skipSubjectInputBlurRef.current = true
                                      e.preventDefault()
                                      e.stopPropagation()
                                    }}
                                    onClick={() =>
                                      handleRemoveSubject(
                                        row.curriculumName,
                                        row.levelName,
                                        subjectName
                                      )
                                    }
                                    aria-label='Remove'
                                  >
                                    <TagRemoveIcon />
                                  </button>
                                </>
                              )}
                            </span>
                          ))}
                          <input
                            id={`step4-subject-input-${key}`}
                            type='text'
                            value={subjectInputByLevel[key] || ''}
                            onChange={(e) => {
                              const value = e.target.value
                              setSubjectInputByLevel((prev) => ({...prev, [key]: value}))
                              if (subjectErrorByLevel[key]) {
                                setSubjectErrorByLevel((prev) => ({...prev, [key]: null}))
                              }
                            }}
                            onKeyDown={(e) =>
                              handleSubjectKeyDown(e, row.curriculumName, row.levelName)
                            }
                            onBlur={(e) => {
                              if (skipSubjectInputBlurRef.current) {
                                skipSubjectInputBlurRef.current = false
                                return
                              }
                              handleAddSubjectWithValue(
                                row.curriculumName,
                                row.levelName,
                                e.currentTarget.value
                              )
                            }}
                            className='step4-subjects-inline-input'
                            placeholder={
                              subjects.length === 0
                                ? intl.formatMessage({
                                    id: 'ONBOARDING_WIZARD.STEP4_MANUAL.SUBJECTS.INPUT_PLACEHOLDER',
                                  })
                                : ''
                            }
                          />
                        </div>
                        {rowError && <div className='step4-subjects-row-error'>{rowError}</div>}
                      </>
                    )
                  },
                },
              ]}
            />
          </div>

          {missingLevels.length > 0 && (
            <p className='step4-subjects-validation-note'>
              {intl.formatMessage({
                id: 'ONBOARDING_WIZARD.STEP4_MANUAL.SUBJECTS.LEVEL_VALIDATION',
              })}
            </p>
          )}
        </div>

        <div className='d-flex justify-content-end gap-3 w-100 mt-4'>
          {showBackButton && (
            <button
              type='button'
              className='onboarding-btn-back me-auto'
              disabled={isSubmitting}
              onClick={handleBackTab}
            >
              <ArrowLeft size={16} className='me-2' />
              {intl.formatMessage({id: 'ONBOARDING_WIZARD.FOOTER.BACK'})}
            </button>
          )}
          {activeTab === tabs[tabs.length - 1].id ? (
            <button
              type='button'
              className='onboarding-btn-next'
              disabled={!isTabValid('subjects') || isSubmitting}
              onClick={() => handleFinishStep()}
            >
              {isSubmitting ? (
                <>
                  <span className='spinner-border spinner-border-sm align-middle me-2' />
                  {intl.formatMessage({id: 'COMMON.LOADING'})}
                </>
              ) : (
                <>
                  {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP4_MANUAL.BTN_NEXT'})}
                  <ArrowRight size={16} className='ms-2' />
                </>
              )}
            </button>
          ) : (
            <button
              type='button'
              className='onboarding-btn-next'
              disabled={!isTabValid('subjects') || isSubmitting}
              onClick={() => handleNextTab()}
            >
              {isSubmitting ? (
                <>
                  <span className='spinner-border spinner-border-sm align-middle me-2' />
                  {intl.formatMessage({id: 'COMMON.LOADING'})}
                </>
              ) : (
                <>
                  {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP4_MANUAL.BTN_NEXT'})}
                  <ArrowRight size={16} className='ms-2' />
                </>
              )}
            </button>
          )}
        </div>
      </div>
    )
  }

  const renderStudentsUI = () => {
    const items = (localData.students as any[]) || []
    const textColor = isDark ? '#ffffff' : '#181C32'
    const mutedColor = isDark ? '#A7A8AA' : '#7E8299'
    const tableBorderColor = isDark ? '#333640' : '#e4e6ef'
    const bodyBg = isDark ? '#1E1E2D' : '#ffffff'
    const headerBlue = '#056EE9'

    const getFullName = (item: any) =>
      (item.full_name || `${item.first_name || ''} ${item.last_name || ''}`.trim() || '').trim()

    // One page of rows is rendered; row indexes stay full-list indexes (`indexOffset`). The page
    // is clamped here, so deleting rows off the last page never leaves it empty.
    const studentsPageCount = Math.max(1, Math.ceil(items.length / studentsPageSize))
    const currentStudentsPage = Math.min(studentsPage, studentsPageCount)
    const studentsPageStart = (currentStudentsPage - 1) * studentsPageSize
    const studentsPageItems = items.slice(studentsPageStart, studentsPageStart + studentsPageSize)
    const showStudentsPager =
      items.length > studentsPageSize || studentsPageSize !== STUDENTS_PAGE_SIZE

    const allStudentsSelected =
      items.length > 0 && items.every((_, index) => selectedStudentRows.has(index))
    const someStudentsSelected = selectedStudentRows.size > 0
    const toggleAllStudents = () =>
      setSelectedStudentRows(
        allStudentsSelected ? new Set() : new Set(items.map((_, index) => index))
      )
    const toggleStudentRow = (index: number) =>
      setSelectedStudentRows((prev) => {
        const next = new Set(prev)
        if (next.has(index)) next.delete(index)
        else next.add(index)
        return next
      })
    /** "Delete Selected": empty rows go at once; a saved student is always confirmed. */
    const requestDeleteStudentRows = (indices: number[]) => {
      if (indices.length === 0) return
      const action: PendingDeleteAction = {type: 'student-rows', indices}
      const allEmpty = indices.every(
        (i) =>
          !getFullName(items[i] ?? {}) &&
          !String(items[i]?.registrationNumber ?? '').trim() &&
          !String(items[i]?.class ?? '').trim()
      )
      if (
        allEmpty ||
        (!alwaysConfirmDelete(action) &&
          localStorage.getItem(ROW_DELETE_SKIP_CONFIRM_KEY) === 'true')
      ) {
        performDeleteAction(action)
      } else {
        setPendingDeleteAction(action)
      }
    }

    return (
      <div className='step4-manual-content'>
        <div className='text-center px-4 mb-4'>
          <h5 className='mb-2' style={{color: textColor, fontWeight: 600, fontSize: '1.35rem'}}>
            {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP4_MANUAL.STUDENTS.HEADING'})}
          </h5>
          <p className='mb-1' style={{color: mutedColor, fontSize: '0.98rem', lineHeight: 1.6}}>
            {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP4_MANUAL.STUDENTS.INSTRUCTION'})}
          </p>
          <p className='mb-0' style={{color: mutedColor, fontSize: '0.98rem', lineHeight: 1.6}}>
            {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP4_MANUAL.STUDENTS.INSTRUCTION_ADD'})}
          </p>
        </div>

        <div className='d-flex flex-wrap justify-content-center align-items-center gap-2 mb-3 px-3'>
          <input
            ref={studentsCsvInputRef}
            type='file'
            accept={
              studentsConversionAvailable
                ? dataUploadAcceptAttribute(studentsUploadConfig.formats)
                : '.csv,text/csv,.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            }
            className='d-none'
            // A school with a sheet per campus picks them all at once; each is read in turn
            // and its rows are appended.
            multiple
            onChange={(e) => {
              const input = e.target
              const selected = Array.from(input.files ?? [])
              input.value = ''
              handleStudentsFiles(selected)
            }}
          />
          <button
            type='button'
            className='btn btn-sm btn-light-primary d-inline-flex align-items-center gap-2'
            onClick={handleDownloadStudentsTemplate}
            disabled={downloadingStudentsTemplate}
          >
            {intl.formatMessage({
              id: 'ONBOARDING_WIZARD.STEP4_MANUAL.STUDENTS.DOWNLOAD_SAMPLE_CSV',
            })}
          </button>
          <button
            type='button'
            className='btn btn-sm btn-primary d-inline-flex align-items-center gap-2'
            onClick={() => studentsCsvInputRef.current?.click()}
            disabled={isImportingStudentsFile}
          >
            <Upload size={16} aria-hidden />
            {intl.formatMessage({
              id: 'ONBOARDING_WIZARD.STEP4_MANUAL.STUDENTS.UPLOAD_DATA_FILE',
            })}
          </button>
        </div>

        {studentsConversionAvailable && (
          <div className='text-center px-4 mb-3' style={{color: mutedColor, fontSize: '0.85rem'}}>
            <div>
              {intl.formatMessage(
                {id: 'DATA_UPLOAD.CONVERT.ACCEPTED_FORMATS'},
                {size: formatDataUploadBytes(studentsUploadConfig.maxFileBytes)}
              )}
            </div>
            <div>{intl.formatMessage({id: 'DATA_UPLOAD.CONVERT.PHOTO_TIPS'})}</div>
          </div>
        )}

        {/* Like the Add Teachers upload: each file shows Uploading, Reading, then its summary;
            its rows go into the table only when the user adds them. */}
        <UploadStatusPanel
          items={studentsUploads.map(studentsUploadItem)}
          // One loader for the card, as on the Add Teachers card: the lines show none of their own.
          busy={(() => {
            const reading = studentsUploads.find((line) => line.status === 'reading')
            if (!reading) return null
            const several =
              studentsUploads.filter(
                (line) => line.status === 'reading' || line.status === 'waiting'
              ).length > 1
            return {
              text:
                studentsUploadProgress !== null
                  ? intl.formatMessage(
                      {id: 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.FILE_STATUS_UPLOADING'},
                      {percent: Math.round(studentsUploadProgress * 100)}
                    )
                  : intl.formatMessage({
                      id: several
                        ? 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.FILE_STATUS_EXTRACTING_FILES'
                        : 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.FILE_STATUS_EXTRACTING',
                    }),
              progress: studentsUploadProgress,
              // A photo or scan is read by AI, page by page: say it takes a while.
              hint:
                studentsUploadProgress === null && !isSpreadsheetUpload(reading.file)
                  ? intl.formatMessage({id: 'DATA_UPLOAD.CONVERT.READING_FILE_SLOW'})
                  : null,
            }
          })()}
          // A line not added yet is discarded with its file.
          onRemove={(id) => {
            const line = studentsUploads.find((entry) => entry.id === id)
            // A file read with others goes with them: they are one upload.
            if (line) dropStudentsUpload(studentsBatchOfRef.current.get(line.file) ?? line.file)
          }}
        />

        {/* Gone with the rows: once none of the prefilled students is left, neither is this. */}
        {studentsPrefill &&
          !isImportingStudentsFile &&
          studentsPrefill.keys.some((key) => presentStudentKeys.has(key)) && (
            <div
              className='d-flex flex-wrap align-items-center justify-content-between gap-3 mb-3'
              role='status'
              style={{
                width: '95%',
                maxWidth: '95%',
                background: isDark ? 'rgba(5, 110, 233, 0.14)' : '#eef6ff',
                border: `1px solid ${isDark ? '#1e3a5f' : '#cfe3fb'}`,
                borderRadius: 10,
                padding: '0.75rem 1rem',
                color: textColor,
                fontSize: '0.92rem',
              }}
            >
              <span className='d-inline-flex align-items-center gap-2'>
                <Info size={16} aria-hidden style={{color: headerBlue, flexShrink: 0}} />
                {intl.formatMessage(
                  {id: 'ONBOARDING_WIZARD.STEP4_MANUAL.STUDENTS.PREFILL_FOUND'},
                  {count: studentsPrefill.count}
                )}
              </span>
              <button
                type='button'
                className='btn btn-sm btn-light-danger d-inline-flex align-items-center gap-2'
                onClick={() => setPendingDeleteAction({type: 'students-prefill'})}
              >
                <Trash2 size={14} aria-hidden />
                {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP4_MANUAL.STUDENTS.PREFILL_REMOVE'})}
              </button>
            </div>
          )}

        {visibleStudentsConversion && !isImportingStudentsFile && !studentsReadingFile && (
          <div className='mb-3' style={{width: '95%', maxWidth: '95%'}}>
            <DataUploadReport
              conversion={visibleStudentsConversion}
              isDark={isDark}
              showKeptStudents
              savedTeachers={
                visibleStudentsConversion.newTeachers.filter((teacher) =>
                  studentsSavedTeacherKeys.has(teacher.key)
                ).length
              }
              // Its allocation lines are imported on Teacher Allocation, not here: say so.
              allocationsElsewhere={dataUploadHasAllocations(visibleStudentsConversion)}
              carriedAllocations={visibleStudentsConversion.carried?.allocation?.records}
              carriedAction={(() => {
                const carried = visibleStudentsConversion.carried?.allocation
                // New teachers still to answer for: the popup offers "Save and assign" first.
                const unsaved = visibleStudentsConversion.newTeachers.filter(
                  (teacher) => !studentsSavedTeacherKeys.has(teacher.key)
                ).length
                if (!carried || unsaved > 0) return null
                return (
                  <button
                    type='button'
                    className='btn btn-sm btn-light-primary'
                    onClick={() => {
                      setQueuedAllocationFile({
                        file:
                          studentsConversionFile ??
                          carriedFileNamed(
                            'ONBOARDING_WIZARD.STEP4_MANUAL.LESSON_PLANNING.CARRIED_FILE'
                          ),
                        draftId: carried.draftId,
                      })
                      setActiveTab('lessonPlanning')
                      saveProgress('lessonPlanning')
                    }}
                  >
                    {intl.formatMessage(
                      {id: 'ONBOARDING_WIZARD.STEP4_MANUAL.STUDENTS.ASSIGN_FROM_FILE'},
                      {count: carried.records}
                    )}
                  </button>
                )
              })()}
              onDismiss={() => setStudentsConversion(null)}
              checkAction={visibleClassQuestions.length > 0 ? studentsClassQuestionsButton : null}
            />
          </div>
        )}

        {/* The branch each sheet was put in, with a way to change it: the server never asks an
            answered sheet again, and a changed answer replaces the branch the old one set. */}
        {/* Tied to the report: once the file's students are deleted, both go. */}
        {studentsBranchAnswered &&
          visibleStudentsConversion &&
          !isImportingStudentsFile &&
          (() => {
            const answers = studentsBranchAnswered.answers
            const lines = studentsBranchAnswered.questions
              .filter((question) => question.key in answers)
              .map((question) => {
                const answer = answers[question.key]
                const branch =
                  answer === MIXED_BRANCH
                    ? intl.formatMessage({id: 'DATA_UPLOAD.BRANCHES.MIXED'})
                    : (question.branches.find((entry) => entry.branchId === answer)?.branch ??
                      String(answer))
                return `${question.sheet || question.fileName || question.key} → ${branch}`
              })
            if (lines.length === 0) return null
            return (
              <div
                className='d-flex flex-wrap align-items-center justify-content-between gap-3 mb-3'
                role='status'
                style={{
                  width: '95%',
                  maxWidth: '95%',
                  border: `1px solid ${tableBorderColor}`,
                  borderRadius: 10,
                  padding: '0.65rem 1rem',
                  color: textColor,
                  fontSize: '0.9rem',
                }}
              >
                <span className='d-inline-flex align-items-center gap-2' style={{minWidth: 0}}>
                  <Info size={16} aria-hidden style={{color: headerBlue, flexShrink: 0}} />
                  <span className='text-break'>
                    {intl.formatMessage(
                      {id: 'DATA_UPLOAD.BRANCHES.ANSWERED'},
                      {sheets: lines.join(' · ')}
                    )}
                  </span>
                </span>
                <button
                  type='button'
                  className='btn btn-sm btn-light-primary'
                  disabled={studentsAnswering || studentsReadingFile}
                  onClick={() =>
                    setStudentsQuestions({
                      file: studentsBranchAnswered.file,
                      columns: null,
                      branches: studentsBranchAnswered.questions,
                      subjects: null,
                      initialBranches: answers,
                    })
                  }
                >
                  {intl.formatMessage({id: 'DATA_UPLOAD.BRANCHES.CHANGE'})}
                </button>
              </div>
            )
          })()}

        {/* Questions closed with the X once the file's rows were in: nothing was read again;
            "Answer questions" opens the same dialog. */}
        {studentsParkedQuestions && !isImportingStudentsFile && (
          <div
            className='d-flex flex-wrap align-items-center justify-content-between gap-3 mb-3'
            role='status'
            style={{
              width: '95%',
              maxWidth: '95%',
              border: `1px solid ${tableBorderColor}`,
              borderRadius: 10,
              padding: '0.65rem 1rem',
              color: textColor,
              fontSize: '0.9rem',
            }}
          >
            <span className='d-inline-flex align-items-center gap-2' style={{minWidth: 0}}>
              <Info size={16} aria-hidden style={{color: headerBlue, flexShrink: 0}} />
              <span className='text-break'>
                {`${studentsParkedQuestions.file.name} · ${
                  pendingQuestionsText(intl.formatMessage, {
                    columns: studentsParkedQuestions.columns,
                    branches: studentsParkedQuestions.branches,
                    answeredBranches: studentsParkedQuestions.initialBranches,
                    subjects: studentsParkedQuestions.subjects,
                  }) ?? intl.formatMessage({id: 'DATA_UPLOAD.QUESTIONS.WAITING'})
                }`}
              </span>
            </span>
            <button
              type='button'
              className='btn btn-sm btn-light-primary'
              disabled={
                studentsAnswering ||
                studentsReadingFile ||
                Boolean(studentsSheetQuestion) ||
                Boolean(studentsQuestions)
              }
              onClick={() => {
                setStudentsQuestions(studentsParkedQuestions)
                setStudentsParkedQuestions(null)
              }}
            >
              {intl.formatMessage({id: 'DATA_UPLOAD.QUESTIONS.ANSWER'})}
            </button>
          </div>
        )}

        {/* Nothing was read yet: the workbook holds several lists of people and says of none of
            them whose it is. Skip sends every sheet as "don't read it", once; the X leaves the
            file unread with its question kept ("Answer" on its line) rather than guessing. */}
        <SheetAssignmentModal
          show={Boolean(studentsSheetQuestion)}
          question={studentsSheetQuestion?.question ?? null}
          files={studentsSheetQuestion ? [studentsSheetQuestion.file] : []}
          submitting={studentsAnswering}
          onCancel={() => {
            // Not shown (`onSkip` and `onClose` are given); kept as the X's fallback.
            const pending = studentsSheetQuestion
            setStudentsSheetQuestion(null)
            if (pending) {
              markStudentsUpload(pending.file, {
                status: 'error',
                message: intl.formatMessage({
                  id: 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.FILE_NOT_READ',
                }),
              })
            }
            pending?.done()
          }}
          onClose={() => {
            // Closed, not answered: nothing is sent. The file waits on its line with "Answer",
            // and the next one in the queue starts.
            const pending = studentsSheetQuestion
            setStudentsSheetQuestion(null)
            if (!pending) return
            markStudentsUpload(pending.file, {
              status: 'question',
              parked: {kind: 'sheet', question: pending.question},
            })
            pending.done()
          }}
          onSkip={answerStudentsSheet}
          onConfirm={answerStudentsSheet}
        />

        {/* Teachers the file also lists that the school has not saved: Save adds them with the
            wizard's normal teacher save; Don't save closes, nothing is added. After the other
            dialogs, never on top of them. */}
        <NewTeachersModal
          // After the step's own rows: not while the file is still being read, asked about, or
          // waiting for "Add N students to the table".
          show={
            Boolean(studentsNewTeachers) &&
            !studentsQuestions &&
            !studentsSheetQuestion &&
            !studentsUploads.some(
              (line) =>
                line.file === studentsNewTeachers?.file &&
                ['waiting', 'reading', 'question', 'found'].includes(line.status)
            )
          }
          teachers={studentsNewTeachers?.teachers ?? NO_NEW_TEACHERS}
          canContinueWithout
          variant='list'
          // The Students upload says nothing of their subjects or classes: all of it is picked
          // on Teacher Allocation, where the same file is read as allocations.
          assignScope='everything'
          submitting={savingStudentsNewTeachers}
          onCancel={() => setStudentsNewTeachers(null)}
          onSaveOnly={(keys) => {
            const pending = studentsNewTeachers
            if (!pending) return
            setSavingStudentsNewTeachers(true)
            saveNewTeachersToWizard(
              pending.teachers.filter((teacher) => keys.includes(teacher.key))
            )
              .then(() => {
                setSavingStudentsNewTeachers(false)
                setStudentsNewTeachers(null)
                setStudentsSavedTeacherKeys((prev) => new Set([...prev, ...keys]))
              })
              .catch(() => setSavingStudentsNewTeachers(false))
          }}
          onSave={(keys) => {
            const pending = studentsNewTeachers
            if (!pending) return
            setSavingStudentsNewTeachers(true)
            saveNewTeachersToWizard(
              pending.teachers.filter((teacher) => keys.includes(teacher.key))
            )
              .then(() => {
                setSavingStudentsNewTeachers(false)
                setStudentsNewTeachers(null)
                setStudentsSavedTeacherKeys((prev) => new Set([...prev, ...keys]))
                // Saved. Its allocations wait for Teacher Allocation (with "which classes?" for
                // subjects with none), which opens the file when the user gets there.
                setQueuedAllocationFile({
                  file: pending.file,
                  draftId: studentsCarriedRef.current.get(pending.file)?.allocation?.draftId,
                })
                toast.info(
                  intl.formatMessage({
                    id: 'ONBOARDING_WIZARD.STEP4_MANUAL.STUDENTS.QUEUED_FOR_ALLOCATION',
                  })
                )
              })
              .catch(() => setSavingStudentsNewTeachers(false))
          }}
        />

        {/* Asked once the rows are in the grid, in one dialog: "which field is this column?",
            "which branch are these students in?" and "Maths -> Mathematics". Confirm saves the
            ticked subjects for the school, then reads the same file again ONCE with every
            answer (a mapping never reaches rows already read); Skip ignores the asked columns
            (read again once with them) and keeps the rest as it is, never asking it again; the
            X keeps them all for later ("Answer questions") and reads nothing. */}
        <ColumnAssignmentModal
          show={Boolean(studentsQuestions)}
          columns={studentsQuestions?.columns ?? null}
          branches={studentsQuestions?.branches ?? NO_BRANCH_QUESTIONS}
          initialBranches={studentsQuestions?.initialBranches}
          subjects={studentsQuestions?.subjects ?? null}
          peopleOnly={studentsQuestions?.askPeopleOnly ? 'students' : undefined}
          files={studentsQuestions ? [studentsQuestions.file] : []}
          submitting={studentsAnswering}
          onClose={() => {
            if (studentsQuestions) parkStudentsQuestions(studentsQuestions)
          }}
          onSkip={(answers: DataUploadSkipAnswers) => {
            const pending = studentsQuestions
            setStudentsQuestions(null)
            if (!pending) return
            // A branch and a subject proposal have no skip value the server keeps: remembered
            // here, so a later read of the file does not ask them again.
            const skippedBefore = studentsSkippedRef.current.get(pending.file)
            studentsSkippedRef.current.set(pending.file, {
              branches: new Set([...(skippedBefore?.branches ?? []), ...answers.branches]),
              subjects: new Set([
                ...(skippedBefore?.subjects ?? []),
                ...answers.subjects.map((written) => written.toLowerCase()),
              ]),
            })
            // Ignored columns are an answer the server reads: sent once, with the file's other
            // answers, and with every later read of it.
            if (Object.keys(answers.columns).length > 0) {
              setStudentsAnswering(true)
              convertStudentsFile(pending.file, () => {}, {
                // As on Confirm: a branch question's read is not in the grid yet, so this one
                // ends on its summary; rows already in the grid are replaced straight away.
                applyNow: !pending.pendingConversion,
                columnAssignments: {
                  ...(answeredColumnsRef.current.get(pending.file) ?? {}),
                  ...answers.columns,
                },
              })
              return
            }
            // Skipped the branch question: the rows go in as read, "Choose classes" for the
            // ones whose class exists in more than one branch.
            const skipped = pending?.pendingConversion
            if (
              pending &&
              skipped &&
              skipped.report.written > 0 &&
              markStudentsUpload(pending.file, {
                status: 'found',
                found: skipped.report.written,
                teachersFound: skipped.counts?.newPeople?.teachers ?? skipped.newTeachers.length,
                allocationsFound: skipped.carried?.allocation?.records,
                needsCheck: skipped.report.needsReview.length,
                ...studentsSkipCounts(skipped),
                staged: {kind: 'conversion', conversion: skipped, columns: null, subjects: null},
              })
            ) {
              return
            }
            if (pending?.pendingConversion) {
              applyStudentsConversion(pending.file, pending.pendingConversion).catch(() =>
                toast.error(
                  dataUploadErrorMessage(intl.formatMessage, 'PARSE_FAILED', {
                    config: studentsUploadConfig,
                    useCaseKey: 'students',
                    fileName: pending.file.name,
                  })
                )
              )
            }
          }}
          onConfirm={(answers) => {
            const pending = studentsQuestions
            if (!pending) return
            const classIds = pending.classQuestionIds ?? []
            if (pending.askPeopleOnly) {
              studentsPeopleOnlyRef.current.set(pending.file, answers.peopleOnly)
              if (answers.peopleOnly) {
                // Their rows keep a blank class, filled in the grid ("N students need a class").
                const dropped = new Set(classIds)
                setStudentsClassQuestions((prev) =>
                  prev.filter((question) => !dropped.has(question.id))
                )
              }
            }
            /** "Also choose classes": the class questions open next, over the grid. */
            const openClassQuestions = () => {
              if (!pending.askPeopleOnly || answers.peopleOnly || classIds.length === 0) return
              setStudentsClassQuestionsSeen(true)
              setStudentsClassQuestionsOpen(true)
            }
            // Only the choice was made: nothing to read again.
            if (
              Object.keys(answers.columns).length === 0 &&
              Object.keys(answers.branches).length === 0 &&
              answers.subjects.length === 0
            ) {
              setStudentsQuestions(null)
              openClassQuestions()
              return
            }
            openClassQuestions()
            setStudentsAnswering(true)
            if (answers.subjects.length > 0) {
              const confirmed = new Map(answeredSubjectsRef.current.get(pending.file) ?? [])
              answers.subjects.forEach((mapping) =>
                confirmed.set(mapping.written.toLowerCase(), mapping)
              )
              answeredSubjectsRef.current.set(pending.file, confirmed)
            }
            if (Object.keys(answers.branches).length > 0) {
              // Kept so an answer can be changed later; a re-answered sheet replaces its entry.
              const same = studentsBranchAnswered?.file === pending.file
              const kept = same ? studentsBranchAnswered.questions : []
              const asked = new Set(pending.branches.map((question) => question.key))
              setStudentsBranchAnswered({
                file: pending.file,
                questions: [
                  ...kept.filter((question) => !asked.has(question.key)),
                  ...pending.branches,
                ],
                answers: {...(same ? studentsBranchAnswered.answers : {}), ...answers.branches},
                ...(same && studentsBranchAnswered.rowKeys
                  ? {rowKeys: studentsBranchAnswered.rowKeys}
                  : {}),
              })
            }
            const reread = () => {
              setStudentsQuestions(null)
              convertStudentsFile(pending.file, () => {}, {
                // A branch question comes before the rows go in: that re-read still ends on its
                // summary. Column and subject questions come after: that one goes straight in.
                applyNow: !pending.pendingConversion,
                columnAssignments: {
                  ...(answeredColumnsRef.current.get(pending.file) ?? {}),
                  ...answers.columns,
                },
                branchAssignments: {
                  ...(answeredBranchesRef.current.get(pending.file) ?? {}),
                  ...answers.branches,
                },
              })
            }
            if (answers.subjects.length === 0) {
              reread()
              return
            }
            dispatchApi(dispatch, (ok, fail) =>
              putDataUploadSubjectAliases(subjectAliasesBody(answers.subjects), ok, fail)
            )
              .then((res: any) => {
                const rejected = rejectedSubjectAliasCount(res)
                if (rejected > 0) {
                  toast.warning(
                    intl.formatMessage({id: 'DATA_UPLOAD.SUBJECTS.REJECTED'}, {count: rejected})
                  )
                }
                reread()
              })
              .catch((error) => {
                setStudentsAnswering(false)
                toast.error(
                  dataUploadErrorMessage(intl.formatMessage, error, {
                    config: studentsUploadConfig,
                    useCaseKey: 'students',
                  })
                )
              })
          }}
        />

        {/* The rows it names live in the grid: an empty grid has nothing left to fix. */}
        {studentsIssues &&
          items.length > 0 &&
          !studentsFileRowsGone &&
          !isImportingStudentsFile && (
            <div
              className='mb-3'
              role='status'
              style={{
                width: '95%',
                maxWidth: '95%',
                background: isDark ? 'rgba(255, 168, 0, 0.12)' : '#FFF8E6',
                border: `1px solid ${isDark ? '#5c4409' : '#FFE2A8'}`,
                borderRadius: 10,
                padding: '0.85rem 1rem',
                color: textColor,
                fontSize: '0.92rem',
              }}
            >
              <div className='d-flex align-items-start justify-content-between gap-3 mb-2'>
                <span className='d-inline-flex align-items-center gap-2 fw-semibold'>
                  <AlertTriangle size={16} aria-hidden style={{color: '#FFA800', flexShrink: 0}} />
                  {intl.formatMessage({
                    id: 'ONBOARDING_WIZARD.STEP4_MANUAL.STUDENTS.NEEDS_ATTENTION',
                  })}
                </span>
                <button
                  type='button'
                  className='btn btn-sm btn-light'
                  onClick={() => setStudentsIssues(null)}
                  // btn-light is a dim grey in dark mode, barely visible on the amber banner.
                  style={
                    isDark
                      ? {
                          background: 'rgba(255, 168, 0, 0.16)',
                          border: '1px solid #FFA800',
                          color: '#FFC14D',
                          fontWeight: 600,
                        }
                      : undefined
                  }
                >
                  {intl.formatMessage({id: 'DATA_UPLOAD.CONVERT.DISMISS'})}
                </button>
              </div>
              <ul className='mb-0 ps-4'>
                {studentsIssues.slice(0, STUDENT_ISSUE_LINES).map((line) => (
                  <li key={line}>{line}</li>
                ))}
                {studentsIssues.length > STUDENT_ISSUE_LINES && (
                  <li>
                    {intl.formatMessage(
                      {id: 'ONBOARDING_WIZARD.STEP4_MANUAL.STUDENTS.MORE_ROWS'},
                      {count: studentsIssues.length - STUDENT_ISSUE_LINES}
                    )}
                  </li>
                )}
              </ul>
            </div>
          )}

        {/* Rows with a name but no class this school has (a class left blank by "Save students
            only", or one the file wrote that isn't here): Next stays off until each is filled. */}
        {!isImportingStudentsFile &&
          (() => {
            const needClass = items.filter(
              (row: any) =>
                String(
                  row?.full_name || `${row?.first_name || ''} ${row?.last_name || ''}`
                ).trim() &&
                (!String(row?.class ?? '').trim() || !isKnownStudentClass(row.class))
            ).length
            if (needClass === 0) return null
            return (
              <div
                className='d-flex align-items-center gap-2 mb-3 fw-semibold'
                role='status'
                style={{
                  width: '95%',
                  maxWidth: '95%',
                  background: isDark ? 'rgba(255, 168, 0, 0.12)' : '#FFF8E6',
                  border: `1px solid ${isDark ? '#5c4409' : '#FFE2A8'}`,
                  borderRadius: 10,
                  padding: '0.65rem 1rem',
                  color: textColor,
                  fontSize: '0.92rem',
                }}
              >
                <AlertTriangle size={16} aria-hidden style={{color: '#FFA800', flexShrink: 0}} />
                {intl.formatMessage(
                  {id: 'ONBOARDING_WIZARD.STEP4_MANUAL.STUDENTS.NEED_CLASS'},
                  {count: needClass}
                )}
              </div>
            )
          })()}

        {/* The report was put away: the class questions still have a way in. */}
        {!visibleStudentsConversion &&
          !isImportingStudentsFile &&
          visibleClassQuestions.length > 0 && (
            <div className='mb-3' style={{width: '95%', maxWidth: '95%'}}>
              {studentsClassQuestionsButton}
            </div>
          )}

        {/* One question per class name the file wrote that matches several classes (two boards
            or two branches both have "Grade 1-A"): the pick fills every row that wrote it, by
            class id. */}
        <StudentClassQuestionsModal
          show={studentsClassQuestionsOpen && visibleClassQuestions.length > 0}
          questions={visibleClassQuestions}
          optionLabel={(option) =>
            withClassBranch(
              option.schoolDisplayName || curriculumNameLabel(option.name, option.curriculum),
              option.branch
            )
          }
          onAnswer={answerStudentsClassQuestion}
          onClose={() => setStudentsClassQuestionsOpen(false)}
        />

        <div
          className='step4-teachers-table-wrapper mb-4'
          style={{
            position: 'relative',
            border: `1px solid ${tableBorderColor}`,
            borderRadius: 10,
            overflowX: 'auto',
            overflowY: 'hidden',
            width: '95%',
            maxWidth: '95%',
          }}
        >
          {/* Several students deleting at once: one spinner over the table, not one per row. */}
          {studentsBulkDeleting > 1 && (
            <TableBusyOverlay
              isDark={isDark}
              label={intl.formatMessage(
                {id: 'ONBOARDING_WIZARD.STEP4_MANUAL.STUDENTS.DELETING'},
                {count: studentsBulkDeleting}
              )}
            />
          )}
          {/* Only while the table's rows are really being replaced: the Teachers file's
              prefill, or a re-read after a question on rows already here. A fresh upload shows
              its progress on its own line above and leaves the table alone. */}
          {studentsTableRefreshing || studentsPrefillReading ? (
            <div className='p-4'>
              {/* The Teachers file is being read for this step: skeleton until its rows land. */}
              {studentsPrefillReading && !studentsTableRefreshing && (
                <div
                  className='text-center mb-4'
                  role='status'
                  aria-live='polite'
                  style={{color: mutedColor, fontSize: '0.9rem'}}
                >
                  {intl.formatMessage({
                    id: 'ONBOARDING_WIZARD.STEP4_MANUAL.STUDENTS.PREFILL_READING',
                  })}
                  {renderPrefillProgress(studentsPrefillPercent)}
                </div>
              )}
              {studentsTableRefreshing && (
                <div className='text-center mb-4' role='status' aria-live='polite'>
                  <div className='d-inline-flex align-items-center gap-2'>
                    <span className='spinner-border spinner-border-sm text-primary' aria-hidden />
                    <strong style={{color: textColor}}>
                      {studentsUploadProgress !== null
                        ? intl.formatMessage(
                            {id: 'DATA_UPLOAD.CONVERT.UPLOADING'},
                            {percent: Math.round(studentsUploadProgress * 100)}
                          )
                        : intl.formatMessage({id: 'DATA_UPLOAD.CONVERT.READING_FILE'})}
                    </strong>
                  </div>
                  {studentsUploadProgress !== null ? (
                    <div
                      className='progress mx-auto mt-2'
                      style={{height: 6, maxWidth: 320}}
                      role='progressbar'
                      aria-valuemin={0}
                      aria-valuemax={100}
                      aria-valuenow={Math.round(studentsUploadProgress * 100)}
                    >
                      <div
                        className='progress-bar'
                        style={{width: `${Math.round(studentsUploadProgress * 100)}%`}}
                      />
                    </div>
                  ) : (
                    <div className='mt-1' style={{color: mutedColor, fontSize: '0.88rem'}}>
                      {intl.formatMessage({id: 'DATA_UPLOAD.CONVERT.READING_FILE_SLOW'})}
                    </div>
                  )}
                </div>
              )}
              <CustomSkeleton variant={CARD_PRESET.TABLE} />
            </div>
          ) : (
            <>
              {items.length > 0 && (
                <div
                  className='d-flex align-items-center gap-3 px-4 py-3'
                  style={{borderBottom: `1px solid ${tableBorderColor}`, backgroundColor: bodyBg}}
                >
                  <button
                    type='button'
                    className='btn btn-sm btn-light-danger d-inline-flex align-items-center gap-2'
                    disabled={selectedStudentRows.size === 0}
                    onClick={() =>
                      requestDeleteStudentRows(
                        Array.from(selectedStudentRows).sort((a, b) => a - b)
                      )
                    }
                  >
                    <Trash2 size={14} aria-hidden />
                    {intl.formatMessage(
                      {id: 'ONBOARDING_WIZARD.STEP4_MANUAL.STUDENTS.DELETE_SELECTED'},
                      {count: selectedStudentRows.size}
                    )}
                  </button>
                </div>
              )}
              <GenericTable
                isDark={isDark}
                data={studentsPageItems}
                indexOffset={studentsPageStart}
                onChange={() => {}}
                hashLabel='#'
                selectPlaceholder=''
                containerClassName=''
                tableClassName='table align-middle mb-0 step4-teachers-table'
                tbodyStyle={{backgroundColor: bodyBg}}
                hashThStyle={{
                  width: 60,
                  background: headerBlue,
                  color: '#fff',
                  fontWeight: 600,
                  border: 'none',
                  padding: '0.75rem 1rem',
                  textAlign: 'center',
                }}
                hashTdStyle={{
                  color: mutedColor,
                  fontWeight: 600,
                  borderColor: tableBorderColor,
                  padding: '0.75rem 1rem',
                  textAlign: 'center',
                }}
                emptyState={
                  <div className='d-flex flex-column align-items-center gap-2'>
                    <span style={{color: mutedColor, fontSize: '0.95rem'}}>
                      {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP4_MANUAL.STUDENTS.EMPTY'})}
                    </span>
                    <button
                      type='button'
                      className='step4-add-row-btn d-inline-flex align-items-center gap-2'
                      onClick={() => handleAddRow('students')}
                    >
                      <span
                        className='step4-add-row-icon'
                        style={{
                          width: 24,
                          height: 24,
                          borderRadius: '50%',
                          background: headerBlue,
                          color: '#fff',
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '1rem',
                          fontWeight: 700,
                          lineHeight: 1,
                        }}
                      >
                        +
                      </span>
                      {intl.formatMessage({
                        id: 'ONBOARDING_WIZARD.STEP4_MANUAL.ADD_ROW',
                      })}
                    </button>
                  </div>
                }
                emptyTdClassName='step4-teachers-empty'
                emptyTdStyle={{
                  backgroundColor: bodyBg,
                  border: 'none',
                  padding: '1.5rem 1rem 1rem',
                  verticalAlign: 'middle',
                }}
                columns={[
                  {
                    key: '_select',
                    // Nothing to select in an empty table: no select-all box either.
                    label: items.length > 0 && (
                      <input
                        type='checkbox'
                        style={STUDENT_SELECT_CHECKBOX_STYLE}
                        checked={allStudentsSelected}
                        ref={(el) => {
                          if (el) el.indeterminate = !allStudentsSelected && someStudentsSelected
                        }}
                        onChange={toggleAllStudents}
                        aria-label={intl.formatMessage({
                          id: 'ONBOARDING_WIZARD.STEP4_MANUAL.STUDENTS.SELECT_ALL',
                        })}
                      />
                    ),
                    type: 'text',
                    thStyle: {
                      width: 48,
                      background: headerBlue,
                      border: 'none',
                      padding: '0.75rem 0.5rem 0.75rem 1rem',
                      textAlign: 'center',
                    },
                    tdStyle: {
                      width: 48,
                      borderColor: tableBorderColor,
                      padding: '0.75rem 0.5rem 0.75rem 1rem',
                      textAlign: 'center',
                    },
                    render: ({rowIndex}) => (
                      <input
                        type='checkbox'
                        style={STUDENT_SELECT_CHECKBOX_STYLE}
                        checked={selectedStudentRows.has(rowIndex)}
                        onChange={() => toggleStudentRow(rowIndex)}
                        aria-label={intl.formatMessage(
                          {id: 'ONBOARDING_WIZARD.STEP4_MANUAL.STUDENTS.SELECT_ROW'},
                          {index: rowIndex + 1}
                        )}
                      />
                    ),
                  },
                  {
                    key: 'full_name',
                    label: intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP4_MANUAL.COL.FULL_NAME'}),
                    type: 'text',
                    thStyle: {
                      background: headerBlue,
                      color: '#fff',
                      fontWeight: 600,
                      border: 'none',
                      padding: '0.75rem 1rem',
                      textAlign: 'center',
                      minWidth: 170,
                    },
                    tdStyle: {
                      borderColor: tableBorderColor,
                      padding: '0.75rem 1rem',
                      textAlign: 'center',
                      minWidth: 170,
                    },
                    render: ({row, rowIndex}) => (
                      <BufferedTextInput
                        id={`step4-row-first-input-students-${rowIndex}`}
                        type='text'
                        className='form-control form-control-solid'
                        placeholder=''
                        value={('full_name' in row ? row.full_name : getFullName(row)) || ''}
                        onCommit={(value) =>
                          handleDataChange('students', rowIndex, 'full_name', value)
                        }
                        onEnter={(e, draft) => {
                          if (!isStudentRowValid({...row, full_name: draft})) return
                          e.preventDefault()
                          addStudentRowAfterCommit()
                        }}
                        style={{
                          padding: '0.55rem 0.75rem',
                          fontSize: '0.95rem',
                          borderRadius: '0.425rem',
                        }}
                      />
                    ),
                  },
                  {
                    key: 'class',
                    label: intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP4_MANUAL.COL.CLASS'}),
                    type: 'text',
                    thStyle: {
                      background: headerBlue,
                      color: '#fff',
                      fontWeight: 600,
                      border: 'none',
                      padding: '0.75rem 1rem',
                      textAlign: 'center',
                      minWidth: 170,
                    },
                    tdStyle: {
                      borderColor: tableBorderColor,
                      padding: '0.75rem 1rem',
                      textAlign: 'center',
                      minWidth: 170,
                    },
                    render: ({row, rowIndex}) => {
                      // A file-imported class that matches none of this school's classes
                      // (tolerant match, same as the subjects dropdown) — its subjects can't
                      // show or sync until the user picks a real class here.
                      const rowClass = String(row.class ?? '').trim()
                      const rowClassKey = normalizeClassMatchKey(rowClass)
                      const classNotFound = rowClass.length > 0 && !classNameKeys.has(rowClassKey)
                      const classNotFoundLabel = intl.formatMessage({
                        id: 'ONBOARDING_WIZARD.STEP4_MANUAL.STUDENTS.CLASS_NOT_FOUND',
                      })
                      // The class went with a removed curriculum and the new one has no class of
                      // that name: highlighted until one is picked (the tooltip names the old one).
                      const classLost = row.classMissing === true && !rowClass
                      const previousClassName = String(row.previousClass?.name ?? '').trim()
                      const classLostLabel = intl.formatMessage({
                        id: 'ONBOARDING_WIZARD.STEP4_MANUAL.STUDENTS.CLASS_LOST',
                      })
                      // The file left the class blank: highlighted until one is picked, with the
                      // server's candidates (two curricula share the name) listed first.
                      const classMissing = !classLost && !rowClass && isMissingFromFile(row, 'class')
                      const choiceKeys = new Set(
                        ((row._classChoices as string[] | undefined) ?? []).map((choice) =>
                          normalizeClassMatchKey(choice)
                        )
                      )
                      /*
                       * A candidate is held in the wizard's own spelling (see
                       * wizardClassChoiceName), which a school that keeps the curriculum prefix in
                       * its class names spells neither like the option's stored name nor like its
                       * label — the label carries the short code as a suffix ("Grade 1-A (FBISE)").
                       * So the stored name is tried three ways: as stored, with a curriculum prefix
                       * dropped, and as the label.
                       */
                      const isChoice = (opt: {value: string; label: string; name: string}) => {
                        const stored = opt.name
                        return (
                          choiceKeys.has(normalizeClassMatchKey(stored)) ||
                          choiceKeys.has(normalizeClassMatchKey(wizardClassChoiceName(stored))) ||
                          choiceKeys.has(normalizeClassMatchKey(opt.label))
                        )
                      }
                      // Most rows have no candidates: they share one options list.
                      const rowClassSelectOptions =
                        choiceKeys.size > 0
                          ? [
                              studentClassPlaceholderOption,
                              ...studentClassOptions.filter(isChoice),
                              ...studentClassOptions.filter((opt) => !isChoice(opt)),
                            ]
                          : studentClassSelectOptions
                      const needsClassLabel = intl.formatMessage({
                        id: 'ONBOARDING_WIZARD.STEP4_MANUAL.STUDENTS.CLASS_MISSING',
                      })
                      // Filled in by the server but worth a look (a curriculum read from the rest
                      // of the file): kept, highlighted, with the server's reason as the tooltip.
                      const review = row._review as StudentReviewNote | undefined
                      const classToCheck =
                        rowClass.length > 0 &&
                        !classNotFound &&
                        Boolean(review?.fields.includes('class'))
                      const classCheckLabel = review?.reasons.class
                      return (
                        <div
                          className='w-100'
                          title={
                            classLost
                              ? previousClassName
                                ? intl.formatMessage(
                                    {id: 'ONBOARDING_WIZARD.STEP4_MANUAL.STUDENTS.CLASS_LOST_WAS'},
                                    {name: previousClassName}
                                  )
                                : classLostLabel
                              : classNotFound
                                ? classNotFoundLabel
                                : classMissing
                                  ? needsClassLabel
                                  : classToCheck
                                    ? classCheckLabel
                                    : undefined
                          }
                          style={
                            classLost || classNotFound || classMissing
                              ? {
                                  border: '1px solid #F64E60',
                                  borderRadius: '0.425rem',
                                  background: '#FFC6CC',
                                }
                              : classToCheck
                                ? {
                                    border: '1px solid #FFA800',
                                    borderRadius: '0.425rem',
                                    background: isDark ? 'rgba(255, 168, 0, 0.12)' : '#FFF8E6',
                                  }
                                : undefined
                          }
                        >
                          <FilterSelect
                            value={studentClassValue(row)}
                            options={rowClassSelectOptions}
                            onChange={(v) => handleDataChange('students', rowIndex, 'class', v)}
                            className='w-100'
                            triggerClassName='filter-select-solid w-100'
                            ariaLabel={intl.formatMessage({
                              id: 'ONBOARDING_WIZARD.STEP4_MANUAL.COL.CLASS',
                            })}
                          />
                          {(classLost || classNotFound || classMissing) && (
                            <div
                              className='text-danger fw-semibold'
                              style={{fontSize: '0.8rem', padding: '0.15rem 0.5rem 0.3rem'}}
                            >
                              {classLost
                                ? classLostLabel
                                : classNotFound
                                  ? classNotFoundLabel
                                  : needsClassLabel}
                            </div>
                          )}
                        </div>
                      )
                    },
                  },
                  {
                    key: 'registrationNumber',
                    label: intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP4_MANUAL.COL.REG_NO'}),
                    type: 'text',
                    thStyle: {
                      background: headerBlue,
                      color: '#fff',
                      fontWeight: 600,
                      border: 'none',
                      padding: '0.75rem 1rem',
                      textAlign: 'center',
                      minWidth: 170,
                    },
                    tdStyle: {
                      borderColor: tableBorderColor,
                      padding: '0.75rem 1rem',
                      textAlign: 'center',
                      minWidth: 170,
                    },
                    render: ({row, rowIndex}) => {
                      // The file left it blank: highlighted until filled, like a blank class.
                      const regMissing =
                        !String(row.registrationNumber ?? '').trim() &&
                        isMissingFromFile(row, 'registrationNumber')
                      const needsRegLabel = intl.formatMessage({
                        id: 'ONBOARDING_WIZARD.STEP4_MANUAL.STUDENTS.REG_MISSING',
                      })
                      return (
                        <>
                          <BufferedTextInput
                            type='text'
                            className='form-control form-control-solid'
                            placeholder=''
                            value={row.registrationNumber || ''}
                            aria-invalid={regMissing || undefined}
                            title={regMissing ? needsRegLabel : undefined}
                            onCommit={(value) =>
                              handleDataChange('students', rowIndex, 'registrationNumber', value)
                            }
                            onEnter={(e, draft) => {
                              if (!isStudentRowValid({...row, registrationNumber: draft})) return
                              e.preventDefault()
                              addStudentRowAfterCommit()
                            }}
                            style={{
                              padding: '0.55rem 0.75rem',
                              fontSize: '0.95rem',
                              borderRadius: '0.425rem',
                              ...(regMissing
                                ? {border: '1px solid #F64E60', background: '#FFC6CC'}
                                : {}),
                            }}
                          />
                          {regMissing && (
                            <div
                              className='text-danger fw-semibold'
                              style={{fontSize: '0.8rem', padding: '0.15rem 0.5rem 0'}}
                            >
                              {needsRegLabel}
                            </div>
                          )}
                        </>
                      )
                    },
                  },
                  {
                    key: 'subjects',
                    label: intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP4_MANUAL.COL.SUBJECTS'}),
                    type: 'text',
                    thStyle: {
                      background: headerBlue,
                      color: '#fff',
                      fontWeight: 600,
                      border: 'none',
                      padding: '0.75rem 1rem',
                      textAlign: 'center',
                      minWidth: 220,
                    },
                    tdStyle: {
                      borderColor: tableBorderColor,
                      padding: '0.75rem 1rem',
                      textAlign: 'center',
                      minWidth: 220,
                    },
                    render: ({row, rowIndex}) => {
                      const selectedClass = String(row.class ?? '').trim()
                      // Subjects open only once the row's class is one of THIS school's classes.
                      // A class that no longer exists (the user went back and changed the
                      // curriculum after assigning students, or a file named a class the school
                      // does not have) shows "Class not found" in the class cell; until a real
                      // class is picked there, the subject picker stays disabled instead of
                      // offering every subject of every class.
                      const classIsKnown =
                        selectedClass.length > 0 && isKnownStudentClass(selectedClass)
                      // The file carried no subject for this row (no Subject columns, or none
                      // this class teaches): highlighted until picked, like a blank class, so
                      // Next never sits disabled without saying which cell is holding it up.
                      const subjectsMissing =
                        ((row.subjects as any[]) ?? []).length === 0 &&
                        isMissingFromFile(row, 'subjects')
                      const needsSubjectsLabel = intl.formatMessage({
                        id: 'ONBOARDING_WIZARD.STEP4_MANUAL.STUDENTS.SUBJECTS_MISSING',
                      })
                      // Kept but worth a look ('"Computer Studies" is not a subject of Grade 7 -
                      // Blue'): highlighted like the class cell, the server's sentence verbatim.
                      const subjectsReview = row._review as StudentReviewNote | undefined
                      const subjectsToCheck =
                        !subjectsMissing && Boolean(subjectsReview?.fields.includes('subjects'))
                      const subjectsCheckLabel = subjectsReview?.reasons.subjects
                      const rowOptions = getStudentSubjectSelectOptions(selectedClass)
                      const selectedValueSet = new Set(
                        ((row.subjects || []) as any[])
                          .map((s: any) => {
                            let name = String(typeof s === 'object' ? s.name : s).trim()
                            let cls = String(
                              typeof s === 'object' ? s.class || selectedClass : selectedClass
                            ).trim()
                            // File-imported subjects arrive as encoded "name|||class" strings
                            if (name.includes(STUDENT_SUBJECT_DELIMITER)) {
                              const [namePart, classPart] = name.split(STUDENT_SUBJECT_DELIMITER)
                              name = String(namePart ?? '').trim()
                              cls = String(classPart ?? '').trim() || cls
                            }
                            // Class part matches tolerantly so a file-imported spelling
                            // ("Class 1A") still ticks the option of "Class 1-A".
                            return name && cls
                              ? `${name.toLowerCase()}${STUDENT_SUBJECT_DELIMITER}${normalizeClassMatchKey(cls)}`
                              : ''
                          })
                          .filter(Boolean)
                      )
                      const selectedOptionValues = rowOptions.filter((opt) =>
                        selectedValueSet.has(
                          `${String(opt.subjectName).toLowerCase()}${STUDENT_SUBJECT_DELIMITER}${normalizeClassMatchKey(opt.className)}`
                        )
                      )
                      const isAllSelected =
                        rowOptions.length > 0 && selectedOptionValues.length === rowOptions.length
                      const selectAllOption = {
                        value: '__select_all__',
                        label: isAllSelected ? 'Deselect All' : 'Select All',
                        subjectName: '',
                        className: '',
                      }
                      return (
                        <div
                          className='w-100'
                          title={
                            subjectsMissing
                              ? needsSubjectsLabel
                              : subjectsToCheck
                                ? subjectsCheckLabel
                                : undefined
                          }
                          style={
                            subjectsMissing
                              ? {
                                  border: '1px solid #F64E60',
                                  borderRadius: '0.425rem',
                                  background: '#FFC6CC',
                                }
                              : subjectsToCheck
                                ? {
                                    border: '1px solid #FFA800',
                                    borderRadius: '0.425rem',
                                    background: isDark ? 'rgba(255, 168, 0, 0.12)' : '#FFF8E6',
                                  }
                                : undefined
                          }
                        >
                          <Select
                            isMulti
                            isDisabled={!classIsKnown}
                            closeMenuOnSelect={false}
                            // react-select blurs the input after a pick on touch devices,
                            // which closes the menu after every single subject on a phone
                            blurInputOnSelect={false}
                            hideSelectedOptions={false}
                            components={{
                              ValueContainer: MultiValueContainer,
                              Option: CheckboxOption,
                            }}
                            menuPortalTarget={document.body}
                            menuPosition='fixed'
                            // Placement is decided in the menuPortal style below from the space
                            // left in the viewport (below the field, else above); react-select's
                            // own flip is switched off so the two never disagree.
                            menuPlacement='bottom'
                            menuShouldScrollIntoView={false}
                            // Upper bound; the portal shrinks it to the space on the chosen side
                            // so the list's own scrollbar can reach the last option.
                            maxMenuHeight={240}
                            options={[selectAllOption, ...rowOptions]}
                            value={selectedOptionValues}
                            onChange={(selectedOptions: any, actionMeta: any) => {
                              if (
                                actionMeta.action === 'select-option' &&
                                actionMeta.option?.value === '__select_all__'
                              ) {
                                if (isAllSelected) {
                                  handleDataChange('students', rowIndex, 'subjects', [])
                                } else {
                                  const allValues = rowOptions.map((opt: any) => opt.value)
                                  handleDataChange('students', rowIndex, 'subjects', allValues)
                                }
                              } else if (
                                actionMeta.action === 'deselect-option' &&
                                actionMeta.option?.value === '__select_all__'
                              ) {
                                handleDataChange('students', rowIndex, 'subjects', [])
                              } else {
                                const selectedValues = selectedOptions
                                  ? selectedOptions
                                      .filter((opt: any) => opt.value !== '__select_all__')
                                      .map((opt: any) => opt.value)
                                  : []
                                handleDataChange('students', rowIndex, 'subjects', selectedValues)
                              }
                            }}
                            className='react-select-styled'
                            classNamePrefix='react-select'
                            placeholder={intl.formatMessage({
                              id: classIsKnown
                                ? 'ONBOARDING_WIZARD.STEP4_MANUAL.SELECT_PLACEHOLDER'
                                : 'ONBOARDING_WIZARD.STEP4_MANUAL.SELECT_CLASS_FIRST',
                            })}
                            onKeyDown={(e: React.KeyboardEvent) =>
                              handleRowEnterKey('students', rowIndex, e, isStudentRowValid(row))
                            }
                            styles={{
                              control: (base: any, state: any) => ({
                                ...base,
                                borderColor: state.isFocused ? '#B5B5C3' : 'transparent',
                                backgroundColor: state.isDisabled
                                  ? isDark
                                    ? '#1E1E2D'
                                    : '#EFF2F5'
                                  : 'var(--bs-gray-100)',
                                color: isDark ? '#ffffff' : base.color,
                                minHeight: '43px',
                              }),
                              singleValue: (base: any) => ({
                                ...base,
                                color: isDark ? '#ffffff' : base.color,
                              }),
                              valueContainer: (base: any) => ({
                                ...base,
                                flexWrap: 'nowrap',
                              }),
                              multiValue: (base: any) => ({
                                ...base,
                                backgroundColor: isDark ? '#323248' : base.backgroundColor,
                              }),
                              multiValueLabel: (base: any) => ({
                                ...base,
                                color: isDark ? '#ffffff' : base.color,
                              }),
                              option: (base: any, state: any) => ({
                                ...base,
                                // Selected options keep the normal background — the checkbox
                                // tick is the only selection indicator.
                                backgroundColor: state.isFocused
                                  ? isDark
                                    ? '#323248'
                                    : '#F4F5F7'
                                  : isDark
                                    ? '#1E1E2D'
                                    : 'transparent',
                                color: state.isFocused
                                  ? isDark
                                    ? '#ffffff'
                                    : '#181C32'
                                  : isDark
                                    ? '#A7A8AA'
                                    : '#181C32',
                                ':active': {
                                  backgroundColor: isDark ? '#323248' : '#F4F5F7',
                                },
                                ...(state.data?.value === '__select_all__'
                                  ? {fontWeight: 600}
                                  : {}),
                              }),
                              menu: (base: any) => ({
                                ...base,
                                backgroundColor: isDark ? '#1E1E2D' : base.backgroundColor,
                                // Fill the portal wrapper, which is sized and placed against the
                                // subject field itself (see menuPortal).
                                width: '100%',
                                // Hang below the wrapper's top edge, or - when menuPortal decided
                                // there is more room above the field - grow upward from it.
                                top: 'var(--rs-menu-top, 100%)',
                                bottom: 'var(--rs-menu-bottom, auto)',
                              }),
                              menuList: (base: any) => ({
                                ...base,
                                // bounded by the space actually left below the control
                                // (set as a CSS variable on the portal wrapper)
                                maxHeight: 'var(--rs-menu-max-height, 240px)',
                                overflowY: 'auto',
                              }),
                              menuPortal: (base: any, state: any) => {
                                const vw = window.innerWidth
                                const margin = 8
                                // Directly under the subject field: as wide as the field, or
                                // 280px if the field is narrower, so labels stay readable. The
                                // field can sit partly off-screen in the horizontally scrolled
                                // table, so the box is nudged back inside the viewport.
                                const width = Math.min(
                                  Math.max(state.rect.width, 280),
                                  vw - margin * 2
                                )
                                const left = Math.max(
                                  margin,
                                  Math.min(state.rect.left, vw - width - margin)
                                )
                                // Open toward the side of the viewport with room: below the field
                                // when the full list fits there (or below has more room than
                                // above), otherwise above it. The list height is bounded by that
                                // side's space so its own scrollbar can always reach the last option.
                                const vh = window.innerHeight
                                const gap = 12
                                const spaceBelow = vh - state.rect.bottom - gap
                                const spaceAbove = state.rect.top - gap
                                const dropUp = spaceBelow < 240 && spaceAbove > spaceBelow
                                const maxHeight = Math.max(
                                  120,
                                  Math.min(240, dropUp ? spaceAbove : spaceBelow)
                                )
                                return {
                                  ...base,
                                  zIndex: 9999,
                                  left,
                                  width,
                                  top: dropUp ? state.rect.top : state.rect.bottom,
                                  ['--rs-menu-top' as any]: dropUp ? 'auto' : '100%',
                                  ['--rs-menu-bottom' as any]: dropUp ? '100%' : 'auto',
                                  ['--rs-menu-max-height' as any]: `${maxHeight}px`,
                                }
                              },
                            }}
                          />
                          {subjectsMissing && (
                            <div
                              className='text-danger fw-semibold'
                              style={{fontSize: '0.8rem', padding: '0.15rem 0.5rem 0.3rem'}}
                            >
                              {needsSubjectsLabel}
                            </div>
                          )}
                        </div>
                      )
                    },
                  },
                  {
                    key: 'actions',
                    label: intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP4_MANUAL.COL.ACTIONS'}),
                    type: 'text',
                    thStyle: {
                      width: 100,
                      background: headerBlue,
                      color: '#fff',
                      fontWeight: 600,
                      border: 'none',
                      padding: '0.75rem 1rem',
                      textAlign: 'center',
                    },
                    tdStyle: {
                      borderColor: tableBorderColor,
                      padding: '0.75rem 1rem',
                      textAlign: 'center',
                    },
                    render: ({row, rowIndex}) => (
                      <button
                        type='button'
                        className='btn btn-icon btn-sm btn-light-danger'
                        disabled={deletingRows.has(`s:${studentRowKey(row)}`)}
                        onClick={() => {
                          const action = {
                            type: 'category-row' as const,
                            category: 'students' as const,
                            index: rowIndex,
                          }
                          if (
                            !alwaysConfirmDelete(action) &&
                            localStorage.getItem(ROW_DELETE_SKIP_CONFIRM_KEY) === 'true'
                          ) {
                            performDeleteAction(action)
                          } else {
                            setPendingDeleteAction(action)
                          }
                        }}
                        title='Delete'
                      >
                        {studentsBulkDeleting <= 1 &&
                        deletingRows.has(`s:${studentRowKey(row)}`) ? (
                          <span
                            className='spinner-border spinner-border-sm'
                            role='status'
                            aria-hidden='true'
                          />
                        ) : (
                          <Trash2 size={16} />
                        )}
                      </button>
                    ),
                  },
                ]}
              />
              {showStudentsPager && (
                <div className='px-4 py-3' style={{borderTop: `1px solid ${tableBorderColor}`}}>
                  <Pagination
                    totalPages={studentsPageCount}
                    pageSize={studentsPageSize}
                    currentPage={currentStudentsPage}
                    totalRecords={items.length}
                    onPageChange={(page, size) => {
                      if (size !== studentsPageSize) {
                        setStudentsPageSize(size)
                        setStudentsPage(1)
                      } else {
                        setStudentsPage(page)
                      }
                    }}
                  />
                </div>
              )}
            </>
          )}
          {items.length > 0 && (
            <div
              className='step4-add-row-below d-flex justify-content-center p-3'
              style={{
                borderTop: `1px solid ${tableBorderColor}`,
                backgroundColor: bodyBg,
              }}
            >
              <button
                type='button'
                className='step4-add-row-btn d-inline-flex align-items-center gap-2'
                onClick={() => handleAddRow('students')}
              >
                <span
                  className='step4-add-row-icon'
                  style={{
                    width: 24,
                    height: 24,
                    borderRadius: '50%',
                    background: headerBlue,
                    color: '#fff',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '1rem',
                    fontWeight: 700,
                    lineHeight: 1,
                  }}
                >
                  +
                </span>
                {intl.formatMessage({
                  id: 'ONBOARDING_WIZARD.STEP4_MANUAL.ADD_ROW',
                })}
              </button>
            </div>
          )}
        </div>

        <div className='d-flex justify-content-end gap-3 w-100 mt-4'>
          {showBackButton && (
            <button
              type='button'
              className='onboarding-btn-back me-auto'
              disabled={isSubmitting}
              onClick={handleBackTab}
            >
              <ArrowLeft size={16} className='me-2' />
              {intl.formatMessage({id: 'ONBOARDING_WIZARD.FOOTER.BACK'})}
            </button>
          )}
          {activeTab === tabs[tabs.length - 1].id ? (
            <button
              type='button'
              className='onboarding-btn-next'
              disabled={!isTabValid('students') || isSubmitting}
              onClick={() => handleFinishStep()}
            >
              {isSubmitting ? (
                <>
                  <span className='spinner-border spinner-border-sm align-middle me-2' />
                  {intl.formatMessage({id: 'COMMON.LOADING'})}
                </>
              ) : (
                <>
                  {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP4_MANUAL.BTN_NEXT'})}
                  <ArrowRight size={16} className='ms-2' />
                </>
              )}
            </button>
          ) : (
            <button
              type='button'
              className='onboarding-btn-next'
              disabled={!isTabValid('students') || isSubmitting}
              onClick={() => handleNextTab()}
            >
              {isSubmitting ? (
                <>
                  <span className='spinner-border spinner-border-sm align-middle me-2' />
                  {intl.formatMessage({id: 'COMMON.LOADING'})}
                </>
              ) : (
                <>
                  {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP4_MANUAL.BTN_NEXT'})}
                  <ArrowRight size={16} className='ms-2' />
                </>
              )}
            </button>
          )}
        </div>
      </div>
    )
  }

  /** Teacher Allocation V2 (Da1TimeTable handover): landing, grid and Versi views. */
  const renderTeacherAllocation = () => {
    const ta = (id: string, values?: Record<string, any>) =>
      intl.formatMessage({id: `ONBOARDING_WIZARD.TEACHER_ALLOCATION.${id}`}, values)
    const mutedColor = isDark ? '#A7A8AA' : '#7E8299'

    // While the wizard is (re)hydrating from the server — initial resume or the post-Excel-import
    // refresh — the data isn't in yet; show a skeleton instead of stale/empty counts.
    if (isHydrating) {
      return (
        <div className='ta-root ta-page'>
          <header className='ta-page__header'>
            <h1 className='ta-page__title'>{ta('LANDING.TITLE')}</h1>
          </header>
          <div className='p-4'>
            <CustomSkeleton variant={CARD_PRESET.TABLE} />
          </div>
        </div>
      )
    }

    const lessonValid = isTabValid('lessonPlanning')
    const handleContinue = () => {
      setLessonPlanAttempted(true)
      if (!isTabValid('lessonPlanning')) {
        const levelWithoutSubjects = getFirstLevelWithoutSubjects()
        toast.error(
          levelWithoutSubjects
            ? intl.formatMessage(
                {id: 'ONBOARDING_WIZARD.STEP4_MANUAL.LESSON_PLANNING.NO_SUBJECTS_VALIDATION_ERROR'},
                {level: displayName(levelWithoutSubjects, curriculumOfLevel(levelWithoutSubjects))}
              )
            : intl.formatMessage({
                id: 'ONBOARDING_WIZARD.STEP4_MANUAL.LESSON_PLANNING.VALIDATION_ERROR',
              })
        )
        return
      }
      if (isLastTab) handleFinishStep()
      else handleNextTab()
    }
    const backToMethods = () => {
      setGridPreviewMode(false)
      setCellSettings(null)
      setAllocationView('landing')
    }
    /**
     * Server flow Continue (handoff §9.1): every edit is already saved, so nothing is pushed
     * through bulk-import — that would overwrite the per-cell saves with this tab's copy.
     *
     * Skipping bulk-import also skips the lesson planning signal it sent (handoff §9.6), and
     * `onboarding/lesson_planning` feeds the Drop-off card and the E6–E10 emails. So the wizard
     * states it here, once per click, before any navigation event: lesson_planning, then the
     * substep's saved. Its `onboarding_substep_final` is left out on purpose: the tab effect
     * already sent it on arrival (resume included, see onActiveSubStepChange), so it is in the
     * stream ahead of this batch. The footer only enables this click when the server's
     * canContinue is true.
     *
     * The edit row comes last, beside its completion row. The wizard's diff-based one
     * (editedIn) never fires here: server edits never change the wizard's lessonPlanningData,
     * so it is stated from allocationServerEditedRef instead. Repeats come back `deduped`.
     */
    const continueFromServerAllocation = () => {
      if (!isSupportSession) {
        const metadata = {source: 'teacher_allocation'}
        const events: OnboardingBatchEvent[] = [
          {event_name: 'onboarding', substep: 'lesson_planning', metadata},
          {event_name: 'onboarding_substep_saved', substep: 'assign_lesson_planning', metadata},
        ]
        if (allocationServerEditedRef.current) {
          events.push({
            event_name: 'onboarding_substep_edited',
            substep: 'assign_lesson_planning',
            metadata,
          })
        }
        trackOnboardingEvents(dispatch, urddId, events)
      }
      saveProgress(activeTab)
      if (isLastTab) {
        onComplete()
        return
      }
      const idx = tabs.findIndex((tab) => tab.id === activeTab)
      const nextTabId = tabs[idx + 1]?.id
      if (nextTabId) setActiveTab(nextTabId)
    }
    const footer = (onLanding: boolean) => (
      <AllocationFooter
        backLabel={onLanding ? ta('BACK') : ta('BACK_TO_METHODS')}
        backVariant={onLanding ? 'solid' : 'outline'}
        onBack={onLanding ? handleBackTab : backToMethods}
        backDisabled={onLanding && (!showBackButton || isGenerating || isSubmitting)}
        canContinue={lessonValid}
        hint={onLanding ? ta('CONTINUE_HINT') : ta('CONTINUE_HINT_REMAINING')}
        busy={isGenerating || isSubmitting}
        onContinue={handleContinue}
      />
    )

    const prefilledCount = allocationRows.reduce(
      (sum, row) =>
        sum +
        row.subjects.filter((subject) => {
          const key = buildAllocationSlotKey(row.classKey, subject)
          return (
            importedSlots.has(key) &&
            !deletedSlots.has(key) &&
            Boolean((lessonPlanningData[row.classKey]?.[subject]?.teacher || '').trim())
          )
        }).length,
      0
    )

    const allocationPrefillShown =
      (allocationPrefillReading || allocationPrefillFetching) && !ownAllocationFileOnCard
    // In the Upload card, the Teachers file's allocations being prepared take the uploader's place.
    const inCard = (node: React.ReactNode) =>
      allocationUploadSlot ? createPortal(node, allocationUploadSlot) : null
    const allocationPrefillNote = (
      <div
        className='d-flex flex-column align-items-center justify-content-center gap-1 mb-3'
        role='status'
        aria-live='polite'
        style={{color: mutedColor, fontSize: '0.9rem'}}
      >
        <div className='d-inline-flex align-items-center gap-2'>
          <span className='spinner-border spinner-border-sm text-primary' aria-hidden />
          {allocationPrefillRowsFound > 0
            ? intl.formatMessage(
                {id: 'ONBOARDING_WIZARD.STEP4_MANUAL.LESSON_PLANNING.PREFILL_READING_COUNT'},
                {count: allocationPrefillRowsFound}
              )
            : intl.formatMessage({
                id: 'ONBOARDING_WIZARD.STEP4_MANUAL.LESSON_PLANNING.PREFILL_READING',
              })}
        </div>
        {renderPrefillProgress(allocationPrefillPercent)}
      </div>
    )
    // The existing upload and pre-fill flow (file status, questions, reports), under the cards.
    const importArea = (
      <div className='ta-import-area'>
        {allocationPrefillShown ? (
          ALLOCATION_UPLOADER_IN_CARD ? (
            inCard(
              <>
                {allocationPrefillNote}
                <div className='ta-import-area__template'>
                  <TemplateDownloadLink
                    className='ta-link'
                    onDownload={handleDownloadExcelTemplate}
                    busy={downloadingExcelTemplate}
                  />
                </div>
              </>
            )
          ) : (
            allocationPrefillNote
          )
        ) : (
          <AllocationFileImport
            file={excelImportFile}
            isDark={isDark}
            uploaderTarget={ALLOCATION_UPLOADER_IN_CARD ? allocationUploadSlot : undefined}
            // Redesigned page: Map columns on the page, not a dialog. Remove to roll back.
            inlineMapper
            // The shared onboarding uploader, the same as on Add Teachers (12 Oct review, point 3):
            // this page reads assignments first and matches them to the school's teachers.
            uploader={{
              onFiles: handleAllocationFiles,
              accept: allocationConversionAvailable
                ? `${dataUploadAcceptAttribute(allocationUploadConfig.formats)},.xls`
                : '.csv,text/csv,.xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
              formatsText: intl.formatMessage({
                id: allocationConversionAvailable
                  ? 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.DROPZONE_FORMATS_ANY'
                  : 'ONBOARDING_WIZARD.STEP4_MANUAL.UPLOAD.FORMATS_SPREADSHEET',
              }),
              limitsText: allocationConversionAvailable
                ? intl.formatMessage(
                    {id: 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.UPLOAD_LIMITS_ANY'},
                    {
                      files: allocationUploadConfig.maxFiles,
                      perFile: formatDataUploadBytes(allocationUploadConfig.maxFileBytes),
                      total: formatDataUploadBytes(
                        allocationUploadConfig.maxTotalBytes ??
                          allocationUploadConfig.maxFileBytes * allocationUploadConfig.maxFiles
                      ),
                    }
                  )
                : '',
              onDownloadTemplate: handleDownloadExcelTemplate,
              templateBusy: downloadingExcelTemplate,
              disabled: allocationPrefillReading || allocationPrefillFetching,
              rejected: allocationRejected,
              onDismissRejected: () => setAllocationRejected([]),
              // In the Upload card the uploader stands alone: no note under it.
              guidance: ALLOCATION_UPLOADER_IN_CARD ? undefined : (
                <p className='step4-te-guide-note'>
                  <Info size={16} aria-hidden />
                  <span>
                    {intl.formatMessage({
                      id: 'ONBOARDING_WIZARD.STEP4_MANUAL.UPLOAD.ALLOCATION_GUIDE',
                    })}
                  </span>
                </p>
              ),
            }}
            notice={
              excelImportFromPrefill
                ? intl.formatMessage({
                    id: 'ONBOARDING_WIZARD.STEP4_MANUAL.LESSON_PLANNING.PREFILL_FOUND',
                  })
                : undefined
            }
            onClose={() => {
              handedAllocationFileRef.current = null
              // The Teachers file's allocations, removed: not offered again.
              if (excelImportFromPrefill) markAllocationPrefillHandled()
              setExcelImportFile(null)
              setExcelImportFromPrefill(false)
              setAllocationBatch(null)
            }}
            onImported={handleExcelImported}
            lastImport={allocationLastImport}
            onClearLastImport={() => setAllocationLastImport(null)}
            onHeldByOther={setAllocationHeldCells}
            onSaveNewTeachers={saveNewTeachersToWizard}
            hasStudentsStep={data.mode === 'subject'}
            extraFiles={allocationCompanionFiles}
            onRemoveFile={removeAllocationFile}
            onNewStudents={(file, students, draftId) =>
              setAllocationNewStudents({file, students, draftId})
            }
            onCarriedTeachers={(file, carried) =>
              setQueuedTeachersFile({file, draftId: carried.draftId})
            }
            knownStudentNumbers={
              new Set(
                ((localData.students as any[]) || [])
                  .map((student: any) =>
                    String(student?.registrationNumber ?? '')
                      .trim()
                      .toLowerCase()
                  )
                  .filter(Boolean)
              )
            }
          />
        )}
        {!allocationPrefillReading &&
          !allocationPrefillFetching &&
          allocationPrefillSkipped &&
          !excelImportFile && (
            <div className='d-flex justify-content-center mb-3'>
              <button
                type='button'
                className='ta-btn ta-btn--outline ta-btn--sm'
                // The questions are the Teachers file's allocations': said on hover.
                title={useAllocationsFrom('teachers')}
                onClick={reopenAllocationPrefillQuestions}
              >
                {allocationPrefillAnswerLabel(allocationPrefillSkipped)}
              </button>
            </div>
          )}
        {/* A re-upload's class subjects another teacher has: asked, never replaced unasked. */}
        <HeldByOtherQuestions
          key={allocationHeldCells.map((cell) => cell.subjectId).join(',')}
          cells={allocationHeldCells}
          onReplace={(cell) =>
            allocationServerApi.setTeacher(dispatch, cell.subjectId, cell.teacherId).then(
              () => setAllocationServerRefreshKey((key) => key + 1),
              (error) => {
                toast.error(
                  error?.message ||
                    intl.formatMessage({id: 'ONBOARDING_WIZARD.TEACHER_ALLOCATION.HELD.FAILED'})
                )
                return Promise.reject(error)
              }
            )
          }
        />
        {/* The Teachers file's allocations, held while a picked file was on the card. */}
        {allocationPrefillHeld && !ownAllocationFileOnCard && !allocationPrefillSkipped && (
          <div className='d-flex justify-content-center mb-3'>
            <button
              type='button'
              className='ta-btn ta-btn--outline ta-btn--sm'
              onClick={() => {
                const held = allocationPrefillHeld
                applyAllocationPrefill(held.draftId, held.conversion)
              }}
            >
              {useAllocationsFrom('teachers')}
            </button>
          </div>
        )}
        {/* Allocations a Students upload read, waiting to be opened here. */}
        {waitingAllocationDraft && !excelImportFile && !allocationPrefillReading && (
          <div className='d-flex justify-content-center mb-3'>
            <button
              type='button'
              className='ta-btn ta-btn--outline ta-btn--sm'
              onClick={() => {
                const {draftId} = waitingAllocationDraft
                setWaitingAllocationDraft(null)
                openedAllocationDraftsRef.current.add(draftId)
                const file = carriedFileNamed(
                  'ONBOARDING_WIZARD.STEP4_MANUAL.LESSON_PLANNING.CARRIED_FILE'
                )
                openCarriedAllocation(file, draftId)
                startAllocationFile(file)
              }}
            >
              {useAllocationsFrom('students')}
            </button>
          </div>
        )}
        {/* Students a file here also listed, waiting for the Students step. */}
        {queuedStudentsFile && data.mode === 'subject' && (
          <div className='d-flex justify-content-center mb-3'>
            <button
              type='button'
              className='ta-btn ta-btn--outline ta-btn--sm'
              onClick={() => {
                setActiveTab('students')
                saveProgress('students')
              }}
            >
              {intl.formatMessage(
                {id: 'ONBOARDING_WIZARD.STEP4_MANUAL.LESSON_PLANNING.GO_TO_STUDENTS'},
                {count: queuedStudentsFile.count}
              )}
            </button>
          </div>
        )}
        {allocationPrefillReport && (
          <div className='mb-3'>
            <DataUploadReport
              conversion={allocationPrefillReport}
              isDark={isDark}
              onDismiss={() => {
                markAllocationPrefillHandled()
                setAllocationPrefillReport(null)
              }}
            />
          </div>
        )}
        {/* The sample template link is in the shared uploader's drop zone, as on Add Teachers;
            while the Teachers file's allocations are prepared the uploader is not shown, so it
            stays reachable here. */}
        {allocationPrefillShown && !ALLOCATION_UPLOADER_IN_CARD && (
          <div className='ta-import-area__template'>
            <TemplateDownloadLink
              className='ta-link'
              onDownload={handleDownloadExcelTemplate}
              busy={downloadingExcelTemplate}
            />
          </div>
        )}
      </div>
    )

    const settingsRow = cellSettings ? allocationRowByKey.get(cellSettings.classKey) : undefined
    const settingsCell = settingsRow
      ? lessonPlanningData[settingsRow.classKey]?.[cellSettings!.subject]
      : undefined
    const settingsDefaults = settingsRow
      ? allocationDefaultCell(settingsRow, cellSettings!.subject)
      : null

    const removal = allocationRemoval
    const removalDialog = (() => {
      if (!removal) return null
      if (removal.type === 'cell') {
        const hasTeacher = Boolean(
          (lessonPlanningData[removal.row.classKey]?.[removal.subject]?.teacher || '').trim()
        )
        const values = {
          subject: removal.subject,
          section: removal.row.label,
          branch: removal.row.branch,
        }
        return {
          title: ta('REMOVE.CELL_TITLE', values),
          messages: [
            [
              ta('REMOVE.CELL_TEXT', values),
              hasTeacher ? ta('REMOVE.CELL_TEXT_TEACHER') : '',
              ta('REMOVE.CELL_TEXT_KEEP', values),
            ]
              .filter(Boolean)
              .join(' '),
          ],
          confirm: ta('REMOVE.CELL_CONFIRM'),
        }
      }
      if (removal.type === 'row') {
        const values = {section: removal.row.label, branch: removal.row.branch}
        return {
          title: ta('REMOVE.ROW_TITLE', values),
          messages: [ta('REMOVE.ROW_TEXT', values)],
          confirm: ta('REMOVE.ROW_CONFIRM'),
        }
      }
      const values = {
        subject: removal.subject,
        count: removal.targets.length,
        scope: removal.scope,
      }
      const byFilter = {
        all: ['REMOVE.COLUMN_TITLE_ALL', 'REMOVE.COLUMN_TEXT_ALL'],
        assigned: ['REMOVE.COLUMN_TITLE_ASSIGNED', 'REMOVE.COLUMN_TEXT_ASSIGNED'],
        unassigned: ['REMOVE.COLUMN_TITLE_UNASSIGNED', 'REMOVE.COLUMN_TEXT_UNASSIGNED'],
      }[removal.filter]
      // Handover 3C: the title, the message (plus the Unassigned add-on) and the buttons only.
      return {
        title: ta(byFilter[0], values),
        messages: [
          ta(byFilter[1], values),
          ...(removal.filter === 'unassigned'
            ? [ta('REMOVE.COLUMN_TEXT_KEEP_ASSIGNED', values)]
            : []),
        ],
        confirm: ta('REMOVE.COLUMN_CONFIRM'),
      }
    })()

    const preview =
      gridPreviewMode && versi.pendingItem
        ? {
            changes: versi.previewChanges,
            count: versi.pendingItem.changes.length,
            isApplying: versi.isApplying,
            onApply: async () => {
              const outcome = await versi.apply()
              // A refreshed (stale) proposal is waiting in the conversation.
              if (outcome === 'stale') setAllocationView('ai')
              setGridPreviewMode(false)
            },
            onBackToConversation: () => {
              setGridPreviewMode(false)
              setAllocationView('ai')
            },
            onDiscard: () => {
              versi.discard()
              setGridPreviewMode(false)
            },
          }
        : null

    return (
      <div className='ta-root step4-manual-content step4-manual-content--lesson-planning'>
        <input
          ref={excelImportInputRef}
          type='file'
          accept={
            allocationConversionAvailable
              ? `${dataUploadAcceptAttribute(allocationUploadConfig.formats)},.xls`
              : '.csv,text/csv,.xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          }
          className='d-none'
          // Several files: read one after another, each on the card in turn.
          multiple
          onChange={(e) => {
            const input = e.target
            const picked = Array.from(input.files ?? [])
            input.value = ''
            handleAllocationFiles(picked)
          }}
        />

        {allocationServerMode && (
          <ServerTeacherAllocation
            onUnavailable={() => {
              // No server-side allocation here: the client flow takes over for this page load.
              markAllocationServerUnavailable()
              setAllocationServerMode(false)
            }}
            onBack={handleBackTab}
            backDisabled={!showBackButton || isGenerating || isSubmitting}
            onContinue={continueFromServerAllocation}
            continueBusy={isGenerating || isSubmitting}
            uploadFormats={
              allocationConversionAvailable
                ? [
                    ta('FORMAT.EXCEL'),
                    ta('FORMAT.CSV'),
                    ta('FORMAT.PDF'),
                    ta('FORMAT.WORD'),
                    ta('FORMAT.IMAGES'),
                  ]
                : [ta('FORMAT.EXCEL'), ta('FORMAT.CSV')]
            }
            uploadDisabled={allocationPrefillReading || allocationPrefillFetching}
            onUploadFiles={() => excelImportInputRef.current?.click()}
            importArea={importArea}
            uploadSlotRef={ALLOCATION_UPLOADER_IN_CARD ? setAllocationUploadSlot : undefined}
            guideUrl={TEACHER_ALLOCATION_GUIDE_URL}
            defaultFrequency={aiDefaultFrequencyStr}
            refreshKey={allocationServerRefreshKey}
            onTrack={trackAllocationEvent}
            onEdited={() => {
              allocationServerDirtyRef.current = true
              allocationServerEditedRef.current = true
            }}
            registerSaveLaterFlush={registerAllocationServerFlush}
          />
        )}

        {!allocationServerMode && allocationView === 'landing' && (
          <AllocationLanding
            counts={allocationCounts}
            prefilledCount={prefilledCount}
            uploadFormats={
              allocationConversionAvailable
                ? [
                    ta('FORMAT.EXCEL'),
                    ta('FORMAT.CSV'),
                    ta('FORMAT.PDF'),
                    ta('FORMAT.WORD'),
                    ta('FORMAT.IMAGES'),
                  ]
                : [ta('FORMAT.EXCEL'), ta('FORMAT.CSV')]
            }
            uploadDisabled={allocationPrefillReading || allocationPrefillFetching}
            guideUrl={TEACHER_ALLOCATION_GUIDE_URL}
            onOpenGrid={() => openAllocationGrid()}
            onOpenAi={() => openVersi(false)}
            onUploadFiles={() => excelImportInputRef.current?.click()}
            importArea={importArea}
            uploadSlotRef={ALLOCATION_UPLOADER_IN_CARD ? setAllocationUploadSlot : undefined}
            footer={footer(true)}
          />
        )}

        {!allocationServerMode && allocationView === 'grid' && (
          <AllocationGrid
            rows={allocationRows}
            grid={lessonPlanningData}
            deleted={deletedSlots}
            teachers={allocationTeacherOptions}
            importedSlots={importedSlots}
            counts={allocationCounts}
            hasImports={prefilledCount > 0}
            branch={resolvedGridBranch}
            level={resolvedGridLevel}
            onBranchChange={(value) => {
              setGridBranch(value)
              // Level options follow the branch; an unavailable level resets by the same rule.
              setGridLevel(resolveFilterValue(gridLevel, levelValues(allocationRows, value)))
            }}
            onLevelChange={setGridLevel}
            levelLabel={allocationLevelLabel}
            defaultFrequency={aiDefaultFrequencyStr}
            activeSlotKey={
              cellSettings
                ? buildAllocationSlotKey(cellSettings.classKey, cellSettings.subject)
                : null
            }
            preview={preview}
            onSelectTeacher={selectAllocationTeacher}
            onOpenSettings={(row, subject) => setCellSettings({classKey: row.classKey, subject})}
            onRemoveColumn={requestColumnRemoval}
            onRemoveRow={(row) => setAllocationRemoval({type: 'row', row})}
            onOpenAi={() => openVersi(true)}
            footer={footer(false)}
          />
        )}

        {!allocationServerMode && allocationView === 'ai' && (
          <VersiAllocationPage
            versi={versi}
            counts={allocationCounts}
            facts={allocationExampleFacts(
              rowsInScope(allocationRows, resolvedAiBranch, resolvedAiLevel),
              lessonPlanningData,
              deletedSlots
            )}
            rowByClassKey={allocationRowByKey}
            branchOptions={allocationBranchValues}
            levelOptions={levelValues(allocationRows, resolvedAiBranch)}
            branch={resolvedAiBranch}
            level={resolvedAiLevel}
            levelLabel={allocationLevelLabel}
            defaultFrequency={aiDefaultFrequencyStr}
            onBranchChange={(value) => {
              setAiBranch(value)
              setAiLevel(resolveFilterValue(aiLevel, levelValues(allocationRows, value)))
            }}
            onLevelChange={setAiLevel}
            onViewGrid={() => openAllocationGrid()}
            onPreviewInGrid={() => openAllocationGrid({preview: true})}
            footer={footer(false)}
          />
        )}

        {/* A file's students, once its allocations are in: save them only, or read the file on
            the Students step to choose their classes and subjects, or not. */}
        <NewPeopleModal
          show={Boolean(allocationNewStudents)}
          teachers={NO_NEW_TEACHERS}
          canContinueWithout
          assignScope='lines'
          students={allocationNewStudents?.students ?? NO_NEW_STUDENTS}
          submitting={savingAllocationStudents}
          onCancel={() => {
            // Not wanted: their carried draft is closed.
            closeCarriedDraft(allocationNewStudents?.draftId)
            setAllocationNewStudents(null)
          }}
          onConfirm={(answer) => {
            const pending = allocationNewStudents
            if (!pending) return
            if (answer.students === 'only') {
              setSavingAllocationStudents(true)
              saveNewStudentsToWizard(pending.students)
                .then(() => {
                  setSavingAllocationStudents(false)
                  setAllocationNewStudents(null)
                  closeCarriedDraft(pending.draftId)
                })
                // The save said why; the dialog stays open to try again.
                .catch(() => setSavingAllocationStudents(false))
              return
            }
            setAllocationNewStudents(null)
            if (answer.students === 'none') closeCarriedDraft(pending.draftId)
            if (answer.students === 'choose') {
              setQueuedStudentsFile({
                file: pending.file,
                count: pending.students.length,
                draftId: pending.draftId,
              })
              toast.info(
                intl.formatMessage({
                  id: 'ONBOARDING_WIZARD.STEP4_MANUAL.LESSON_PLANNING.STUDENTS_QUEUED',
                })
              )
            }
          }}
        />

        <CellSettingsPanel
          open={!allocationServerMode && Boolean(settingsRow) && allocationView === 'grid'}
          subject={cellSettings?.subject ?? ''}
          classLabel={settingsRow?.label ?? ''}
          branch={settingsRow?.branch ?? ''}
          notTaught={
            cellSettings
              ? deletedSlots.has(
                  buildAllocationSlotKey(cellSettings.classKey, cellSettings.subject)
                )
              : false
          }
          frequency={
            settingsCell?.frequency || settingsDefaults?.frequency || aiDefaultFrequencyStr
          }
          duration={settingsCell?.duration || settingsDefaults?.duration || '1'}
          readOnly={Boolean(preview)}
          onClose={() => setCellSettings(null)}
          onSave={({frequency, duration}) => {
            if (!cellSettings) return
            const {classKey, subject} = cellSettings
            setLessonPlanningData((prev) => {
              const existing = prev[classKey]?.[subject]
              return {
                ...prev,
                [classKey]: {
                  ...prev[classKey],
                  [subject]: {
                    teacher: existing?.teacher || '',
                    teacherEmployeeId: existing?.teacherEmployeeId || '',
                    frequency,
                    duration,
                  },
                },
              }
            })
            setCellSettings(null)
          }}
          onMarkNotTaught={() => {
            if (settingsRow && cellSettings) {
              setAllocationRemoval({type: 'cell', row: settingsRow, subject: cellSettings.subject})
            }
          }}
          onRestore={() => {
            if (cellSettings) restoreAllocationSlot(cellSettings.classKey, cellSettings.subject)
          }}
        />

        <AllocationConfirmDialog
          open={!allocationServerMode && Boolean(removalDialog)}
          title={removalDialog?.title ?? ''}
          messages={removalDialog?.messages ?? []}
          confirmLabel={removalDialog?.confirm ?? ''}
          onCancel={() => setAllocationRemoval(null)}
          onConfirm={confirmAllocationRemoval}
        />
      </div>
    )
  }

  const renderLessonPlanningPlaceholder = () => {
    const allClasses = (localData.classes as any[]) || []
    const teachers = (localData.teachers as any[]) || []
    const branches = Array.from(
      new Set(
        [
          ...branchOptions.map(normalizeBranchName),
          ...allClasses.map((c: any) => normalizeBranchName(c?.branch)),
        ].filter(Boolean)
      )
    )
    const resolvedActiveBranch =
      activeLessonBranch && branches.includes(activeLessonBranch)
        ? activeLessonBranch
        : branches[0] || 'Main'
    const classesByBranch = allClasses.filter(
      (c: any) => normalizeBranchName(c?.branch) === resolvedActiveBranch
    )
    const levels = Array.from(
      new Set(classesByBranch.map((c: any) => (c.level || '').trim()).filter(Boolean))
    ) as string[]
    const resolvedActiveLevel =
      activeLessonLevel && levels.includes(activeLessonLevel) ? activeLessonLevel : ''
    const classesAtResolvedLevel = resolvedActiveLevel
      ? classesByBranch.filter((c: any) => (c.level || '').trim() === resolvedActiveLevel)
      : classesByBranch

    const curriculaForResolvedLevel = uniqueStrings(
      classesAtResolvedLevel.map((c: any) => String(c?.curriculum || '').trim()).filter(Boolean)
    )

    const resolvedActiveCurriculum =
      activeLessonCurriculum && curriculaForResolvedLevel.includes(activeLessonCurriculum)
        ? activeLessonCurriculum
        : curriculaForResolvedLevel[0] || ''

    const classes =
      resolvedActiveLevel && curriculaForResolvedLevel.length > 1 && resolvedActiveCurriculum
        ? classesAtResolvedLevel.filter(
            (c: any) => String(c?.curriculum || '').trim() === resolvedActiveCurriculum
          )
        : classesAtResolvedLevel

    const textColor = isDark ? '#ffffff' : '#181C32'
    const mutedColor = isDark ? '#A7A8AA' : '#7E8299'
    const tableBorderColor = isDark ? '#2b2b40' : '#e4e6ef'
    const bodyBg = isDark ? '#151521' : '#ffffff'
    // brighter header blue to match reference
    const headerBg = '#0B79F0'
    const headerBlue = '#0B79F0'
    const classNameColumnBg = isDark ? '#15171C' : '#f5f8fa'
    const accentBlue = '#056EE9'
    // light sky-blue cell background (matches the chat UI's light blue tones)
    const cellBgOffset = isDark ? '#1f2530' : '#E8F4FC'
    const defaultFreq = data.manualData?.defaultSubjectFrequency
    const defaultPer = data.manualData?.defaultFrequencyPer || 'week'
    const defaultFrequencyStr =
      defaultFreq && defaultFreq >= 1 ? `${defaultFreq}/${defaultPer}` : '1/week'
    const defaultDurationStr = '1' // 1 slot per session (hardcoded)
    // Per-subject default: the curriculum prefill's weekly frequency for this class's level
    // (a heuristic — inferred from the subject's role, never published — so only ever a
    // starting point the school is expected to edit), else Step 2's institution-wide default.
    const storedPrefills = readStoredCurriculumPrefills()
    const defaultFrequencyFor = (className: string, subjectName: string): string => {
      const classItem = classes.find((c: any) => String(c?.name ?? '') === className)
      const hit = classItem
        ? cachedPrefillFrequency(
            storedPrefills,
            String(classItem?.curriculum ?? ''),
            String(classItem?.level ?? ''),
            subjectName
          )
        : null
      return hit ?? defaultFrequencyStr
    }

    const getTeacherFullName = (t: any) =>
      (t?._nameInput !== undefined
        ? String(t._nameInput || '').trim()
        : `${t?.first_name || ''} ${t?.last_name || ''}`.trim()
      ).trim() ||
      (t?.employeeId || '').toString().trim() ||
      ''

    const subjectNames = Array.from(
      new Set(classes.flatMap((classItem: any) => getSubjectsForClass(classItem)).filter(Boolean))
    )

    // Subjects that appear only because a user added them in Lesson Planning for a specific class.
    // If a subject isn't part of any class's "allowed" set (class subjects / curriculum-level subjects),
    // we treat it as dynamic-only and avoid showing "+" add actions for other classes.
    const dynamicOnlySubjects = (() => {
      const allowedAcrossClasses = new Set<string>()
      classes.forEach((classItem: any) => {
        const curriculumName = (classItem?.curriculum || '').toString().trim()
        const levelName = (classItem?.level || '').toString().trim()
        const allowed = (() => {
          if (Array.isArray(classItem?.subjects) && classItem.subjects.length > 0) {
            return uniqueStrings(
              classItem.subjects.map((s: any) => (typeof s === 'string' ? s : s?.name || ''))
            )
          }
          const levelSubjects = curriculumName
            ? getSubjectsForCurriculumLevel(curriculumName, levelName)
            : []
          if (levelSubjects.length > 0) return uniqueStrings(levelSubjects)
          return uniqueStrings(subjectOptions)
        })()
        allowed.forEach((sn) => allowedAcrossClasses.add(sn))
      })

      return new Set(subjectNames.filter((sn) => !allowedAcrossClasses.has(sn)))
    })()
    const classKeys = classes.map((c: any) =>
      getLessonClassKey(c.name || '', c.branch, c.curriculum)
    )

    // Helper to check if cell has any custom data (for edit modal/delete)
    const hasCellData = (className: string, subjectName: string): boolean => {
      const cellData = lessonPlanningData[className]?.[subjectName]
      return !!(cellData && (cellData.teacher || cellData.frequency || cellData.duration))
    }

    // Handle cell click to open edit modal for frequency/duration
    const handleCellClick = (className: string, subjectName: string) => {
      const cellData = lessonPlanningData[className]?.[subjectName] || {
        teacher: '',
        frequency: '',
        duration: '',
      }
      setLessonEditModal({
        show: true,
        className,
        subjectName,
        frequency: cellData.frequency || defaultFrequencyFor(className, subjectName),
        duration: cellData.duration || defaultDurationStr,
        hasExistingData: hasCellData(className, subjectName),
      })
    }

    const showAllLevelsError =
      lessonPlanAttempted &&
      levels.some((level) => !isLevelLessonPlanningComplete(resolvedActiveBranch, level))

    const gridLocked = Boolean(allocationAi.hasActionPreview || allocationAi.isLoading)

    // While the wizard is (re)hydrating from the server — initial resume or the post-Excel-import
    // refresh — the grid data isn't in yet; show a table skeleton instead of stale/empty cells.
    if (isHydrating) {
      return (
        <div
          className='step4-manual-content step4-manual-content--lesson-planning'
          style={{maxWidth: '100%', width: '100%'}}
        >
          <div className='text-center px-4 mb-4'>
            <h5 className='mb-2' style={{color: textColor, fontWeight: 600, fontSize: '1.35rem'}}>
              {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP4_MANUAL.LESSON_PLANNING.HEADING'})}
            </h5>
            <p className='mb-0' style={{color: mutedColor, fontSize: '0.98rem', lineHeight: 1.6}}>
              {intl.formatMessage({
                id: 'ONBOARDING_WIZARD.STEP4_MANUAL.LESSON_PLANNING.INSTRUCTION',
              })}
            </p>
          </div>
          <div className='p-4'>
            <CustomSkeleton variant={CARD_PRESET.TABLE} />
          </div>
        </div>
      )
    }

    return (
      <>
        <div
          className='step4-manual-content step4-manual-content--lesson-planning'
          style={{maxWidth: '100%', width: '100%'}}
        >
          <div className='text-center px-4 mb-4'>
            <h5 className='mb-2' style={{color: textColor, fontWeight: 600, fontSize: '1.35rem'}}>
              {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP4_MANUAL.LESSON_PLANNING.HEADING'})}
            </h5>
            <p className='mb-0' style={{color: mutedColor, fontSize: '0.98rem', lineHeight: 1.6}}>
              {intl.formatMessage({
                id: 'ONBOARDING_WIZARD.STEP4_MANUAL.LESSON_PLANNING.INSTRUCTION',
              })}
            </p>
          </div>

          {/* Excel import: same pair of actions as the Teachers tab, but wired to the Excel
              Import module's APIs. The file's status and summary show under the buttons. */}
          <div className='d-flex flex-wrap justify-content-center align-items-center gap-2 mb-3 px-3'>
            <input
              ref={excelImportInputRef}
              type='file'
              accept={
                allocationConversionAvailable
                  ? `${dataUploadAcceptAttribute(allocationUploadConfig.formats)},.xls`
                  : '.csv,text/csv,.xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
              }
              className='d-none'
              // Several files: read one after another, each on the card in turn.
              multiple
              onChange={(e) => {
                const input = e.target
                const picked = Array.from(input.files ?? [])
                input.value = ''
                handleAllocationFiles(picked)
              }}
            />
            <button
              type='button'
              className='btn btn-sm btn-light-primary d-inline-flex align-items-center gap-2'
              onClick={handleDownloadExcelTemplate}
              disabled={downloadingExcelTemplate}
            >
              {downloadingExcelTemplate ? (
                <span
                  className='spinner-border spinner-border-sm'
                  role='status'
                  aria-hidden='true'
                />
              ) : (
                <Download size={16} aria-hidden />
              )}
              {intl.formatMessage({
                id: 'ONBOARDING_WIZARD.STEP4_MANUAL.LESSON_PLANNING.DOWNLOAD_TEMPLATE',
              })}
            </button>
            <button
              type='button'
              className='btn btn-sm btn-primary d-inline-flex align-items-center gap-2'
              // The Teachers file's allocations are on their way and would replace a pick.
              disabled={allocationPrefillReading || allocationPrefillFetching}
              onClick={() => excelImportInputRef.current?.click()}
            >
              <Upload size={16} aria-hidden />
              {intl.formatMessage({
                id: allocationConversionAvailable
                  ? 'ONBOARDING_WIZARD.STEP4_MANUAL.LESSON_PLANNING.UPLOAD_DATA_FILE'
                  : 'ONBOARDING_WIZARD.STEP4_MANUAL.LESSON_PLANNING.UPLOAD_EXCEL',
              })}
            </button>
          </div>
          {allocationConversionAvailable && (
            <div className='text-center px-4 mb-3' style={{color: mutedColor, fontSize: '0.85rem'}}>
              <div>
                {intl.formatMessage(
                  {id: 'DATA_UPLOAD.CONVERT.ACCEPTED_FORMATS'},
                  {size: formatDataUploadBytes(allocationUploadConfig.maxFileBytes)}
                )}
              </div>
              <div>{intl.formatMessage({id: 'DATA_UPLOAD.CONVERT.PHOTO_TIPS'})}</div>
            </div>
          )}

          {/* Read first, in place of the card: the Teachers file's allocations then land on it
              (or ask which branch), instead of appearing later over an older file. */}
          {(allocationPrefillReading || allocationPrefillFetching) && !ownAllocationFileOnCard ? (
            <div
              className='d-flex flex-column align-items-center justify-content-center gap-1 mb-3'
              role='status'
              aria-live='polite'
              style={{color: mutedColor, fontSize: '0.9rem'}}
            >
              <div className='d-inline-flex align-items-center gap-2'>
                <span className='spinner-border spinner-border-sm text-primary' aria-hidden />
                {allocationPrefillRowsFound > 0
                  ? intl.formatMessage(
                      {id: 'ONBOARDING_WIZARD.STEP4_MANUAL.LESSON_PLANNING.PREFILL_READING_COUNT'},
                      {count: allocationPrefillRowsFound}
                    )
                  : intl.formatMessage({
                      id: 'ONBOARDING_WIZARD.STEP4_MANUAL.LESSON_PLANNING.PREFILL_READING',
                    })}
              </div>
              {renderPrefillProgress(allocationPrefillPercent)}
            </div>
          ) : (
            <AllocationFileImport
              file={excelImportFile}
              isDark={isDark}
              notice={
                excelImportFromPrefill
                  ? intl.formatMessage({
                      id: 'ONBOARDING_WIZARD.STEP4_MANUAL.LESSON_PLANNING.PREFILL_FOUND',
                    })
                  : undefined
              }
              onClose={() => {
                handedAllocationFileRef.current = null
                // The Teachers file's allocations, removed: not offered again.
                if (excelImportFromPrefill) markAllocationPrefillHandled()
                setExcelImportFile(null)
                setExcelImportFromPrefill(false)
                setAllocationBatch(null)
              }}
              onImported={handleExcelImported}
              lastImport={allocationLastImport}
              onClearLastImport={() => setAllocationLastImport(null)}
              onSaveNewTeachers={saveNewTeachersToWizard}
              hasStudentsStep={data.mode === 'subject'}
              extraFiles={allocationCompanionFiles}
              onRemoveFile={removeAllocationFile}
              onNewStudents={(file, students, draftId) =>
                setAllocationNewStudents({file, students, draftId})
              }
              onCarriedTeachers={(file, carried) =>
                setQueuedTeachersFile({file, draftId: carried.draftId})
              }
              knownStudentNumbers={
                new Set(
                  ((localData.students as any[]) || [])
                    .map((student: any) =>
                      String(student?.registrationNumber ?? '')
                        .trim()
                        .toLowerCase()
                    )
                    .filter(Boolean)
                )
              }
            />
          )}
          {!allocationPrefillReading &&
            !allocationPrefillFetching &&
            allocationPrefillSkipped &&
            !excelImportFile && (
              <div className='d-flex justify-content-center mb-3'>
                <button
                  type='button'
                  className='btn btn-sm btn-light-primary'
                  // The questions are the Teachers file's allocations': said on hover.
                  title={useAllocationsFrom('teachers')}
                  onClick={reopenAllocationPrefillQuestions}
                >
                  {allocationPrefillAnswerLabel(allocationPrefillSkipped)}
                </button>
              </div>
            )}
          {/* The Teachers file's allocations, held while a picked file was on the card. */}
          {allocationPrefillHeld && !ownAllocationFileOnCard && !allocationPrefillSkipped && (
            <div className='d-flex justify-content-center mb-3'>
              <button
                type='button'
                className='btn btn-sm btn-light-primary'
                onClick={() => {
                  const held = allocationPrefillHeld
                  applyAllocationPrefill(held.draftId, held.conversion)
                }}
              >
                {useAllocationsFrom('teachers')}
              </button>
            </div>
          )}
          {/* Allocations a Students upload read, waiting to be opened here. */}
          {waitingAllocationDraft && !excelImportFile && !allocationPrefillReading && (
            <div className='d-flex justify-content-center mb-3'>
              <button
                type='button'
                className='btn btn-sm btn-light-primary'
                onClick={() => {
                  const {draftId} = waitingAllocationDraft
                  setWaitingAllocationDraft(null)
                  openedAllocationDraftsRef.current.add(draftId)
                  const file = carriedFileNamed(
                    'ONBOARDING_WIZARD.STEP4_MANUAL.LESSON_PLANNING.CARRIED_FILE'
                  )
                  openCarriedAllocation(file, draftId)
                  startAllocationFile(file)
                }}
              >
                {useAllocationsFrom('students')}
              </button>
            </div>
          )}
          {/* Students a file here also listed, waiting for the Students step. */}
          {queuedStudentsFile && data.mode === 'subject' && (
            <div className='d-flex justify-content-center mb-3'>
              <button
                type='button'
                className='btn btn-sm btn-light-primary'
                onClick={() => {
                  setActiveTab('students')
                  saveProgress('students')
                }}
              >
                {intl.formatMessage(
                  {id: 'ONBOARDING_WIZARD.STEP4_MANUAL.LESSON_PLANNING.GO_TO_STUDENTS'},
                  {count: queuedStudentsFile.count}
                )}
              </button>
            </div>
          )}
          {/* A file's students, once its allocations are in: save them only, or read the file on
              the Students step to choose their classes and subjects, or not. */}
          <NewPeopleModal
            show={Boolean(allocationNewStudents)}
            teachers={NO_NEW_TEACHERS}
            canContinueWithout
            assignScope='lines'
            students={allocationNewStudents?.students ?? NO_NEW_STUDENTS}
            submitting={savingAllocationStudents}
            onCancel={() => {
              // Not wanted: their carried draft is closed.
              closeCarriedDraft(allocationNewStudents?.draftId)
              setAllocationNewStudents(null)
            }}
            onConfirm={(answer) => {
              const pending = allocationNewStudents
              if (!pending) return
              if (answer.students === 'only') {
                setSavingAllocationStudents(true)
                saveNewStudentsToWizard(pending.students)
                  .then(() => {
                    setSavingAllocationStudents(false)
                    setAllocationNewStudents(null)
                    closeCarriedDraft(pending.draftId)
                  })
                  // The save said why; the dialog stays open to try again.
                  .catch(() => setSavingAllocationStudents(false))
                return
              }
              setAllocationNewStudents(null)
              if (answer.students === 'none') closeCarriedDraft(pending.draftId)
              if (answer.students === 'choose') {
                setQueuedStudentsFile({
                  file: pending.file,
                  count: pending.students.length,
                  draftId: pending.draftId,
                })
                toast.info(
                  intl.formatMessage({
                    id: 'ONBOARDING_WIZARD.STEP4_MANUAL.LESSON_PLANNING.STUDENTS_QUEUED',
                  })
                )
              }
            }}
          />
          {allocationPrefillReport && (
            <div className='mb-3 px-3'>
              <DataUploadReport
                conversion={allocationPrefillReport}
                isDark={isDark}
                onDismiss={() => {
                  markAllocationPrefillHandled()
                  setAllocationPrefillReport(null)
                }}
              />
            </div>
          )}

          {(() => {
            // How much of the grid has a teacher: moves as slots are filled by hand, by AI,
            // by an Excel import or by the Teachers file's prefill.
            const {total, filled} = getLessonPlanningFill()
            if (total === 0) return null
            const percent = Math.round((filled / total) * 100)
            return (
              <div
                className='mx-auto mb-3 px-3'
                style={{maxWidth: 480, width: '100%'}}
                role='status'
                aria-live='polite'
              >
                <div
                  className='d-flex justify-content-between align-items-center mb-1'
                  style={{fontSize: '0.88rem'}}
                >
                  <span style={{color: textColor, fontWeight: 600}}>
                    {intl.formatMessage(
                      {id: 'ONBOARDING_WIZARD.STEP4_MANUAL.LESSON_PLANNING.FILL_PROGRESS'},
                      {filled, total}
                    )}
                  </span>
                  <span style={{color: mutedColor}}>{percent}%</span>
                </div>
                <div
                  className='progress'
                  style={{height: 6}}
                  role='progressbar'
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-valuenow={percent}
                >
                  <div
                    className={`progress-bar${percent === 100 ? ' bg-success' : ''}`}
                    style={{width: `${percent}%`}}
                  />
                </div>
              </div>
            )
          })()}

          <div className='step4-lesson-planning-layout d-flex flex-column gap-3'>
            <TeacherAllocationAiPanel
              isDark={isDark}
              isLoading={allocationAi.isLoading}
              isApplying={allocationAi.isApplying}
              // Always in front of the user on arrival at lesson planning, not tucked away
              // behind the floating button.
              defaultOpen
              onOpenStateChange={(open) => {
                if (open) {
                  aiUsedSinceOpenRef.current = false
                } else if (
                  !aiUsedSinceOpenRef.current &&
                  // A tenant that already applied an AI allocation this mount is
                  // an AI user — a later open-and-close must not brand them
                  // "opened but not used". (Analytics also derives usage-wins
                  // server-side for the cross-session order.)
                  !trackedAllocationEventsRef.current.has('ai_assignment')
                ) {
                  trackAllocationEvent('ai_opened_not_used')
                }
              }}
              disabled={(localData.teachers as any[])?.length === 0}
              scope={allocationAi.scope}
              onScopeChange={allocationAi.setScope}
              hasMultipleBranches={hasMultipleBranches}
              onAsk={allocationAi.askAi}
              hasPreview={Boolean(allocationAi.preview)}
              conversationHistory={allocationAi.conversationHistory}
              preview={allocationAi.preview}
              lastContext={allocationAi.lastContext}
              onApply={allocationAi.applyPreview}
              onCancel={allocationAi.cancelPreview}
              onResolveDisambiguation={allocationAi.resolveDisambiguation}
              onClearAll={allocationAi.clearAllGridChanges}
              onRevert={allocationAi.revertPreviousGridChange}
              onStartNewChat={allocationAi.startNewChat}
              canClearAll={allocationAi.canClearAll}
              canRevert={allocationAi.canRevert}
              canStartNewChat={allocationAi.canStartNewChat}
              closeSignal={aiWidgetCloseSignal}
              formatText={displayFreeText}
            />

            <div className='step4-lesson-planning-main flex-grow-1 min-w-0'>
              {branches.length > 0 && (
                <div className='step4-lesson-branch-tabs-wrap mb-3'>
                  <div
                    className='mb-2'
                    style={{
                      color: mutedColor,
                      fontSize: '0.8rem',
                      fontWeight: 700,
                      letterSpacing: '0.04em',
                      textTransform: 'uppercase',
                    }}
                  >
                    Branch
                  </div>
                  <div className='step4-lesson-branch-tabs'>
                    {branches.map((branch) => {
                      const isActiveBranch = branch === resolvedActiveBranch
                      const isBranchComplete = isBranchLessonPlanningComplete(branch)
                      const showBranchError = lessonPlanAttempted && !isBranchComplete
                      return (
                        <button
                          key={branch}
                          type='button'
                          className={`step4-lesson-branch-tab${isActiveBranch ? ' active' : ''}${showBranchError ? ' incomplete' : ''}`}
                          onClick={() => {
                            setIsLessonPlanningLoading(true)
                            setActiveLessonBranch(branch)
                            setActiveLessonLevel('')
                            setActiveLessonCurriculum('')
                            setSelectedRows([])
                            setSelectedCols([])
                          }}
                          title={
                            showBranchError
                              ? 'Incomplete - Please complete lesson planning for this branch'
                              : ''
                          }
                        >
                          <span className='d-inline-flex align-items-center gap-1'>
                            {showBranchError && <AlertTriangle size={12} strokeWidth={2.2} />}
                            <span>{branch}</span>
                          </span>
                        </button>
                      )
                    })}
                  </div>
                </div>
              )}

              {levels.length > 1 && (
                <div className='step4-lesson-branch-tabs-wrap mb-3'>
                  <div
                    className='mb-2'
                    style={{
                      color: mutedColor,
                      fontSize: '0.8rem',
                      fontWeight: 700,
                      letterSpacing: '0.04em',
                      textTransform: 'uppercase',
                    }}
                  >
                    Level
                  </div>
                  <div className='step4-lesson-branch-tabs'>
                    <button
                      type='button'
                      className={`step4-lesson-branch-tab${resolvedActiveLevel ? '' : ' active'}${showAllLevelsError ? ' incomplete' : ''}`}
                      onClick={() => {
                        setIsLessonPlanningLoading(true)
                        setActiveLessonLevel('')
                        setActiveLessonCurriculum('')
                        setSelectedRows([])
                        setSelectedCols([])
                      }}
                      title={
                        showAllLevelsError
                          ? 'Incomplete - Please complete lesson planning for one or more levels'
                          : ''
                      }
                    >
                      <span className='d-inline-flex align-items-center gap-1'>
                        {showAllLevelsError && <AlertTriangle size={12} strokeWidth={2.2} />}
                        <span>All</span>
                      </span>
                    </button>
                    {levels.map((level) => {
                      const isActiveLevel = level === resolvedActiveLevel
                      const isLevelComplete = isLevelLessonPlanningComplete(
                        resolvedActiveBranch,
                        level
                      )
                      const showLevelError = lessonPlanAttempted && !isLevelComplete
                      return (
                        <button
                          key={level}
                          type='button'
                          className={`step4-lesson-branch-tab${isActiveLevel ? ' active' : ''}${showLevelError ? ' incomplete' : ''}`}
                          onClick={() => {
                            setIsLessonPlanningLoading(true)
                            setActiveLessonLevel(level)
                            setActiveLessonCurriculum('')
                            setSelectedRows([])
                            setSelectedCols([])
                          }}
                          title={
                            showLevelError
                              ? 'Incomplete - Please complete lesson planning for this level'
                              : ''
                          }
                        >
                          <span className='d-inline-flex align-items-center gap-1'>
                            {showLevelError && <AlertTriangle size={12} strokeWidth={2.2} />}
                            <span>{displayName(level, curriculumOfLevel(level))}</span>
                          </span>
                        </button>
                      )
                    })}
                  </div>
                </div>
              )}

              {resolvedActiveLevel && curriculaForResolvedLevel.length > 1 && (
                <div className='step4-lesson-branch-tabs-wrap mb-3'>
                  <div
                    className='mb-2'
                    style={{
                      color: mutedColor,
                      fontSize: '0.8rem',
                      fontWeight: 700,
                      letterSpacing: '0.04em',
                      textTransform: 'uppercase',
                    }}
                  >
                    Curriculum
                  </div>
                  <div style={{maxWidth: 420}}>
                    <select
                      className='form-select form-select-solid'
                      value={resolvedActiveCurriculum}
                      onChange={(e) => {
                        setIsLessonPlanningLoading(true)
                        setActiveLessonCurriculum(e.target.value)
                        setSelectedRows([])
                        setSelectedCols([])
                      }}
                      style={{
                        backgroundColor: isDark ? '#262638' : undefined,
                        color: isDark ? '#ffffff' : undefined,
                        borderColor: isDark ? '#323248' : undefined,
                      }}
                    >
                      {curriculaForResolvedLevel.map((c) => (
                        <option key={c} value={c}>
                          {c}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              )}

              <div
                className='step4-lesson-planning-table-wrapper mb-4'
                style={{
                  border: `1px solid ${tableBorderColor}`,
                  borderRadius: 12,
                  overflow: 'hidden',
                  background: bodyBg,
                  boxShadow: isDark ? '0 10px 30px rgba(0,0,0,0.3)' : '0 5px 15px rgba(0,0,0,0.05)',
                  margin: '0 auto',
                  width: '100%',
                  position: 'relative',
                }}
              >
                {/* Check if any subjects are selected for current level */}
                {resolvedActiveLevel &&
                !hasSubjectsForLevelInBranch(resolvedActiveBranch, resolvedActiveLevel) ? (
                  <div
                    style={{
                      padding: '2rem',
                      textAlign: 'center',
                      background: bodyBg,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '1rem',
                    }}
                  >
                    <AlertTriangle size={40} color='#FFA500' strokeWidth={1.5} />
                    <div>
                      <h6 style={{color: textColor, fontWeight: 600, marginBottom: '0.5rem'}}>
                        {intl.formatMessage(
                          {
                            id: 'ONBOARDING_WIZARD.STEP4_MANUAL.LESSON_PLANNING.NO_SUBJECTS_FOR_LEVEL',
                          },
                          {
                            level: displayName(
                              resolvedActiveLevel,
                              resolvedActiveCurriculum || curriculumOfLevel(resolvedActiveLevel)
                            ),
                          }
                        )}
                      </h6>
                      <p
                        style={{
                          color: mutedColor,
                          fontSize: '0.9rem',
                          margin: 0,
                          lineHeight: 1.5,
                        }}
                      >
                        {intl.formatMessage({
                          id: 'ONBOARDING_WIZARD.STEP4_MANUAL.LESSON_PLANNING.NO_SUBJECTS_INSTRUCTION',
                        })}
                      </p>
                    </div>
                  </div>
                ) : (
                  <>
                    {/* Loading Spinner Overlay — only for the grid's own data load;
                        while the AI is thinking the chat widget shows its own
                        typing indicator, so the grid stays fully visible. */}
                    {isLessonPlanningLoading && (
                      <div
                        className='step4-lesson-planning-loading-overlay'
                        role='status'
                        aria-live='polite'
                        aria-label={intl.formatMessage({id: 'COMMON.LOADING'})}
                      >
                        <img
                          src={toAbsoluteUrl('media/logos/LogoAnim1.gif')}
                          alt={intl.formatMessage({id: 'ONBOARDING_WIZARD.LOADING'})}
                          className='onboarding-loading-logo'
                        />
                      </div>
                    )}
                    <div className='step4-lesson-planning-table-scroll'>
                      <table
                        className='table align-middle mb-0 step4-lesson-planning-table'
                        style={{
                          width: '100%',
                          tableLayout: 'fixed',
                          // Grow with the number of subject columns (class col 150 + 200 per subject)
                          // so wide grids overflow and scroll horizontally instead of squishing.
                          minWidth: `${150 + subjectNames.length * 200}px`,
                          borderCollapse: 'separate',
                          borderSpacing: 0,
                        }}
                      >
                        <thead style={{position: 'sticky', top: 0, zIndex: 10}}>
                          <tr>
                            <th
                              style={{
                                backgroundColor: headerBg,
                                color: '#ffffff',
                                fontWeight: 600,
                                fontSize: '14px',
                                padding: '12px',
                                textAlign: 'center',
                                position: 'sticky',
                                left: 0,
                                top: 0,
                                zIndex: 30, // Higher than row headers and column headers
                                borderRight: `1px solid #A7A7A7`,
                                borderBottom: `1px solid #A7A7A7`,
                                minWidth: '150px',
                                width: '150px',
                                boxShadow: '1px 1px 0 #A7A7A7',
                                cursor: 'pointer',
                              }}
                              onClick={(e) => {
                                e.stopPropagation()
                                toggleAllRows(classes)
                              }}
                            >
                              {intl.formatMessage({
                                id: 'ONBOARDING_WIZARD.STEP4_MANUAL.COL.CLASS_NAME',
                              })}
                            </th>
                            {subjectNames.map((subjectName: string, idx: number) => {
                              const colHasData = classes.some((ci: any) => {
                                const classKey = getLessonClassKey(
                                  ci.name || '',
                                  ci.branch,
                                  ci.curriculum
                                )
                                const classSubjects = getValidatedSubjectsForClass(ci)
                                return (
                                  classSubjects.includes(subjectName) &&
                                  !isSlotDeleted(classKey, subjectName)
                                )
                              })
                              return (
                                <th
                                  key={idx}
                                  style={{
                                    backgroundColor: headerBlue,
                                    color: '#ffffff',
                                    fontWeight: 500,
                                    fontSize: '14px',
                                    padding: '12px',
                                    textAlign: 'center',
                                    borderRight: `1px solid #A7A7A7`,
                                    borderBottom: `1px solid #A7A7A7`,
                                    minWidth: '200px',
                                    width: '200px',
                                    position: 'relative',
                                    cursor: 'pointer',
                                    top: 0,
                                    zIndex: 10,
                                  }}
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    if (
                                      (e.target as HTMLElement).closest(
                                        '.step4-lp-header-delete-btn'
                                      )
                                    )
                                      return
                                    toggleCol(subjectName)
                                  }}
                                >
                                  <span>{subjectName}</span>
                                  <button
                                    type='button'
                                    className='step4-lp-header-delete-btn'
                                    disabled={!colHasData}
                                    title={
                                      colHasData
                                        ? intl.formatMessage({
                                            id: 'ONBOARDING_WIZARD.STEP4_MANUAL.LESSON_PLANNING.DELETE_COL',
                                          })
                                        : 'No data to delete in this column'
                                    }
                                    onClick={(e) => {
                                      e.preventDefault()
                                      e.stopPropagation()
                                      if (!colHasData) return
                                      setDeleteRowColConfirm({type: 'col', name: subjectName})
                                    }}
                                    style={{
                                      position: 'absolute',
                                      top: 4,
                                      right: 4,
                                      padding: 2,
                                      border: 'none',
                                      background: 'none',
                                      borderRadius: 4,
                                      cursor: colHasData ? 'pointer' : 'not-allowed',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      color: '#ffffff',
                                      opacity: colHasData ? 1 : 0.35,
                                    }}
                                  >
                                    <CircleX size={14} strokeWidth={2.5} />
                                  </button>
                                </th>
                              )
                            })}
                          </tr>
                        </thead>
                        <tbody>
                          {classes.map((classItem: any, rowIdx: number) => {
                            const classKey = getLessonClassKey(
                              classItem.name || '',
                              classItem.branch,
                              classItem.curriculum
                            )
                            const classSubjects = getValidatedSubjectsForClass(classItem)
                            const rowSelected = selectedRows.includes(classKey)
                            const rowHasData = subjectNames.some(
                              (sn) => classSubjects.includes(sn) && !isSlotDeleted(classKey, sn)
                            )
                            return (
                              <tr key={classKey || rowIdx}>
                                <td
                                  style={{
                                    backgroundColor: classNameColumnBg,
                                    color: textColor,
                                    fontWeight: 500,
                                    fontSize: '14px',
                                    padding: '12px',
                                    textAlign: 'center',
                                    position: 'sticky',
                                    left: 0,
                                    zIndex: 5,
                                    borderRight: `1px solid #A7A7A7`,
                                    borderBottom: `1px solid #A7A7A7`,
                                    boxShadow: '1px 0 0 #A7A7A7',
                                    cursor: 'pointer',
                                  }}
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    if (
                                      (e.target as HTMLElement).closest(
                                        '.step4-lp-header-delete-btn'
                                      )
                                    ) {
                                      return
                                    }
                                    toggleRow(classKey)
                                  }}
                                >
                                  <span>{displayName(classItem.name, classItem.curriculum)}</span>
                                  <button
                                    type='button'
                                    className='step4-lp-header-delete-btn'
                                    disabled={!rowHasData}
                                    title={
                                      rowHasData
                                        ? intl.formatMessage({
                                            id: 'ONBOARDING_WIZARD.STEP4_MANUAL.LESSON_PLANNING.DELETE_ROW',
                                          })
                                        : 'No data to delete in this row'
                                    }
                                    onClick={(e) => {
                                      e.preventDefault()
                                      e.stopPropagation()
                                      if (!rowHasData) return
                                      setDeleteRowColConfirm({
                                        type: 'row',
                                        // Shown in the confirm text only; delete uses rowKey.
                                        name: displayName(classItem.name, classItem.curriculum),
                                        rowKey: classKey,
                                        rowSubjects: classSubjects,
                                      })
                                    }}
                                    style={{
                                      position: 'absolute',
                                      top: 4,
                                      right: 4,
                                      padding: 2,
                                      border: 'none',
                                      background: 'none',
                                      borderRadius: 4,
                                      cursor: rowHasData ? 'pointer' : 'not-allowed',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      color: textColor,
                                      opacity: rowHasData ? 1 : 0.35,
                                    }}
                                  >
                                    <CircleX size={14} strokeWidth={2.5} />
                                  </button>
                                </td>

                                {subjectNames.map((subjectName: string, colIdx: number) => {
                                  const cn = classKey
                                  const sn = subjectName
                                  const isApplicable = classSubjects.includes(sn)
                                  const isDynamicOnly = dynamicOnlySubjects.has(sn)
                                  const isSelected = rowSelected || selectedCols.includes(sn)
                                  const slotDeleted = isSlotDeleted(cn, sn)
                                  const cellData = lessonPlanningData[cn]?.[sn]
                                  const teacherName = cellData?.teacher || ''
                                  const teacherEmployeeId =
                                    (cellData as any)?.teacherEmployeeId || ''
                                  const frequency = cellData?.frequency || defaultFrequencyStr
                                  const duration = cellData?.duration || defaultDurationStr
                                  const cellHasError =
                                    lessonPlanAttempted &&
                                    !slotDeleted &&
                                    !teacherName &&
                                    !errorClearedSlots.has(slotKey(cn, sn))
                                  const isPreviewCell = allocationAi.previewSlotKeys.has(
                                    slotKey(cn, sn)
                                  )
                                  const incomingTeacherName = isPreviewCell
                                    ? allocationAi.previewIncomingTeachers.get(slotKey(cn, sn))
                                    : undefined
                                  const isAppliedCell = allocationAi.appliedSlotKeys.has(
                                    slotKey(cn, sn)
                                  )
                                  const isDiscardedCell = allocationAi.discardedSlotKeys.has(
                                    slotKey(cn, sn)
                                  )

                                  return (
                                    <td
                                      key={colIdx}
                                      className={
                                        isPreviewCell
                                          ? `step4-ai-preview-cell${
                                              incomingTeacherName
                                                ? ' step4-ai-preview-cell--incoming'
                                                : ''
                                            }`
                                          : isDiscardedCell
                                            ? 'step4-ai-discarded-cell'
                                            : isAppliedCell
                                              ? 'step4-ai-applied-cell'
                                              : undefined
                                      }
                                      style={{
                                        backgroundColor: cellBgOffset,
                                        padding: 0,
                                        textAlign: 'center',
                                        borderRight: `1px solid #A7A7A7`,
                                        borderBottom: `1px solid #A7A7A7`,
                                        boxShadow: isSelected
                                          ? 'inset 0 0 0 1px rgba(5, 110, 233, 0.45)'
                                          : undefined,
                                        cursor: gridLocked ? 'default' : 'pointer',
                                        verticalAlign: 'middle',
                                        minHeight: '44px',
                                        height: '72px',
                                        position: 'relative',
                                      }}
                                      onClick={() => {
                                        if (gridLocked) return
                                        if (slotDeleted) {
                                          setErrorClearedSlots((prev) =>
                                            new Set(prev).add(slotKey(cn, sn))
                                          )
                                          setDeletedSlots((prev) => {
                                            const next = new Set(prev)
                                            next.delete(slotKey(cn, sn))
                                            return next
                                          })
                                          return
                                        }
                                        if (!isApplicable) {
                                          // If this subject only exists because it was added for some other class
                                          // via Lesson Planning, don't allow adding it everywhere by default.
                                          if (isDynamicOnly) return
                                          const existing = lessonPlanningData[cn]?.[sn]
                                          setLessonPlanningData((prev) => ({
                                            ...prev,
                                            [cn]: {
                                              ...prev[cn],
                                              [sn]: {
                                                teacher: existing?.teacher || '',
                                                frequency:
                                                  existing?.frequency ||
                                                  defaultFrequencyFor(cn, sn),
                                                duration: existing?.duration || defaultDurationStr,
                                              },
                                            },
                                          }))
                                          return
                                        }
                                        handleCellClick(cn, sn)
                                      }}
                                    >
                                      {!isApplicable ? (
                                        <div
                                          style={{
                                            height: '100%',
                                            width: '100%',
                                            backgroundColor: cellBgOffset,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            color: accentBlue,
                                            opacity: isDynamicOnly ? 0.0 : 1,
                                          }}
                                        >
                                          {!isDynamicOnly ? (
                                            <Plus size={18} strokeWidth={2} />
                                          ) : null}
                                        </div>
                                      ) : slotDeleted ? (
                                        <div
                                          className='step4-empty-slot'
                                          style={{
                                            height: '100%',
                                            width: '100%',
                                            backgroundColor: cellBgOffset,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            color: accentBlue,
                                          }}
                                        >
                                          <Plus size={24} strokeWidth={2} />
                                        </div>
                                      ) : (
                                        <div
                                          className='draggable-event'
                                          style={{
                                            backgroundColor: isDark ? '#CECECE' : '#DBEAFE',
                                            color: textColor,
                                            borderRadius: 0,
                                            padding: '4px 6px',
                                            textAlign: 'center',
                                            position: 'relative',
                                            width: '100%',
                                            height: '100%',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            justifyContent: 'center',
                                            gap: '4px',
                                          }}
                                        >
                                          <button
                                            type='button'
                                            onClick={(e) => {
                                              e.stopPropagation()
                                              const slot = {
                                                className: cn,
                                                subjectName: sn,
                                              }
                                              if (
                                                localStorage.getItem(
                                                  LESSON_DELETE_SKIP_CONFIRM_KEY
                                                ) === 'true'
                                              ) {
                                                performDeleteSlot(slot)
                                              } else {
                                                deleteSlotRef.current = slot
                                                setDeleteSlotConfirm(slot)
                                              }
                                            }}
                                            className='step4-lp-slot-delete-btn'
                                            style={{
                                              position: 'absolute',
                                              top: 2,
                                              right: 2,
                                              padding: 2,
                                              border: 'none',
                                              background: 'none',
                                              borderRadius: 4,
                                              cursor: 'pointer',
                                              display: 'flex',
                                              alignItems: 'center',
                                              justifyContent: 'center',
                                              color: accentBlue,
                                            }}
                                            aria-label='Delete'
                                          >
                                            <CircleX size={14} strokeWidth={2.5} />
                                          </button>

                                          <div
                                            onClick={(e) => e.stopPropagation()}
                                            className='step4-ai-lp-teacher-slot'
                                            style={{
                                              maxWidth: 'calc(100% - 28px)',
                                              minWidth: 0,
                                              marginLeft: 6,
                                              position: 'relative',
                                              pointerEvents: gridLocked ? 'none' : undefined,
                                              opacity: gridLocked ? 0.75 : 1,
                                            }}
                                          >
                                            <TeacherSearchSelect
                                              isDark={isDark}
                                              teachers={teachers
                                                .map((t: any) => ({
                                                  name: getTeacherFullName(t),
                                                  employeeId:
                                                    (t.employeeId || '').toString().trim() ||
                                                    undefined,
                                                }))
                                                .filter((t) => t.name)}
                                              value={teacherName}
                                              selectedEmployeeId={
                                                (cellData as any)?.teacherEmployeeId || ''
                                              }
                                              hasError={cellHasError}
                                              onSelectOption={(opt) => {
                                                trackAllocationEvent('manual_assignment')
                                                const existing = lessonPlanningData[cn]?.[sn]
                                                setLessonPlanningData((prev) => ({
                                                  ...prev,
                                                  [cn]: {
                                                    ...prev[cn],
                                                    [sn]: {
                                                      teacher: opt.name,
                                                      teacherEmployeeId: opt.employeeId || '',
                                                      frequency:
                                                        existing?.frequency ||
                                                        defaultFrequencyFor(cn, sn),
                                                      duration:
                                                        existing?.duration || defaultDurationStr,
                                                    },
                                                  },
                                                }))
                                              }}
                                              onChange={(val) => {
                                                if (val.trim()) {
                                                  trackAllocationEvent('manual_assignment')
                                                }
                                                const existing = lessonPlanningData[cn]?.[sn]
                                                setLessonPlanningData((prev) => ({
                                                  ...prev,
                                                  [cn]: {
                                                    ...prev[cn],
                                                    [sn]: {
                                                      teacher: val,
                                                      frequency:
                                                        existing?.frequency ||
                                                        defaultFrequencyFor(cn, sn),
                                                      duration:
                                                        existing?.duration || defaultDurationStr,
                                                    },
                                                  },
                                                }))
                                              }}
                                            />
                                            {incomingTeacherName && (
                                              <div className='step4-ai-replace-incoming'>
                                                <span title={incomingTeacherName}>
                                                  {incomingTeacherName}
                                                </span>
                                              </div>
                                            )}
                                          </div>

                                          <div
                                            style={{
                                              display: 'flex',
                                              flexDirection: 'column',
                                              gap: '1px',
                                              fontSize: '10px',
                                              fontWeight: 600,
                                              opacity: 0.9,
                                              color: accentBlue,
                                            }}
                                          >
                                            <div>
                                              {intl.formatMessage(
                                                {
                                                  id: 'ONBOARDING_WIZARD.STEP4_MANUAL.LESSON_PLANNING.FREQUENCY_LABEL',
                                                },
                                                {value: frequency}
                                              )}
                                            </div>
                                            <div>
                                              {intl.formatMessage(
                                                {
                                                  id: 'ONBOARDING_WIZARD.STEP4_MANUAL.LESSON_PLANNING.DURATION_LABEL',
                                                },
                                                {value: duration}
                                              )}
                                            </div>
                                          </div>
                                        </div>
                                      )}
                                    </td>
                                  )
                                })}
                              </tr>
                            )
                          })}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </div>

              <div
                className='mb-4'
                style={{
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                }}
              >
                <button
                  type='button'
                  className='d-flex align-items-center justify-content-center p-2 rounded border-0'
                  style={{
                    backgroundColor: isDark ? 'rgba(43, 43, 64, 0.4)' : '#F5F8FA',
                    border: `1px solid ${tableBorderColor}`,
                    color: accentBlue,
                    cursor: 'pointer',
                    transition: 'opacity 0.2s ease',
                  }}
                  onClick={() => setInfoTooltipOpen((prev) => !prev)}
                  title={infoTooltipOpen ? 'Click to close' : 'Frequency & Duration'}
                >
                  <Info size={20} />
                </button>
                {infoTooltipOpen && (
                  <div
                    className='mt-2 p-4 rounded'
                    style={{
                      backgroundColor: isDark ? 'rgba(43, 43, 64, 0.4)' : '#F5F8FA',
                      border: `1px solid ${tableBorderColor}`,
                      borderRadius: 12,
                      width: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '0.5rem',
                    }}
                  >
                    <div className='d-flex align-items-center gap-2'>
                      <span style={{color: accentBlue}}>
                        <Plus size={16} />
                      </span>
                      <p className='mb-0' style={{color: mutedColor, fontSize: '0.9rem'}}>
                        <span style={{fontWeight: 700, color: textColor}}>Frequency</span> = how
                        many times the subject is taught in a day or week
                      </p>
                    </div>
                    <div className='d-flex align-items-center gap-2'>
                      <span style={{color: accentBlue}}>
                        <Plus size={16} />
                      </span>
                      <p className='mb-0' style={{color: mutedColor, fontSize: '0.9rem'}}>
                        <span style={{fontWeight: 700, color: textColor}}>Duration</span> = number
                        of consecutive lectures each day
                      </p>
                    </div>
                    <div className='d-flex align-items-center gap-2 mt-1'>
                      <p
                        className='mb-0 ms-4'
                        style={{
                          color: mutedColor,
                          fontSize: '0.85rem',
                          fontStyle: 'italic',
                          opacity: 0.8,
                        }}
                      >
                        {intl.formatMessage({
                          id: 'ONBOARDING_WIZARD.STEP4_MANUAL.LESSON_PLANNING.EXAMPLE_TEXT',
                        })}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className='d-flex justify-content-end gap-3 w-100 mt-4'>
            {showBackButton && (
              <button
                type='button'
                className='onboarding-btn-back me-auto'
                disabled={isGenerating || isSubmitting}
                onClick={handleBackTab}
              >
                <ArrowLeft size={16} className='me-2' />
                {intl.formatMessage({id: 'ONBOARDING_WIZARD.FOOTER.BACK'})}
              </button>
            )}
            {(() => {
              const lessonValid = isTabValid('lessonPlanning')
              const nextDisabled = isGenerating || isSubmitting || !lessonValid
              const tooltip =
                !isGenerating && !isSubmitting && !lessonValid
                  ? 'To enable, assign a teacher to every subject.'
                  : ''

              return (
                <span
                  style={{
                    display: 'inline-block',
                    cursor: nextDisabled ? 'not-allowed' : 'default',
                    position: 'relative',
                  }}
                  onMouseEnter={() => {
                    if (tooltip) setShowLessonNextHint(true)
                  }}
                  onMouseLeave={() => setShowLessonNextHint(false)}
                >
                  {showLessonNextHint && tooltip && (
                    <div
                      style={{
                        position: 'absolute',
                        right: 0,
                        bottom: 'calc(100% + 10px)',
                        background: 'rgba(0,0,0,0.85)',
                        color: '#fff',
                        padding: '8px 10px',
                        borderRadius: 8,
                        fontSize: 12,
                        maxWidth: 260,
                        lineHeight: 1.35,
                        boxShadow: '0 8px 24px rgba(0,0,0,0.35)',
                        zIndex: 9999,
                        pointerEvents: 'none',
                      }}
                      role='tooltip'
                    >
                      {tooltip}
                    </div>
                  )}
                  <button
                    type='button'
                    className='onboarding-btn-next'
                    disabled={nextDisabled}
                    style={nextDisabled ? {pointerEvents: 'none'} : undefined}
                    onClick={() => {
                      setLessonPlanAttempted(true)
                      const valid = isTabValid('lessonPlanning')
                      if (!valid) {
                        // Check if the issue is missing subjects for a level
                        const levelWithoutSubjects = getFirstLevelWithoutSubjects()
                        if (levelWithoutSubjects) {
                          toast.error(
                            intl.formatMessage(
                              {
                                id: 'ONBOARDING_WIZARD.STEP4_MANUAL.LESSON_PLANNING.NO_SUBJECTS_VALIDATION_ERROR',
                              },
                              {
                                level: displayName(
                                  levelWithoutSubjects,
                                  curriculumOfLevel(levelWithoutSubjects)
                                ),
                              }
                            )
                          )
                        } else {
                          toast.error(
                            intl.formatMessage({
                              id: 'ONBOARDING_WIZARD.STEP4_MANUAL.LESSON_PLANNING.VALIDATION_ERROR',
                              defaultMessage:
                                'Please assign a teacher to every subject before proceeding.',
                            })
                          )
                        }
                        return
                      }
                      // Collapse the AI allocation chatbot before advancing.
                      setAiWidgetCloseSignal((n) => n + 1)
                      if (isLastTab) handleFinishStep()
                      else handleNextTab()
                    }}
                  >
                    {isGenerating || isSubmitting ? (
                      <>
                        <span className='spinner-border spinner-border-sm align-middle me-2' />
                        {intl.formatMessage({id: 'COMMON.LOADING'})}
                      </>
                    ) : (
                      <>
                        {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP4_MANUAL.BTN_NEXT'})}
                        <ArrowRight size={16} className='ms-2' />
                      </>
                    )}
                  </button>
                </span>
              )
            })()}
          </div>
        </div>

        {/* Lesson Edit Sheet (right-side panel) */}
        <LessonPlanEditSheet
          show={lessonEditModal.show}
          isDark={isDark}
          onHide={() =>
            setLessonEditModal({
              show: false,
              className: '',
              subjectName: '',
              frequency: '',
              duration: '',
              hasExistingData: false,
            })
          }
          className={lessonEditModal.className}
          classLabel={(() => {
            const parts = String(lessonEditModal.className ?? '').split('|||')
            return displayName(parts[parts.length - 1], parts.length >= 3 ? parts[1] : undefined)
          })()}
          subjectName={lessonEditModal.subjectName}
          frequency={lessonEditModal.frequency}
          duration={lessonEditModal.duration}
          onSave={({frequency: f, duration: d}) => {
            const {className: cn, subjectName: sn} = lessonEditModal
            const existingTeacher = lessonPlanningData[cn]?.[sn]?.teacher || ''
            setLessonPlanningData((prev) => ({
              ...prev,
              [cn]: {
                ...prev[cn],
                [sn]: {teacher: existingTeacher, frequency: f, duration: d},
              },
            }))
            setLessonEditModal({
              show: false,
              className: '',
              subjectName: '',
              frequency: '',
              duration: '',
              hasExistingData: false,
            })
          }}
        />

        {/* Delete slot confirmation modal */}
        <Modal
          show={!!deleteSlotConfirm}
          onHide={() => {
            setDeleteSlotConfirm(null)
            setDonAskAgainDelete(false)
          }}
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title>{intl.formatMessage({id: 'GENERAL.DELETE'})}</Modal.Title>
          </Modal.Header>
          <Modal.Body className='py-4'>
            {deleteSlotConfirm && (
              <p className='mb-0'>
                {intl.formatMessage({
                  id: 'ONBOARDING_WIZARD.STEP4_MANUAL.LESSON_DELETE_CONFIRM',
                })}{' '}
                ?
              </p>
            )}
          </Modal.Body>
          <Modal.Footer className='d-flex justify-content-between align-items-center'>
            <label className='form-check form-check-custom form-check-solid mb-0 me-auto'>
              <input
                className='form-check-input'
                type='checkbox'
                checked={donAskAgainDelete}
                onChange={(e) => setDonAskAgainDelete(e.target.checked)}
              />
              <span className='form-check-label'>
                {intl.formatMessage({
                  id: 'ONBOARDING_WIZARD.STEP4_MANUAL.LESSON_DELETE_DONT_ASK_AGAIN',
                })}
              </span>
            </label>
            <div className='d-flex gap-2'>
              <button
                type='button'
                className='btn btn-light'
                onClick={() => {
                  setDeleteSlotConfirm(null)
                  setDonAskAgainDelete(false)
                }}
              >
                {intl.formatMessage({id: 'GENERAL.CANCEL'})}
              </button>
              <button
                type='button'
                className='btn btn-danger'
                onClick={() => {
                  const slot = deleteSlotRef.current || deleteSlotConfirm
                  if (slot) {
                    if (donAskAgainDelete) {
                      localStorage.setItem(LESSON_DELETE_SKIP_CONFIRM_KEY, 'true')
                    }
                    performDeleteSlot(slot)
                    setDonAskAgainDelete(false)
                  }
                }}
              >
                {intl.formatMessage({id: 'GENERAL.DELETE'})}
              </button>
            </div>
          </Modal.Footer>
        </Modal>

        {/* Delete row/column confirmation modal */}
        <Modal show={!!deleteRowColConfirm} onHide={() => setDeleteRowColConfirm(null)} centered>
          <Modal.Header closeButton>
            <Modal.Title>{intl.formatMessage({id: 'GENERAL.DELETE'})}</Modal.Title>
          </Modal.Header>
          <Modal.Body className='py-4'>
            {deleteRowColConfirm && (
              <p className='mb-0'>
                {deleteRowColConfirm.type === 'row'
                  ? intl.formatMessage(
                      {id: 'ONBOARDING_WIZARD.STEP4_MANUAL.LESSON_PLANNING.DELETE_ROW_CONFIRM'},
                      {name: deleteRowColConfirm.name}
                    )
                  : intl.formatMessage(
                      {id: 'ONBOARDING_WIZARD.STEP4_MANUAL.LESSON_PLANNING.DELETE_COL_CONFIRM'},
                      {name: deleteRowColConfirm.name}
                    )}
              </p>
            )}
          </Modal.Body>
          <Modal.Footer>
            <button
              type='button'
              className='btn btn-light'
              onClick={() => setDeleteRowColConfirm(null)}
            >
              {intl.formatMessage({id: 'GENERAL.CANCEL'})}
            </button>
            <button
              type='button'
              className='btn btn-danger'
              onClick={() => {
                if (deleteRowColConfirm) {
                  if (deleteRowColConfirm.type === 'row') {
                    const targetRowKey = deleteRowColConfirm.rowKey || deleteRowColConfirm.name
                    const targetSubjects = deleteRowColConfirm.rowSubjects || subjectNames
                    deleteRow(targetRowKey, targetSubjects)
                  } else {
                    deleteColumn(deleteRowColConfirm.name, classKeys)
                  }
                  setDeleteRowColConfirm(null)
                }
              }}
            >
              {intl.formatMessage({id: 'GENERAL.DELETE'})}
            </button>
          </Modal.Footer>
        </Modal>
      </>
    )
  }

  const stepperSteps = useMemo(() => {
    const out: Array<{
      id: string
      topTabId: string
      classesFlowStep?: number
      label: string
    }> = []

    // Keep stepper order aligned with `tabs` order, but expand the `classes` tab
    // into its internal flow (Classes -> Sections -> Review).
    for (const tab of tabs) {
      if (tab.id === 'teachers') {
        // Review Teachers is still its own sub-step (URL/tracking), but has no stepper label —
        // the Teachers step stays highlighted while the review screen is open.
        out.push({id: 'teachers', topTabId: 'teachers', label: tab.label})
      } else if (tab.id === 'classes') {
        out.push({
          id: 'classes-classes',
          topTabId: 'classes',
          classesFlowStep: 2,
          label: tab.label || 'Classes',
        })
        out.push({
          id: 'classes-sections',
          topTabId: 'classes',
          classesFlowStep: 1,
          label: 'Sections',
        })
        out.push({
          id: 'classes-review',
          topTabId: 'classes',
          classesFlowStep: 3,
          label: 'Review',
        })
      } else if (tab.id === 'subjects') {
        out.push({id: 'subjects', topTabId: 'subjects', label: tab.label})
      } else if (tab.id === 'lessonPlanning') {
        out.push({
          id: 'lessonPlanning',
          topTabId: 'lessonPlanning',
          label: tab.label,
        })
      } else if (tab.id === 'students') {
        out.push({id: 'students', topTabId: 'students', label: tab.label})
      }
    }

    return out
  }, [tabs, intl])

  const activeStepId = useMemo(() => {
    if (activeTab === 'classes') {
      if (classesFlowStep === 1) return 'classes-sections'
      if (classesFlowStep === 2) return 'classes-classes'
      return 'classes-review'
    }
    return activeTab
  }, [activeTab, classesFlowStep])

  const effectiveStepperSteps = stepperOverride?.steps ?? stepperSteps
  const effectiveActiveStepId = useMemo(() => {
    if (!stepperOverride) return activeStepId

    const ids = new Set(stepperOverride.steps.map((s) => s.id))
    // When Step4Manual is used inside Step 3 (Curriculum flow), the stepper should
    // reflect the live internal state (activeTab/classesFlowStep) rather than relying
    // on persisted wizard state that may lag behind.
    if (activeTab === 'subjects' && ids.has('subjects')) return 'subjects'
    if (activeTab === 'classes') {
      if (classesFlowStep === 1 && ids.has('sections')) return 'sections'
      if (classesFlowStep === 3 && ids.has('review')) return 'review'
      if (ids.has('classes')) return 'classes'
    }
    return stepperOverride.activeStepId ?? activeStepId
  }, [activeStepId, activeTab, classesFlowStep, stepperOverride])
  const activeIdx = effectiveStepperSteps.findIndex((s) => s.id === effectiveActiveStepId)

  return (
    <div
      className='d-flex flex-column align-items-center h-100 step4-manual'
      style={{paddingTop: '0.5rem'}}
    >
      <div
        className={`step4-stepper-wrapper mb-3${stepperOverride ? ' step4-stepper-wrapper--curriculum' : ''}`}
      >
        <HorizontalStepper
          isDark={isDark}
          labels={effectiveStepperSteps.map((s) => s.label)}
          activeIndex={Math.max(0, activeIdx)}
          maxReachedIndex={Math.max(0, activeIdx)}
          completing={Boolean(isGenerating)}
          isStepClickable={(idx) => {
            if (idx > activeIdx) return false
            const step = effectiveStepperSteps[idx]
            if (!step) return false
            if (stepperOverride?.onStepClick) return true
            return Boolean(step.topTabId)
          }}
          onStepClick={(idx) => {
            if (idx > activeIdx) return
            const step = effectiveStepperSteps[idx]
            if (!step) return
            if (stepperOverride?.onStepClick) {
              stepperOverride.onStepClick(step.id)
              // Don't return here: in Step3Curriculum the override only handles
              // "curriculum" and "levels". For "classes/sections/review/subjects",
              // Step4Manual must still perform internal navigation.
            }
            if (!step.topTabId) return
            if (step.topTabId === 'classes' && typeof step.classesFlowStep === 'number') {
              setActiveTab('classes')
              goToClassesFlowStep(step.classesFlowStep)
              return
            }
            if (step.topTabId === 'teachers') {
              goToTeachersPhase('entry')
            }
            setActiveTab(step.topTabId)
            saveProgress(step.topTabId)
          }}
        />
      </div>

      <div
        className={`flex-grow-1 w-100 d-flex flex-column align-items-center step4-content-area ${activeTab === 'teachers' ? 'step4-tab-teachers' : 'step4-tab-others'}`}
      >
        {activeTab === 'teachers' && renderTeachersUI()}
        {activeTab === 'classes' && renderClassesUI()}
        {activeTab === 'subjects' && renderSubjectsUI()}
        {activeTab === 'lessonPlanning' &&
          (TEACHER_ALLOCATION_V2 ? renderTeacherAllocation() : renderLessonPlanningPlaceholder())}
        {activeTab === 'students' && renderStudentsUI()}
      </div>

      <Modal
        show={!!pendingDeleteAction}
        onHide={() => {
          setPendingDeleteAction(null)
          setDonAskAgainDeleteRow(false)
        }}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>{intl.formatMessage({id: 'GENERAL.DELETE'})}</Modal.Title>
        </Modal.Header>
        {/* One-line confirmation: the theme's default modal-body padding leaves a tall empty
            band above and below it, so tighten the vertical padding. */}
        <Modal.Body className='py-4'>
          {pendingDeleteAction?.type === 'students-prefill'
            ? intl.formatMessage(
                {id: 'ONBOARDING_WIZARD.STEP4_MANUAL.STUDENTS.PREFILL_REMOVE_CONFIRM'},
                {
                  count: studentsPrefillRows().length,
                  saved: studentsPrefillRows().filter(isSavedStudent).length,
                }
              )
            : pendingDeleteAction?.type === 'category-row' &&
                pendingDeleteAction.category === 'students' &&
                isSavedStudent(((localData.students as any[]) || [])[pendingDeleteAction.index])
              ? intl.formatMessage({
                  id: 'ONBOARDING_WIZARD.STEP4_MANUAL.STUDENTS.DELETE_SAVED_CONFIRM',
                })
              : pendingDeleteAction?.type === 'student-rows'
                ? intl.formatMessage(
                    {id: 'ONBOARDING_WIZARD.STEP4_MANUAL.STUDENTS.BULK_DELETE_CONFIRM'},
                    {
                      count: pendingDeleteAction.indices.length,
                      saved: pendingDeleteAction.indices.filter((i) =>
                        isSavedStudent(((localData.students as any[]) || [])[i])
                      ).length,
                    }
                  )
                : pendingDeleteAction?.type === 'teacher-rows' &&
                    pendingDeleteAction.indices.length > 1
                  ? intl.formatMessage(
                      {id: 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.REVIEW.BULK_DELETE_CONFIRM'},
                      {count: pendingDeleteAction.indices.length}
                    )
                  : intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP4_MANUAL.DELETE_ROW_CONFIRM'})}
        </Modal.Body>
        <Modal.Footer className='d-flex justify-content-between align-items-center'>
          <label
            className='form-check form-check-custom form-check-solid mb-0 me-auto'
            // Deleting from the school is always confirmed: no "don't ask again" there.
            style={alwaysConfirmDelete(pendingDeleteAction) ? {visibility: 'hidden'} : undefined}
          >
            <input
              className='form-check-input'
              type='checkbox'
              checked={donAskAgainDeleteRow}
              onChange={(e) => setDonAskAgainDeleteRow(e.target.checked)}
            />
            <span className='form-check-label'>
              {intl.formatMessage({
                id: 'ONBOARDING_WIZARD.STEP4_MANUAL.LESSON_DELETE_DONT_ASK_AGAIN',
              })}
            </span>
          </label>
          <div className='d-flex gap-2'>
            <button
              type='button'
              className='btn btn-light'
              onClick={() => {
                setPendingDeleteAction(null)
                setDonAskAgainDeleteRow(false)
              }}
            >
              {intl.formatMessage({id: 'GENERAL.CANCEL'})}
            </button>
            <button type='button' className='btn btn-danger' onClick={handleConfirmDeleteAction}>
              {intl.formatMessage({id: 'GENERAL.DELETE'})}
            </button>
          </div>
        </Modal.Footer>
      </Modal>

      {/* The allocation prefill's "which branch?" (wording from `rowNoun`: allocations) and
          "which classes?". Confirm fetches its template again with every answer so far, then
          asks what is still open or opens the import. Skip answers the classes with none (those
          lines are left out) and fetches it again, so the rest imports; a branch question left
          alone keeps it behind "Answer questions". The X keeps every question there too, and
          fetches nothing. */}
      <ColumnAssignmentModal
        show={Boolean(allocationPrefillQuestion)}
        columns={null}
        branches={allocationPrefillQuestion?.questions ?? NO_BRANCH_QUESTIONS}
        allocations={allocationPrefillQuestion?.allocations ?? NO_ALLOCATION_QUESTIONS}
        subjects={null}
        submitting={allocationPrefillAnswering}
        onClose={() => {
          // Closed, not answered: offered again from the button, as a skipped branch question.
          const pending = allocationPrefillQuestion
          if (!pending) return
          setAllocationPrefillQuestion(null)
          skippedAllocationDraftsRef.current.add(pending.draftId)
          setAllocationPrefillSkipped(pending)
        }}
        onSkip={() => {
          const pending = allocationPrefillQuestion
          if (!pending) return
          const skipped = skippedAllocationAnswers(pending.allocations)
          // Only "which branch?": nothing to answer for the user; offered again from the button.
          if (skipped.length === 0) {
            setAllocationPrefillQuestion(null)
            skippedAllocationDraftsRef.current.add(pending.draftId)
            setAllocationPrefillSkipped(pending)
            return
          }
          // A branch question too stays unanswered: after the fetch it waits behind the button.
          if (pending.questions.length > 0) skippedAllocationDraftsRef.current.add(pending.draftId)
          const skippedKeys = new Set(skipped.map(allocationAnswerKey))
          const merged: AllocationPrefillAnswers = {
            branches: pending.answers.branches,
            allocations: [
              ...pending.answers.allocations.filter(
                (answer) => !skippedKeys.has(allocationAnswerKey(answer))
              ),
              ...skipped,
            ],
          }
          rememberAllocationPrefillAnswers(pending.draftId, merged)
          setAllocationPrefillAnswering(true)
          fetchAllocationPrefill(pending.draftId, merged)
            .then((conversion) => {
              setAllocationPrefillQuestion(null)
              if (conversion) continueAllocationPrefill(pending.draftId, conversion, merged)
            })
            .catch(allocationPrefillFailed)
            .finally(() => setAllocationPrefillAnswering(false))
        }}
        onConfirm={(answers) => {
          const pending = allocationPrefillQuestion
          if (!pending) return
          // A re-answered teacher + subject replaces its earlier answer.
          const answeredNow = new Set(answers.allocations.map(allocationAnswerKey))
          const merged: AllocationPrefillAnswers = {
            branches: {...pending.answers.branches, ...answers.branches},
            allocations: [
              ...pending.answers.allocations.filter(
                (answer) => !answeredNow.has(allocationAnswerKey(answer))
              ),
              ...answers.allocations,
            ],
          }
          rememberAllocationPrefillAnswers(pending.draftId, merged)
          setAllocationPrefillAnswering(true)
          fetchAllocationPrefill(pending.draftId, merged)
            .then((conversion) => {
              setAllocationPrefillQuestion(null)
              if (conversion) continueAllocationPrefill(pending.draftId, conversion, merged)
            })
            .catch(allocationPrefillFailed)
            .finally(() => setAllocationPrefillAnswering(false))
        }}
      />
    </div>
  )
}

export default Step4Manual
